// ═══════════════════════════════════════════════════════════════
// ARCHIVO DESTINO: frontend/src/Admin.jsx
// ═══════════════════════════════════════════════════════════════
import { useState, useRef, useEffect } from "react";
import { getFrontendConfig } from "./config";
import { fechaHora, fechaCorta, horaLocal } from "./fechas";
import CameraCapture from "./CameraCapture";
import { oscuro as Q, F_BODY, F_MONO, RADII, SPACING, TEXT , ELEVATION, inkOn } from "./theme";
import { money, codigoDeMoneda } from "./moneda";
import BrandMark from "./BrandMark";
import Icon from "./Icon";
import PageHeader from "./PageHeader";
import LineaTiempo from "./LineaTiempo";
import { Zap, Gift, Handshake, Video, ArrowLeftRight, Ban, Banknote, Bell, Bot, Building2, Calendar, CalendarDays, CircleOff, Coins, Dices, Disc, Eye, Flame, FlaskConical, Gamepad2, GitBranch, Globe, Hand, Headphones, Image as ImageIcon, Inbox, Key, Link, Lock, Mail, Megaphone, MessageSquare, Monitor, PartyPopper, PenLine, Pencil, Plug, Printer, RefreshCw, Rocket, RotateCcw, Save, Scale, Shield, Smartphone, Star, Stethoscope, Store, Target, Trash2, TrendingDown, Volume2, VolumeX, Wrench } from "lucide-react";
import FilaProveedor from "./FilaProveedor";
import {
  estadoInicial, recargar, iniciarCambio, cambioConfirmado, cambioFallido,
  cambiarActiva, requiereConfirmacion, textoConfirmacionApagado,
} from "./proveedoresApi";
import { useDesktopShellWidth } from "./desktopShellLayout";
// La cuota de una combinada la cotiza el servidor y este hook es el único
// lugar del frontend que la pregunta. El armador de combos de la casa
// mostraba un producto pelado y después le pedía al servidor que guardara ese
// precio; con dos mercados del mismo partido los dos números no coinciden.
// Ver `odd/tasks/cuota-del-combo.md`.
import { useCuotaCombinada } from "./combosMismoPartido";
import { SelectorDesplegable } from "./filtrosDeEvento";
// La fila de cuotas y el diccionario de mercados en español salen del
// módulo compartido del sportsbook, el mismo que importan `App.jsx` y
// `Agencia.jsx`. El panel los usa en modo informativo: ver
// `cuotasDeEvento.jsx`, arriba de `OddsButtons`.
import { OddsButtons, tituloMercado } from "./cuotasDeEvento";
import MobileTabMenu from "./MobileTabMenu";
import {
  AMBITO_SMS, AMBITO_CORREO, CANAL_WHATSAPP, NOMBRE_PROVEEDOR,
  etiquetaOrigen,
  listarMensajeria, guardarCredencial, borrarCredencial, probarEnvio,
  listarPaises, listarRemitentes, guardarRemitente, borrarRemitente,
  paisesSinCobertura, whatsappListo,
} from "./mensajeriaApi";

// Atajo para los montos que todavia no saben de que moneda son. Delega en
// el formateador unico; M4-M6 cambian cada llamada por money(n, moneda).
const ars = n => money(n, "ARS");

// ── CÓMO SE DIBUJA LA PLATA DE UNA LISTA ──────────────────────
//
// EL DEFECTO QUE VINO A ARREGLAR: el 2026-10-22 el dueño creó la agencia
// `AGE002` en dólares y "ÚLTIMOS MOVIMIENTOS" le mostró `$-500`, `$500` y
// `$2.000`. Dólares pintados con el signo de los pesos. Los barridos
// anteriores no lo vieron porque buscaban `SUM(`: éstos no agrupan nada, son
// listas que dibujan una fila por entidad.
//
// POR FILA Y NO POR BLOQUE. Estas listas mezclan agencias renglón a renglón,
// así que un cartelito arriba diría "acá hay dólares" sin decir CUÁL de los
// `$-500` lo es, que es exactamente la pregunta. Cada monto se formatea con
// la moneda de SU fila.
//
// EL CÓDIGO APARECE SÓLO CUANDO HAY CON QUÉ CONFUNDIRSE, y la unidad de
// decisión es la lista entera, no la fila: con una sola moneda la pantalla
// se ve IGUAL que hoy —ninguna etiqueta nueva, que es la garantía de M1— y
// con dos o más lo llevan todas, porque el riesgo no es confundir dos
// monedas dentro de una fila, es leer el `$` de una fila en pesos al lado
// del de otra en dólares, en la misma columna.
//
// Es la MISMA regla que ya usan `TabGlobal` con `por_moneda` y la lista de
// proveedores de casino de este archivo (`variasMonedas`). Una segunda regla
// en la misma pantalla sería la trampa de `pantalla-y-saldo-de-acuerdo` en
// versión visual: dos lecturas distintas del mismo dato, confirmándose.
const formatoDeLista = (filas, campo = "moneda") => {
  const monedas = new Set();
  for (const f of (filas || [])) monedas.add(codigoDeMoneda(f && f[campo]));
  const conCodigo = monedas.size > 1;
  return (n, moneda) => money(n, moneda, { conCodigo });
};
const fmt  = n => Number(n||0).toFixed(2);
const { apiUrl: API } = getFrontendConfig();

// La clave viaja en un header, nunca se compara en el cliente.
const adminHeaders = (key) => key ? {"X-Admin-Key": key} : {};

// ── EL NOMBRE DEL EMISOR DE LA FILA FIJA DE 1X2 ───────────────
//
// Las tres pantallas que dibujan el 1X2 con el atajo `ev.odds.L/E/V` —acá,
// `Web.jsx` y `Box.jsx`— necesitan el NOMBRE con el que GR8 emite ese
// resultado, que es lo único que `resolver_una` encuentra en el índice de
// claves de selección. `ev.odds` no lo trae: lo arma
// `gr8_catalogo._odds_compactas` colapsando `h2h_3_way` y `h2h` en tres
// posiciones, así que mirándolo no se puede saber de qué mercado salió.
//
// Lo que sí trae el catálogo es `ev.markets`, el diccionario del emisor. Acá se
// lee UNA clave literal —`h2h`— y el nombre sale de adentro de ese mercado. No
// se deriva nada: no se prueba `h2h_3_way` "por si acaso" ni se traduce
// "Empate" a "Draw" con una tabla nuestra, porque la etiqueta de pantalla no es
// inyectiva sobre los nombres del emisor y la mejor tabla posible acierta 1 de
// 4 (medido en L3).
//
// Cuando el nombre NO está en ese diccionario, el nombre y el mercado van los
// dos VACÍOS. Es el caso del 1X2 del fútbol, que GR8 emite en `h2h_3_way`: el
// pick sigue sin liquidar —como antes— pero no miente diciendo que GR8 publicó
// ese resultado en `h2h`. Un pick sin clave es honesto; uno con la clave
// inventada se liquida contra la pregunta que el jugador no hizo. Es la misma
// regla y el mismo repliegue que `OddsButtons` (`cuotasDeEvento.jsx:152`).
//
// Van juntos a propósito: los dos salen del MISMO diccionario, así que mandar
// uno sin el otro sería afirmar la mitad de algo que no se leyó.
//
// Está copiada textual en `Web.jsx` y en `Box.jsx` —no pueden importar de
// acá sin arrastrarse el panel entero al bundle, igual que `picksCotizados`—
// y `losCombosSeLiquidan.test.js` ejecuta las tres y exige que den lo mismo.
export function nombresDe1x2(ev){
  const mkt = (ev?.markets || {}).h2h || {};
  const home = ev?.h || ev?.home || "";
  const away = ev?.a || ev?.away || "";
  const de = (nombre) => (nombre in mkt
    ? { nombre, market: "h2h" }
    : { nombre: "", market: "" });
  return { L: de(home), E: de("Draw"), V: de(away) };
}

// ── COMPONENTS ────────────────────────────────────────────────
function GCard({ children, style={}, glow, onClick, level=1 }){
  // `level` is what a card is sitting on, not how important it is.
  //
  // Level 1 sits on the page and keeps the prototype's panel treatment: an
  // opaque surface, one hairline, and elevation. Level 2 sits inside another
  // card or a sheet, where a second identical hairline separates without
  // ranking — nest three of them and every box looks equally important. It
  // steps the surface up to `raised` instead and drops the border, so depth
  // is read from the surface rather than from yet another line.
  //
  // `glow` outranks both: a coloured border means this box needs attention.
  // That is the one job a border does here that a surface cannot.
  const raised = level >= 2;
  return(
    <div onClick={onClick} style={{
      background: raised ? Q.raised : Q.card,
      border: glow ? `1px solid ${glow}44`
        : raised ? "none" : `1px solid ${Q.border}`,
      borderRadius: raised ? RADII.md : RADII.lg,
      boxShadow: glow ? `${ELEVATION}, 0 0 24px ${glow}22`
        : raised ? "none" : ELEVATION,
      // overflow visible: con "hidden" la tarjeta recorta lo que exceda
      // su alto y un campo entero puede quedar invisible sin ningún
      // error. Ya pasó en Agencia y costó horas de diagnóstico.
      position:"relative", overflow:"visible",
      cursor:onClick?"pointer":"default", ...style,
    }}>
      {!raised&&<div style={{position:"absolute",top:0,left:0,right:0,height:1,
        background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.1),transparent)",
        pointerEvents:"none"}}/>}
      {children}
    </div>
  );
}

// Antes dibujaba su propio hexágono en SVG junto a "IAQP" en degradé,
// como placeholder de un logo que todavía no existía. Ahora que el logo
// real está disponible (BrandMark), el hexágono queda de más: la imagen
// misma es la marca, tal como la usa el prototipo (sin hexágono al lado).
function QPLogo({ size=20 }){
  return <BrandMark size={size}/>;
}

function HBadge({ label, color=Q.violet }){
  return(
    <span style={{
      background:`linear-gradient(135deg,${color}33,${color}11)`,
      border:`1px solid ${color}66`, borderRadius:RADII.xl,
      padding:"4px 12px", fontSize:12, fontWeight:700, color,
      letterSpacing:0.5, fontFamily:F_BODY,
    }}>{label}</span>
  );
}

function Btn({ label, onClick, color=Q.violet, outline=false, size="md", full=false, disabled=false, icon }){
  const h=size==="lg"?"48px":size==="sm"?"32px":"42px";
  const fs=size==="lg"?14:size==="sm"?12:12;
  return(
    <button onClick={onClick} disabled={disabled} style={{
      height:h, width:full?"100%":"auto",
      padding:`0 ${size==="sm"?"10px":"16px"}`,
      // An outline button used to be a coloured 1px box. Stack four of
      // them and the screen is all edges and no ranking, which is the
      // whole complaint. A secondary action reads perfectly well as a
      // raised surface with a coloured label: the colour still says what
      // kind of action it is, without drawing a frame around it.
      background:disabled?"rgba(255,255,255,0.04)":outline?"rgba(255,255,255,0.06)":`linear-gradient(135deg,${color},${color}CC)`,
      border:outline&&!disabled?"none":`1px solid ${disabled?Q.dim:color}`, borderRadius:RADII.md,
      // inkOn measures contrast against the fill and picks dark or light
      // ink — a hardcoded "#fff" is unreadable on a light accent like
      // Q.green. Do not special-case any one colour here; let the helper
      // decide for all of them.
      color:disabled?Q.muted:outline?color:inkOn(color),
      fontSize:fs, fontWeight:700, cursor:disabled?"not-allowed":"pointer",
      display:"flex", alignItems:"center", justifyContent:"center", gap:SPACING[8],
      fontFamily:F_BODY, textTransform:"uppercase",
      // A neutral shadow, not a coloured halo: ELEVATION is the
      // prototype's panel-level shadow (18px offset, 32px blur) and
      // reads as an oversized floating box under a button this small.
      boxShadow:(!outline&&!disabled)?"0 4px 12px rgba(0,0,0,0.35)":"none",
    }}>
      {icon&&<span style={{fontSize:fs+2}}>{icon}</span>}{label}
    </button>
  );
}

function KPI({ label, value, sub, color=Q.violet, icon, trend, onClick }){
  // No glow: the value below is already in `color`, so a coloured border
  // around it repeats the signal and spends the one thing that was
  // supposed to mean "look at this".
  return(
    <GCard onClick={onClick} style={{padding:"12px 16px"}}>
      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:6}}>
        {icon&&<span style={{fontSize:16}}>{icon}</span>}
        <span style={{color:Q.muted,fontSize:12,fontWeight:600,letterSpacing:1,
          textTransform:"uppercase",fontFamily:F_BODY}}>{label}</span>
      </div>
      <div style={{color,fontWeight:900,fontSize:16,fontFamily:F_BODY,
        textShadow:`0 0 10px ${color}55`}}>{value}</div>
      {sub&&<div style={{color:Q.dim,fontSize:12,marginTop:3,fontFamily:F_BODY}}>{sub}</div>}
      {trend&&<div style={{color:trend>0?Q.green:Q.red,fontSize:12,marginTop:3,
        fontWeight:700,fontFamily:F_BODY}}>
        {trend>0?"▲":"▼"} {Math.abs(trend)}% vs ayer
      </div>}
    </GCard>
  );
}

function Input({ label, value, onChange, type="text", placeholder="" }){
  return(
    <div style={{marginBottom:12}}>
      {label&&<div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
        letterSpacing:1,fontFamily:F_BODY,marginBottom:5}}>{label}</div>}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)}
        placeholder={placeholder}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
          color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
    </div>
  );
}

// Datos de ejemplo — todavía sin endpoint real detrás
const MOCK = {
  stats:{
    users:3841, active:1247, pro:683, newToday:47,
    depHoy:4820000, retHoy:1240000,
    netwinHoy:241000, netwin30d:7240000,
    apuestasActivas:247,
  },
  depositos:[
    {id:142,username:"martin_ar",amount:50000,method:"MercadoPago",created_at:"14:23"},
    {id:143,username:"carlos_bet",amount:20000,method:"USDT",created_at:"14:45"},
    {id:144,username:"pablo_slots",amount:80000,method:"CVU",created_at:"15:01"},
  ],
  retiros:[
    {id:87,username:"lucia_slots",amount:80000,method:"CVU",created_at:"13:00"},
    {id:88,username:"champion_ar",amount:200000,method:"USDT",created_at:"14:10"},
  ],
  topUsers:[
    {username:"champion_ar",ggr:60500,plan:"VIP",level:18},
    {username:"apostador99",ggr:40500,plan:"Pro",level:14},
    {username:"porteno_bets",ggr:33000,plan:"Pro",level:12},
    {username:"bet_king_ar",ggr:26700,plan:"Pro",level:11},
    {username:"suerte_total",ggr:18800,plan:"Basic",level:8},
  ],
  influencers:[
    {influencer_code:"messi10",clics:147,registros:38,apuestas:22,volumen:440000},
    {influencer_code:"toro23",clics:89,registros:21,apuestas:14,volumen:280000},
  ],
};

// Aviso reutilizable para las secciones que todavía no persisten
function AvisoMock(){
  return(
    <div style={{background:`${Q.amber}12`,border:`1px solid ${Q.amber}55`,
      borderRadius:RADII.md,padding:"8px 12px",marginBottom:12,
      color:Q.amber,fontSize:12,fontFamily:F_BODY}}>
      Datos de ejemplo — esta sección todavía no está conectada a la base.
    </div>
  );
}

// ── LOGIN ──────────────────────────────────────────────────────
function AdminLogin({ onLogin }){
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  // La clave se valida contra el servidor, no en el navegador.
  const login=async()=>{
    if(loading) return;
    setErr(""); setLoading(true);
    let r;
    try {
      r = await fetch(`${API}/api/agencias`, { headers: adminHeaders(pass) });
    } catch(e){
      setErr("Sin conexión con el servidor");
      setLoading(false);
      return;
    }
    setLoading(false);
    if(r.status===401){ setErr("Contraseña incorrecta"); return; }
    if(r.status===503){ setErr("ADMIN_API_KEY no está configurada en el servidor"); return; }
    if(!r.ok){ setErr(`Error del servidor (${r.status})`); return; }
    onLogin(pass);
  };

  return(
    <div style={{background:Q.void,minHeight:"100vh",display:"flex",
      alignItems:"center",justifyContent:"center",padding:SPACING[24],position:"relative"}}>
      <div style={{position:"fixed",inset:0,pointerEvents:"none",
        backgroundImage:`linear-gradient(${Q.violet}04 1px,transparent 1px),linear-gradient(90deg,${Q.violet}04 1px,transparent 1px)`,
        backgroundSize:"28px 28px"}}/>
      <div style={{width:"100%",maxWidth:360,position:"relative",zIndex:1}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <QPLogo size={44}/>
          <div style={{color:Q.muted,fontSize:12,marginTop:8,
            fontFamily:F_BODY}}>Panel Administrador</div>
        </div>
        <GCard style={{padding:SPACING[32]}}>
          <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
            letterSpacing:1,fontFamily:F_BODY,marginBottom:6}}>
            Clave admin
          </div>
          <input type="password" value={pass}
            onChange={e=>setPass(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&login()}
            placeholder="••••••••"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
              color:Q.text,fontSize:16,marginBottom:14,
              fontFamily:F_BODY}}/>
          {err&&<div style={{color:Q.red,fontSize:12,marginBottom:10}}>{err}</div>}
          <Btn label={loading?"VERIFICANDO...":"INGRESAR"} onClick={login}
            color={Q.violet} size="lg" full disabled={loading||!pass}/>
        </GCard>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ════════════════════════════════════════
// TAB GLOBAL — vista real de todo el sistema
// ════════════════════════════════════════
function TabCierre({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const [desde,setDesde]=useState(hoy);
  const [hasta,setHasta]=useState(hoy);
  const [data,setData]=useState(null);
  const [cargando,setCargando]=useState(false);
  const [ags,setAgs]=useState([]);
  const [filtroAg,setFiltroAg]=useState("");   // code de agencia elegida ("" = todas)
  const [buscar,setBuscar]=useState("");
  const [vista,setVista]=useState("resumen");  // resumen | movimientos | apuestas
  const [movs,setMovs]=useState(null);
  const [cargMovs,setCargMovs]=useState(false);
  const [apuestas,setApuestas]=useState(null);
  const [cargAp,setCargAp]=useState(false);
  const [apAbierta,setApAbierta]=useState(null);   // code de la apuesta expandida
  const [combos,setCombos]=useState(null);
  const [cargCombos,setCargCombos]=useState(false);
  // Caja: cargas, descargas y saldo vivo, para el cuadre del día.
  const [caja,setCaja]=useState(null);

  const cargarCaja=async()=>{
    setCaja(null);
    try{
      const q=new URLSearchParams({desde,hasta,detalle:"true"});
      if(filtroAg) q.set("agencia",filtroAg);
      const r=await fetch(`${API}/api/admin/caja?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){
        const e=await r.json().catch(()=>({}));
        // Sin esto la pantalla quedaba "Cargando..." para siempre y no
        // había forma de saber por qué.
        setCaja({error:e.detail||`Error ${r.status}`});
        return;
      }
      setCaja(await r.json());
    }catch(e){
      setCaja({error:e.message==="Failed to fetch"?"Sin conexión":e.message});
    }
  };

  const cargarCombos=async()=>{
    setCargCombos(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"");
      const r=await fetch(`${API}/api/admin/historial/combos?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setCombos((await r.json()).combos||[]);
    }catch(e){}
    setCargCombos(false);
  };
  useEffect(()=>{ if(vista==="caja") cargarCaja(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg]);

  useEffect(()=>{ if(vista==="combos") cargarCombos(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg]);

  // Serie de apostado para el resumen: el cierre es, ante todo, un
  // período de tiempo, y el resumen ya lo describe con totales fijos.
  // Una línea de tendencia sobre ese mismo rango le da al total algo
  // que un número solo no puede: si el período mejoró o empeoró.
  const [serie,setSerie]=useState(null);
  const [cargSerie,setCargSerie]=useState(false);

  const cargarSerie=async()=>{
    setCargSerie(true);
    try{
      const q=`metrica=apostado&desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"");
      const r=await fetch(`${API}/api/admin/serie?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setSerie((await r.json()).puntos||[]);
    }catch(e){}
    setCargSerie(false);
  };
  useEffect(()=>{ if(vista==="resumen") cargarSerie(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg]);
  const [clientesF,setClientesF]=useState([]);     // clientes de la agencia elegida
  const [filtroCli,setFiltroCli]=useState("");     // cliente elegido ("" = todos)
  const [liqMsg,setLiqMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [liqProc,setLiqProc]=useState(false);
  const [verLiq,setVerLiq]=useState(false);
  const [liquidaciones,setLiquidaciones]=useState(null);
  const [liqAuto,setLiqAuto]=useState([]);
  const [liqAutoMsg,setLiqAutoMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [liqAutoProc,setLiqAutoProc]=useState(false);

  useEffect(()=>{
    fetch(`${API}/api/admin/liquidacion-config`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{periodos:[]}).then(d=>setLiqAuto(d.periodos||[]))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const togglePeriodo=(p)=>{
    const nuevo = liqAuto.includes(p) ? liqAuto.filter(x=>x!==p) : [...liqAuto,p];
    setLiqAuto(nuevo);
    fetch(`${API}/api/admin/liquidacion-config`,{
      method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
      body:JSON.stringify({periodos:nuevo}),
    }).catch(()=>{});
  };

  const dispararAuto=async(periodo)=>{
    setLiqAutoProc(true); setLiqAutoMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/liquidar-auto`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({periodo}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      setLiqAutoMsg({text:`${d.generadas} liquidaciones (${d.desde} a ${d.hasta})`,ok:true});
    }catch(e){ setLiqAutoMsg({text:e.message,ok:false}); }
    setLiqAutoProc(false);
  };

  // ── LA MONEDA DE UNA AGENCIA, POR CÓDIGO ──────────────────────
  //
  // `liquidaciones` NO tiene columna de moneda: `listar_liquidaciones`
  // (`bot/casino_api.py:5668`) devuelve `apostado`, `ggr` y `comision_total`
  // pelados. La escribió la agencia del renglón, así que la unidad sale de
  // ahí, y `ags` —que viene de `/api/admin/arbol`— sí la trae
  // (`bot/casino_api.py:17678`).
  //
  // Cuando una agencia ya no está en el árbol devuelve `undefined`, y
  // `money()` cae a ARS: exactamente lo que se dibuja hoy. Sin evidencia no
  // se inventa una moneda ni se cambia lo que ya se ve.
  const monedaDeAg = (code) => (ags.find(a=>a.code===code)||{}).moneda;

  const generarLiquidacion=async()=>{
    if(!filtroAg) return;
    setLiqProc(true); setLiqMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/liquidar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({agencia:filtroAg,desde,hasta}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      // ACÁ EL FILTRO NUNCA ESTÁ VACÍO: la función arranca con
      // `if(!filtroAg) return`, y `/api/admin/liquidar` liquida "Solo esa
      // agencia (sus clientes directos)" (`bot/casino_api.py:5627`). O sea
      // que `comision_total` es de UNA agencia, la del filtro, y tiene su
      // moneda. No es el caso de la lista de abajo.
      setLiqMsg({text:`Liquidación generada · Comisión `+
        `${money(d.comision_total, monedaDeAg(filtroAg))}`,ok:true});
    }catch(e){ setLiqMsg({text:e.message,ok:false}); }
    setLiqProc(false);
  };

  const cargarLiquidaciones=async()=>{
    setLiquidaciones(null);
    try{
      const q=filtroAg?`?agencia=${filtroAg}`:"";
      const r=await fetch(`${API}/api/admin/liquidaciones${q}`,{headers:adminHeaders(adminKey)});
      if(r.ok) setLiquidaciones((await r.json()).liquidaciones||[]);
    }catch(e){ setLiquidaciones([]); }
  };

  // Al elegir una agencia, cargar sus clientes para el filtro
  useEffect(()=>{
    setFiltroCli("");
    if(!filtroAg){ setClientesF([]); return; }
    fetch(`${API}/api/admin/clientes?agencia=${filtroAg}`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{clientes:[]}).then(d=>setClientesF(d.clientes||[]))
      .catch(()=>setClientesF([]));
  // eslint-disable-next-line
  },[filtroAg]);

  const cargarApuestas=async()=>{
    setCargAp(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"")
              +(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(`${API}/api/admin/historial/apuestas?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setApuestas((await r.json()).apuestas||[]);
    }catch(e){}
    setCargAp(false);
  };
  useEffect(()=>{ if(vista==="apuestas") cargarApuestas(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg,filtroCli]);

  const [cashouts,setCashouts]=useState([]);
  const [cargCO,setCargCO]=useState(false);
  const cargarCashouts=async()=>{
    setCargCO(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"");
      const r=await fetch(`${API}/api/admin/historial/cashout?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setCashouts((await r.json()).cashouts||[]);
    }catch(e){}
    setCargCO(false);
  };
  useEffect(()=>{ if(vista==="cashout") cargarCashouts(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg]);

  const cargarMovs=async()=>{
    setCargMovs(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"")
              +(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(`${API}/api/admin/historial/movimientos?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setMovs((await r.json()).movimientos||[]);
    }catch(e){}
    setCargMovs(false);
  };
  useEffect(()=>{ if(vista==="movimientos") cargarMovs(); // eslint-disable-next-line
  },[vista,desde,hasta,filtroAg,filtroCli]);

  const exportarMovs=()=>{
    const q=`desde=${desde}&hasta=${hasta}&export=csv`+(filtroAg?`&agencia=${filtroAg}`:"");
    fetch(`${API}/api/admin/historial/movimientos?${q}`,{headers:adminHeaders(adminKey)})
      .then(r=>r.blob()).then(b=>{
        const a=document.createElement("a");
        a.href=URL.createObjectURL(b); a.download=`movimientos_${desde}_${hasta}.csv`;
        a.click();
      }).catch(()=>{});
  };

  useEffect(()=>{
    fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{agencias:[]}).then(d=>setAgs(d.agencias||[]))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const rango=(tipo)=>{
    const h=new Date(); let d=new Date();
    if(tipo==="hoy"){ d=new Date(); }
    else if(tipo==="semana"){ d.setDate(h.getDate()-7); }
    else if(tipo==="mes"){ d.setMonth(h.getMonth()-1); }
    const iso=x=>x.toISOString().slice(0,10);
    setDesde(iso(d)); setHasta(iso(h));
  };

  const cargar=async()=>{
    setCargando(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroAg?`&agencia=${filtroAg}`:"")
              +(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(`${API}/api/admin/cierre?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setData(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[desde,hasta,filtroAg,filtroCli]);

  const exportar=()=>{
    const q=`desde=${desde}&hasta=${hasta}&export=csv`+(filtroAg?`&agencia=${filtroAg}`:"");
    fetch(`${API}/api/admin/cierre?${q}`,
      {headers:adminHeaders(adminKey)})
      .then(r=>r.blob()).then(b=>{
        const a=document.createElement("a");
        a.href=URL.createObjectURL(b);
        a.download=`cierre_${desde}_${hasta}.csv`;
        a.click();
      }).catch(()=>{});
  };

  // Las dos listas de auditoría traen la moneda por fila desde
  // `/api/admin/historial/movimientos` y `/historial/apuestas`. Cada una
  // decide por separado si muestra el código: son pestañas distintas y un
  // período con movimientos en dos monedas puede tener apuestas en una sola.
  const montoMov = formatoDeLista(movs);
  const montoAp  = formatoDeLista(apuestas);

  // ── LA CAJA DEL ADMIN VIENE AGRUPADA POR MONEDA ──────────────
  //
  // `/api/admin/caja` ya no publica un total plano de plata: sin filtro suma
  // todas las agencias, y ahí "Cargas" serían pesos más dólares en el mismo
  // número. Las cuentas —clientes, boletos, cargas— siguen planas.
  //
  // Las guardas no son defensivas por costumbre: `por_moneda` puede faltar si
  // contesta una respuesta vieja de caché o si el frontend queda desfasado del
  // backend en un despliegue. Entonces no se dibuja plata, que es mejor que
  // una pestaña que revienta entera por un `.map` sobre `undefined`.
  //
  // `total` y `operativa` DECIDEN POR SEPARADO si muestran el código, porque
  // son dos bloques distintos: `total` es la rama pedida y `operativa` es
  // toda la casa (el filtro `agencia` no la alcanza, ver el endpoint), así
  // que un pedido filtrado puede tener una sola moneda arriba y dos abajo.
  const cajaMonedas = Array.isArray(caja?.total?.por_moneda)
    ? caja.total.por_moneda : [];
  const variasCaja = cajaMonedas.length > 1;
  const rotCaja = (texto, m) => variasCaja ? `${texto} · ${m.moneda}` : texto;
  const opMonedas = Array.isArray(caja?.operativa?.por_moneda)
    ? caja.operativa.por_moneda : [];
  const variasOp = opMonedas.length > 1;
  const rotOp = (texto, m) => variasOp ? `${texto} · ${m.moneda}` : texto;
  // Las dos listas por fila de la pestaña: una agencia por renglón y un
  // movimiento por renglón, cada uno con la moneda de SU fila.
  const montoAgCaja = formatoDeLista(caja?.agencias);
  const montoDetCaja = formatoDeLista(caja?.movimientos);

  // ── EL CIERRE DEL ADMIN VIENE AGRUPADO POR MONEDA ────────────
  //
  // Los cuatro bloques de totales —`global`, `casino`, `bonos`, `total`— ya no
  // publican plata plana: suman TODAS las agencias, y ahí "GGR" eran pesos más
  // dólares en el mismo número. Las CUENTAS siguen planas y el desglose por
  // producto trae la moneda por fila.
  //
  // Las guardas con `Array.isArray` no son defensivas por costumbre:
  // `por_moneda` puede faltar si contesta una respuesta vieja de caché o si el
  // frontend queda desfasado del backend en un despliegue. Entonces no se
  // dibuja plata, que es mejor que una pestaña que revienta entera por un
  // `.map` sobre `undefined`.
  //
  // LA RAMA DEL CLIENTE SE NORMALIZA A UNA FILA en vez de tener su propia
  // tarjeta: `?cliente=<id>` devuelve un `global` plano con `moneda` al lado,
  // y pasa por acá como una sola fila con esa moneda. Así la tarjeta se dibuja
  // una vez y no hay dos lecturas del mismo bloque confirmándose entre sí.
  //
  // CADA BLOQUE DECIDE POR SEPARADO si muestra el código, igual que `total` y
  // `operativa` en la caja. No es simetría decorativa: el filtro `agencia`
  // alcanza a `global` y `casino` pero NO a `bonos` —su consulta no se acota
  // por rama—, así que un pedido filtrado puede tener una sola moneda arriba y
  // dos en los bonos. Con UNA sola moneda no se dibuja ningún código y la
  // pantalla queda idéntica a la de hoy.
  const globFilas = data?.es_cliente
    ? (data?.global ? [{ ...data.global, moneda: data.moneda }] : [])
    : (Array.isArray(data?.global?.por_moneda) ? data.global.por_moneda : []);
  const variasGlob = globFilas.length > 1;
  const rotGlob = (texto, m) => variasGlob ? `${texto} · ${m.moneda}` : texto;
  const casinoFilas = Array.isArray(data?.casino?.por_moneda)
    ? data.casino.por_moneda : [];
  const variasCasino = casinoFilas.length > 1;
  const rotCasino = (texto, m) => variasCasino ? `${texto} · ${m.moneda}` : texto;
  const totalFilas = Array.isArray(data?.total?.por_moneda)
    ? data.total.por_moneda : [];
  const variasTotal = totalFilas.length > 1;
  const rotTotal = (texto, m) => variasTotal ? `${texto} · ${m.moneda}` : texto;
  // Los bonos se buscan POR MONEDA del total: la tarjeta del total los resta,
  // así que cada fila tiene que restar la suya y no la de la otra moneda.
  const bonosFilas = Array.isArray(data?.bonos?.por_moneda)
    ? data.bonos.por_moneda : [];
  const bonosDe = (m) => bonosFilas.find(b => b.moneda === m?.moneda) || null;
  // `por_producto` del cierre NO se dibuja en esta pestaña —el desglose por
  // producto vive en `TabProductos`, que pega a `/reportes/productos`—, así
  // que acá no hay nada que formatear aunque el endpoint lo devuelva.
  //
  // ── "POR AGENCIA" DIBUJABA DÓLARES CON EL SIGNO DE LOS PESOS ──
  //
  // Encontrado el 2026-10-27 arreglando los totales de arriba, y no estaba en
  // el pedido. La lista de abajo pintaba `comision`, `apostado`, `premios` y
  // `ggr` con `ars()` —el signo de los pesos clavado— aunque cada fila TRAE su
  // `moneda` desde el endpoint desde antes de este trabajo. Es el mismo
  // defecto que `formatoDeLista` vino a cerrar en "ÚLTIMOS MOVIMIENTOS": una
  // lista que mezcla agencias renglón a renglón, con una fila en dólares y su
  // vecina en pesos, las dos con `$`.
  //
  // Va POR FILA y no con un cartel arriba, por la misma razón de siempre: el
  // riesgo no es confundir dos monedas dentro de una fila, es leer el `$` de
  // una fila en pesos al lado del de otra en dólares, en la misma columna.
  const montoAgCierre = formatoDeLista(data?.agencias);

  // ── LAS LIQUIDACIONES DEL MODAL ───────────────────────────────
  //
  // EL FILTRO DE AGENCIA PUEDE ESTAR VACÍO —`useState("")` con el comentario
  // `"" = todas` y la opción `— Todo el sistema —` del `select`—, y
  // `cargarLiquidaciones` manda `?agencia=` SÓLO cuando hay filtro: sin
  // filtro trae las últimas 100 de TODAS las agencias. Así que la moneda NO
  // puede salir del filtro.
  //
  // Pero no hace falta: no son un total mezclado, es una LISTA, y cada
  // renglón es la liquidación de UNA agencia (`l.agencia`). Va por fila con
  // la misma regla del resto del archivo, cruzando contra el árbol.
  const liqFilas = (liquidaciones||[]).map(l=>({...l,moneda:monedaDeAg(l.agencia)}));
  const montoLiq = formatoDeLista(liqFilas);

  return(
    <div>
      <PageHeader icon={<Icon name="chart-no-axes-combined"/>} title="Cierre"
        description="El cierre de caja, liquidaciones y movimientos de todas las agencias, por el período que elijas."/>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["resumen",<><Icon name="chart-no-axes-combined" size={13}/> Resumen</>],["productos",<><Icon name="spade" size={13}/> Productos</>],["liquidacion",<><Icon name="wallet-cards" size={13}/> Liquidar</>],["historial",<><Icon name="clipboard-list" size={13}/> Historial</>],["caja",<><Icon name="receipt-text" size={13}/> Caja</>],["movimientos",<><Banknote size={13}/> Movs</>],["apuestas",<><Icon name="receipt-text" size={13}/> Apuestas</>],["cashout",<><Icon name="wallet-cards" size={13}/> Cash out</>],["combos",<><Zap size={13}/> Combos</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px",cursor:"pointer",color:vista===k?Q.cyan:Q.muted,
            fontSize:13,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {/* Filtro de agencia: buscador + selector */}
      <input value={buscar} onChange={e=>setBuscar(e.target.value)}
        placeholder="🔍 Buscar agencia..."
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.text,fontSize:14,marginBottom:8,
          fontFamily:F_BODY}}/>
      <select value={filtroAg} onChange={e=>setFiltroAg(e.target.value)}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${filtroAg?Q.cyan:Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
          color:Q.text,fontSize:14,marginBottom:12,
          fontFamily:F_BODY}}>
        <option value="">— Todo el sistema —</option>
        {ags.filter(a=>!buscar||a.name.toLowerCase().includes(buscar.toLowerCase())
                     ||a.code.toLowerCase().includes(buscar.toLowerCase()))
            .map(a=>(
          <option key={a.code} value={a.code}>
            {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
        ))}
      </select>
      {filtroAg&&<div style={{color:Q.cyan,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>
        Mostrando esa agencia y toda su rama hacia abajo ·{" "}
        <span onClick={()=>{setFiltroAg("");setBuscar("");}}
          style={{color:Q.muted,textDecoration:"underline",cursor:"pointer"}}>ver todo</span>
      </div>}

      {filtroAg&&clientesF.length>0&&(
        <select value={filtroCli} onChange={e=>setFiltroCli(e.target.value)}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${filtroCli?Q.gold:Q.border}`,borderRadius:RADII.md,
            padding:"8px 12px",color:Q.text,fontSize:14,marginBottom:12,
            fontFamily:F_BODY}}>
          <option value="">— Todos los clientes de {filtroAg} —</option>
          {clientesF.map(c=>(
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
      )}
      {filtroCli&&<div style={{color:Q.gold,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>
        Viendo solo ese cliente ·{" "}
        <span onClick={()=>setFiltroCli("")}
          style={{color:Q.muted,textDecoration:"underline",cursor:"pointer"}}>ver todos</span>
      </div>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        {[["hoy","Hoy"],["semana","7 días"],["mes","30 días"]].map(([k,l])=>(
          <button key={k} onClick={()=>rango(k)} style={{flex:1,
            background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.cyan,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
      </div>

      {/* Todo lo jugado por producto: deportivas, casino y ruleta */}
      {vista==="productos"&&(
        <TabProductos adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}

      {/* La liquidación es parte del cierre: es el número que sale
          de todo lo demás. */}
      {vista==="liquidacion"&&(
        <Liquidacion adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}

      {vista==="historial"&&(
        <HistorialAdmin adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}

      {vista==="caja"&&(
        <div>
          {!caja&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
            fontFamily:F_BODY}}>Cargando...</div>}
          {caja?.error&&(
            <GCard glow={Q.red} style={{padding:SPACING[16],textAlign:"center"}}>
              <div style={{color:Q.red,fontSize:13,
                fontFamily:F_BODY}}>{caja.error}</div>
            </GCard>
          )}

          {caja&&!caja.error&&(
            <>
              {/* El efectivo que pasó por el mostrador. Es distinto
                  de las cargas de saldo: acá está la plata que el
                  cajero recibió y pagó en mano. */}
              {opMonedas.filter(m=>m.entro!==0||m.salio!==0).map(m=>(
                <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12}}>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                    fontFamily:F_BODY}}>
                    {rotOp("Efectivo del mostrador",m)}</div>

                  <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
                    <div style={{flex:1}}>
                      <div style={{color:Q.muted,fontSize:12}}>Entró</div>
                      <div style={{color:Q.green,fontWeight:800,fontSize:17,
                        fontFamily:F_BODY}}>
                        {money(m.entro,m.moneda)}</div>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{color:Q.muted,fontSize:12}}>Salió</div>
                      <div style={{color:Q.red,fontWeight:800,fontSize:17,
                        fontFamily:F_BODY}}>
                        {money(m.salio,m.moneda)}</div>
                    </div>
                  </div>

                  {/* El desglose: el cajero tiene que poder cuadrar
                      el cajón contra estos números.

                      LAS CANTIDADES SE MUESTRAN SÓLO CON UNA MONEDA, y no es
                      pereza: son cuentas de toda la casa —un boleto es un
                      boleto en cualquier moneda— y pegadas al monto de una
                      sola moneda dirían "3 boletos por US$40" cuando dos de
                      los tres fueron en pesos. Con una sola moneda la
                      pantalla queda idéntica a la de hoy. */}
                  <div style={{marginBottom:10}}>
                    {[["Ventas de boletos", m.ventas,
                       `${caja.operativa.ventas?.cantidad||0} boletos`],
                      ["Cargas de saldo", m.cargas,
                       `${caja.operativa.cargas?.cantidad||0} cargas`],
                      ["Premios pagados", -(m.premios||0),
                       `${caja.operativa.premios?.cantidad||0} pagados`],
                      ["Retiros de saldo", -(m.retiros||0),
                       `${caja.operativa.retiros?.cantidad||0} retiros`],
                    ].filter(([,v])=>v!==0).map(([l,v,det])=>(
                      <div key={l} style={{display:"flex",
                        justifyContent:"space-between",alignItems:"baseline",
                        fontSize:12,padding:"4px 0",
                        borderTop:`1px solid ${Q.border}`}}>
                        <span style={{color:Q.muted,
                          fontFamily:F_BODY}}>
                          {l}
                          {!variasOp&&<span style={{color:Q.dim,fontSize:12,
                            marginLeft:6}}>{det}</span>}</span>
                        <span style={{color:v>=0?Q.green:Q.red,
                          fontFamily:F_BODY}}>
                          {v>=0?"+":"−"}{money(Math.abs(v),m.moneda)}</span>
                      </div>
                    ))}
                  </div>

                  {m.anulados>0&&(
                    <div style={{color:Q.dim,fontSize:12,marginBottom:8,
                      lineHeight:1.45}}>
                      {variasOp?"No se cuentan los boletos anulados, por "
                        :`No se cuentan ${caja.operativa.anulados?.cantidad||0} ${
                          (caja.operativa.anulados?.cantidad||0)===1
                            ?"boleto anulado":"boletos anulados"} por `}
                      {money(m.anulados,m.moneda)}</div>
                  )}

                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",paddingTop:SPACING[8],
                    borderTop:`1px solid ${Q.border}`}}>
                    <span style={{color:Q.text,fontWeight:700,fontSize:13,
                      fontFamily:F_BODY}}>
                      Debería haber en caja</span>
                    <span style={{
                      color:m.en_caja>=0?Q.green:Q.red,
                      fontWeight:900,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(m.en_caja,m.moneda)}</span>
                  </div>
                  {(m.cargas>0||m.retiros>0)&&(
                    <div style={{color:Q.dim,fontSize:12,marginTop:7,
                      lineHeight:1.5,
                      fontFamily:F_BODY}}>
                      Las cargas y retiros son efectivo del mostrador
                      aunque no sean ganancia: ese saldo se le debe al
                      cliente hasta que lo juegue.</div>
                  )}
                </GCard>
              ))}

              {cajaMonedas.map(m=>(
              <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                  fontFamily:F_BODY}}>
                  {rotCaja("Billetera de los clientes",m)} · {caja.desde===caja.hasta?caja.desde
                    :`${caja.desde} a ${caja.hasta}`}
                  {filtroAg?` · ${filtroAg} y su rama`:" · todas"}
                  <div style={{color:Q.dim,fontSize:12,marginTop:3,
                    lineHeight:1.4}}>
                    Solo cargas y retiros de saldo. El efectivo del
                    mostrador, con ventas y premios, está arriba.</div></div>
                <div style={{display:"flex",gap:SPACING[12],marginBottom:12}}>
                  <div style={{flex:1}}>
                    <div style={{color:Q.muted,fontSize:12}}>Cargas</div>
                    <div style={{color:Q.green,fontWeight:800,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(m.cargas,m.moneda)}</div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:Q.muted,fontSize:12}}>Retiros</div>
                    <div style={{color:Q.amber,fontWeight:800,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(m.descargas,m.moneda)}</div>
                  </div>
                </div>
                <div style={{borderTop:`1px solid ${Q.border}`,paddingTop:SPACING[12],
                  display:"flex",justifyContent:"space-between",
                  alignItems:"center"}}>
                  <span style={{color:Q.text,fontSize:13,fontWeight:700,
                    fontFamily:F_BODY}}>En caja</span>
                  <span style={{color:m.neto_caja>=0?Q.green:Q.red,
                    fontWeight:900,fontSize:24,
                    fontFamily:F_BODY}}>
                    {money(m.neto_caja,m.moneda)}</span>
                </div>
                {m.premios_pagados>0&&(
                  <div style={{color:Q.muted,fontSize:12,marginTop:6}}>
                    Premios pagados aparte: {money(m.premios_pagados,m.moneda)}</div>
                )}
              </GCard>
              ))}

              <GCard style={{padding:SPACING[16],marginBottom:12}}>
                {/* El saldo de los jugadores también va por moneda: es el
                    pasivo de la casa y un pasivo en dos monedas no es un
                    número. La CUENTA de clientes queda una sola, arriba. */}
                <div style={{color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}>
                  Saldo de los jugadores</div>
                <div style={{color:Q.dim,fontSize:12,marginBottom:6}}>
                  {caja.total.clientes} clientes con cuenta</div>
                {cajaMonedas.map(m=>(
                  <div key={m.moneda} style={{display:"flex",
                    justifyContent:"space-between",alignItems:"baseline",
                    paddingTop:SPACING[4]}}>
                    <span style={{color:Q.muted,fontSize:12,
                      fontFamily:F_BODY}}>{variasCaja?m.moneda:""}</span>
                    <div style={{textAlign:"right"}}>
                      <div style={{color:Q.cyan,fontWeight:900,fontSize:22,
                        fontFamily:F_BODY}}>
                        {money(m.saldo_clientes,m.moneda)}</div>
                      {m.bono_clientes>0&&(
                        <div style={{color:Q.gold,fontSize:12}}>
                          + {money(m.bono_clientes,m.moneda)} en bonos</div>
                      )}
                    </div>
                  </div>
                ))}
                <div style={{color:Q.amber,fontSize:12,marginTop:10,
                  lineHeight:1.5,fontFamily:F_BODY}}>
                  Es plata de los jugadores, no ganancia: un pasivo. La
                  utilidad está en el Resumen.</div>
              </GCard>

              <div style={{color:Q.muted,fontSize:12,marginBottom:6,
                fontFamily:F_BODY}}>Por agencia</div>
              {(caja.agencias||[]).filter(a=>a.movimientos>0||a.clientes>0)
                .map(a=>(
                <GCard key={a.code} style={{padding:"12px 12px",marginBottom:7}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:12.5,fontWeight:700,
                        fontFamily:F_BODY}}>{a.name}</div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:1}}>
                        {a.code} · {a.clientes} clientes · {a.movimientos} movs</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:a.neto_caja>=0?Q.green:Q.red,
                        fontWeight:800,fontSize:14,
                        fontFamily:F_BODY}}>
                        {montoAgCaja(a.neto_caja,a.moneda)}</div>
                      <div style={{color:Q.dim,fontSize:12}}>
                        +{montoAgCaja(a.cargas,a.moneda)} / −{montoAgCaja(a.descargas,a.moneda)}</div>
                    </div>
                  </div>
                  {a.saldo_clientes>0&&(
                    <div style={{color:Q.cyan,fontSize:12,marginTop:5,
                      paddingTop:SPACING[4],borderTop:`1px solid ${Q.border}`}}>
                      Saldo de sus jugadores: {montoAgCaja(a.saldo_clientes,a.moneda)}</div>
                  )}
                </GCard>
              ))}

              {(caja.movimientos||[]).length>0&&(
                <>
                  <div style={{color:Q.muted,fontSize:12,margin:"14px 0 6px",
                    fontFamily:F_BODY}}>
                    Detalle ({caja.movimientos.length})</div>
                  {caja.movimientos.map((m,i)=>{
                    const esCarga=(m.tipo||"").startsWith("carga");
                    return(
                      <div key={i} style={{display:"flex",
                        justifyContent:"space-between",alignItems:"center",
                        padding:"8px 4px",gap:SPACING[8],
                        borderBottom:`1px solid ${Q.border}`}}>
                        <div style={{minWidth:0,flex:1}}>
                          <div style={{color:Q.text,fontSize:12,
                            overflow:"hidden",textOverflow:"ellipsis",
                            whiteSpace:"nowrap"}}>{m.cliente}</div>
                          <div style={{color:Q.dim,fontSize:12}}>
                            {fechaHora(m.fecha)} · {m.agencia} · {m.tipo}</div>
                        </div>
                        <div style={{color:esCarga?Q.green:Q.amber,
                          fontWeight:700,fontSize:13,flexShrink:0,
                          fontFamily:F_BODY}}>
                          {esCarga?"+":"−"}{montoDetCaja(m.monto,m.moneda)}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}

      {vista==="resumen"&&<>
      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Calculando...</div>}

      {data&&(
        <div>
          {/* Cierre is, above all, a time window: the totals below fix one
              number per figure, but say nothing about whether the period
              trended up or down within itself. The chart reads the exact
              desde/hasta already chosen above, no second date control. */}
          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,marginBottom:2,fontFamily:F_BODY}}>Apostado deportivo por día</div>
            {/* The /serie endpoint sums sports only (_calcular_ggr) — the
                casino/ruleta cascade above is not groupable by day cheaply
                yet. Labelling this the same as the total above it, or
                omitting the difference, would read as a wrong total. */}
            <div style={{color:Q.dim,fontSize:12,marginBottom:8,
              fontFamily:F_BODY}}>Serie solo deportivas; el total de arriba incluye casino.</div>
            {cargSerie&&serie===null
              ? <div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[20],
                  fontFamily:F_BODY}}>Cargando tendencia...</div>
              : <LineaTiempo puntos={serie||[]} color={Q.cyan} formato={ars}
                  etiqueta="Apostado deportivo por día"/>}
          </GCard>

          {globFilas.map(m=>(
          <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12,
            background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}06)`}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,marginBottom:10,
              fontFamily:F_BODY}}>
              {data.es_cliente?`Rentabilidad · ${data.cliente_nombre}`
                :rotGlob(filtroAg?"Total de la rama":"Total del sistema",m)}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:SPACING[12]}}>
              {[["Apostado",m.apostado,Q.cyan],
                ["Premios",m.premios,Q.pink],
                ["GGR",m.ggr,Q.green],
                ["Comisiones",m.comisiones,Q.amber]].map(([l,v,c])=>(
                <div key={l}>
                  <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  <div style={{color:c,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>{money(v,m.moneda)}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>Neto de la casa</span>
              <span style={{color:m.neto_casa>=0?Q.green:Q.red,
                fontWeight:900,fontSize:22,
                fontFamily:F_BODY}}>{money(m.neto_casa,m.moneda)}</span>
            </div>
            {m.comision_influencers!=null&&m.comision_influencers>0&&(
              <div style={{marginTop:8,display:"flex",justifyContent:"space-between",
                alignItems:"center"}}>
                <span style={{color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}><Star size={12}/> Comisión influencers</span>
                <span style={{color:Q.gold,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>−{money(m.comision_influencers,m.moneda)}</span>
              </div>
            )}
            {m.neto_final!=null&&(
              <div style={{marginTop:8,paddingTop:SPACING[8],borderTop:`1px solid ${Q.dim}`,
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  Neto final
                  {casinoFilas.some(c=>c.ggr!==0)&&(
                    <span style={{color:Q.dim,fontSize:12,marginLeft:5}}>
                      solo deportivas</span>
                  )}</span>
                <span style={{color:m.neto_final>=0?Q.green:Q.red,
                  fontWeight:900,fontSize:20,
                  fontFamily:F_BODY}}>{money(m.neto_final,m.moneda)}</span>
              </div>
            )}
          </GCard>
          ))}

          {/* Casino y ruleta: se liquidan aparte porque tienen su
              propia comisión, pero el resultado del período es la
              suma de todo.

              Se filtran las filas SIN movimiento para que una moneda que
              aparece sólo porque tiene agencias no dibuje una tarjeta en
              cero, que es la misma regla que ya usa la caja operativa. */}
          {casinoFilas.filter(m=>m.apostado>0||m.ggr!==0).map(m=>(
            <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.gold,fontWeight:700,fontSize:12.5,
                marginBottom:8,fontFamily:F_BODY}}>
                <Icon name="spade" size={14}/> {rotCasino("Casino y ruleta",m)}</div>
              {[["Apostado",m.apostado,Q.text],
                ["Premios",m.premios,Q.muted],
                ["GGR",m.ggr,m.ggr>=0?Q.green:Q.red],
                ["Comisiones",m.comisiones,Q.amber]].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,padding:"4px 0"}}>
                  <span style={{color:Q.muted}}>{l}</span>
                  <span style={{color:c,fontWeight:600,
                    fontFamily:F_BODY}}>{money(v,m.moneda)}</span>
                </div>
              ))}
            </GCard>
          ))}
          {data.casino&&data.casino.error&&(
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.red,fontSize:12,lineHeight:1.45,
                fontFamily:F_BODY}}>
                El casino no se pudo calcular del todo: {data.casino.error}</div>
            </GCard>
          )}

          {/* El resultado de toda la operación, una tarjeta por moneda.

              Los bonos de ESTA fila se buscan por su moneda: la tarjeta los
              resta, así que restar los de la otra moneda sería el mismo
              defecto de mezcla con otra cara. */}
          {totalFilas.map(m=>{
            const b = bonosDe(m);
            return (
            <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                marginBottom:8}}>
                {rotTotal("TOTAL DE LA OPERACIÓN",m)}</div>
              <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Apostado</div>
                  <div style={{color:Q.text,fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {money(m.apostado,m.moneda)}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>GGR</div>
                  <div style={{color:m.ggr>=0?Q.green:Q.red,
                    fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {money(m.ggr,m.moneda)}</div>
                </div>
              </div>
              {/* Bonos y recompensas: plata que salió, así que resta.
                  Se separa por quién la puso. */}
              {b&&(b.casa>0||b.agencias>0)&&(
                <div style={{paddingTop:SPACING[8],marginTop:4,
                  borderTop:`1px solid ${Q.border}`}}>
                  {b.casa>0&&(
                    <div style={{display:"flex",
                      justifyContent:"space-between",fontSize:12,
                      padding:"4px 0"}}>
                      <span style={{color:Q.muted}}>
                        Bono sports sistema</span>
                      <span style={{color:Q.red,
                        fontFamily:F_BODY}}>
                        −{money(b.casa,b.moneda)}</span>
                    </div>
                  )}
                  {b.agencias>0&&(
                    <div style={{display:"flex",
                      justifyContent:"space-between",fontSize:12,
                      padding:"4px 0"}}>
                      <span style={{color:Q.muted}}>
                        Bono sports agencias</span>
                      <span style={{color:Q.amber,
                        fontFamily:F_BODY}}>
                        −{money(b.agencias,b.moneda)}</span>
                    </div>
                  )}
                  {b.recompensas>0&&(
                    <div style={{color:Q.dim,fontSize:12,marginTop:3}}>
                      Incluye {money(b.recompensas,b.moneda)} de recompensas
                      por compartir</div>
                  )}
                </div>
              )}

              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",paddingTop:SPACING[8],
                borderTop:`1px solid ${Q.border}`}}>
                <span style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  Neto de todo
                  {m.margen_pct!=null&&(
                    <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                      margen {m.margen_pct}%</span>
                  )}</span>
                <span style={{color:m.neto_final>=0?Q.green:Q.red,
                  fontWeight:900,fontSize:22,
                  fontFamily:F_BODY}}>
                  {money(m.neto_final,m.moneda)}</span>
              </div>
            </GCard>
            );
          })}

          {data.es_cliente&&data.rendimiento&&(
            <div style={{display:"flex",gap:SPACING[8],marginBottom:12,fontSize:12,
              fontFamily:F_BODY}}>
              <span style={{color:Q.green}}>{data.rendimiento.ganadas} ganadas</span>
              <span style={{color:Q.red}}>{data.rendimiento.perdidas} perdidas</span>
              <span style={{color:Q.amber}}>{data.rendimiento.pendientes} pendientes</span>
            </div>
          )}

          {!filtroAg&&(
            <GCard style={{padding:SPACING[16],marginBottom:12,
              background:`linear-gradient(135deg,${Q.violet}10,${Q.cyan}06)`}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
                fontFamily:F_BODY}}><Icon name="sliders-horizontal" size={14}/> Liquidación automática</div>
              <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                fontFamily:F_BODY}}>
                Genera liquidaciones de agencias e influencers al cierre del período.
                No paga: deja el registro para que lo revises.</div>
              <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
                {[["semanal",<><Calendar size={13}/> Semanal</>],["mensual",<><CalendarDays size={13}/> Mensual</>]].map(([k,l])=>(
                  <button key={k} onClick={()=>togglePeriodo(k)} style={{flex:1,
                    background:liqAuto.includes(k)?`${Q.green}22`:"rgba(255,255,255,0.04)",
                    border:`1px solid ${liqAuto.includes(k)?Q.green:Q.border}`,
                    borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:liqAuto.includes(k)?Q.green:Q.muted,fontSize:12,fontWeight:700,
                    fontFamily:F_BODY}}>
                    {liqAuto.includes(k)?"✓ ":""}{l}</button>
                ))}
              </div>
              <div style={{display:"flex",gap:SPACING[8]}}>
                <Btn label={liqAutoProc?"...":<><Icon name="play" size={13}/> Correr semanal ahora</>}
                  onClick={()=>dispararAuto("semanal")} color={Q.cyan} outline full disabled={liqAutoProc}/>
                <Btn label={liqAutoProc?"...":<><Icon name="play" size={13}/> Correr mensual ahora</>}
                  onClick={()=>dispararAuto("mensual")} color={Q.cyan} outline full disabled={liqAutoProc}/>
              </div>
              {liqAutoMsg&&<div style={{fontSize:12,marginTop:8,
                color:liqAutoMsg.ok?Q.green:Q.red,
                fontFamily:F_BODY}}><Icon name={liqAutoMsg.ok?"circle-check":"triangle-alert"} size={13}/> {liqAutoMsg.text}</div>}
            </GCard>
          )}

          {filtroAg&&!data.es_cliente&&(
            <GCard style={{padding:SPACING[16],marginBottom:12,
              background:`linear-gradient(135deg,${Q.gold}10,${Q.violet}06)`}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
                fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Liquidación de comisiones</div>
              <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                fontFamily:F_BODY}}>
                Genera la liquidación de {filtroAg} para el período elegido.</div>
              <div style={{display:"flex",gap:SPACING[8]}}>
                <Btn label={<><Icon name="clipboard-list" size={13}/> Ver anteriores</>}
                  onClick={()=>{setVerLiq(true);cargarLiquidaciones();}}
                  color={Q.cyan} outline full/>
                <Btn label={liqProc?"...":"Generar"} onClick={generarLiquidacion}
                  color={Q.gold} full disabled={liqProc}/>
              </div>
              {liqMsg&&<div style={{fontSize:12,marginTop:8,
                color:liqMsg.ok?Q.green:Q.red,
                fontFamily:F_BODY}}><Icon name={liqMsg.ok?"circle-check":"triangle-alert"} size={13}/> {liqMsg.text}</div>}
            </GCard>
          )}

          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",marginBottom:8}}>
            <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,fontFamily:F_BODY}}>Por agencia</span>
            <Btn label={<><Icon name="arrow-down-left" size={13}/> CSV</>} onClick={exportar} color={Q.cyan} size="sm"/>
          </div>

          {data.agencias.map(a=>(
            <GCard key={a.code} style={{padding:"12px 12px",marginBottom:6,
              marginLeft:(a.nivel||0)*12}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",marginBottom:6}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{a.name}</div>
                <div style={{color:Q.gold,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>Com {montoAgCierre(a.comision,a.moneda)}</div>
              </div>
              <div style={{display:"flex",gap:SPACING[12],fontSize:12,
                fontFamily:F_BODY}}>
                <span style={{color:Q.muted}}>Apostado <span style={{color:Q.cyan}}>{montoAgCierre(a.apostado,a.moneda)}</span></span>
                <span style={{color:Q.muted}}>Premios <span style={{color:Q.pink}}>{montoAgCierre(a.premios,a.moneda)}</span></span>
                <span style={{color:Q.muted}}>GGR <span style={{color:a.ggr>=0?Q.green:Q.red}}>{montoAgCierre(a.ggr,a.moneda)}</span></span>
              </div>
            </GCard>
          ))}
        </div>
      )}
      </>}

      {vista==="movimientos"&&<>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:8}}>
          <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
            letterSpacing:1,fontFamily:F_BODY}}>
            Movimientos {filtroAg?"de la rama":"del sistema"}</span>
          <Btn label={<><Icon name="arrow-down-left" size={13}/> CSV</>} onClick={exportarMovs} color={Q.cyan} size="sm"/>
        </div>
        {cargMovs&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {movs&&movs.length===0&&!cargMovs&&<div style={{color:Q.muted,fontSize:12,
          textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
          Sin movimientos en el período</div>}
        {(movs||[]).map((m,i)=>{
          const tipoTxt={carga_admin:"Carga admin",retiro_admin:"Descuento admin",
            carga_cliente:"Crédito a cliente",retiro_cliente:"Retiro cliente",
            pago_premio:"Premio pagado",transferencia:"A sub-agencia",recibido:"Recibido",
            carga:"Carga cliente",retiro:"Retiro cliente"}[m.tipo]||m.tipo;
          const pos=m.monto>=0;
          return(
            <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>
                    {tipoTxt}
                    {m.origen==="cliente"&&m.cliente?<span style={{color:Q.cyan}}> · {m.cliente}</span>:null}
                  </div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {m.agencia} · {fechaHora(m.fecha)}{m.por?` · por ${m.por}`:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:pos?Q.green:Q.amber,fontWeight:700,fontSize:14,
                    fontFamily:F_BODY}}>
                    {pos?"+":""}{montoMov(m.monto,m.moneda)}</div>
                  {m.saldo!=null&&<div style={{color:Q.dim,fontSize:12}}>saldo {montoMov(m.saldo,m.moneda)}</div>}
                </div>
              </div>
            </GCard>
          );
        })}
      </>}

      {vista==="apuestas"&&<>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
          letterSpacing:1,marginBottom:8,marginLeft:4,
          fontFamily:F_BODY}}>
          Apuestas {filtroCli?"del cliente":filtroAg?"de la rama":"del sistema"}</div>
        {cargAp&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {apuestas&&apuestas.length===0&&!cargAp&&<div style={{color:Q.muted,fontSize:12,
          textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
          Sin apuestas en el período</div>}
        {(apuestas||[]).map((a,i)=>{
          const col={won:Q.green,ganada:Q.green,lost:Q.red,perdida:Q.red,
            pending:Q.amber,pendiente:Q.amber,active:Q.cyan,
            paid:Q.green}[(a.status||"").toLowerCase()]||Q.muted;
          const abierta=apAbierta===a.code;
          return(
            <GCard key={i} style={{padding:"12px 12px",marginBottom:6,cursor:"pointer"}}
              onClick={()=>setApAbierta(abierta?null:a.code)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>{a.code}
                    <span style={{color:Q.cyan,fontWeight:400}}> · {a.cliente}</span></div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {a.agencia} · {fechaHora(a.fecha)} · {a.picks.length} sel.</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:col,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>{a.status}</div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {montoAp(a.stake,a.moneda)} · {fmt(a.odd)}x</div>
                </div>
              </div>

              {abierta&&(
                <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,
                    fontSize:12,fontFamily:F_BODY}}>
                    <span style={{color:Q.muted}}>Monto <span style={{color:Q.cyan}}>{montoAp(a.stake,a.moneda)}</span></span>
                    <span style={{color:Q.muted}}>Cuota <span style={{color:Q.violet2}}>{fmt(a.odd)}x</span></span>
                    <span style={{color:Q.muted}}>Premio <span style={{color:Q.green}}>{montoAp(a.premio,a.moneda)}</span></span>
                  </div>
                  {a.picks.map((p,j)=>(
                    <div key={j} style={{background:"rgba(255,255,255,0.03)",
                      borderRadius:RADII.md,padding:"8px 12px",marginBottom:5}}>
                      <div style={{color:Q.text,fontSize:12,fontWeight:600,
                        fontFamily:F_BODY}}>
                        {p.home||p.equipo_local||""}{(p.home||p.away)?" vs ":""}{p.away||p.equipo_visitante||""}</div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                        {p.market||p.mercado||p.tipo||"—"}: <span style={{color:Q.cyan}}>
                        {p.selection||p.seleccion||p.pick||"—"}</span>
                        {p.odd||p.cuota?<span style={{color:Q.violet2}}> @ {fmt(p.odd||p.cuota)}</span>:null}</div>
                      {(p.resultado||p.result)&&<div style={{color:col,fontSize:12,marginTop:2,
                        fontFamily:F_BODY}}>{p.resultado||p.result}</div>}
                    </div>
                  ))}
                  {a.picks.length===0&&<div style={{color:Q.muted,fontSize:12,
                    fontFamily:F_BODY}}>Sin detalle de selecciones</div>}
                </div>
              )}
            </GCard>
          );
        })}
      </>}

      {vista==="cashout"&&<>
        <InterruptorCashoutCasa adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
          letterSpacing:1,marginBottom:8,marginLeft:4,
          fontFamily:F_BODY}}>
          Cash outs {filtroAg?"de la rama":"del sistema"}</div>
        {cargCO&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {cashouts&&cashouts.length===0&&!cargCO&&<div style={{color:Q.muted,fontSize:12,
          textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
          Sin cash outs en el período</div>}
        {(cashouts||[]).map((c,i)=>{
          const col={cashed_out:Q.green,cashout_pending:Q.amber}[c.estado]||Q.muted;
          const rot={cashed_out:"Pagado",cashout_pending:"Pend. caja"}[c.estado]||c.estado;
          return(
            <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>{c.code}
                    <span style={{color:Q.cyan,fontWeight:400}}> · {c.cliente}</span></div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {c.agencia} · {fechaHora(c.fecha)}{c.pagado_por?` · pagó ${c.pagado_por}`:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:col,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>{rot}</div>
                  {/* Dibujaba `{ars(c.valor)} {c.moneda}`: un cash out en
                      dólares salía `$500 USD`, el signo de los pesos con el
                      código correcto al lado. La moneda estaba a dos
                      caracteres de donde hacía falta.

                      Va con `{conCodigo:true}` y NO con `formatoDeLista`
                      porque el código ya estaba suelto en TODAS las filas:
                      así una lista en pesos sigue diciendo `$500 ARS`,
                      carácter por carácter igual que hoy, y la de dólares
                      pasa de `$500 USD` a `US$500.00 USD`. Con la regla de
                      la lista, una pantalla en pesos PERDERÍA la etiqueta
                      que hoy tiene, que es un cambio visual que nadie
                      pidió. */}
                  <div style={{color:Q.gold,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>
                    {money(c.valor, c.moneda, {conCodigo:true})}</div>
                </div>
              </div>
            </GCard>
          );
        })}
      </>}

      {vista==="combos"&&<>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
          letterSpacing:1,marginBottom:8,marginLeft:4,
          fontFamily:F_BODY}}>
          Combos generados {filtroAg?"de la rama":"del sistema"}</div>
        {cargCombos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {combos&&combos.length===0&&!cargCombos&&<div style={{color:Q.muted,fontSize:12,
          textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
          Sin combos en el período</div>}
        {(combos||[]).map((c,i)=>(
          <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:12,
                  fontFamily:F_BODY}}>{c.nombre||"Combo"}
                  {c.es_ia
                    ?<span style={{background:`${Q.violet}33`,border:`1px solid ${Q.violet}`,
                        borderRadius:RADII.lg,padding:"4px 8px",fontSize:12,fontWeight:700,
                        color:Q.violet2,marginLeft:6}}><Bot size={12}/> IA</span>
                    :<span style={{background:`${Q.cyan}22`,border:`1px solid ${Q.cyan}`,
                        borderRadius:RADII.lg,padding:"4px 8px",fontSize:12,fontWeight:700,
                        color:Q.cyan,marginLeft:6}}><PenLine size={12}/> Manual</span>}
                  {!c.visible&&<span style={{color:Q.muted,fontSize:12,marginLeft:5}}>oculto</span>}
                </div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {c.creado_por||c.origen} · {fechaHora(c.fecha)}</div>
              </div>
              <div style={{color:Q.green,fontWeight:700,fontSize:14,flexShrink:0,
                fontFamily:F_BODY}}>{fmt(c.odd)}x</div>
            </div>
          </GCard>
        ))}
      </>}

      {verLiq&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
          zIndex:210,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>setVerLiq(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
            borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
            maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
            border:`1px solid ${Q.border}`,borderBottom:"none"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:16}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:16,
                fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Liquidaciones {filtroAg}</div>
              <button onClick={()=>setVerLiq(false)} style={{background:"transparent",
                border:"none",color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            {!liquidaciones&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
              fontFamily:F_BODY}}>Cargando...</div>}
            {liquidaciones&&liquidaciones.length===0&&<div style={{color:Q.muted,
              textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
              No hay liquidaciones generadas</div>}
            {liqFilas.map((l,i)=>(
              <GCard key={i} style={{padding:"12px 16px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",marginBottom:6}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>{l.agencia}</div>
                  <div style={{color:Q.gold,fontWeight:800,fontSize:15,
                    fontFamily:F_BODY}}>{montoLiq(l.comision_total,l.moneda)}</div>
                </div>
                <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
                  {fechaCorta(l.desde)} → {fechaCorta(l.hasta)} · generada {fechaCorta(l.fecha)}</div>
                <div style={{display:"flex",gap:SPACING[12],fontSize:12,
                  fontFamily:F_BODY}}>
                  <span style={{color:Q.muted}}>Apostado <span style={{color:Q.cyan}}>{montoLiq(l.apostado,l.moneda)}</span></span>
                  <span style={{color:Q.muted}}>GGR <span style={{color:Q.green}}>{montoLiq(l.ggr,l.moneda)}</span></span>
                </div>
              </GCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// TabGlobal has no other date state to reuse — every other tab already
// carries a desde/hasta the chart can read, but this one is a snapshot
// dashboard. So this is the one screen where the chart needs its own
// period selector rather than reusing a control that doesn't exist yet.
const RANGOS_GLOBAL = [
  ["semana","Semana"],["mes","Mes"],["trimestre","Trimestre"],
  ["semestre","Semestre"],["año","Año"],["custom","Personalizado"],
];

function TabGlobal({ adminKey, onNoAutorizado, onIr }){
  const [r,setR]=useState(null);
  const [ags,setAgs]=useState([]);
  const [movs,setMovs]=useState([]);
  const [err,setErr]=useState("");

  useEffect(()=>{ (async()=>{
    try{
      const [rr,ra,rm]=await Promise.all([
        fetch(`${API}/api/admin/resumen`,{headers:adminHeaders(adminKey)}),
        fetch(`${API}/api/admin/agencias-detalle`,{headers:adminHeaders(adminKey)}),
        fetch(`${API}/api/admin/historial/movimientos`,{headers:adminHeaders(adminKey)}),
      ]);
      if(rr.status===401){ onNoAutorizado(); return; }
      if(rr.ok) setR(await rr.json());
      if(ra.ok) setAgs((await ra.json()).agencias||[]);
      if(rm.ok) setMovs(((await rm.json()).movimientos||[]).slice(0,10));
    }catch(e){ setErr("Sin conexión con el servidor"); }
  })(); // eslint-disable-next-line
  },[]);

  // Período de la tendencia: no hay "últimas 24h" a propósito — el dueño
  // la descartó y, de todos modos, un cubo por día no puede servirla.
  const [periodo,setPeriodo]=useState("mes");
  const [customDesde,setCustomDesde]=useState(()=>{
    const d=new Date(); d.setMonth(d.getMonth()-1); return d.toISOString().slice(0,10);
  });
  const [customHasta,setCustomHasta]=useState(()=>new Date().toISOString().slice(0,10));
  const [serieG,setSerieG]=useState(null);
  const [cargSerieG,setCargSerieG]=useState(false);

  const rangoDe=(tipo)=>{
    const hoy=new Date(); let d=new Date();
    if(tipo==="semana") d.setDate(hoy.getDate()-7);
    else if(tipo==="mes") d.setMonth(hoy.getMonth()-1);
    else if(tipo==="trimestre") d.setMonth(hoy.getMonth()-3);
    else if(tipo==="semestre") d.setMonth(hoy.getMonth()-6);
    else if(tipo==="año") d.setFullYear(hoy.getFullYear()-1);
    const iso=x=>x.toISOString().slice(0,10);
    return [iso(d),iso(hoy)];
  };
  const [desdeG,hastaG]=periodo==="custom"?[customDesde,customHasta]:rangoDe(periodo);

  const cargarSerieG=async()=>{
    setCargSerieG(true);
    try{
      const q=`metrica=neto_caja&desde=${desdeG}&hasta=${hastaG}`;
      const r=await fetch(`${API}/api/admin/serie?${q}`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setSerieG((await r.json()).puntos||[]);
    }catch(e){}
    setCargSerieG(false);
  };
  useEffect(()=>{ cargarSerieG(); // eslint-disable-next-line
  },[desdeG,hastaG]);

  const tipoTxt={carga:"Carga",retiro:"Retiro",pago_premio:"Premio",ajuste:"Ajuste"};
  const tipoColor={carga:Q.green,retiro:Q.amber,pago_premio:Q.violet2,ajuste:Q.muted};

  if(err) return <div style={{color:Q.red,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>{err}</div>;
  if(!r) return <div style={{color:Q.muted,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>Cargando datos reales...</div>;

  // La plata del tablero viene agrupada por moneda: `resumen` ya no publica
  // un total plano, porque sumar pesos con dólares da un número inventado.
  //
  // La guarda no es defensiva por costumbre. `por_moneda` puede faltar si
  // contesta una respuesta vieja de caché o si el frontend queda desfasado
  // del backend en un despliegue: entonces no se dibuja plata, y es mejor
  // que una pestaña que revienta entera por un `.map` sobre `undefined`.
  const monedas = Array.isArray(r.por_moneda) ? r.por_moneda : [];
  // Con UNA sola moneda la pantalla tiene que verse IGUAL que hoy: mismo
  // KPI, misma tarjeta, sin rótulo nuevo. El dueño abre esto todos los días
  // y una regresión visual en el mundo de una sola moneda es costo real. El
  // código de moneda aparece sólo cuando hay con qué confundirse —ARS, CLP,
  // COP y MXN comparten el signo `$`—.
  const variasMonedas = monedas.length > 1;
  const rotulo = (texto, m) => variasMonedas ? `${texto} · ${m.moneda}` : texto;

  // ÉSTA ES LA LISTA DEL DEFECTO: "ÚLTIMOS MOVIMIENTOS" dibujaba con `ars()`
  // los `$-500`, `$500` y `$2.000` que el dueño vio de `AGE002`, una agencia
  // en dólares. Los diez renglones salen de `/historial/movimientos` y
  // pueden ser de agencias distintas, así que la moneda va por fila.
  //
  // SE DECIDE SOBRE `movs` Y NO SOBRE `r.por_moneda`: los KPI de arriba
  // resumen el día entero y estos diez son los últimos movimientos, que
  // pueden ser todos de una sola agencia. Mirando el tablero se le pondría
  // el código a una lista donde no hay nada que distinguir.
  const montoMov = formatoDeLista(movs);

  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:SPACING[8],marginBottom:12}}>
        <KPI label="Agencias" value={r.agencias} color={Q.cyan} icon={<Store size={16}/>}
          onClick={()=>onIr&&onIr("agencias")}/>
        <KPI label="Usuarios" value={r.usuarios} color={Q.violet} icon={<Icon name="users" size={16}/>}
          onClick={()=>onIr&&onIr("usuarios")}/>
        <KPI label="Tickets hoy" value={r.tickets_hoy} color={Q.amber} icon={<Icon name="receipt-text" size={16}/>}
          onClick={()=>onIr&&onIr("cierre")}/>
        {monedas.map(m=>(
          <KPI key={m.moneda} label={rotulo("Cobrado hoy",m)}
            value={money(m.cobrado_hoy,m.moneda)} color={Q.green}
            icon={<Icon name="wallet-cards" size={16}/>}
            onClick={()=>onIr&&onIr("cierre")}/>
        ))}
      </div>

      {monedas.map(m=>(
      <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12,
        background:`linear-gradient(135deg,${Q.green}10,${Q.violet}06)`}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
          <span style={{color:Q.muted,fontSize:12,
            fontFamily:F_BODY}}>{rotulo("Neto en caja hoy",m)}</span>
          <span style={{color:Q.green,fontWeight:900,fontSize:20,
            fontFamily:F_BODY}}>{money(m.neto_hoy,m.moneda)}</span>
        </div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <span style={{color:Q.muted,fontSize:12}}>Premios pagados hoy</span>
          <span style={{color:Q.pink,fontSize:13,
            fontFamily:F_BODY}}>{money(m.premios_hoy,m.moneda)}</span>
        </div>
      </GCard>
      ))}

      {/* The only KPI here with a trend worth seeing: this is the tab an
          admin opens to judge how the business is doing, not just today's
          snapshot, so it earns the one period selector this screen has. */}
      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
          marginBottom:10,fontFamily:F_BODY}}>Neto en caja por día</div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:12,flexWrap:"wrap"}}>
          {RANGOS_GLOBAL.map(([k,l])=>(
            <button key={k} onClick={()=>setPeriodo(k)} style={{flex:"1 1 auto",
              background:periodo===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${periodo===k?Q.violet:Q.border}`,borderRadius:RADII.md,
              padding:"8px",cursor:"pointer",color:periodo===k?Q.cyan:Q.muted,
              fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>
        {periodo==="custom"&&(
          <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
            <input type="date" value={customDesde} onChange={e=>setCustomDesde(e.target.value)}
              style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
                fontFamily:F_BODY}}/>
            <span style={{color:Q.muted}}>→</span>
            <input type="date" value={customHasta} onChange={e=>setCustomHasta(e.target.value)}
              style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
                fontFamily:F_BODY}}/>
          </div>
        )}
        {cargSerieG&&serieG===null
          ? <div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[20],
              fontFamily:F_BODY}}>Cargando tendencia...</div>
          : <LineaTiempo puntos={serieG||[]} color={Q.green} formato={ars}
              etiqueta="Neto en caja por día"/>}
      </GCard>

      {/* Small single-figure stats used to sit in a `flex:1` row: with
          only one of the two present it stretched to the full content
          width to say one number. An auto-fit grid gives two columns
          when both stats exist and lets a lone one settle at a sane
          card width instead of the whole row. */}
      {(r.boletos_pendientes>0||r.sin_liquidar>0)&&(
        <div style={{display:"grid",
          gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
          gap:SPACING[12],marginBottom:12}}>
          {r.boletos_pendientes>0&&<GCard style={{padding:SPACING[12],textAlign:"center"}}>
            <div style={{color:Q.amber,fontWeight:700,fontSize:18,
              fontFamily:F_BODY}}>{r.boletos_pendientes}</div>
            <div style={{color:Q.muted,fontSize:12}}>boletos sin cobrar</div>
          </GCard>}
          {r.sin_liquidar>0&&<GCard style={{padding:SPACING[12],textAlign:"center"}}>
            <div style={{color:Q.cyan,fontWeight:700,fontSize:18,
              fontFamily:F_BODY}}>{r.sin_liquidar}</div>
            <div style={{color:Q.muted,fontSize:12}}>sin liquidar</div>
          </GCard>}
        </div>
      )}

      {/* No GCard here on purpose: a rounded box per row-list is one
          more block for the eye to parse on a screen that is already
          mostly cards. The rows sit straight on the page background,
          separated by a hairline instead, and the type goes up a step
          since there is no card padding fighting it for room. */}
      {/* Cards mean you can go in, rows mean it already happened. Agencias
          hoy opens into an agency on click — an entity list — so it gets
          the same card grid TabUsuarios uses. Últimos movimientos is a
          log of what already happened, read in time order and not
          clicked, so it stays as rows. Do not re-merge these two
          styles: they were identical before and read as one list. */}
      <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
        textTransform:"uppercase",marginBottom:SPACING[12],
        fontFamily:F_BODY}}><Store size={13}/> Agencias hoy</div>
      {ags.length===0&&<div style={{color:Q.muted,fontSize:12,marginBottom:SPACING[24],
        fontFamily:F_BODY}}>Sin agencias</div>}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
        gap:SPACING[12]}}>
        {ags.map(a=>(
          <GCard key={a.code} onClick={()=>onIr&&onIr("agencias")}
            style={{padding:SPACING[12],cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:TEXT[15],fontWeight:600,
                  fontFamily:F_BODY}}>{a.name}</div>
                <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                  fontFamily:F_BODY}}>{a.code} · {a.tickets_hoy} tickets hoy</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:Q.green,fontWeight:700,fontSize:TEXT[16],
                  fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{money(a.cobrado_hoy,a.moneda)}</div>
                <span style={{color:Q.muted,fontSize:16}}>›</span>
              </div>
            </div>
          </GCard>
        ))}
      </div>

      <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
        textTransform:"uppercase",marginTop:SPACING[32],marginBottom:SPACING[12],
        fontFamily:F_BODY}}><Banknote size={13}/> Últimos movimientos</div>
      {movs.length===0&&<div style={{color:Q.muted,fontSize:12,
        fontFamily:F_BODY}}>Sin movimientos</div>}
      {movs.map((m,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
          borderBottom:i<movs.length-1?`1px solid ${Q.border}55`:"none"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:tipoColor[m.tipo]||Q.text,fontWeight:600,fontSize:TEXT[15],
              fontFamily:F_BODY}}>{tipoTxt[m.tipo]||m.tipo}</div>
            {/* m.usuario can be absent (e.g. an agency-level movement with
                no specific user attached) — was rendered as a bare
                " · · " with the field silently empty. Drop the separator
                when there is no value instead of always printing it. */}
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
              fontFamily:F_BODY}}>{m.agencia}{m.usuario?` · ${m.usuario}`:""} · {fechaHora(m.fecha)}</div>
          </div>
          <div style={{color:m.tipo==="retiro"||m.tipo==="pago_premio"?Q.amber:Q.green,
            fontWeight:700,fontSize:TEXT[16],flexShrink:0,
            fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{montoMov(m.monto,m.moneda)}</div>
        </div>
      ))}
    </div>
  );
}

function TabCombos({ adminKey, onNoAutorizado }){
  const [modo,setModo]=useState("lista");   // lista | crear | escanear
  const [combos,setCombos]=useState(null);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/combos`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setCombos((await r.json()).combos||[]);
    }catch(e){ setCombos([]); }
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const borrar=async(id)=>{
    try{
      await fetch(`${API}/api/admin/combos/${id}`,
        {method:"DELETE",headers:adminHeaders(adminKey)});
      cargar();
    }catch(e){}
  };

  if(modo==="crear") return <CrearComboAdmin adminKey={adminKey}
    onNoAutorizado={onNoAutorizado} onListo={()=>{setModo("lista");cargar();}}
    onVolver={()=>setModo("lista")}/>;
  if(modo==="escanear") return <EscanearComboAdmin adminKey={adminKey}
    onNoAutorizado={onNoAutorizado} onListo={()=>{setModo("lista");cargar();}}
    onVolver={()=>setModo("lista")}/>;

  return(
    <div>
      <PageHeader icon={<Zap/>} title="Combos"
        description="Los combos cargados en el sistema: creá nuevos, escaneá tickets o eliminalos."/>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
        <Btn label="+ Crear combo" onClick={()=>setModo("crear")} color={Q.violet} full/>
        <Btn label={<><Icon name="scan-line" size={13}/> Escanear</>} onClick={()=>setModo("escanear")} color={Q.cyan} full/>
      </div>

      {combos===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {combos&&combos.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{fontSize:26,marginBottom:8}}><Zap size={26}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>No hay combos cargados</div>
        </GCard>
      )}
      {(combos||[]).map(c=>(
        <GCard key={c.id} style={{padding:"12px 16px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:SPACING[8],marginBottom:6}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:600,fontSize:13,
                fontFamily:F_BODY}}>{c.nombre}</div>
              <div style={{color:Q.muted,fontSize:12}}>
                {c.picks.length} picks · {fmt(c.odd_total)}x ·
                {c.origen==="admin"?" Casa":" "+c.creado_por}</div>
            </div>
            <button onClick={()=>borrar(c.id)} style={{background:`${Q.red}18`,
              border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"4px 12px",
              cursor:"pointer",color:Q.red,fontSize:12,fontWeight:700,flexShrink:0,
              fontFamily:F_BODY}}>Borrar</button>
          </div>
          {c.origen==="admin"&&(
            <div style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY}}>
              Agencias: {c.agencias}</div>
          )}
        </GCard>
      ))}
    </div>
  );
}

function CrearComboAdmin({ adminKey, onListo, onVolver, onNoAutorizado }){
  const [deportes,setDeportes]=useState(null);
  // Combos ya creados, para mostrarlos debajo del formulario
  const [lista,setLista]=useState([]);
  const [nombre,setNombre]=useState("");
  const [picks,setPicks]=useState([]);
  const [busqueda,setBusqueda]=useState("");
  const [destBox,setDestBox]=useState(true);
  const [destApp,setDestApp]=useState(false);
  const [destAg,setDestAg]=useState(false);
  const [agencias,setAgencias]=useState("");   // vacío = todas
  const [guardando,setGuardando]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [infList,setInfList]=useState([]);
  const [infCode,setInfCode]=useState("");
  const [codigoSalida,setCodigoSalida]=useState("");
  const [destinatarios,setDestinatarios]=useState({subs:[],influencers:[]});
  const [destSubs,setDestSubs]=useState([]);
  const [destInfs,setDestInfs]=useState([]);
  const [todosSubs,setTodosSubs]=useState(false);
  const [todosInfs,setTodosInfs]=useState(false);

  useEffect(()=>{
    fetch(`${API}/api/live/prematch`).then(r=>r.ok?r.json():null)
      .then(d=>setDeportes(d?.sports||[])).catch(()=>setDeportes([]));
    fetch(`${API}/api/influencers`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{influencers:[]}).then(d=>setInfList(d.influencers||[]))
      .catch(()=>{});
    fetch(`${API}/api/admin/destinatarios`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{subs:[],influencers:[]})
      .then(d=>setDestinatarios({subs:d.subs||[],influencers:d.influencers||[]}))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const toggleDest=(code,lista,setLista)=>{
    setLista(prev=>prev.includes(code)?prev.filter(c=>c!==code):[...prev,code]);
  };

  // `market` y `nombre` son el mercado y el nombre del resultado TAL COMO LOS
  // EMITE GR8, y los trae `nombresDe1x2` leyendo `ev.markets.h2h`. Este
  // `toggle` declaraba CUATRO argumentos y no guardaba ninguno de los dos, así
  // que el combo de la casa armado a mano se publicaba con la etiqueta de
  // pantalla donde va la clave y sin mercado: se vende y no se puede pagar
  // solo. Es la misma clase que el combo ESCANEADO de `publicar`, por otra
  // puerta del mismo panel.
  //
  // Van ADEMÁS de `sel`, que sigue siendo la etiqueta —lo que el jugador lee
  // en la tarjeta del combo— y la identidad del carrito (`id`).
  const toggle=(ev,dep,label,odd,market,nombre)=>{
    const id=`${ev.h}-${ev.a}-${label}`;
    setPicks(p=>{
      const w=p.filter(x=>x.id!==id);
      if(p.find(x=>x.id===id)) return w;
      return[...w,{id,home:ev.h,away:ev.a,sel:label,odd,sport:dep,
        market:market||"",nombre:nombre||"",
        event_id:ev.id,sport_key:ev.sport_key}];
    });
  };
  const tiene=(ev,l)=>picks.some(x=>x.id===`${ev.h}-${ev.a}-${l}`);
  // La misma cuota que el servidor va a guardar, no el producto de las
  // cuotas: `_odd_del_combo` aplica el margen de correlación por partido y el
  // carrito de acá deja combinar dos mercados del mismo partido.
  const {totOdd}=useCuotaCombinada(picks);

  const guardar=async()=>{
    if(!picks.length||guardando) return;
    setGuardando(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/combos`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({
          nombre:nombre||"Combo de la casa",
          // `sel` se queda como está —es la etiqueta que el jugador lee— y el
          // mercado y el nombre del emisor viajan AL LADO. Sin ellos el combo
          // se vendía y lo liquidaba una persona a mano.
          picks:picks.map(p=>({home:p.home,away:p.away,sel:p.sel,odd:p.odd,
            sport:p.sport,event_id:p.event_id,sport_key:p.sport_key,
            market:p.market||"",nombre:p.nombre||""})),
          destino_box:destBox, destino_app:destApp, destino_agencia:destAg,
          agencias: agencias.trim()?agencias.split(",").map(x=>x.trim()):null,
          influencer_code: infCode||null,
          codigo: codigoSalida||null,
          compartir_subs: todosSubs ? "todos" : destSubs,
          compartir_influencers: todosInfs ? "todos" : destInfs,
        }),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Combo publicado",ok:true});
      setTimeout(onListo, 800);
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setGuardando(false);
  };

  const filtrados=(deportes||[])
    .map(d=>({...d,events:(d.events||[]).filter(ev=>{
      const q=busqueda.trim().toLowerCase();
      return !q||`${ev.h} ${ev.a}`.toLowerCase().includes(q);
    })})).filter(d=>d.events.length>0);

  return(
    <div>
      <button onClick={onVolver} style={{background:"transparent",border:"none",
        color:Q.muted,fontSize:22,cursor:"pointer",marginBottom:6,padding:0}}>‹ Volver</button>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:10,
        fontFamily:F_BODY}}>Crear combo de la casa</div>

      <input value={nombre} onChange={e=>setNombre(e.target.value)}
        placeholder="Nombre del combo"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
          color:Q.text,fontSize:16,marginBottom:12,
          fontFamily:F_BODY}}/>

      <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
        marginBottom:6,fontFamily:F_BODY}}>Dónde se muestra</div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
        {[["Box",destBox,setDestBox],["App",destApp,setDestApp],
          ["Agencias",destAg,setDestAg]].map(([l,v,sv])=>(
          <button key={l} onClick={()=>sv(!v)} style={{flex:1,
            background:v?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1.5px solid ${v?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"12px",cursor:"pointer",color:v?Q.cyan:Q.muted,
            fontSize:12,fontWeight:v?700:400,
            fontFamily:F_BODY}}>{v?"✓ ":""}{l}</button>
        ))}
      </div>
      <input value={agencias} onChange={e=>setAgencias(e.target.value)}
        placeholder="Agencias (AGE001,AGE002) o vacío = todas"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
          color:Q.text,fontSize:14,marginBottom:12,
          fontFamily:F_BODY}}/>

      <select value={infCode} onChange={e=>setInfCode(e.target.value)}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${infCode?Q.violet:Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
          color:infCode?Q.text:Q.muted,fontSize:14,marginBottom:8,
          fontFamily:F_BODY}}>
        <option value="">Asignar a influencer (opcional)</option>
        {infList.map(inf=>(
          <option key={inf.code} value={inf.code}>{inf.name} · {inf.codigo_ref}</option>
        ))}
      </select>
      {infCode&&<input value={codigoSalida} onChange={e=>setCodigoSalida(e.target.value.toUpperCase())}
        placeholder="Código de salida del combo (ej: COMBODELDIA)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
          color:Q.text,fontSize:14,marginBottom:12,
          fontFamily:F_BODY}}/>}

      {/* Compartir con agencias e influencers */}
      {(destinatarios.subs.length>0||destinatarios.influencers.length>0)&&(
        <div style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:SPACING[12],marginBottom:12}}>
          <div style={{color:Q.violet,fontWeight:700,fontSize:12,marginBottom:8,
            fontFamily:F_BODY}}><Icon name="arrow-up-right" size={14}/> Compartir este combo</div>

          {destinatarios.subs.length>0&&(
            <div style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                  fontFamily:F_BODY}}><Building2 size={12}/> Agencias</span>
                <button onClick={()=>{setTodosSubs(v=>!v); if(!todosSubs)setDestSubs([]);}}
                  style={{background:todosSubs?`${Q.green}22`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${todosSubs?Q.green:Q.border}`,borderRadius:RADII.md,
                    padding:"4px 12px",cursor:"pointer",color:todosSubs?Q.green:Q.muted,
                    fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                  {todosSubs?"✓ Todas":"Todas"}</button>
              </div>
              {!todosSubs&&<div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8]}}>
                {destinatarios.subs.map(x=>(
                  <button key={x.code} onClick={()=>toggleDest(x.code,destSubs,setDestSubs)}
                    style={{background:destSubs.includes(x.code)?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                      border:`1px solid ${destSubs.includes(x.code)?Q.violet:Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",cursor:"pointer",color:destSubs.includes(x.code)?Q.text:Q.muted,
                      fontSize:12,fontFamily:F_BODY}}>
                    {destSubs.includes(x.code)?"✓ ":""}{x.name}</button>
                ))}
              </div>}
            </div>
          )}

          {destinatarios.influencers.length>0&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                  fontFamily:F_BODY}}><Star size={12}/> Influencers</span>
                <button onClick={()=>{setTodosInfs(v=>!v); if(!todosInfs)setDestInfs([]);}}
                  style={{background:todosInfs?`${Q.green}22`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${todosInfs?Q.green:Q.border}`,borderRadius:RADII.md,
                    padding:"4px 12px",cursor:"pointer",color:todosInfs?Q.green:Q.muted,
                    fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                  {todosInfs?"✓ Todos":"Todos"}</button>
              </div>
              {!todosInfs&&<div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8]}}>
                {destinatarios.influencers.map(x=>(
                  <button key={x.code} onClick={()=>toggleDest(x.code,destInfs,setDestInfs)}
                    style={{background:destInfs.includes(x.code)?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                      border:`1px solid ${destInfs.includes(x.code)?Q.violet:Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",cursor:"pointer",color:destInfs.includes(x.code)?Q.text:Q.muted,
                      fontSize:12,fontFamily:F_BODY}}>
                    {destInfs.includes(x.code)?"✓ ":""}{x.name}</button>
                ))}
              </div>}
            </div>
          )}
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
        background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
        borderRadius:RADII.md,padding:"8px 12px",marginBottom:10}}>
        <span><Icon name="search" size={14}/></span>
        <input value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          placeholder="Buscar equipo..."
          style={{background:"transparent",border:"none",color:Q.text,fontSize:14,
            flex:1,minWidth:0,fontFamily:F_BODY}}/>
      </div>

      {!deportes&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Cargando...</div>}

      <div style={{maxHeight:"36vh",overflowY:"auto",marginBottom:12}}>
        {filtrados.map(d=>(
          <div key={d.name} style={{marginBottom:10}}>
            <div style={{color:Q.violet2,fontSize:12,fontWeight:700,marginBottom:5,
              fontFamily:F_BODY}}>{d.icon} {d.name}</div>
            {d.events.map(ev=>(
              <div key={ev.id||(ev.h+ev.a)} style={{marginBottom:8,paddingBottom:SPACING[8],
                borderBottom:`1px solid ${Q.dim}`}}>
                <div style={{color:Q.text,fontSize:12,marginBottom:5,
                  fontFamily:F_BODY}}>{ev.h} vs {ev.a}</div>
                <div style={{display:"flex",gap:SPACING[4]}}>
                  {/* La etiqueta NO se toca: el jugador sigue leyendo
                      "Boca gana" y "Empate". Lo que se agrega es el nombre
                      del emisor y su mercado, que vienen de `nombresDe1x2`
                      leyendo el diccionario del catálogo y van vacíos cuando
                      ese diccionario no tiene el resultado. */}
                  {[{l:ev.h+" gana",o:ev.odds?.L,...nombresDe1x2(ev).L},
                    ev.odds?.E?{l:"Empate",o:ev.odds.E,
                                ...nombresDe1x2(ev).E}:null,
                    {l:ev.a+" gana",o:ev.odds?.V,...nombresDe1x2(ev).V}]
                    .filter(Boolean).filter(x=>x.o).map(opt=>(
                    <button key={opt.l} onClick={()=>toggle(ev,d.name,opt.l,
                      opt.o,opt.market,opt.nombre)} style={{
                      flex:1,minWidth:0,
                      background:tiene(ev,opt.l)?`${Q.cyan}33`:"rgba(255,255,255,0.04)",
                      border:`1.5px solid ${tiene(ev,opt.l)?Q.cyan:Q.border}`,
                      borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",textAlign:"center"}}>
                      <div style={{color:Q.muted,fontSize:12,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{opt.l}</div>
                      <div style={{color:tiene(ev,opt.l)?Q.cyan:Q.text,fontWeight:700,
                        fontSize:13,fontFamily:F_BODY}}>{opt.o}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {msg&&<div style={{fontSize:12,marginBottom:10,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {picks.length>0&&(
        <GCard style={{padding:SPACING[16]}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <span style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>{picks.length} picks</span>
            <span style={{color:Q.gold,fontWeight:900,fontSize:18,
              fontFamily:F_BODY}}>{fmt(totOdd)}x</span>
          </div>
          <Btn label={guardando?"PUBLICANDO...":"Publicar combo"} onClick={guardar}
            color={Q.violet} full size="lg" disabled={guardando}/>
        </GCard>
      )}
    </div>
  );
}

function CorregirPickAdmin({ pick, adminKey, onAplicar, onQuitar }){
  const [modo,setModo]=useState("seleccion");
  const [buscar,setBuscar]=useState("");
  const [encontrados,setEncontrados]=useState([]);
  const [buscando,setBuscando]=useState(false);
  const [ajuste,setAjuste]=useState("");
  const opciones=pick.opciones||[];

  const buscarEventos=async()=>{
    if(buscar.trim().length<2||buscando) return;
    setBuscando(true);
    try{
      const r=await fetch(`${API}/api/buscar-eventos?q=${encodeURIComponent(buscar.trim())}`,
        {headers:adminHeaders(adminKey)});
      const d=await r.json();
      setEncontrados(d.eventos||[]);
    }catch(e){ setEncontrados([]); }
    setBuscando(false);
  };
  // El mismo par que `CorregirPickApp` (`App.jsx`), `CorregirPickBox`
  // (`Box.jsx`) y `CorregirPickWeb` (`Web.jsx`), y por lo mismo: `op.sel` y
  // `op.mkt` son las ETIQUETAS que el admin lee, `op.nombre` y `op.market`
  // son los nombres que EMITE GR8 y los únicos que el índice de claves de
  // selección conoce.
  //
  // `market:op.mkt` guardaba la etiqueta "1X2" como mercado del pick, y esa
  // etiqueta corresponde a DOS mercados del emisor según el deporte
  // (`h2h_3_way` si se puede empatar, `h2h` si no): ninguna traducción
  // nuestra puede acertar para los dos.
  //
  // Ojo con el alcance de ESTE corrector: lo único que consume su pick es
  // `publicar`, que escribe en `/api/admin/combos`, y ese endpoint todavía
  // no guarda `market` (casino_api.py:16600). O sea que acá se deja de
  // mentir, pero el combo publicado sigue sin clave de liquidación hasta que
  // el backend acepte el campo. Está anotado en
  // `odd/tasks/la-etiqueta-donde-va-la-clave.md`. Ver E5a-1b.
  const elegirOpcion=(op)=>{
    const cuota=ajuste.trim()?parseFloat(ajuste):op.odd;
    onAplicar({selection:op.sel,nombre:op.nombre,market:op.market,
      odd_nuestra:op.odd,odd_final:cuota,estado:"ok"});
  };
  const elegirNuevo=(ev,op)=>{
    const cuota=ajuste.trim()?parseFloat(ajuste):op.odd;
    onAplicar({home:ev.home,away:ev.away,home_real:ev.home,away_real:ev.away,
      event_id:ev.event_id,sport_key:ev.sport_key,selection:op.sel,
      nombre:op.nombre,market:op.market,
      odd_nuestra:op.odd,odd_final:cuota,estado:"ok",opciones:ev.opciones});
  };

  return(
    <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
      {(pick.home_real||pick.away_real)&&(
        <div style={{color:Q.muted,fontSize:12,marginBottom:8,
          fontFamily:F_BODY}}>
          En el sistema: <span style={{color:Q.cyan}}>{pick.home_real} vs {pick.away_real}</span>
        </div>
      )}
      {/* Candidatos parecidos (cuando no encontró el partido) */}
      {(pick.candidatos&&pick.candidatos.length>0)&&(
        <div style={{marginBottom:10}}>
          <div style={{color:Q.amber,fontSize:12,marginBottom:6,
            fontFamily:F_BODY}}>
            ¿Quisiste decir alguno de estos?
          </div>
          {pick.candidatos.map((ev,ci)=>(
            <div key={ci} style={{background:`${Q.amber}0E`,
              border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
              padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:5,
                fontFamily:F_BODY}}>{ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevo(ev,op)} style={{
                    background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <button onClick={()=>setModo("seleccion")} style={{flex:1,
          background:modo==="seleccion"?`${Q.violet}33`:"rgba(255,255,255,0.04)",
          border:`1px solid ${modo==="seleccion"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="seleccion"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700,fontFamily:F_BODY}}>Selección</button>
        <button onClick={()=>setModo("partido")} style={{flex:1,
          background:modo==="partido"?`${Q.violet}33`:"rgba(255,255,255,0.04)",
          border:`1px solid ${modo==="partido"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="partido"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700,fontFamily:F_BODY}}>Otro partido</button>
      </div>

      {modo==="seleccion"&&(
        <div>
          {opciones.length===0&&<div style={{color:Q.muted,fontSize:12,marginBottom:8,
            fontFamily:F_BODY}}>Sin opciones. Probá "Otro partido".</div>}
          {opciones.map((op,k)=>(
            <button key={k} onClick={()=>elegirOpcion(op)} style={{width:"100%",
              display:"flex",justifyContent:"space-between",alignItems:"center",
              background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",marginBottom:5,cursor:"pointer"}}>
              <span style={{color:Q.text,fontSize:12,
                fontFamily:F_BODY}}>{op.sel}</span>
              <span style={{color:Q.cyan,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{fmt(op.odd)}</span>
            </button>
          ))}
        </div>
      )}

      {modo==="partido"&&(
        <div>
          <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
            <input value={buscar} onChange={e=>setBuscar(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&buscarEventos()}
              placeholder="Nombre del equipo..."
              style={{flex:1,background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
            <button onClick={buscarEventos} style={{background:`${Q.violet}33`,
              border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"0 16px",
              cursor:"pointer",color:Q.cyan,fontSize:12,fontWeight:700,
              fontFamily:F_BODY}}>{buscando?"...":"Buscar"}</button>
          </div>
          {encontrados.map((ev,k)=>(
            <div key={k} style={{background:"rgba(255,255,255,0.03)",
              border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:6,
                fontFamily:F_BODY}}>{ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevo(ev,op)} style={{
                    background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginTop:8}}>
        <input value={ajuste} onChange={e=>setAjuste(e.target.value)}
          placeholder="Cuota a mano (opcional)" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
        <button onClick={onQuitar} style={{background:`${Q.red}18`,
          border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"8px 12px",
          cursor:"pointer",color:Q.red,fontSize:12,fontWeight:700,
          fontFamily:F_BODY}}>Quitar</button>
      </div>
    </div>
  );
}

function EscanearComboAdmin({ adminKey, onListo, onVolver, onNoAutorizado }){
  const [imgs,setImgs]=useState([]);
  const [camaraAbierta,setCamaraAbierta]=useState(false);
  const [analizando,setAnalizando]=useState(false);
  const [res,setRes]=useState(null);
  const [corrigiendo,setCorrigiendo]=useState(null);
  const aplicarCorreccion=(ix,nuevo)=>{
    setRes(r=>{
      const picks=r.picks.map((p,i)=>i===ix?{...p,...nuevo}:p);
      return {...r,picks,picks_ok:picks.filter(p=>p.odd_final).length};
    });
    setCorrigiendo(null);
  };
  const quitarPickM=(ix)=>{
    setRes(r=>{
      const picks=r.picks.filter((_,i)=>i!==ix);
      return {...r,picks,picks_total:picks.length,
        picks_ok:picks.filter(p=>p.odd_final).length};
    });
    setCorrigiendo(null);
  };
  const [usarAjustada,setUsarAjustada]=useState(true);   // true=nuestra, false=original
  const [nombre,setNombre]=useState("");
  const [destBox,setDestBox]=useState(true);
  const [destApp,setDestApp]=useState(false);
  const [destAg,setDestAg]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [pub,setPub]=useState(false);

  const elegir=(e)=>{
    const files=Array.from(e.target.files||[]);
    if(!files.length) return;
    files.forEach(f=>{
      if(f.size>8*1024*1024){ setMsg({text:"Una imagen supera 8MB",ok:false}); return; }
      const rd=new FileReader();
      rd.onload=()=>setImgs(prev=>[...prev,{b64:rd.result.split(",")[1],
        tipo:f.type||"image/jpeg",preview:rd.result}]);
      rd.readAsDataURL(f);
    });
    e.target.value="";
  };
  const quitarImg=(i)=>setImgs(prev=>prev.filter((_,k)=>k!==i));
  const analizar=async()=>{
    if(!imgs.length||analizando) return;
    setAnalizando(true); setMsg(null); setRes(null);
    try{
      const r=await fetch(`${API}/api/admin/escanear-combo`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({imagenes:imgs.map(im=>({data:im.b64,media_type:im.tipo}))}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!d.ok) setMsg({text:d.mensaje||"No se pudo leer",ok:false});
      else setRes(d);
    }catch(e){ setMsg({text:"Error al analizar",ok:false}); }
    setAnalizando(false);
  };
  const publicar=async()=>{
    if(!res||pub) return;
    // El MISMO campo que se publica abajo. Filtrar por uno y publicar otro es
    // literalmente el defecto que esto arregla: el escáner escribía el precio
    // de la rama de coincidencia exacta en `odd_ajustada` y el de la rama de
    // línea sugerida en `odd_final`, esta pantalla filtraba por `odd_nuestra`
    // y publicaba `odd_ajustada`, y el pick sugerido salía con `odd: null`.
    // Hoy las dos ramas de los dos escáneres escriben `odd_final`, que es
    // además el nombre que leen `betslipPicks.js`, `App.jsx`, `Web.jsx`,
    // `Box.jsx` y `Agencia.jsx`.
    const validos=res.picks.filter(p=>p.odd_final);
    if(!validos.length){ setMsg({text:"No hay picks que podamos tomar",ok:false}); return; }
    setPub(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/combos`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({
          nombre:nombre||"Combo escaneado",
          fuente:"escaneo",
          // `sel` se queda como está —es la etiqueta que el jugador lee en la
          // tarjeta del combo— y el mercado y el nombre del emisor viajan AL
          // LADO, en `market` y `nombre`. El escáner ya los trae desde L1
          // (`CuotaNuestra` pasó de 3 a 5 campos) y esta pantalla los tiraba;
          // y hasta L4a `/api/admin/combos` los descartaba de su lista
          // blanca, así que el combo publicado no podía liquidar NUNCA.
          //
          // `sport:p.market` queda como estaba: guarda el mercado en el campo
          // del deporte, es un defecto anotado aparte y cambiarlo acá movería
          // lo que el jugador lee sin arreglar ninguna liquidación.
          picks:validos.map(p=>({home:p.home,away:p.away,sel:p.selection,
            odd: usarAjustada? p.odd_final : (p.odd_original||p.odd_final),
            sport:p.market,event_id:p.event_id,sport_key:p.sport_key,
            market:p.market||"",nombre:p.nombre||""})),
          destino_box:destBox, destino_app:destApp, destino_agencia:destAg,
        }),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Combo publicado",ok:true});
      setTimeout(onListo, 800);
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setPub(false);
  };

  return(
    <div>
      <button onClick={onVolver} style={{background:"transparent",border:"none",
        color:Q.muted,fontSize:22,cursor:"pointer",marginBottom:6,padding:0}}>‹ Volver</button>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:10,
        fontFamily:F_BODY}}>Escanear y publicar</div>

      {imgs.length>0&&(
        <div style={{display:"flex",gap:SPACING[8],flexWrap:"wrap",marginBottom:10}}>
          {imgs.map((im,i)=>(
            <div key={i} style={{position:"relative"}}>
              <img src={im.preview} alt={"f"+i} style={{width:64,height:64,
                objectFit:"cover",borderRadius:RADII.md,border:`1px solid ${Q.border}`}}/>
              <button onClick={()=>quitarImg(i)} style={{position:"absolute",
                top:-6,right:-6,width:20,height:20,borderRadius:"50%",
                background:Q.red,border:"none",color:"#fff",fontSize:12,
                cursor:"pointer",lineHeight:1}}>✕</button>
            </div>
          ))}
        </div>
      )}
      {!res&&(
        <div style={{display:"flex",gap:SPACING[12],marginBottom:12}}>
          <button onClick={()=>setCamaraAbierta(true)} style={{flex:1,
            background:"transparent",border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
            padding:"24px 12px",textAlign:"center",cursor:"pointer"}}>
            <div style={{fontSize:26,marginBottom:5}}><Icon name="camera" size={26}/></div>
            <div style={{fontWeight:700,fontSize:12,color:Q.text,
              fontFamily:F_BODY}}>Cámara</div>
          </button>
          <label style={{flex:1,border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
            padding:"24px 12px",textAlign:"center",cursor:"pointer"}}>
            <input type="file" accept="image/*" multiple onChange={elegir} style={{display:"none"}}/>
            <div style={{marginBottom:5}}><ImageIcon size={26}/></div>
            <div style={{fontWeight:700,fontSize:12,color:Q.text,
              fontFamily:F_BODY}}>
              {imgs.length>0?"Agregar más":"Archivo"}</div>
          </label>
        </div>
      )}

      {camaraAbierta&&(
        <CameraCapture Q={Q} F_BODY={F_BODY}
          onCapture={(img)=>{ setImgs(prev=>[...prev,img]); setCamaraAbierta(false); }}
          onClose={()=>setCamaraAbierta(false)}/>
      )}

      {imgs.length>0&&!res&&(
        <div style={{marginBottom:12}}>
          <Btn label={analizando?"LEYENDO...":<><Icon name="search" size={13}/> Analizar {imgs.length} foto{imgs.length>1?"s":""}</>}
            onClick={analizar} color={Q.cyan} full disabled={analizando}/>
        </div>
      )}

      {msg&&<div style={{fontSize:12,marginBottom:10,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {res&&(
        <div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:10,
            fontFamily:F_BODY}}>
            Leímos {res.picks_total} · tomamos {res.picks_ok}</div>

          {res.picks.map((p,i)=>(
            <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,
                fontFamily:F_BODY}}>{p.home} vs {p.away}</div>
              <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>{p.selection}</div>
              <div style={{display:"flex",gap:SPACING[12],fontSize:12,marginBottom:6,
                fontFamily:F_BODY}}>
                {p.odd_original&&<span style={{color:Q.dim}}>Origen {fmt(p.odd_original)}</span>}
                {p.odd_final?(
                  <span style={{color:Q.green}}>Nuestra {fmt(p.odd_nuestra)}
                    {p.odd_final!==p.odd_nuestra?` → ${fmt(p.odd_final)}`:""}</span>
                ):<span style={{color:Q.red}}>No disponible</span>}
              </div>
              <button onClick={()=>setCorrigiendo(corrigiendo===i?null:i)} style={{
                background:corrigiendo===i?`${Q.cyan}22`:"transparent",
                border:`1px solid ${corrigiendo===i?Q.cyan:Q.dim}`,borderRadius:RADII.md,
                padding:"4px 12px",cursor:"pointer",color:corrigiendo===i?Q.cyan:Q.muted,
                fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                {corrigiendo===i?"✕ Cerrar":<><Pencil size={12}/> Está mal / Corregir</>}</button>
              {corrigiendo===i&&(
                <CorregirPickAdmin pick={p} adminKey={adminKey}
                  onAplicar={(nuevo)=>aplicarCorreccion(i,nuevo)}
                  onQuitar={()=>quitarPickM(i)}/>
              )}
            </GCard>
          ))}

          {res.picks_ok>0&&(
            <GCard style={{padding:SPACING[16],marginTop:8}}>
              <input value={nombre} onChange={e=>setNombre(e.target.value)}
                placeholder="Nombre del combo"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                  color:Q.text,fontSize:16,marginBottom:10,
                  fontFamily:F_BODY}}/>

              <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
                letterSpacing:1,marginBottom:5,
                fontFamily:F_BODY}}>Qué cuota usar</div>
              <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
                <button onClick={()=>setUsarAjustada(true)} style={{flex:1,
                  background:usarAjustada?`${Q.green}22`:"rgba(255,255,255,0.04)",
                  border:`1.5px solid ${usarAjustada?Q.green:Q.border}`,borderRadius:RADII.md,
                  padding:"8px",cursor:"pointer",color:usarAjustada?Q.green:Q.muted,
                  fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>Nuestra (ajustada)</button>
                <button onClick={()=>setUsarAjustada(false)} style={{flex:1,
                  background:!usarAjustada?`${Q.amber}22`:"rgba(255,255,255,0.04)",
                  border:`1.5px solid ${!usarAjustada?Q.amber:Q.border}`,borderRadius:RADII.md,
                  padding:"8px",cursor:"pointer",color:!usarAjustada?Q.amber:Q.muted,
                  fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>Original</button>
              </div>

              <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
                letterSpacing:1,marginBottom:5,
                fontFamily:F_BODY}}>Dónde publicar</div>
              <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
                {[["Box",destBox,setDestBox],["App",destApp,setDestApp],
                  ["Agencias",destAg,setDestAg]].map(([l,v,sv])=>(
                  <button key={l} onClick={()=>sv(!v)} style={{flex:1,
                    background:v?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                    border:`1.5px solid ${v?Q.violet:Q.border}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:v?Q.cyan:Q.muted,
                    fontSize:12,fontWeight:v?700:400,
                    fontFamily:F_BODY}}>{v?"✓ ":""}{l}</button>
                ))}
              </div>

              <Btn label={pub?"PUBLICANDO...":"Publicar combo"} onClick={publicar}
                color={Q.violet} full size="lg" disabled={pub}/>
            </GCard>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FICHA DE CLIENTE — ver, cargar/retirar, bloquear
// ═══════════════════════════════════════════════════════════════
// ── Código de reserva ─────────────────────────────────────────
// Se muestra grande y con QR en todas las pantallas donde aparece un
// boleto pendiente: es lo que el cliente lleva al mostrador. Tenerlo
// escondido en un detalle obligaba a buscarlo con el cajero esperando.
function CodigoReserva({ code, compacto=false, vence=null }){
  const [copiado,setCopiado]=useState(false);
  if(!code) return null;

  const copiar=()=>{
    try{
      navigator.clipboard.writeText(code);
      setCopiado(true);
      setTimeout(()=>setCopiado(false),1500);
    }catch(e){}
  };

  return(
    <div style={{background:Q.inset||"rgba(255,255,255,0.04)",
      border:`1px solid ${Q.gold}`,borderRadius:RADII.lg,
      padding:compacto?"10px 12px":"14px",marginTop:8,
      display:"flex",alignItems:"center",gap:SPACING[12]}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,letterSpacing:1.4,color:Q.dim,
          fontWeight:700}}>CÓDIGO DE RESERVA</div>
        <div onClick={copiar} style={{fontFamily:F_BODY,
          fontSize:compacto?26:32,fontWeight:700,color:Q.gold,
          letterSpacing:1.5,lineHeight:1.1,cursor:"pointer"}}>
          {code}</div>
        <div style={{fontSize:12,color:copiado?Q.green:Q.muted,marginTop:2}}>
          {copiado?"✓ Copiado":"Tocá para copiar · Presentalo en la agencia"}</div>
        {vence&&<div style={{fontSize:12,color:Q.amber,marginTop:2}}>
          Vence {vence}</div>}
      </div>
      <img alt={`Código QR ${code}`} loading="lazy"
        src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=0&data=${encodeURIComponent(code)}`}
        style={{width:compacto?64:84,height:compacto?64:84,borderRadius:RADII.md,
          background:"#fff",padding:SPACING[4],flexShrink:0}}/>
    </div>
  );
}

// ── Detalle de una apuesta ────────────────────────────────────
// Usa /api/betslip/{code}, que NO es público: pide la clave de admin o una
// sesión de agencia de la rama del boleto. Esta llamada iba sin cabeceras y
// recibía 401 contra producción, así que la pantalla no cargaba nunca; el
// `adminKey` ya estaba en el alcance y no se usaba. La casa manda su clave
// porque no tiene rama y abre el detalle de todas las agencias.
function DetalleApuesta({ code, adminKey, onCerrar, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [err,setErr]=useState("");

  useEffect(()=>{
    let vivo=true;
    fetch(`${API}/api/betslip/${encodeURIComponent(code)}`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{
        // El 401 lo atiende el que nos abrió, igual que el resto del panel:
        // la clave venció o cambió y hay que volver a pedirla, no mostrar
        // "Error 401" adentro del detalle.
        if(r.status===401){ onNoAutorizado&&onNoAutorizado(); return null; }
        return r.ok?r.json():Promise.reject(new Error(`Error ${r.status}`));
      })
      .then(x=>vivo&&x&&setD(x))
      .catch(e=>vivo&&setErr(e.message||"No se pudo cargar"));
    return()=>{ vivo=false; };
  },[code,adminKey,onNoAutorizado]);

  const ESTADOS={pending:"Reservada, sin pagar", active:"En juego",
    won:"Ganada", lost:"Perdida", paid:"Pagada",
    cashed_out:"Cerrada con cash out", cancelled:"Cancelada"};

  return(
    <div onClick={onCerrar} style={{position:"fixed",inset:0,zIndex:300,
      background:"rgba(2,2,8,.9)",display:"flex",alignItems:"center",
      justifyContent:"center",padding:SPACING[16]}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",
        maxWidth:420,maxHeight:"85vh",overflowY:"auto",background:Q.surface,
        border:`1px solid ${Q.border}`,borderRadius:RADII.lg,padding:SPACING[16]}}>

        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:800,fontSize:16,
            fontFamily:F_BODY}}>{code}</div>
          <button onClick={onCerrar} style={{background:"transparent",
            border:"none",color:Q.muted,fontSize:22,cursor:"pointer",
            padding:0}}>×</button>
        </div>

        {err&&<div style={{color:Q.red,fontSize:12.5}}>{err}</div>}
        {!d&&!err&&<div style={{color:Q.muted,fontSize:12.5}}>Cargando…</div>}

        {d&&(
          <>
            {(d.status||"").toLowerCase()==="pending"&&(
              <CodigoReserva code={code} compacto/>
            )}
            <div style={{display:"flex",justifyContent:"space-between",
              marginBottom:10,marginTop:10,fontSize:12}}>
              <span style={{color:Q.muted}}>
                {ESTADOS[(d.status||"").toLowerCase()]||d.status}</span>
              <span style={{color:Q.gold,fontWeight:800,fontSize:17,
                fontFamily:F_BODY}}>
                {Number(d.odd_total||0).toFixed(2)}</span>
            </div>

            {(d.picks||[]).map((p,i)=>(
              <div key={i} style={{padding:"8px 0",
                borderTop:`1px solid ${Q.border}`}}>
                <div style={{color:Q.muted,fontSize:12}}>
                  {p.home} — {p.away}</div>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline",gap:SPACING[8],marginTop:2}}>
                  <span style={{color:Q.text,fontSize:12.5,fontWeight:600}}>
                    {p.sel}</span>
                  <span style={{color:Q.gold,fontSize:13,
                    fontFamily:F_BODY}}>
                    {Number(p.odd||0).toFixed(2)}</span>
                </div>
                {p.event_id&&(
                  <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                    id {String(p.event_id).slice(0,26)}</div>
                )}
              </div>
            ))}

            <div style={{borderTop:`1px solid ${Q.border}`,marginTop:10,
              paddingTop:SPACING[12],display:"flex",justifyContent:"space-between",
              fontSize:12}}>
              <span style={{color:Q.muted}}>Apostado</span>
              <span style={{color:Q.text}}>{money(d.stake, d?.moneda)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,marginTop:4}}>
              <span style={{color:Q.muted}}>A cobrar</span>
              <span style={{color:Q.gold,fontWeight:700}}>
                {money(d.potential_win, d?.moneda)}</span>
            </div>

            {!(d.picks||[]).some(p=>p.event_id)&&(
              <div style={{marginTop:12,color:Q.amber,fontSize:12,
                lineHeight:1.5}}>
                Esta apuesta no guardó el identificador del evento, así que
                la liquidación automática no la puede cruzar por ID. Hay que
                resolverla a mano.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FichaCliente({ userId, adminKey, onCerrar, onCambio, onNoAutorizado }){
  const [f,setF]=useState(null);
  const [resetOpen,setResetOpen]=useState(false);
  const [err,setErr]=useState("");
  const [monto,setMonto]=useState("");
  const [detalle,setDetalle]=useState("");
  const [operando,setOperando]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [motivo,setMotivo]=useState("");
  const [confirmBloq,setConfirmBloq]=useState(false);
  const [verApuesta,setVerApuesta]=useState(null);
  const [todasApuestas,setTodasApuestas]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/clientes/${userId}`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setF(await r.json());
      else setErr("No se pudo cargar la ficha");
    }catch(e){ setErr("Sin conexión"); }
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[userId]);

  const mover=async(signo)=>{
    const m=parseInt(monto);
    if(!m||m<=0){ setMsg({text:"Poné un monto",ok:false}); return; }
    setOperando(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/clientes/${userId}/saldo`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({monto:m*signo, detalle}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:signo>0?"Crédito cargado":"Retiro hecho",ok:true});
      setMonto(""); setDetalle(""); cargar(); onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setOperando(false);
  };

  const toggleBloqueo=async()=>{
    const bloquear=!f.bloqueado;
    if(bloquear && !confirmBloq){ setConfirmBloq(true); return; }
    setOperando(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/bloquear`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({tipo:"cliente",objetivo:userId,
          bloquear, motivo}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setConfirmBloq(false); setMotivo("");
      // "🔒 Cliente bloqueado" carries no ✅, but the old render already
      // matched either status glyph before deciding green — unlike
      // Agencia.jsx, this screen never had the red-block asymmetry.
      setMsg({text:bloquear?"🔒 Cliente bloqueado":"Cliente desbloqueado",ok:true});
      cargar(); onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setOperando(false);
  };

  const estadoBet={won:{t:"Ganada",c:Q.green},ganada:{t:"Ganada",c:Q.green},
    lost:{t:"Perdida",c:Q.red},perdida:{t:"Perdida",c:Q.red},
    pending:{t:"Pendiente",c:Q.amber},pendiente:{t:"Pendiente",c:Q.amber},
    active:{t:"En juego",c:Q.cyan}};

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",
      zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"88dvh",overflowY:"auto",
        padding:"0 20px calc(24px + env(safe-area-inset-bottom))",
        border:`1px solid ${Q.border}`,borderBottom:"none",
        WebkitOverflowScrolling:"touch"}}>

        {/* El encabezado queda fijo: por larga que sea la ficha, el
            botón de cerrar siempre está a mano */}
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16,position:"sticky",top:0,
          background:Q.deep,paddingTop:SPACING[20],paddingBottom:SPACING[12],zIndex:2}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:17,
            fontFamily:F_BODY}}>Ficha de cliente</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        {err&&<div style={{color:Q.red,padding:SPACING[12],
          fontFamily:F_BODY}}>{err}</div>}
        {!f&&!err&&<div style={{color:Q.muted,padding:SPACING[20],textAlign:"center",
          fontFamily:F_BODY}}>Cargando ficha...</div>}

        {f&&(
          <div>
            {/* Cabecera */}
            <GCard glow={f.bloqueado?Q.red:null} style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start"}}>
                <div>
                  <div style={{color:Q.text,fontWeight:700,fontSize:18,
                    fontFamily:F_BODY}}>{f.nombre}</div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                    {f.es_directo
                      ? <><Smartphone size={12}/> Cliente de Telegram (sin agencia)</>
                      : `${f.agencia_nombre} (${f.agencia})`}
                    {f.telefono?` · ${f.telefono}`:""}</div>
                  <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                    Cliente desde {fechaCorta(f.creado)}</div>
                </div>
                {f.es_directo&&<span style={{background:`${Q.cyan}22`,
                  border:`1px solid ${Q.cyan}`,borderRadius:RADII.xl,padding:"4px 12px",
                  fontSize:12,fontWeight:700,color:Q.cyan,
                  fontFamily:F_BODY}}><Smartphone size={12}/> DIRECTO</span>}
                {f.bloqueado&&<span style={{background:`${Q.red}22`,
                  border:`1px solid ${Q.red}`,borderRadius:RADII.xl,padding:"4px 12px",
                  fontSize:12,fontWeight:700,color:Q.red,
                  fontFamily:F_BODY}}><Lock size={12}/> BLOQUEADO</span>}
              </div>
              <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`,
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
                  gap:SPACING[4],fontFamily:F_BODY}}><Icon name="wallet-cards" size={13}/> Saldo actual</span>
                <span style={{color:Q.green,fontWeight:900,fontSize:24,
                  fontFamily:F_BODY}}>{money(f.saldo, f?.moneda)}</span>
              </div>
            </GCard>

            {/* Rendimiento — an icon per figure so this reads as three
                distinct metrics at a glance, not a row of plain numbers. */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:12}}>
              <GCard level={2} style={{padding:"12px",textAlign:"center"}}>
                <div style={{color:Q.cyan,fontWeight:700,fontSize:14,
                  fontFamily:F_BODY}}>{money(f.rendimiento.apostado, f?.moneda)}</div>
                <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
                  justifyContent:"center",gap:SPACING[4]}}><Icon name="ticket" size={12}/> Apostado</div>
              </GCard>
              <GCard level={2} style={{padding:"12px",textAlign:"center"}}>
                <div style={{color:Q.violet2,fontWeight:700,fontSize:14,
                  fontFamily:F_BODY}}>{money(f.rendimiento.ganado, f?.moneda)}</div>
                <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
                  justifyContent:"center",gap:SPACING[4]}}><Icon name="trophy" size={12}/> Ganado</div>
              </GCard>
              <GCard level={2} style={{padding:"12px",textAlign:"center"}}>
                <div style={{color:f.rendimiento.neto_cliente>=0?Q.green:Q.red,
                  fontWeight:700,fontSize:14,fontFamily:F_BODY}}>
                  {money(f.rendimiento.neto_cliente, f?.moneda)}</div>
                <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
                  justifyContent:"center",gap:SPACING[4]}}><Scale size={12}/> Neto</div>
              </GCard>
            </div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:14,
              fontSize:12,fontFamily:F_BODY}}>
              <span style={{color:Q.green}}>{f.rendimiento.ganadas} ganadas</span>
              <span style={{color:Q.red}}>{f.rendimiento.perdidas} perdidas</span>
              <span style={{color:Q.amber}}>{f.rendimiento.pendientes} pendientes</span>
            </div>

            {/* Cargas y retiros: sin esto la ficha muestra un saldo
                sin explicación y no se puede reconstruir de dónde
                salió ni quién lo cargó. */}
            {(f.movimientos||[]).length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline",marginBottom:6}}>
                  <span style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>
                    <Icon name="wallet-cards" size={14}/> Movimientos de saldo</span>
                  <span style={{color:Q.dim,fontSize:12}}>
                    +{money(f.cargado||0, f?.moneda)} / −{money(f.retirado||0, f?.moneda)}</span>
                </div>
                {(f.movimientos||[]).slice(0,8).map((m,i)=>(
                  <div key={i} style={{display:"flex",
                    justifyContent:"space-between",alignItems:"baseline",
                    padding:"8px 0",gap:SPACING[8],
                    borderTop:i?`1px solid ${Q.border}`:"none"}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:12,
                        fontFamily:F_BODY}}>
                        {{carga:"Carga",retiro:"Retiro",
                          descarga:"Retiro",
                          pago_premio:"Premio pagado"}[m.tipo]||m.tipo}</div>
                      <div style={{color:Q.dim,fontSize:12}}>
                        {fechaHora(m.fecha)}{m.operador&&` · ${m.operador}`}
                        {m.detalle&&` · ${m.detalle}`}</div>
                    </div>
                    <span style={{color:m.monto>=0?Q.green:Q.red,
                      fontSize:12.5,fontWeight:700,flexShrink:0,
                      fontFamily:F_BODY}}>
                      {m.monto>=0?"+":"−"}{money(Math.abs(m.monto), f?.moneda)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Historial de apuestas. El backend ya lo mandaba en f.apuestas;
                la pantalla simplemente no lo dibujaba. */}
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:8}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>
                <Icon name="receipt-text" size={14}/> Apuestas {f.apuestas?.length ? `(${f.apuestas.length})` : ""}</div>
              {(f.apuestas||[]).length>6&&(
                <button onClick={()=>setTodasApuestas(v=>!v)}
                  style={{background:"transparent",border:"none",color:Q.cyan,
                    fontSize:12,fontWeight:700,cursor:"pointer",padding:0}}>
                  {todasApuestas?"Ver menos":`Ver las ${f.apuestas.length}`}</button>
              )}
            </div>

            {!f.apuestas?.length&&(
              <GCard level={2} style={{padding:"16px 16px",marginBottom:12,
                textAlign:"center"}}>
                <div style={{color:Q.muted,fontSize:12}}>
                  Este cliente todavía no tiene apuestas.</div>
              </GCard>
            )}

            {(todasApuestas ? (f.apuestas||[]) : (f.apuestas||[]).slice(0,6)).map(a=>{
              const st=(a.status||"").toLowerCase();
              const col = ["won","ganada","ganado"].includes(st) ? Q.green
                        : ["lost","perdida","perdido"].includes(st) ? Q.red
                        : ["cancelled","cancelada","void"].includes(st) ? Q.muted
                        : Q.amber;
              const etiqueta = {won:"Ganada",lost:"Perdida",active:"En juego",
                pending:"Reservada",paid:"Pagada",cashed_out:"Cash out",
                cancelled:"Cancelada"}[st] || a.status || "—";
              return(
                <GCard level={2} key={a.code} glow={null}
                  onClick={()=>setVerApuesta(a.code)}
                  style={{padding:"12px 12px",marginBottom:6,cursor:"pointer"}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                        fontFamily:F_BODY}}>{a.code}</div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                        {fechaHora(a.fecha)} · cuota {Number(a.odd||0).toFixed(2)}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:Q.text,fontSize:12,
                        fontFamily:F_BODY}}>
                        {money(a.stake, f?.moneda)}</div>
                      <div style={{color:col,fontSize:12,fontWeight:700}}>
                        {etiqueta}</div>
                    </div>
                    <span style={{color:Q.dim,fontSize:14}}>›</span>
                  </div>
                </GCard>
              );
            })}

            {verApuesta&&(
              <DetalleApuesta code={verApuesta} adminKey={adminKey}
                onCerrar={()=>setVerApuesta(null)}
                onNoAutorizado={onNoAutorizado}/>
            )}

            {/* Cargar / retirar */}
            {!f.bloqueado&&(
              <GCard level={2} style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
                  fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Cargar / retirar</div>
                <input value={monto} onChange={e=>setMonto(e.target.value)}
                  placeholder="Monto ($)" inputMode="numeric"
                  style={{width:"100%",background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
                    color:Q.text,fontSize:16,marginBottom:8,
                    fontFamily:F_BODY}}/>
                <input value={detalle} onChange={e=>setDetalle(e.target.value)}
                  placeholder="Detalle (opcional)"
                  style={{width:"100%",background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                    color:Q.text,fontSize:14,marginBottom:10,
                    fontFamily:F_BODY}}/>
                <div style={{display:"flex",gap:SPACING[8]}}>
                  <Btn label="+ Cargar" onClick={()=>mover(1)} color={Q.green}
                    full disabled={operando}/>
                  <Btn label="− Retirar" onClick={()=>mover(-1)} color={Q.amber}
                    outline full disabled={operando}/>
                </div>
              </GCard>
            )}

            {/* Bloqueo */}
            {/* Glow follows the client's blocked state, not a button's
                colour — a blocked account is the alarm-worthy state, not
                a normal one. */}
            <GCard glow={f.bloqueado?Q.red:null} style={{padding:SPACING[16],marginBottom:12}}>
              {f.bloqueado?(
                <div>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                    fontFamily:F_BODY}}>
                    Bloqueado por {f.bloqueado_por||"—"}
                    {f.bloqueado_motivo?` · ${f.bloqueado_motivo}`:""}</div>
                  {/* Unblocking, resetting the password and reassigning the agency are independent one-tap actions, so they share a line */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
                    <div>
                      <Btn label="Desbloquear cliente" onClick={toggleBloqueo}
                        color={Q.green} full disabled={operando}/>
                    </div>
                    <div>
                      <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
                        color={Q.amber} full/>
                    </div>
                    <AsignarAgenciaAdmin adminKey={adminKey} userId={userId}
                      esDirecto={f.es_directo} agenciaActual={f.agencia}
                      onCambio={()=>{ if(onCambio) onCambio(); }}/>
                  </div>
                </div>
              ):confirmBloq?(
                <div>
                  <div style={{color:Q.red,fontWeight:700,fontSize:13,marginBottom:8,
                    fontFamily:F_BODY}}>
                    ¿Bloquear a {f.nombre}?</div>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                    fontFamily:F_BODY}}>
                    No va a poder apostar hasta que lo desbloquees.</div>
                  <input value={motivo} onChange={e=>setMotivo(e.target.value)}
                    placeholder="Motivo del bloqueo"
                    style={{width:"100%",background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                      color:Q.text,fontSize:14,marginBottom:10,
                      fontFamily:F_BODY}}/>
                  <div style={{display:"flex",gap:SPACING[8]}}>
                    {/* Cancelar stays outline: it is the quiet half of this
                        confirm pair, next to a filled "Sí, bloquear". */}
                    <Btn label="Cancelar" onClick={()=>{setConfirmBloq(false);setMotivo("");}}
                      outline color={Q.muted} full/>
                    <Btn label="Sí, bloquear" onClick={toggleBloqueo} color={Q.red}
                      full disabled={operando}/>
                  </div>
                  <div style={{height:8}}/>
                  {/* Reset and agency reassignment are unrelated to this confirmation, so they get their own line below it */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
                    <div>
                      <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
                        color={Q.amber} full/>
                    </div>
                    <AsignarAgenciaAdmin adminKey={adminKey} userId={userId}
                      esDirecto={f.es_directo} agenciaActual={f.agencia}
                      onCambio={()=>{ if(onCambio) onCambio(); }}/>
                  </div>
                </div>
              ):(
                <div>
                  {/* Blocking is destructive but is still a quick account action like the other two, so it shares the line too — flagged in the change report */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
                    <div>
                      <Btn label={<><Lock size={13}/> Bloquear cliente</>} onClick={toggleBloqueo}
                        color={Q.red} full disabled={operando}/>
                    </div>
                    <div>
                      <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
                        color={Q.amber} full/>
                    </div>
                    <AsignarAgenciaAdmin adminKey={adminKey} userId={userId}
                      esDirecto={f.es_directo} agenciaActual={f.agencia}
                      onCambio={()=>{ if(onCambio) onCambio(); }}/>
                  </div>
                </div>
              )}
            </GCard>

            {resetOpen&&<ResetPasswordAdmin adminKey={adminKey} userId={userId}
              nombre={f?(f.nombre_completo||f.username):"el cliente"} onCerrar={()=>setResetOpen(false)}/>}
            {msg&&<div style={{fontSize:12,marginBottom:10,textAlign:"center",
              color:msg.ok?Q.green:Q.red,
              fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

            {/* The bets list that used to sit here rendered `f.apuestas`
                a second time, unclickable, under the heading "Últimas
                apuestas". The richer list above it — clickable rows with a
                "ver las N" toggle — already shows the same array, and this
                copy read `f.apuestas.length` with no guard, so a response
                without `apuestas` took the whole sheet down with it. */}
          </div>
        )}
      </div>
    </div>
  );
}

function TabClientes({ adminKey, onNoAutorizado }){
  const [clientes,setClientes]=useState(null);
  const [sel,setSel]=useState(null);   // cliente abierto en ficha
  const [ags,setAgs]=useState([]);
  const [filtroAg,setFiltroAg]=useState("");
  const [buscar,setBuscar]=useState("");
  const [creando,setCreando]=useState(false);
  const [nombre,setNombre]=useState("");
  const [agNueva,setAgNueva]=useState("");
  const [tel,setTel]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text

  const cargarAgs=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)});
      if(r.ok) setAgs((await r.json()).agencias||[]);
    }catch(e){}
  };
  const cargar=async()=>{
    try{
      const q=new URLSearchParams();
      if(filtroAg) q.set("agencia",filtroAg);
      if(buscar) q.set("buscar",buscar);
      const r=await fetch(`${API}/api/admin/clientes?${q}`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setClientes((await r.json()).clientes||[]);
    }catch(e){ setClientes([]); }
  };
  useEffect(()=>{ cargarAgs(); // eslint-disable-next-line
  },[]);
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[filtroAg]);

  const crear=async()=>{
    if(!nombre.trim()||!agNueva){ setMsg({text:"Poné nombre y agencia",ok:false}); return; }
    setCreando(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/clientes`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({nombre,agencia:agNueva,telefono:tel}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Cliente creado",ok:true});
      setNombre(""); setTel(""); cargar();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setCreando(false);
  };

  // `/api/admin/clientes` manda `moneda` por cliente, con `ARS` por defecto
  // (`bot/casino_api.py:17719`). Esta lista cruza agencias renglón a renglón,
  // así que el balance va con la moneda de SU fila.
  const montoCli = formatoDeLista(clientes);

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:12,
        fontFamily:F_BODY}}><Icon name="users" size={14}/> Clientes</div>

      {/* Crear cliente */}
      <GCard style={{padding:SPACING[16],marginBottom:14,
        background:`linear-gradient(135deg,${Q.violet}10,${Q.cyan}06)`}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
          fontFamily:F_BODY}}>Nuevo cliente</div>
        <input value={nombre} onChange={e=>setNombre(e.target.value)}
          placeholder="Nombre del cliente"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
        <input value={tel} onChange={e=>setTel(e.target.value)}
          placeholder="Teléfono (opcional)"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
        <select value={agNueva} onChange={e=>setAgNueva(e.target.value)}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
            color:agNueva?Q.text:Q.muted,fontSize:15,marginBottom:10,
            fontFamily:F_BODY}}>
          <option value="">Ubicar en agencia...</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>
              {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
          ))}
        </select>
        <Btn label={creando?"CREANDO...":"Crear cliente"} onClick={crear}
          color={Q.violet} full disabled={creando}/>
        {msg&&<div style={{fontSize:12,marginTop:8,
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>

      {/* Filtros */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <select value={filtroAg} onChange={e=>setFiltroAg(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:14,fontFamily:F_BODY}}>
          <option value="">Todas las agencias</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
          ))}
        </select>
        <button onClick={cargar} style={{background:`${Q.violet}33`,
          border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"0 16px",
          cursor:"pointer",color:Q.cyan,fontSize:13,fontWeight:700,
          fontFamily:F_BODY}}><Icon name="search" size={14}/></button>
      </div>

      {clientes===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {clientes&&clientes.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{fontSize:26,marginBottom:8}}><Icon name="users" size={26}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>Sin clientes</div>
        </GCard>
      )}
      {(clientes||[]).map(c=>(
        <GCard key={c.id} style={{padding:"12px 12px",marginBottom:7,cursor:"pointer"}}
          onClick={()=>setSel(c.id)}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:600,fontSize:13,
                fontFamily:F_BODY}}>{c.nombre}
                {c.bloqueado&&<span style={{color:Q.red,fontSize:12,marginLeft:6}}><Lock size={11}/> bloqueado</span>}
              </div>
              <div style={{color:Q.muted,fontSize:12}}>
                {c.agencia_nombre} ({c.agencia}) · {fechaCorta(c.fecha)}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:SPACING[8],flexShrink:0}}>
              <div style={{color:Q.green,fontWeight:700,fontSize:14,
                fontFamily:F_BODY}}>{montoCli(c.balance,c.moneda)}</div>
              <span style={{color:Q.muted,fontSize:16}}>›</span>
            </div>
          </div>
        </GCard>
      ))}

      {sel&&<FichaCliente userId={sel} adminKey={adminKey}
        onCerrar={()=>setSel(null)} onCambio={cargar}
        onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB ÁRBOL — estructura jerárquica de agencias
// ═══════════════════════════════════════════════════════════════
function TabArbol({ adminKey, onNoAutorizado }){
  const [ags,setAgs]=useState(null);
  const [err,setErr]=useState("");

  useEffect(()=>{ (async()=>{
    try{
      const r=await fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setAgs((await r.json()).agencias||[]);
    }catch(e){ setErr("Sin conexión"); }
  })(); // eslint-disable-next-line
  },[]);

  if(err) return <div style={{color:Q.red,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>{err}</div>;
  if(!ags) return <div style={{color:Q.muted,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>Cargando árbol...</div>;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="network" size={14}/> Árbol de agencias</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,
        fontFamily:F_BODY}}>
        {ags.length} agencias en total. La sangría muestra la jerarquía.
      </div>

      {ags.length===0&&<GCard style={{padding:SPACING[24],textAlign:"center"}}>
        <div style={{color:Q.muted,fontSize:13,
          fontFamily:F_BODY}}>No hay agencias</div>
      </GCard>}

      {ags.map(a=>(
        <div key={a.code} style={{marginLeft:(a.nivel||0)*16,marginBottom:6}}>
          <GCard style={{padding:"12px 12px",
            borderLeft:`3px solid ${["#00F0FF","#7C3AED","#00FF88","#FFB800","#FF0080"][(a.nivel||0)%5]}`}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  {a.nivel>0?"└ ":""}{a.name}</div>
                <div style={{color:Q.muted,fontSize:12,marginTop:1}}>
                  {a.code} · {a.moneda} · nivel {a.nivel}
                  {a.parent_code
                    ? ` · cuelga de ${(ags.find(x=>x.code===a.parent_code)||{}).name||a.parent_code}`
                    : " · raíz"}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:Q.cyan,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>GGR {fmt(a.pct_ggr)}%</div>
                <div style={{color:Q.violet2,fontSize:12,
                  fontFamily:F_BODY}}>Ventas {fmt(a.pct_ventas)}%</div>
                {/* El subtítulo de esta misma tarjeta ya escribe
                    `a.moneda`, y el saldo de cuenta corriente iba en pesos
                    clavados. Sin `conCodigo`, por eso mismo: el código ya
                    está arriba, en la misma fila. */}
                <div style={{color:(a.saldo_cc||0)>=0?Q.green:Q.red,fontSize:12,
                  fontWeight:700,fontFamily:F_BODY}}>
                  CC {money(a.saldo_cc, a.moneda)}</div>
              </div>
            </div>
          </GCard>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB EVENTOS — partidos con cuotas (prematch / en vivo)
// ═══════════════════════════════════════════════════════════════
function TabEventos({ adminKey, onNoAutorizado }){
  const [tipo,setTipo]=useState("prematch");
  const [data,setData]=useState(null);
  const [cargando,setCargando]=useState(true);
  // Bloqueos vigentes y el panel para crear uno nuevo.
  // El nivel superior manda: si el admin bloquea algo global, ninguna
  // agencia puede tomarlo aunque a ella no se lo hayan bloqueado.
  const [bloqueos,setBloqueos]=useState([]);
  const [ags,setAgs]=useState([]);
  const [panel,setPanel]=useState(null);   // el evento que se está bloqueando
  // Abierta por defecto: plegada, no había forma evidente de
  // encontrar dónde se desbloquea.
  const [verBloqueos,setVerBloqueos]=useState(true);
  // Filtros: con cientos de eventos por deporte, sin esto no se
  // encuentra nada. El buscador va contra el backend porque el
  // listado viene recortado por liga.
  const [fDeporte,setFDeporte]=useState("");
  const [fLiga,setFLiga]=useState("");
  const [buscar,setBuscar]=useState("");
  const [catalogo,setCatalogo]=useState([]);
  // Las ligas del feed, para el selector de abajo
  const [ligas,setLigas]=useState([]);
  // Apuestas vivas sobre el evento que se está por bloquear
  const [expo,setExpo]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const [ajustes,setAjustes]=useState([]);

  const cargarAjustes=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/ajustes`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setAjustes((await r.json()).ajustes||[]);
    }catch(e){}
  };

  const guardarAjuste=async(cfg)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/ajustes`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(cfg)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Cuota ajustada", ok:true}); setPanel(null); cargarAjustes();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const quitarAjuste=async(id)=>{
    try{
      const r=await fetch(`${API}/api/admin/ajustes/${id}`,
        {method:"DELETE",headers:adminHeaders(adminKey)});
      if(r.ok){ setMsg({text:"Ajuste quitado", ok:true}); cargarAjustes(); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
  };

  const tieneAjuste=(ev)=>ajustes.find(a=>a.event_id===ev.event_id);

  const cargarBloqueos=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/eventos/bloqueos`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setBloqueos((await r.json()).bloqueos||[]);
    }catch(e){}
  };
  useEffect(()=>{
    cargarBloqueos();
    cargarAjustes();
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAgs(Array.isArray(d)?d:(d.agencias||[]))).catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const bloquear=async(cfg)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/eventos/bloqueos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(cfg)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Bloqueado", ok:true}); setPanel(null); cargarBloqueos();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const quitar=async(id)=>{
    try{
      const r=await fetch(`${API}/api/admin/eventos/bloqueos/${id}`,
        {method:"DELETE",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ setMsg({text:"Desbloqueado", ok:true}); cargarBloqueos(); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
  };

  // ¿Este evento ya está bloqueado en algún nivel?
  const estaBloqueado=(ev,sportKey)=>bloqueos.find(b=>
    (b.objeto==="evento"&&b.event_id===ev.event_id)||
    (b.objeto==="deporte"&&b.sport_key===(ev.sport_key||sportKey)));

  // Los mercados bloqueados de un evento puntual
  const mercadosBloqueados=(eventId)=>bloqueos.filter(b=>
    b.objeto==="mercado"&&b.event_id===eventId);

  // Qué eventos tienen los mercados desplegados
  const [verMercados,setVerMercados]=useState({});

  // Bloquear un mercado sin pasar por el panel: es la acción más
  // frecuente y abrir el panel para eso era una vuelta de más.
  const bloquearMercado=async(ev,liga,mercado)=>{
    await bloquear({
      objeto:"mercado", event_id:ev.event_id, mercado,
      alcance:"global", modo:"ambos",
      etiqueta:`${ev.home} vs ${ev.away}`,
      motivo:`Mercado ${mercado} bloqueado desde el listado`,
    });
  };

  const cargar=async(t)=>{
    setCargando(true);
    try{
      const q=new URLSearchParams({tipo:t});
      if(fDeporte) q.set("deporte",fDeporte);
      if(fLiga) q.set("liga",fLiga);
      if(buscar.trim()) q.set("buscar",buscar.trim());
      const r=await fetch(`${API}/api/admin/eventos?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const d=await r.json();
        setData(d.deportes||[]);
        // El catálogo llega completo, sin filtrar: si se recortara,
        // el filtro se comería sus propias opciones.
        if(d.catalogo?.length) setCatalogo(d.catalogo);
        if(d.ligas) setLigas(d.ligas);
      }
    }catch(e){ setData([]); }
    setCargando(false);
  };
  useEffect(()=>{ cargar(tipo); // eslint-disable-next-line
  },[tipo,fDeporte,fLiga]);

  // El buscador espera a que dejes de tipear, para no pedir en cada tecla
  useEffect(()=>{
    const t=setTimeout(()=>cargar(tipo),450);
    return()=>clearTimeout(t);
    // eslint-disable-next-line
  },[buscar]);

  // Antes de bloquear, cuántas apuestas vivas hay sobre ese evento
  const verExposicion=async(eventId)=>{
    setExpo({cargando:true});
    try{
      const r=await fetch(
        `${API}/api/admin/eventos/${encodeURIComponent(eventId)}/apuestas`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setExpo(r.ok?await r.json():{error:`Error ${r.status}`});
    }catch(e){ setExpo({error:e.message}); }
  };

  // ── Las opciones de los dos desplegables ────────────────────
  // El backend ya manda `catalogo` (deportes) y `ligas` contados. Acá sólo
  // se renombran los campos a los que espera `SelectorDesplegable`
  // —`clave`, `rotulo`, `cantidad`—; no se recuenta nada, porque el que
  // cuenta tiene que ser uno solo y es el que arma la lista.
  //
  // Las ligas se recortan al deporte elegido: con el feed completo son
  // cientos y una lista de todas no se recorre. Es la misma regla que
  // tenía el `<select>` que esto reemplaza.
  const acento = tipo==="live" ? Q.pink : Q.violet;
  const opcionesDeporte = catalogo.map(c=>({
    clave: c.key,
    rotulo: `${c.icon||""} ${c.nombre||c.key}`.trim(),
    cantidad: c.eventos||0,
  }));
  const opcionesLiga = ligas
    .filter(l=>!fDeporte||l.deporte===fDeporte)
    .map(l=>({ clave:l.key, rotulo:l.nombre||l.key, cantidad:l.eventos||0 }));
  const totalDeEventos = catalogo.reduce((n,c)=>n+(c.eventos||0),0);

  // ── LA EXPOSICIÓN DE UN EVENTO, POR MONEDA ──────────────────
  //
  // El panel se abre ANTES de bloquear, para ver cuánto hay en juego. Un
  // evento lo juegan las agencias de todas las ramas, así que "si ganan
  // todas, la casa paga" sumaba pesos con dólares — y es el número con el
  // que se decide si el bloqueo llega tarde.
  //
  // Los tres bloques de abajo —selección, origen y quiénes— vienen del
  // borde con la moneda en la FILA, así que cada uno se formatea con la
  // suya y el código aparece sólo si la lista entera tiene más de una. Con
  // una sola moneda la pantalla se ve exactamente igual que hoy.
  const expoFilas=Array.isArray(expo?.por_moneda)?expo.por_moneda:[];
  const variasExpo=expoFilas.length>1;
  const rotExpo=(texto,m)=>variasExpo?`${texto} · ${m.moneda}`:texto;
  const montoSel=formatoDeLista(expo?.por_seleccion);
  const montoAgExpo=formatoDeLista(expo?.por_agencia);
  const montoCliExpo=formatoDeLista(expo?.top_clientes);

  return(
    <div>
      <PageHeader icon={<Calendar/>} title="Eventos con cuotas"
        description="Buscá y filtrá los eventos deportivos con cuotas cargadas, por deporte, liga o equipo."/>

      {/* Buscador: va contra el backend porque el listado viene
          recortado por liga y un partido puntual podría no estar. */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:9}}>
        <input value={buscar} onChange={e=>setBuscar(e.target.value)}
          placeholder="Buscar equipo o partido…"
          style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${buscar?Q.cyan:Q.border}`,borderRadius:RADII.md,
            padding:"12px 12px",color:Q.text,fontSize:14,
            fontFamily:F_BODY}}/>
        {(buscar||fDeporte||fLiga)&&(
          <button onClick={()=>{setBuscar("");setFDeporte("");setFLiga("");}}
            style={{background:"transparent",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"0 12px",cursor:"pointer",
              color:Q.muted,fontSize:12,flexShrink:0,
              fontFamily:F_BODY}}>Limpiar</button>
        )}
      </div>

      {/* Deporte y liga, en los MISMOS dos desplegables que el jugador y
          la agencia (`filtrosDeEvento.jsx`).

          Acá había una tira de chips que se deslizaba de costado para el
          deporte y un `<select>` del navegador para la liga. La tira es
          justo el control que ese módulo vino a reemplazar, y por un
          motivo medido, no estético: el desplazamiento horizontal ESCONDE
          lo que hay. Quien no desliza ve cuatro deportes y no sabe que
          existen los demás. El `<select>` del sistema, además, es la única
          pieza de esta pantalla que no se parece a nada del resto del
          producto, y con cientos de ligas no se puede buscar adentro.

          El desplegable compartido abre a ancho completo, una opción por
          fila, con buscador cuando hay más de doce. Al estar cerrado dice
          QUÉ está elegido y trae la cruz para quitarlo, que es lo que
          evita que alguien filtre, se olvide, y después lea una lista
          corta creyendo que no hay partidos.

          El color sigue la pestaña: violeta en prematch, rosa en vivo,
          igual que en la agencia. */}
      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
        marginBottom:10}}>
        {opcionesDeporte.length>1&&(
          <SelectorDesplegable rotulo="Deporte" todas="Todos los deportes"
            opciones={opcionesDeporte} activa={fDeporte||null}
            total={totalDeEventos} color={acento}
            onElegir={clave=>{setFDeporte(clave||"");setFLiga("");}}/>
        )}
        {opcionesLiga.length>1&&(
          <SelectorDesplegable rotulo="Liga" todas="Todas las ligas"
            opciones={opcionesLiga} activa={fLiga||null}
            total={totalDeEventos} color={acento}
            onElegir={clave=>setFLiga(clave||"")}/>
        )}
      </div>

      {/* Las dos pestañas salen del MISMO catálogo, el de GR8: `tipo=live`
          es ese catálogo filtrado por `etapa: "Live"`. Antes leía
          `/api/live/combined` —la pantalla en vivo de la cadena vieja— que
          devolvía la lista vacía, así que esta pestaña no servía para
          bloquear ni ajustar un partido en curso, que es justo la acción más
          urgente del panel. */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
        <button onClick={()=>setTipo("prematch")} style={{flex:1,
          background:tipo==="prematch"?`${Q.violet}33`:"rgba(255,255,255,0.04)",
          border:`1.5px solid ${tipo==="prematch"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"12px",cursor:"pointer",color:tipo==="prematch"?Q.cyan:Q.muted,
          fontSize:13,fontWeight:700,fontFamily:F_BODY}}>
          Prematch</button>
        <button onClick={()=>setTipo("live")} style={{flex:1,
          background:tipo==="live"?`${Q.pink}33`:"rgba(255,255,255,0.04)",
          border:`1.5px solid ${tipo==="live"?Q.pink:Q.border}`,borderRadius:RADII.md,
          padding:"12px",cursor:"pointer",color:tipo==="live"?Q.pink:Q.muted,
          fontSize:13,fontWeight:700,fontFamily:F_BODY}}>
          <Icon name="circle-dot" size={13}/> En vivo</button>
      </div>

      {/* Bloqueos vigentes: incluye los que no están en la lista de
          arriba, como un deporte entero o un evento que ya no figura. */}
      {bloqueos.length>0&&(
        <GCard glow={Q.red} style={{padding:SPACING[12],marginBottom:12}}>
          <div onClick={()=>setVerBloqueos(v=>!v)} style={{cursor:"pointer",
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{color:Q.red,fontWeight:700,fontSize:12.5,
              fontFamily:F_BODY}}>
              <Icon name="triangle-alert" size={13}/> {bloqueos.length} {bloqueos.length===1?"bloqueo activo"
                :"bloqueos activos"}</span>
            <span style={{color:Q.muted,fontSize:12}}>
              {verBloqueos?"▲":"▼"}</span>
          </div>

          {verBloqueos&&bloqueos.map(b=>(
            <div key={b.id} style={{display:"flex",
              justifyContent:"space-between",alignItems:"center",gap:SPACING[8],
              padding:"8px 0",borderTop:`1px solid ${Q.border}`,marginTop:8}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12,
                  fontFamily:F_BODY}}>
                  {b.objeto==="deporte"?`Deporte: ${b.sport_key}`
                    :(b.etiqueta||b.event_id)}
                  {b.mercado?` · ${b.mercado}`:""}</div>
                <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                  {b.alcance==="global"?"Todo el sistema"
                    :`${b.agencia_nombre||b.agencia}${b.alcance==="rama"
                        ?" y su rama":""}`}
                  {b.modo!=="ambos"?` · solo ${b.modo}`:""}
                  {b.motivo?` · ${b.motivo}`:""}</div>
              </div>
              <button onClick={()=>quitar(b.id)} style={{
                background:"transparent",border:`1px solid ${Q.green}66`,
                borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                color:Q.green,fontSize:12,flexShrink:0,
                fontFamily:F_BODY}}>Quitar</button>
            </div>
          ))}
        </GCard>
      )}

      {ajustes.length>0&&(
        <GCard glow={Q.amber} style={{padding:SPACING[12],marginBottom:12}}>
          <div style={{color:Q.amber,fontWeight:700,fontSize:12.5,
            marginBottom:6,fontFamily:F_BODY}}>
            <TrendingDown size={12}/> {ajustes.length} {ajustes.length===1?"cuota ajustada"
              :"cuotas ajustadas"}</div>
          {ajustes.map(a=>(
            <div key={a.id} style={{display:"flex",
              justifyContent:"space-between",alignItems:"center",gap:SPACING[8],
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12,
                  fontFamily:F_BODY}}>
                  {a.etiqueta||a.event_id}
                  {a.mercado?` · ${a.mercado}`:""}</div>
                <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                  {a.cuota_fija?`fija en ${a.cuota_fija}`
                    :`${a.ajuste_pct}%`}
                  {a.alcance==="global"?" · todos"
                    :` · ${a.agencia_nombre||a.agencia}`}
                  {a.motivo?` · ${a.motivo}`:""}</div>
              </div>
              <button onClick={()=>quitarAjuste(a.id)} style={{
                background:"transparent",border:`1px solid ${Q.border}`,
                borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                color:Q.muted,fontSize:12,flexShrink:0,
                fontFamily:F_BODY}}>Quitar</button>
            </div>
          ))}
        </GCard>
      )}

      {msg&&!panel&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,
        marginBottom:10,textAlign:"center",
        fontFamily:F_BODY}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando cuotas...</div>}
      {data&&data.length===0&&!cargando&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Inbox size={26} color={Q.muted}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>No hay eventos ahora</div>
        </GCard>
      )}

      {(data||[]).map((d,di)=>(
        <div key={di} style={{marginBottom:16}}>
          <div style={{color:Q.violet2,fontWeight:700,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}>{d.icon} {d.liga}</div>
          {/* La séptima lista de eventos del repositorio, y la última que
              seguía siendo una columna única: la misma rejilla que las
              seis del sportsbook (`rejillaDeTarjetas.test.js`). En el
              monitor del admin el contenedor da ~1300px, así que una
              columna sola estiraba cada tarjeta —y sus cuotas— a todo lo
              ancho de la pantalla. `auto-fit` vuelve a una columna por su
              cuenta en teléfono y el piso `min(360px,100%)` evita el
              desborde; el `gap` reemplaza el margen de cada tarjeta. */}
          <div style={{display:"grid",
            gridTemplateColumns:"repeat(auto-fit,minmax(min(360px,100%),1fr))",
            gap:SPACING[8]}}>
          {d.eventos.map((ev,ei)=>{
            const bloq=estaBloqueado(ev,d.key);
            const aju=tieneAjuste(ev);
            // Los mercados se ven y se bloquean en la tarjeta. Si el
            // evento entero está bloqueado no hay nada que desplegar,
            // porque ya no se puede apostar ninguno.
            const hayMercados=!bloq&&(ev.mercados||[]).length>0;
            const abierto=!!verMercados[ev.event_id];
            const alternarMercados=()=>setVerMercados(v=>({...v,
              [ev.event_id]:!v[ev.event_id]}));
            return(
            <GCard key={ei} glow={bloq?Q.red:null}
              style={{padding:SPACING[16],opacity:bloq?0.75:1}}>
              {/* ── LA TARJETA DE UN EVENTO ──────────────────────────
                  Misma forma que la del jugador y la de la agencia: el
                  renglón del partido con el "vs" atenuado y la hora a la
                  derecha, abajo la fila de cuotas, y recién después las
                  acciones del panel.

                  Acá había una grilla propia de tres celdas enormes
                  rotuladas `1`, `X`, `2`, con un guión adentro cuando el
                  precio no venía. Tres defectos en una: `1` y `2` no
                  dicen QUÉ equipo es —hay que volver al título y contar—,
                  el guión reserva el lugar de un número que no existe, y
                  el estilo era un cuarto juego de cuotas escrito a mano. */}
              <div onClick={hayMercados?alternarMercados:undefined}
                style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",gap:SPACING[8],marginBottom:6,
                  cursor:hayMercados?"pointer":"default"}}>
                <div style={{minWidth:0,flex:1,color:Q.text,fontSize:13,
                  fontWeight:600,fontFamily:F_BODY,overflow:"hidden",
                  textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {bloq&&<span style={{color:Q.red,marginRight:4}}><Icon name="triangle-alert" size={11}/></span>}
                  {!bloq&&aju&&<span style={{color:Q.amber,marginRight:4}}><TrendingDown size={11}/></span>}
                  {ev.home} <span style={{color:Q.dim}}>vs</span> {ev.away}</div>
                <span style={{display:"flex",alignItems:"center",
                  gap:SPACING[8],flexShrink:0}}>
                  {/* NI MARCADOR NI MINUTO. El catálogo de GR8 no trae
                      resultado ni reloj: el `minute` que llegaba acá se
                      ESTIMA restando horas al comienzo del partido
                      (`casino_api.py:19978`), y en una pantalla de
                      apuestas un minuto estimado se lee como el minuto
                      real. En la previa va la hora, que es un dato del
                      catálogo; en vivo, la misma chapa que el jugador
                      —"EN VIVO"—, que es todo lo que hay para decir sin
                      inventarlo. */}
                  {tipo==="live"
                    ? <span style={{color:Q.pink,fontSize:12,fontWeight:700,
                        fontFamily:F_BODY}}>
                        <Icon name="circle-dot" size={11}/> EN VIVO</span>
                    : <span style={{color:Q.muted,fontSize:12}}>{horaLocal(ev)}</span>}
                  {hayMercados&&<span style={{color:Q.muted,fontSize:18}}>
                    {abierto?"▲":"▼"}</span>}
                </span>
              </div>

              {/* Las cuotas las dibuja el MISMO componente que las otras
                  dos pantallas, en `soloLectura`: trae las etiquetas
                  cortas de equipo —"Minas | X | Comercio" en vez de
                  "1 | X | 2"— y, por informativo, no se puede apretar.
                  El admin no vende: estos precios están para decidir si
                  se bloquea o se limita, así que la celda no insinúa un
                  boleto. El por qué del modo está en `cuotasDeEvento.jsx`,
                  arriba de `OddsButtons`.

                  `odds` es el atajo de tres posiciones que el catálogo ya
                  trae derivado y es lo único que este endpoint manda; sin
                  diccionario de mercados `OddsButtons` cae en ese mismo
                  repliegue. Un partido a dos vías —hockey, tenis,
                  básquet— llega con `E` nulo porque no hay empate que
                  ofrecer, y la fila entonces dibuja DOS celdas en vez de
                  dejar un hueco: es la misma regla que ya aplica en el
                  sportsbook, donde un resultado sin precio no se dibuja. */}
              <OddsButtons market="h2h" soloLectura
                ev={{home:ev.home,away:ev.away,
                  odds:{L:ev.L,E:ev.E,V:ev.V}}}/>

              {!bloq&&aju&&(
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",marginTop:7,paddingTop:SPACING[8],
                  borderTop:`1px solid ${Q.border}`,gap:SPACING[8]}}>
                  <div style={{color:Q.amber,fontSize:12,minWidth:0,flex:1}}>
                    Cuota {aju.cuota_fija?`fijada en ${aju.cuota_fija}`
                      :`bajada ${Math.abs(aju.ajuste_pct)}%`}
                    {aju.alcance!=="global"?` · ${aju.agencia}`:" · para todos"}
                    {aju.mercado?` · ${aju.mercado}`:""}</div>
                  <button onClick={()=>quitarAjuste(aju.id)} style={{
                    background:"transparent",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                    color:Q.muted,fontSize:12,flexShrink:0,
                    fontFamily:F_BODY}}>Quitar</button>
                </div>
              )}

              {/* Mercados del evento: se ven y se bloquean acá mismo.
                  Sin recuadro y en el mismo renglón que el "▼ Todos los
                  mercados" del jugador y de la agencia: desplegar es la
                  acción secundaria de la tarjeta, y delineado pesaba
                  igual que "Bloquear / limitar", que es la principal.
                  Lo que cuenta —cuántos mercados hay y cuántos están
                  bloqueados— se sigue leyendo cerrado. */}
              {hayMercados&&(
                <>
                  <button onClick={alternarMercados} style={{width:"100%",
                    background:"transparent",border:"none",
                    borderRadius:RADII.md,padding:SPACING[4],cursor:"pointer",
                    color:Q.dim,fontSize:12,marginTop:6,fontFamily:F_BODY}}>
                    {abierto?"▲":"▼"} {ev.mercados.length} mercados
                    {mercadosBloqueados(ev.event_id).length>0&&(
                      <span style={{color:Q.red,marginLeft:5}}>
                        · {mercadosBloqueados(ev.event_id).length} bloqueado
                        {mercadosBloqueados(ev.event_id).length>1?"s":""}</span>
                    )}
                  </button>

                  {abierto&&(
                    <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",
                      marginTop:7}}>
                      {/* El rótulo es el nombre en español del diccionario
                          compartido —el mismo que leen el carrusel y
                          "Todos los mercados"—, no la clave cruda del
                          emisor: `h2h_3_way` no dice nada y "Ganador
                          (1X2)" sí. La clave igual queda a mano en el
                          `title`, porque es con lo que se bloquea. */}
                      {ev.mercados.map(m=>{
                        const mb=mercadosBloqueados(ev.event_id)
                          .find(x=>x.mercado===m);
                        return mb?(
                          <button key={m} onClick={()=>quitar(mb.id)}
                            title={`${m} · Tocá para habilitar`}
                            style={{background:`${Q.red}18`,
                              border:`1px solid ${Q.red}`,borderRadius:RADII.sm,
                              padding:"4px 12px",cursor:"pointer",
                              color:Q.red,fontSize:12,
                              fontFamily:F_BODY}}>
                            <Icon name="triangle-alert" size={12}/> {tituloMercado(m)}</button>
                        ):(
                          <button key={m}
                            onClick={()=>bloquearMercado(ev,d.liga,m)}
                            title={`${m} · Tocá para bloquear`}
                            style={{background:"rgba(255,255,255,0.04)",
                              border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
                              padding:"4px 12px",cursor:"pointer",
                              color:Q.muted,fontSize:12,
                              fontFamily:F_BODY}}>
                            {tituloMercado(m)}</button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {bloq?(
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",marginTop:7,paddingTop:SPACING[8],
                  borderTop:`1px solid ${Q.border}`,gap:SPACING[8]}}>
                  <div style={{color:Q.red,fontSize:12,minWidth:0,flex:1}}>
                    Bloqueado {bloq.alcance==="global"?"para todos"
                      :`en ${bloq.agencia}${bloq.alcance==="rama"?" y su rama":""}`}
                    {bloq.modo!=="ambos"?` · solo ${bloq.modo}`:""}
                    {bloq.motivo?` · ${bloq.motivo}`:""}</div>
                  <button onClick={()=>quitar(bloq.id)} style={{
                    background:"transparent",border:`1px solid ${Q.green}66`,
                    borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                    color:Q.green,fontSize:12,flexShrink:0,
                    fontFamily:F_BODY}}>Habilitar</button>
                </div>
              ):(
                <button onClick={()=>{
                    verExposicion(ev.event_id);
                    setPanel({
                    event_id:ev.event_id, sport_key:ev.sport_key||d.key,
                    etiqueta:`${ev.home} vs ${ev.away}`,
                    mercados:ev.mercados||[], liga:d.liga,
                    objeto:"evento", alcance:"global", modo:"ambos",
                    agencia_code:"", mercado:"", motivo:"",
                  });}}
                  disabled={!ev.event_id}
                  style={{width:"100%",marginTop:7,background:"transparent",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
                    padding:"8px",cursor:ev.event_id?"pointer":"not-allowed",
                    color:ev.event_id?Q.muted:Q.dim,fontSize:12,
                    fontFamily:F_BODY}}>
                  {ev.event_id?<><Ban size={12}/> Bloquear / limitar</>:"sin identificador"}</button>
              )}
            </GCard>
          );})}
          </div>
        </div>
      ))}

      {/* ── PANEL DE BLOQUEO ── */}
      {panel&&(
        <div onClick={()=>{setPanel(null);setExpo(null);}}
          style={{position:"fixed",inset:0,
          zIndex:300,background:"rgba(2,2,8,.92)",display:"flex",
          alignItems:"center",justifyContent:"center",padding:SPACING[16]}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",
            maxWidth:420,maxHeight:"88dvh",overflowY:"auto",
            background:Q.deep||"#080814",border:`1px solid ${Q.border}`,
            borderRadius:RADII.lg,padding:SPACING[16]}}>

            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",marginBottom:12}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:800,fontSize:15,
                  fontFamily:F_BODY}}>
                  {panel.etiqueta}</div>
                <div style={{color:Q.muted,fontSize:12}}>{panel.liga}</div>
              </div>
              <button onClick={()=>setPanel(null)} style={{
                background:"transparent",border:"none",color:Q.muted,
                fontSize:22,cursor:"pointer",padding:0}}>×</button>
            </div>

            {/* Qué hay apostado ahí. Bloquear no cancela lo existente,
                así que conviene saber la exposición antes de decidir:
                si hay mucho en juego, el bloqueo llega tarde. */}
            {expo?.cargando&&(
              <div style={{color:Q.muted,fontSize:12,textAlign:"center",
                padding:"8px 0",marginBottom:10,
                fontFamily:F_BODY}}>
                Revisando apuestas…</div>
            )}

            {expo&&!expo.cargando&&!expo.error&&expo.total>0&&(
              <div style={{background:`${Q.amber}0F`,
                border:`1px solid ${Q.amber}55`,borderRadius:RADII.md,
                padding:SPACING[12],marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline",marginBottom:8}}>
                  <span style={{color:Q.amber,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>
                    <Icon name="triangle-alert" size={12}/> {expo.total} {expo.total===1?"apuesta viva"
                      :"apuestas vivas"}</span>
                  <span style={{color:Q.dim,fontSize:12}}>
                    {expoFilas.map(m=>(
                      <span key={m.moneda} style={{marginLeft:6}}>
                        apostado {money(m.apostado,m.moneda,
                                        {conCodigo:variasExpo})}</span>
                    ))}</span>
                </div>

                {expoFilas.map(m=>(
                <div key={m.moneda} style={{display:"flex",
                  justifyContent:"space-between",
                  alignItems:"baseline",paddingBottom:SPACING[8],marginBottom:8,
                  borderBottom:`1px solid ${Q.border}`}}>
                  <span style={{color:Q.muted,fontSize:12}}>
                    {rotExpo("Si ganan todas, la casa paga",m)}</span>
                  <span style={{color:Q.red,fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {money(m.exposicion,m.moneda,{conCodigo:variasExpo})}</span>
                </div>
                ))}

                {(expo.por_seleccion||[]).map(x=>(
                  <div key={`${x.seleccion}-${x.moneda}`} style={{display:"flex",
                    justifyContent:"space-between",fontSize:12,
                    padding:"4px 0",color:Q.muted}}>
                    <span>{x.seleccion} · {x.apuestas}</span>
                    <span style={{color:Q.text,
                      fontFamily:F_BODY}}>
                      {montoSel(x.a_pagar,x.moneda)}</span>
                  </div>
                ))}

                {(expo.por_agencia||[]).length>0&&(
                  <div style={{marginTop:8,paddingTop:SPACING[8],
                    borderTop:`1px solid ${Q.border}`}}>
                    <div style={{color:Q.dim,fontSize:12,letterSpacing:0.6,
                      marginBottom:3}}>ORIGEN</div>
                    {expo.por_agencia.map(a=>(
                      <div key={`${a.code}-${a.moneda}`} style={{display:"flex",
                        justifyContent:"space-between",fontSize:12,
                        padding:"4px 0",color:Q.muted}}>
                        <span>{a.nombre} · {a.apuestas}</span>
                        <span style={{color:Q.text,
                          fontFamily:F_BODY}}>
                          {montoAgExpo(a.a_pagar,a.moneda)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(expo.top_clientes||[]).length>0&&(
                  <div style={{marginTop:8,paddingTop:SPACING[8],
                    borderTop:`1px solid ${Q.border}`}}>
                    <div style={{color:Q.dim,fontSize:12,letterSpacing:0.6,
                      marginBottom:3}}>QUIÉNES</div>
                    {expo.top_clientes.slice(0,5).map((c,i)=>(
                      <div key={i} style={{display:"flex",
                        justifyContent:"space-between",fontSize:12,
                        padding:"4px 0",color:Q.muted}}>
                        <span>{c.nombre}{c.apuestas>1?` (${c.apuestas})`:""}</span>
                        <span style={{color:Q.text,
                          fontFamily:F_BODY}}>
                          {montoCliExpo(c.a_pagar,c.moneda)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(expo.avisos||[]).map((a,i)=>(
                  <div key={i} style={{color:Q.red,fontSize:12,marginTop:8,
                    lineHeight:1.45,
                    fontFamily:F_BODY}}>▸ {a}</div>
                ))}

                <div style={{color:Q.dim,fontSize:12,marginTop:8,
                  lineHeight:1.45}}>{expo.nota}</div>
              </div>
            )}

            {expo&&!expo.cargando&&!expo.error&&expo.total===0&&(
              <div style={{color:Q.green,fontSize:12,marginBottom:12,
                textAlign:"center",
                fontFamily:F_BODY}}>
                Sin apuestas vivas en este evento</div>
            )}

            {/* Bloquear saca el evento; ajustar lo deja pero baja la
                cuota. Ajustar sirve cuando no querés perder la jugada
                pero sí reducir cuánto pagás si sale. */}
            <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
              {[["bloquear",<><Ban size={13}/> Bloquear</>],["ajustar",<><TrendingDown size={13}/> Bajar cuota</>]].map(([k,l])=>(
                <button key={k} onClick={()=>setPanel(p=>({...p,accion:k}))}
                  style={{flex:1,
                  background:(panel.accion||"bloquear")===k?`${Q.violet}33`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${(panel.accion||"bloquear")===k
                    ?Q.violet:Q.border}`,
                  borderRadius:RADII.md,padding:"12px 8px",cursor:"pointer",
                  color:(panel.accion||"bloquear")===k?Q.cyan:Q.muted,
                  fontSize:12,fontWeight:(panel.accion||"bloquear")===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>

            {panel.accion==="ajustar"&&(
              <>
                <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                  marginBottom:5}}>CUÁNTO BAJAR</div>
                <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
                  {[-10,-20,-30,-50].map(v=>(
                    <button key={v} onClick={()=>setPanel(p=>({...p,
                        ajuste_pct:v,cuota_fija:""}))}
                      style={{flex:1,
                      background:panel.ajuste_pct===v?`${Q.violet}33`
                        :"rgba(255,255,255,0.04)",
                      border:`1px solid ${panel.ajuste_pct===v?Q.violet:Q.border}`,
                      borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                      color:panel.ajuste_pct===v?Q.cyan:Q.muted,fontSize:12,
                      fontWeight:panel.ajuste_pct===v?700:400,
                      fontFamily:F_BODY}}>{v}%</button>
                  ))}
                </div>

                <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
                  marginBottom:12}}>
                  <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>
                    O cuota fija:</span>
                  <input value={panel.cuota_fija||""} inputMode="decimal"
                    onChange={e=>setPanel(p=>({...p,
                      cuota_fija:e.target.value,ajuste_pct:0}))}
                    placeholder="1.50"
                    style={{flex:1,minWidth:0,
                      background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",color:Q.text,fontSize:14,
                      fontWeight:700,textAlign:"center",
                      fontFamily:F_BODY}}/>
                </div>

                <div style={{color:Q.dim,fontSize:12,marginBottom:12,
                  lineHeight:1.5,fontFamily:F_BODY}}>
                  El ajuste solo puede bajar la cuota. Se aplica al crear
                  el boleto, así que el cliente ve la cuota ya ajustada.</div>
              </>
            )}

            {/* Qué se bloquea */}
            {panel.accion!=="ajustar"&&<div style={{color:Q.muted,fontSize:12,
              letterSpacing:0.8,marginBottom:5}}>QUÉ SE BLOQUEA</div>}
            {panel.accion!=="ajustar"&&
            <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
              {[["evento","Este evento"],["deporte","Todo el deporte"],
                ["mercado","Un mercado"]].map(([k,l])=>(
                <button key={k} onClick={()=>setPanel(p=>({...p,objeto:k}))}
                  style={{flex:1,
                  background:panel.objeto===k?`${Q.violet}33`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${panel.objeto===k?Q.violet:Q.border}`,
                  borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                  color:panel.objeto===k?Q.cyan:Q.muted,fontSize:12,
                  fontWeight:panel.objeto===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>}

            {panel.accion!=="ajustar"&&panel.objeto==="mercado"&&(
              <>
                <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                  marginBottom:5}}>CUÁL MERCADO</div>
                <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",
                  marginBottom:12}}>
                  {(panel.mercados||[]).length===0&&(
                    <div style={{color:Q.dim,fontSize:12}}>
                      Este evento no informa sus mercados.</div>
                  )}
                  {/* El mismo rótulo en español que las fichas de la
                      tarjeta: el panel y la tarjeta son la misma pantalla,
                      y el mercado que se elige acá es el que se acaba de
                      leer allá. La clave cruda —lo que viaja al backend—
                      queda en el `title`. */}
                  {(panel.mercados||[]).map(m=>(
                    <button key={m} onClick={()=>setPanel(p=>({...p,mercado:m}))}
                      title={m}
                      style={{background:panel.mercado===m?`${Q.violet}33`
                        :"rgba(255,255,255,0.04)",
                      border:`1px solid ${panel.mercado===m?Q.violet:Q.border}`,
                      borderRadius:RADII.sm,padding:"8px 12px",cursor:"pointer",
                      color:panel.mercado===m?Q.cyan:Q.muted,fontSize:12,
                      fontFamily:F_BODY}}>{tituloMercado(m)}</button>
                  ))}
                </div>
              </>
            )}

            {/* Dónde aplica */}
            <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
              marginBottom:5}}>DÓNDE APLICA</div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
              {[["global","Todo el sistema"],["rama","Una rama"],
                ["agencia","Una agencia"]].map(([k,l])=>(
                <button key={k} onClick={()=>setPanel(p=>({...p,alcance:k}))}
                  style={{flex:1,
                  background:panel.alcance===k?`${Q.violet}33`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${panel.alcance===k?Q.violet:Q.border}`,
                  borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                  color:panel.alcance===k?Q.cyan:Q.muted,fontSize:12,
                  fontWeight:panel.alcance===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>

            {panel.alcance!=="global"&&(
              <select value={panel.agencia_code}
                onChange={e=>setPanel(p=>({...p,agencia_code:e.target.value}))}
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"8px 12px",color:Q.text,fontSize:13,marginBottom:12,
                  fontFamily:F_BODY}}>
                <option value="">Elegí la agencia…</option>
                {ags.map(a=>(
                  <option key={a.code} value={a.code}>
                    {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
                ))}
              </select>
            )}

            {/* Cuándo */}
            <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
              marginBottom:5}}>CUÁNDO</div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
              {[["ambos","Siempre"],["prematch","Solo previa"],
                ["live","Solo en vivo"]].map(([k,l])=>(
                <button key={k} onClick={()=>setPanel(p=>({...p,modo:k}))}
                  style={{flex:1,
                  background:panel.modo===k?`${Q.violet}33`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${panel.modo===k?Q.violet:Q.border}`,
                  borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                  color:panel.modo===k?Q.cyan:Q.muted,fontSize:12,
                  fontWeight:panel.modo===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>

            <input value={panel.motivo}
              onChange={e=>setPanel(p=>({...p,motivo:e.target.value}))}
              placeholder="Motivo (queda registrado)"
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"8px 12px",color:Q.text,fontSize:13,marginBottom:12,
                fontFamily:F_BODY}}/>

            <div style={{background:`${Q.amber}12`,
              border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
              padding:"8px 12px",marginBottom:12,fontSize:12,
              color:Q.amber,lineHeight:1.5,
              fontFamily:F_BODY}}>
              {panel.accion==="ajustar"
                ? "Si hay varios ajustes que apliquen, gana el más bajo."
                : "El nivel superior manda: lo que bloquees acá no lo puede habilitar ninguna agencia de abajo."}</div>

            {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:10,
              textAlign:"center"}}>
              <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

            {panel.accion==="ajustar"?(
              <Btn label={proc?"Guardando…":<><TrendingDown size={13}/> Aplicar ajuste</>}
                color={Q.violet} full disabled={proc}
                onClick={()=>guardarAjuste({
                  event_id:panel.event_id,
                  mercado:panel.mercado||null,
                  alcance:panel.alcance,
                  agencia_code:panel.alcance==="global"?null:panel.agencia_code,
                  ajuste_pct:panel.cuota_fija?0:(panel.ajuste_pct||0),
                  cuota_fija:panel.cuota_fija||null,
                  motivo:panel.motivo, etiqueta:panel.etiqueta,
                })}/>
            ):(
              <Btn label={proc?"Bloqueando…":<><Ban size={13}/> Bloquear</>} color={Q.red} full
                disabled={proc}
                onClick={()=>bloquear({
                  objeto:panel.objeto,
                  event_id:panel.objeto==="deporte"?null:panel.event_id,
                  sport_key:panel.objeto==="deporte"?panel.sport_key:null,
                  mercado:panel.objeto==="mercado"?panel.mercado:null,
                  alcance:panel.alcance,
                  agencia_code:panel.alcance==="global"?null:panel.agencia_code,
                  modo:panel.modo, motivo:panel.motivo,
                  etiqueta:panel.etiqueta,
                })}/>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// TAB DASHBOARD
// ═══════════════════════════════════════════════════════════════
function TabDash(){
  const s=MOCK.stats;
  return(
    <div>
      <AvisoMock/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:SPACING[8],marginBottom:12}}>
        <KPI label="Usuarios"      value={s.users.toLocaleString()} sub={`+${s.newToday} hoy`} color={Q.cyan}   icon={<Icon name="users" size={16}/>} trend={3.2}/>
        <KPI label="GGR hoy"       value={ars(s.netwinHoy)}                                     color={Q.green}  icon={<Icon name="chart-no-axes-combined" size={16}/>} trend={4.1}/>
        <KPI label="Depósitos hoy" value={ars(s.depHoy)}                                        color={Q.violet} icon={<Icon name="arrow-down-left" size={16}/>} trend={12}/>
        <KPI label="Apuestas vivas"value={s.apuestasActivas.toString()}                         color={Q.amber}  icon={<Target size={16}/>}/>
      </div>

      <GCard style={{padding:SPACING[16],marginBottom:12,
        background:`linear-gradient(135deg,${Q.green}10,${Q.violet}06)`}}>
        <div style={{color:Q.muted,fontSize:12,letterSpacing:2,textTransform:"uppercase",
          fontFamily:F_BODY,marginBottom:3}}>NET WIN 30 DÍAS</div>
        <div style={{color:Q.green,fontWeight:900,fontSize:26,
          fontFamily:F_BODY,textShadow:`0 0 14px ${Q.green}66`}}>
          {ars(s.netwin30d)}
        </div>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:10,
          fontFamily:F_BODY}}><Icon name="trophy" size={14}/> Top usuarios por GGR</div>
        {MOCK.topUsers.map((u,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:SPACING[12],
            padding:"8px 0",borderBottom:i<4?`1px solid ${Q.dim}`:"none"}}>
            <span style={{color:Q.dim,fontWeight:700,fontSize:12,width:16,
              fontFamily:F_BODY}}>{i+1}</span>
            <div style={{flex:1}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,
                fontFamily:F_BODY}}>@{u.username}</div>
              <div style={{display:"flex",gap:SPACING[4],marginTop:2}}>
                <HBadge label={u.plan} color={Q.violet}/>
                <HBadge label={`LV ${u.level}`} color={Q.cyan}/>
              </div>
            </div>
            <div style={{color:Q.gold,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{ars(u.ggr)}</div>
          </div>
        ))}
      </GCard>

      <GCard style={{padding:SPACING[16]}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:10,
          fontFamily:F_BODY}}><Icon name="chart-no-axes-combined" size={14}/> Influencers</div>
        {MOCK.influencers.map((inf,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",
            padding:"8px 0",borderBottom:i<MOCK.influencers.length-1?`1px solid ${Q.dim}`:"none"}}>
            <div>
              <div style={{color:Q.violet2,fontWeight:700,fontSize:12,
                fontFamily:F_BODY}}>@{inf.influencer_code}</div>
              <div style={{color:Q.muted,fontSize:12}}>
                {inf.clics} clics · {inf.registros} registros · {inf.apuestas} apuestas
              </div>
            </div>
            <div style={{color:Q.gold,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{ars(inf.volumen)}</div>
          </div>
        ))}
      </GCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB AGENCIAS — datos reales
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// HISTORIAL DE BLOQUEOS (modal)
// ═══════════════════════════════════════════════════════════════
function HistorialImpresiones({ adminKey, onCerrar, onNoAutorizado }){
  const [data,setData]=useState(null);
  useEffect(()=>{
    fetch(`${API}/api/admin/historial/impresiones`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){ onNoAutorizado(); return {impresiones:[]}; } return r.json(); })
      .then(d=>setData(d.impresiones||[])).catch(()=>setData([]));
  // eslint-disable-next-line
  },[]);
  const tipoTxt={ticket:"Ticket",apuesta:"Apuesta",cobro:"Cobro",cierre:"Cierre de caja",combo:"Combo"};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
      zIndex:210,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
        border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Printer size={16}/> Historial de impresiones</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {data&&data.length===0&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Todavía no hubo impresiones</div>}
        {(data||[]).map((im,i)=>(
          <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:12,
                  fontFamily:F_BODY}}>
                  {tipoTxt[im.tipo]||im.tipo}
                  {im.referencia?<span style={{color:Q.cyan,fontWeight:400}}> · {im.referencia}</span>:null}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {im.agencia} · por {im.quien} · {fechaHora(im.fecha)}</div>
              </div>
              <Printer size={16}/>
            </div>
          </GCard>
        ))}
      </div>
    </div>
  );
}

function HistorialBloqueos({ adminKey, onCerrar, onNoAutorizado }){
  const [data,setData]=useState(null);

  useEffect(()=>{
    fetch(`${API}/api/admin/eventos/bloqueos`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){ onNoAutorizado(); return {bloqueos:[]}; } return r.json(); })
      .then(d=>setData(d.bloqueos||[])).catch(()=>setData([]));
  // eslint-disable-next-line
  },[]);

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
      zIndex:210,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
        border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Lock size={16}/> Historial de bloqueos</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {data&&data.length===0&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Todavía no hubo bloqueos</div>}

        {(data||[]).map((b,i)=>{
          const esBloqueo=b.accion==="bloqueo";
          return(
            <GCard key={i} glow={esBloqueo?Q.red:null}
              style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:esBloqueo?Q.red:Q.green,fontWeight:700,fontSize:12,
                    fontFamily:F_BODY}}>
                    {esBloqueo?<><Lock size={12}/> Bloqueó</>:<><Icon name="circle-check" size={12}/> Desbloqueó</>}{" "}
                    <span style={{color:Q.text}}>{b.nombre}</span>
                    <span style={{color:Q.muted,fontWeight:400}}> ({b.tipo})</span>
                    {b.cascada&&<span style={{color:Q.amber,fontSize:12,marginLeft:5}}>EN CASCADA</span>}
                  </div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                    por {b.quien} · {fechaHora(b.fecha)}</div>
                  {b.motivo&&<div style={{color:Q.dim,fontSize:12,marginTop:4,
                    fontStyle:"italic",fontFamily:F_BODY}}>
                    "{b.motivo}"</div>}
                </div>
              </div>
            </GCard>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ÁRBOL VISUAL — organigrama navegable de agencias
// ═══════════════════════════════════════════════════════════════
function ArbolVisual({ agencias, onElegir, onCerrar }){
  // Armar el árbol: cada agencia con sus hijos
  const porCode = {};
  agencias.forEach(a=>{ porCode[a.code]={...a,hijos:[]}; });
  const raices = [];
  agencias.forEach(a=>{
    if(a.parent_code && porCode[a.parent_code])
      porCode[a.parent_code].hijos.push(porCode[a.code]);
    else
      raices.push(porCode[a.code]);
  });

  const colorNivel = (n)=>["#00F0FF","#7C3AED","#00FF88","#FFB800","#FF0080"][n%5];

  const Nodo = ({ nodo })=>(
    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start"}}>
      <button onClick={()=>onElegir(nodo.code)} style={{
        background:`${colorNivel(nodo.nivel||0)}18`,
        border:`1.5px solid ${colorNivel(nodo.nivel||0)}`,
        borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",textAlign:"left",
        marginBottom:nodo.hijos.length?8:6,minWidth:150}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:12,
          fontFamily:F_BODY}}>{nodo.name}</div>
        <div style={{color:Q.muted,fontSize:12}}>{nodo.code} · {nodo.moneda}</div>
        {/* El renglón de arriba ya dice la moneda del nodo; el saldo iba en
            pesos clavados. Sin `conCodigo` para no escribirla dos veces. */}
        <div style={{color:(nodo.saldo_cc||0)>=0?Q.green:Q.red,fontSize:12,fontWeight:700,
          fontFamily:F_BODY}}>{money(nodo.saldo_cc, nodo.moneda)}</div>
      </button>
      {nodo.hijos.length>0&&(
        <div style={{display:"flex",flexDirection:"column",gap:0,
          marginLeft:18,paddingLeft:SPACING[16],
          borderLeft:`2px solid ${colorNivel(nodo.nivel||0)}55`}}>
          {nodo.hijos.map(h=>(
            <div key={h.code} style={{position:"relative"}}>
              <div style={{position:"absolute",left:-16,top:20,width:14,height:2,
                background:`${colorNivel(nodo.nivel||0)}55`}}/>
              <Nodo nodo={h}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",
      zIndex:210,display:"flex",alignItems:"flex-start",justifyContent:"center",
      padding:"40px 12px",overflowY:"auto"}} onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderRadius:RADII.xl,width:"100%",maxWidth:640,padding:SPACING[20],
        border:`1px solid ${Q.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Icon name="network" size={14}/> Árbol de agencias</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:16,
          fontFamily:F_BODY}}>
          Tocá cualquier agencia para ir a ella.</div>
        <div style={{overflowX:"auto"}}>
          {raices.map(r=>(
            <div key={r.code} style={{marginBottom:14}}>
              <Nodo nodo={r}/>
            </div>
          ))}
          {raices.length===0&&<div style={{color:Q.muted,fontSize:13,textAlign:"center",
            padding:SPACING[20],fontFamily:F_BODY}}>No hay agencias</div>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SECCIÓN AGENCIAS (unificada: árbol + agencias + clientes)
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// TAB INFLUENCERS — crear + reporte
// ═══════════════════════════════════════════════════════════════
function TabInfluencers({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const primero=new Date(); primero.setDate(1);
  const [desde,setDesde]=useState(primero.toISOString().slice(0,10));
  const [hasta,setHasta]=useState(hoy);
  const [data,setData]=useState(null);
  const [escaneos,setEscaneos]=useState(null);
  const [vista,setVista]=useState("reporte");   // reporte | escaneos
  const [showForm,setShowForm]=useState(false);
  const [sel,setSel]=useState(null);   // code del influencer abierto

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/influencers/reporte?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setData(await r.json());
    }catch(e){}
  };
  const cargarEscaneos=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/influencers/escaneos?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setEscaneos(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); if(vista==="escaneos") cargarEscaneos();
  // eslint-disable-next-line
  },[desde,hasta,vista]);

  // ── LA COMISIÓN DE CADA INFLUENCER, POR MONEDA ───────────────
  //
  // Cada influencer trae SU `por_moneda` y no un monto solo: con
  // `alcance='global'` —el valor por omisión del que crea el admin— firma
  // jugadas en cualquier rama, así que su propia comisión ya puede ser
  // pesos más dólares. Por eso la fila dibuja una línea por moneda en vez
  // de un número rotulado.
  //
  // EL CÓDIGO SE DECIDE SOBRE LA LISTA ENTERA Y NO FILA POR FILA, y es el
  // punto del aplanado de acá abajo: con un influencer en pesos y otro en
  // dólares, cada fila tiene UNA sola moneda, así que un formateador por
  // fila no pondría ningún código y quedarían dos `$` distintos uno debajo
  // del otro en la misma columna. Es exactamente el defecto que
  // `formatoDeLista` vino a cerrar.
  const filasInf = (data?.influencers||[])
    .flatMap(i=>i.por_moneda||[])
    .concat(Array.isArray(data?.total?.por_moneda)?data.total.por_moneda:[]);
  const montoInf = formatoDeLista(filasInf);
  const variasInf = new Set(filasInf.map(m=>m.moneda)).size > 1;

  return(
    <div>
      <PageHeader icon={<Star/>} title="Influencers"
        description="Los códigos de influencer de todo el sistema, su costo y sus escaneos."
        action={<Btn label="+ Nuevo" onClick={()=>setShowForm(s=>!s)} color={Q.violet} size="sm"/>}/>

      {showForm&&<CrearInfluencer adminKey={adminKey}
        onListo={()=>{setShowForm(false);cargar();}} onNoAutorizado={onNoAutorizado}/>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["reporte",<><Star size={13}/> Reporte</>],["escaneos",<><Icon name="scan-line" size={13}/> Escaneos</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px",cursor:"pointer",color:vista===k?Q.cyan:Q.muted,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
      </div>

      {vista==="reporte"&&!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}

      {vista==="reporte"&&data&&(
        <div>
          {/* UNA TARJETA POR MONEDA, igual que el cierre del admin. Un
              influencer con `alcance` global firma jugadas en cualquier
              rama, así que este total cruzaba pesos con dólares; la
              tarjeta de arriba era la suma de las dos. Con una sola
              moneda se dibuja una vez y la pantalla queda idéntica.

              La guarda con `Array.isArray` cubre la respuesta vieja de
              caché y el frontend desfasado del backend: sin ella la
              pestaña revienta entera por un `.map` sobre `undefined`. */}
          {(Array.isArray(data.total?.por_moneda)
            ? data.total.por_moneda : []).map(m=>(
            <GCard key={m.moneda} style={{padding:SPACING[16],marginBottom:12,
              background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}06)`}}>
              {variasInf&&(
                <div style={{color:Q.muted,fontSize:12,letterSpacing:1,
                  textTransform:"uppercase",marginBottom:8,
                  fontFamily:F_BODY}}>{m.moneda}</div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8]}}>
                {[["Ventas",m.ventas,Q.cyan],
                  ["GGR",m.ggr,Q.green],
                  ["Comisiones",m.comisiones,Q.gold]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{color:c,fontWeight:800,fontSize:15,
                      fontFamily:F_BODY}}>{money(v,m.moneda)}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  </div>
                ))}
              </div>
            </GCard>
          ))}

          {data.influencers.length===0&&<div style={{color:Q.muted,fontSize:12,
            textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
            No hay influencers todavía</div>}

          {data.influencers.map(inf=>(
            <GCard key={inf.code} onClick={()=>setSel(inf.code)}
              style={{padding:"12px 12px",marginBottom:6,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>{inf.name}
                    <span style={{color:Q.muted,fontWeight:400,fontSize:12}}> · {inf.codigo_ref}</span></div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {inf.combos} combos · {inf.jugadas} jugadas · GGR {fmt(inf.pct_ggr)}%/Vta {fmt(inf.pct_ventas)}%</div>
                  {inf.parent_name&&<div style={{color:Q.violet2,fontSize:12,marginTop:1}}>
                    <Building2 size={11}/> {inf.parent_name}</div>}
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  {(inf.por_moneda||[]).map(m=>(
                    <div key={m.moneda} style={{color:Q.gold,fontWeight:700,
                      fontSize:14,fontFamily:F_BODY}}>
                      {montoInf(m.comision,m.moneda)}</div>
                  ))}
                  <div style={{color:Q.muted,fontSize:12}}>comisión ›</div>
                </div>
              </div>
            </GCard>
          ))}
        </div>
      )}

      {vista==="escaneos"&&<VistaEscaneos data={escaneos}/>}

      {sel&&<DetalleInfluencer code={sel} adminKey={adminKey} desde={desde} hasta={hasta}
        onCerrar={()=>setSel(null)} onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}

function VistaEscaneos({ data }){
  if(!data) return <div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
    fontFamily:F_BODY}}>Cargando...</div>;
  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:12}}>
        {[["Escaneos",data.total_escaneos,Q.cyan],["Jugaron",data.total_jugadas,Q.green],
          ["Conversión",data.conversion+"%",Q.gold]].map(([l,v,c])=>(
          <GCard key={l} style={{padding:"16px 8px",textAlign:"center"}}>
            <div style={{color:c,fontWeight:800,fontSize:18,
              fontFamily:F_BODY}}>{v}</div>
            <div style={{color:Q.muted,fontSize:12,marginTop:2}}>{l}</div>
          </GCard>
        ))}
      </div>
      {data.detalle.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
        padding:SPACING[20],fontFamily:F_BODY}}>Sin escaneos en el período</div>}
      {data.detalle.map(d=>(
        <GCard key={d.code} style={{padding:"12px 12px",marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{d.name}
                <span style={{color:Q.muted,fontWeight:400,fontSize:12}}> · {d.codigo_ref}</span></div>
              <div style={{color:Q.muted,fontSize:12}}>
                {d.escaneos} escaneos · {d.jugadas} jugaron · cuota prom {fmt(d.cuota_prom)}x</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{color:d.conversion>=30?Q.green:d.conversion>=10?Q.gold:Q.muted,
                fontWeight:700,fontSize:16,fontFamily:F_BODY}}>{d.conversion}%</div>
              <div style={{color:Q.muted,fontSize:12}}>conversión</div>
            </div>
          </div>
        </GCard>
      ))}
    </div>
  );
}

function CrearInfluencer({ adminKey, onListo, onNoAutorizado }){
  const [form,setForm]=useState({name:"",username:"",password:"",pct_ggr:"",pct_ventas:"",parent_code:"",alcance:""});
  const [ags,setAgs]=useState([]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  useEffect(()=>{
    fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{agencias:[]}).then(d=>setAgs(d.agencias||[])).catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const crear=async()=>{
    if(!form.name||!form.username||!form.password){ setMsg({text:"Completá nombre, usuario y clave",ok:false}); return; }
    if(form.password.length<8){ setMsg({text:"La clave debe tener 8+ caracteres",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/influencers`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(form),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setMsg({text:`Creado: ${d.code} · código ${d.codigo_ref}`,ok:true});
      setTimeout(onListo,900);
    }catch(e){ setMsg({text:e.message,ok:false}); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[16],marginBottom:14}}>
      <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:12,
        fontFamily:F_BODY}}><Star size={14}/> Nuevo influencer</div>
      {[["name","Nombre","Juan Tips"],["username","Usuario","juantips"],
        ["password","Contraseña (8+)","••••••••"]].map(([k,l,ph])=>(
        <div key={k} style={{marginBottom:8}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>{l}</div>
          <input value={form[k]} type={k==="password"?"password":"text"}
            onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={ph}
            style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:16,
              fontFamily:F_BODY}}/>
        </div>
      ))}
      <div style={{marginBottom:8}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Cuelga de (vacío = del admin)</div>
        <select value={form.parent_code} onChange={e=>setForm(f=>({...f,parent_code:e.target.value}))}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:form.parent_code?Q.text:Q.muted,fontSize:15,
            fontFamily:F_BODY}}>
          <option value="">— Del admin (raíz) —</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>{"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
          ))}
        </select>
      </div>
      <div style={{marginBottom:8}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Dónde valen sus links</div>
        <select value={form.alcance} onChange={e=>setForm(f=>({...f,alcance:e.target.value}))}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:form.alcance?Q.text:Q.muted,fontSize:15,
            fontFamily:F_BODY}}>
          <option value="">— Automático según agencia —</option>
          <option value="solo_agencia">Solo esa agencia</option>
          <option value="rama">Toda su rama</option>
          <option value="global">Cualquier lado + Telegram</option>
        </select>
        <div style={{color:Q.dim,fontSize:12,marginTop:4,
          fontFamily:F_BODY}}>
          {form.parent_code
            ?"Con agencia asignada, por defecto solo juega en esa agencia."
            :"Sin agencia (del admin), por defecto vale en todos lados."}</div>
      </div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% GGR</div>
          <input value={form.pct_ggr} onChange={e=>setForm(f=>({...f,pct_ggr:e.target.value}))}
            placeholder="10" inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:16,
              fontFamily:F_BODY}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% Ventas</div>
          <input value={form.pct_ventas} onChange={e=>setForm(f=>({...f,pct_ventas:e.target.value}))}
            placeholder="3" inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:16,
              fontFamily:F_BODY}}/>
        </div>
      </div>
      <Btn label={proc?"CREANDO...":"Crear influencer"} onClick={crear} color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </GCard>
  );
}

function DetalleInfluencer({ code, adminKey, desde, hasta, onCerrar, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [resetOpen,setResetOpen]=useState(false);
  const [configOpen,setConfigOpen]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/influencers/${code}/detalle?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[code]);

  const liquidar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/influencers/${code}/liquidar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({desde,hasta}),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const j=await r.json();
      setMsg({text:`Liquidación generada · ${ars(j.comision)}`,ok:true});
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  const rep=d&&d.reporte;
  // Las jugadas firmadas son una lista POR FILA: cada boleto trae su
  // moneda del borde, y un influencer con alcance global firma en ramas de
  // monedas distintas, así que dos renglones de esta misma lista pueden no
  // compartir unidad. El código aparece sólo si la lista tiene más de una.
  const montoJugada = formatoDeLista(d?.jugadas);
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:210,
      display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"90vh",overflowY:"auto",padding:SPACING[20],border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Star size={16}/> {rep?rep.name:code}</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {!d&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {d&&(
          <div>
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                fontFamily:F_BODY}}>Código: {rep.codigo_ref}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:12}}>
                {[["Ventas",rep.apostado,Q.cyan],["GGR",rep.ggr,Q.green],
                  ["Comisión",rep.comision,Q.gold]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{color:c,fontWeight:800,fontSize:15,
                      fontFamily:F_BODY}}>{ars(v)}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  </div>
                ))}
              </div>
              {/* Settling, resetting and configuring are independent one-tap actions on this account, so they share a line */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
                <div>
                  <Btn label={proc?"...":<><Icon name="wallet-cards" size={13}/> Liquidar comisión</>} onClick={liquidar}
                    color={Q.gold} full disabled={proc}/>
                </div>
                <div>
                  <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
                    color={Q.amber} full/>
                </div>
                <div>
                  <Btn label={<><Icon name="sliders-horizontal" size={13}/> Configurar influencer</>} onClick={()=>setConfigOpen(v=>!v)}
                    color={Q.violet} full/>
                </div>
              </div>
              {configOpen&&d.reporte&&(
                <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                  <ConfigurarCuenta esAdmin adminKey={adminKey}
                    cuenta={{code, tipo:"influencer", name:d.reporte.name,
                      username:d.reporte.username, pct_ggr:d.reporte.pct_ggr,
                      pct_ventas:d.reporte.pct_ventas, alcance:d.reporte.alcance}}
                    onCambio={()=>{setConfigOpen(false);}}
                    onNoAutorizado={onNoAutorizado}/>
                </div>
              )}
              {msg&&<div style={{fontSize:12,marginTop:8,
                color:msg.ok?Q.green:Q.red,
                fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
              {resetOpen&&<ResetPasswordAdmin adminKey={adminKey} code={code}
                nombre={d.reporte?d.reporte.name:code} onCerrar={()=>setResetOpen(false)}/>}
            </GCard>

            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              marginBottom:8,fontFamily:F_BODY}}>Combos ({d.combos.length})</div>
            {d.combos.map(c=>(
              <GCard key={c.id} style={{padding:"8px 12px",marginBottom:5}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{color:Q.text,fontSize:12,fontWeight:600,
                      fontFamily:F_BODY}}>{c.nombre}
                      {c.codigo&&<span style={{color:Q.cyan,fontSize:12}}> · {c.codigo}</span>}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{fechaCorta(c.fecha)}</div>
                  </div>
                  <div style={{color:Q.green,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>{fmt(c.odd)}x</div>
                </div>
              </GCard>
            ))}

            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              margin:"12px 0 8px",fontFamily:F_BODY}}>Jugadas ({d.jugadas.length})</div>
            {d.jugadas.length===0&&<div style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>Sin jugadas en el período</div>}
            {d.jugadas.map((j,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:"8px 0",
                borderBottom:i<d.jugadas.length-1?`1px solid ${Q.dim}`:"none"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>{j.code}</div>
                  <div style={{color:Q.muted,fontSize:12}}>{j.cliente} · {montoJugada(j.stake,j.moneda)} · {fmt(j.odd)}x · {fechaHora(j.fecha)}</div>
                </div>
                <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>{j.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabAgencias({ adminKey, onNoAutorizado }){
  const [ags,setAgs]=useState(null);
  const [sel,setSel]=useState(null);        // agencia seleccionada (code)
  const [clientes,setClientes]=useState(null);
  const [selCliente,setSelCliente]=useState(null);  // user_id de ficha
  const [showForm,setShowForm]=useState(false);
  const [showCrearCliente,setShowCrearCliente]=useState(false);
  const [showArbol,setShowArbol]=useState(false);
  const [showBloqueos,setShowBloqueos]=useState(false);
  const [showImpresiones,setShowImpresiones]=useState(false);
  const [msg,setMsg]=useState("");

  const cargarAgs=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setAgs((await r.json()).agencias||[]);
    }catch(e){ setMsg("Sin conexión"); }
  };
  const cargarClientes=async(code)=>{
    setClientes(null);
    try{
      const r=await fetch(`${API}/api/admin/clientes?agencia=${code}`,{headers:adminHeaders(adminKey)});
      if(r.ok) setClientes((await r.json()).clientes||[]);
    }catch(e){ setClientes([]); }
  };
  useEffect(()=>{ cargarAgs(); // eslint-disable-next-line
  },[]);

  const elegirAgencia=(code)=>{
    if(sel===code){ setSel(null); setClientes(null); return; }
    setSel(code); setSelCliente(null); cargarClientes(code);
  };

  const agSel = ags?.find(a=>a.code===sel);
  // `/api/admin/clientes?agencia=` manda `moneda` por cliente. Esta lista es
  // de UNA agencia, así que en la práctica comparte moneda y el código no
  // aparece; igual se formatea por fila, que es de dónde sale el dato.
  const montoCliAg = formatoDeLista(clientes);

  return(
    <div>
      <PageHeader icon={<Building2/>} title="Agencias"
        description="Todas las agencias del sistema: creá nuevas, revisá su red y gestioná bloqueos e impresiones."
        action={<div style={{display:"flex",gap:SPACING[8]}}>
          <Btn label={<><Icon name="network" size={13}/> Árbol</>} onClick={()=>setShowArbol(true)} color={Q.cyan} size="sm"/>
          <Btn label={<><Lock size={13}/> Bloqueos</>} onClick={()=>setShowBloqueos(true)} color={Q.red} size="sm"/>
          <Btn label={<><Printer size={13}/> Impresiones</>} onClick={()=>setShowImpresiones(true)} color={Q.amber} size="sm"/>
          <Btn label="+ Nueva" onClick={()=>setShowForm(s=>!s)} color={Q.violet} size="sm"/>
        </div>}/>

      {showForm&&<CrearAgenciaAdmin adminKey={adminKey} agencias={ags||[]}
        onListo={()=>{setShowForm(false);cargarAgs();}}
        onNoAutorizado={onNoAutorizado}/>}

      {!ags&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando árbol...</div>}

      {/* Árbol de agencias */}
      {(ags||[]).map(a=>(
        <div key={a.code} style={{marginLeft:(a.nivel||0)*14,marginBottom:6}}>
          <GCard glow={sel===a.code?Q.cyan:(a.status==="active"?undefined:Q.red)}
            onClick={()=>elegirAgencia(a.code)}
            style={{padding:"12px 12px",cursor:"pointer",
              borderLeft:`3px solid ${["#00F0FF","#7C3AED","#00FF88","#FFB800","#FF0080"][(a.nivel||0)%5]}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  {a.nivel>0?"└ ":""}{a.name}
                  {a.status!=="active"&&<span style={{color:Q.red,fontSize:12,marginLeft:6}}>SUSP</span>}
                </div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {a.code}
                  {a.username&&<> · <span style={{color:Q.cyan}}>
                    @{a.username}</span></>}
                  {" · "}{a.moneda} · GGR {fmt(a.pct_ggr)}% · Vta {fmt(a.pct_ventas)}%</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                {/* El subtítulo de la fila ya escribe `a.moneda` entre el
                    usuario y los porcentajes; el saldo iba en pesos
                    clavados. Sin `conCodigo`, ya está dicho en la fila. */}
                <div style={{color:(a.saldo_cc||0)>=0?Q.green:Q.red,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{money(a.saldo_cc, a.moneda)}</div>
                <span style={{color:Q.muted,fontSize:14}}>{sel===a.code?"▾":"›"}</span>
              </div>
            </div>
          </GCard>

          {/* Panel expandido de la agencia seleccionada */}
          {sel===a.code&&(
            <div style={{marginTop:8,marginBottom:10,paddingLeft:SPACING[8]}}>
              <FichaAgencia agencia={agSel} adminKey={adminKey}
                onCambio={cargarAgs} onNoAutorizado={onNoAutorizado}/>

              {/* Clientes de esta agencia */}
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",margin:"12px 0 8px"}}>
                <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
                  letterSpacing:1,fontFamily:F_BODY}}>
                  Clientes de {a.name}</span>
                <Btn label="+ Cliente" onClick={()=>setShowCrearCliente(true)}
                  color={Q.cyan} size="sm"/>
              </div>

              {showCrearCliente&&(
                <CrearClienteAdmin adminKey={adminKey} agencia={a.code}
                  onListo={()=>{setShowCrearCliente(false);cargarClientes(a.code);}}
                  onCancel={()=>setShowCrearCliente(false)}
                  onNoAutorizado={onNoAutorizado}/>
              )}

              {clientes===null&&<div style={{color:Q.muted,fontSize:12,padding:SPACING[12],
                fontFamily:F_BODY}}>Cargando clientes...</div>}
              {clientes&&clientes.length===0&&(
                <div style={{color:Q.muted,fontSize:12,padding:SPACING[12],textAlign:"center",
                  fontFamily:F_BODY}}>Esta agencia no tiene clientes</div>
              )}
              {(clientes||[]).map(c=>(
                <GCard key={c.id} onClick={()=>setSelCliente(c.id)}
                  style={{padding:"12px 12px",marginBottom:6,cursor:"pointer"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:600,fontSize:12,
                        fontFamily:F_BODY}}>{c.nombre}
                        {c.bloqueado&&<span style={{color:Q.red,fontSize:12,marginLeft:5}}><Lock size={11}/></span>}
                      </div>
                      <div style={{color:Q.muted,fontSize:12}}>{fechaCorta(c.fecha)}</div>
                    </div>
                    <div style={{color:Q.green,fontWeight:700,fontSize:13,
                      fontFamily:F_BODY}}>{montoCliAg(c.balance,c.moneda)} ›</div>
                  </div>
                </GCard>
              ))}
            </div>
          )}
        </div>
      ))}

      {showBloqueos&&<HistorialBloqueos adminKey={adminKey}
        onCerrar={()=>setShowBloqueos(false)} onNoAutorizado={onNoAutorizado}/>}

      {showImpresiones&&<HistorialImpresiones adminKey={adminKey}
        onCerrar={()=>setShowImpresiones(false)} onNoAutorizado={onNoAutorizado}/>}

      {showArbol&&<ArbolVisual agencias={ags||[]}
        onElegir={(code)=>{ setShowArbol(false); elegirAgencia(code);
          setTimeout(()=>window.scrollTo({top:0,behavior:"smooth"}),100); }}
        onCerrar={()=>setShowArbol(false)}/>}

      {selCliente&&<FichaCliente userId={selCliente} adminKey={adminKey}
        onCerrar={()=>setSelCliente(null)}
        onCambio={()=>sel&&cargarClientes(sel)}
        onNoAutorizado={onNoAutorizado}/>}

      {msg&&<div style={{color:Q.red,fontSize:12,marginTop:10,textAlign:"center",
        fontFamily:F_BODY}}>{msg}</div>}
    </div>
  );
}

// Ficha compacta de agencia (dentro del árbol expandido)
function AsignarAgenciaAdmin({ adminKey, userId, esDirecto, agenciaActual, onCambio }){
  const [abierto,setAbierto]=useState(false);
  const [agencias,setAgencias]=useState([]);
  const [sel,setSel]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  useEffect(()=>{
    if(!abierto) return;
    fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{agencias:[]})
      .then(d=>setAgencias((d.agencias||[]).map(a=>({code:a.code,name:a.name||a.code}))))
      .catch(()=>{});
  },[abierto,adminKey]);

  const asignar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/clientes/${userId}/agencia`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({agencia_code:sel}),
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setMsg({text:"Agencia actualizada",ok:true}); if(onCambio) onCambio(); }
      else setMsg({text:(d.detail||"Error"),ok:false});
    }catch(e){ setMsg({text:"Error",ok:false}); }
  };

  // Collapsed state renders as one flex item so it can sit in the same
  // row as the sibling account-action buttons instead of stacking below them.
  if(!abierto) return(
    <div>
      <Btn label={esDirecto?<><Link size={13}/> Vincular a una agencia</>:<><RefreshCw size={13}/> Cambiar de agencia</>}
        onClick={()=>setAbierto(true)} color={Q.cyan} outline full/>
    </div>
  );

  return(
    <div style={{marginTop:8,width:"100%",padding:SPACING[12],background:`${Q.cyan}0C`,
      border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md}}>
      <div style={{color:Q.cyan,fontWeight:700,fontSize:13,marginBottom:8,
        fontFamily:F_BODY}}>Asignar agencia</div>
      <select value={sel} onChange={e=>setSel(e.target.value)}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"12px",color:Q.text,fontSize:13,marginBottom:8,
          fontFamily:F_BODY}}>
        <option value="">— Dejar sin agencia (directo) —</option>
        {agencias.map(a=>(<option key={a.code} value={a.code}>{a.code} · {a.name}</option>))}
      </select>
      <div style={{display:"flex",gap:SPACING[8]}}>
        <Btn label="Cerrar" onClick={()=>{setAbierto(false);setMsg(null);}} outline color={Q.muted} full/>
        <Btn label="Guardar" onClick={asignar} color={Q.cyan} full/>
      </div>
      {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </div>
  );
}

function ResetPasswordAdmin({ adminKey, code, userId, nombre, onCerrar }){
  const [nueva,setNueva]=useState("");
  const [repetir,setRepetir]=useState("");
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null — status lives here, not in the text

  const guardar=async()=>{
    if(nueva.length<8){ setMsg({text:"La contraseña debe tener 8+ caracteres",ok:false}); return; }
    if(nueva!==repetir){ setMsg({text:"Las contraseñas no coinciden",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const body = code ? {code,nueva} : {user_id:userId,nueva};
      const r=await fetch(`${API}/api/admin/reset-password`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(body),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Contraseña reseteada. Deberá cambiarla en su próximo ingreso.",ok:true});
      setTimeout(onCerrar,1200);
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:230,
      display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}} onClick={onCerrar}>
      <GCard glow={Q.amber} onClick={e=>e.stopPropagation()}
        style={{padding:SPACING[20],maxWidth:400,width:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:15,
            fontFamily:F_BODY}}><Key size={15}/> Resetear contraseña</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:12,lineHeight:1.5,
          fontFamily:F_BODY}}>
          Nueva contraseña para <span style={{color:Q.cyan,fontWeight:700}}>{nombre||code||"la cuenta"}</span>.
          Deberá cambiarla en su próximo ingreso.</div>
        <input type="password" value={nueva} onChange={e=>setNueva(e.target.value)}
          placeholder="Nueva contraseña (8+)"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
        <input type="password" value={repetir} onChange={e=>setRepetir(e.target.value)}
          placeholder="Repetir contraseña"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:12,
            fontFamily:F_BODY}}/>
        <Btn label={proc?"GUARDANDO...":"Resetear"} onClick={guardar} color={Q.amber} full disabled={proc}/>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>
    </div>
  );
}

function ConfigurarCuenta({ cuenta, esAdmin, adminKey, token, onCambio, onNoAutorizado }){
  const esInf = cuenta.tipo==="influencer" || String(cuenta.code||"").startsWith("INF");
  const [name,setName]=useState(cuenta.name||"");
  const [address,setAddress]=useState(cuenta.address||"");
  const [phone,setPhone]=useState(cuenta.phone||"");
  const [ggr,setGgr]=useState(String(cuenta.pct_ggr??""));
  const [ventas,setVentas]=useState(String(cuenta.pct_ventas??""));
  // Casino y desafíos se liquidan aparte del deportivo: distinto
  // porcentaje y distinta cascada. Estaban en otro componente que
  // no se usa, por eso no aparecían en pantalla.
  const [ggrCasino,setGgrCasino]=useState(String(cuenta?.pct_ggr_casino||0));
  const [pctDesafios,setPctDesafios]=useState(String(cuenta?.pct_desafios||0));
  const [alcance,setAlcance]=useState(cuenta.alcance||"");
  const [permiso,setPermiso]=useState(cuenta.permiso||"ambos");
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null — status lives here, not in the text

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const headers = esAdmin
        ? {"Content-Type":"application/json",...adminHeaders(adminKey)}
        : {"Content-Type":"application/json","Authorization":`Bearer ${token}`};
      const body = {name,address,phone,
        pct_ggr:parseFloat(ggr)||0, pct_ventas:parseFloat(ventas)||0};
      if(esInf&&alcance) body.alcance=alcance;
      if(!esInf){
        body.permiso=permiso;
        body.pct_ggr_casino=parseFloat(ggrCasino)||0;
        body.pct_desafios=parseFloat(pctDesafios)||0;
      }
      const r=await fetch(`${API}/api/cuenta/${cuenta.code}/configurar`,{
        method:"POST",headers,body:JSON.stringify(body)});
      if(r.status===401){ onNoAutorizado&&onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      let extra="";
      if(d.hijos_sobre_limite&&d.hijos_sobre_limite.length)
        extra=` ⚠️ ${d.hijos_sobre_limite.length} cuenta(s) hija(s) quedaron con % mayor al tuyo.`;
      setMsg({text:"Configuración guardada."+extra,ok:true});
      onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  const inpStyle={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:15,marginBottom:8,fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:4,
        fontFamily:F_BODY}}>
        Usuario: <span style={{color:Q.dim}}>{cuenta.username||"—"} (no editable)</span></div>

      <div style={{color:Q.muted,fontSize:12,marginBottom:2,marginTop:8,
        fontFamily:F_BODY}}>Nombre</div>
      <input value={name} onChange={e=>setName(e.target.value)} style={inpStyle}/>

      {!esInf&&<>
        <div style={{color:Q.muted,fontSize:12,marginBottom:2,
          fontFamily:F_BODY}}>Dirección</div>
        <input value={address} onChange={e=>setAddress(e.target.value)} style={inpStyle}/>
        <div style={{color:Q.muted,fontSize:12,marginBottom:2,
          fontFamily:F_BODY}}>Teléfono</div>
        <input value={phone} onChange={e=>setPhone(e.target.value)} style={inpStyle}/>
      </>}

      <div style={{display:"flex",gap:SPACING[8]}}>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>% GGR</div>
          <input value={ggr} onChange={e=>setGgr(e.target.value)} inputMode="decimal" style={inpStyle}/>
        </div>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>% Ventas</div>
          <input value={ventas} onChange={e=>setVentas(e.target.value)} inputMode="decimal" style={inpStyle}/>
        </div>
      </div>

      {/* Casino y desafíos van por su cuenta: cada uno con su
          porcentaje y su cascada. */}
      {!esInf&&(
        <div style={{display:"flex",gap:SPACING[8]}}>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:2,
              fontFamily:F_BODY}}>% GGR casino</div>
            <input value={ggrCasino}
              onChange={e=>setGgrCasino(e.target.value)}
              inputMode="decimal" style={inpStyle}/>
          </div>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:2,
              fontFamily:F_BODY}}>% Desafíos</div>
            <input value={pctDesafios}
              onChange={e=>setPctDesafios(e.target.value)}
              inputMode="decimal" style={inpStyle}/>
          </div>
        </div>
      )}
      {!esInf&&(
        <div style={{color:Q.dim,fontSize:12,marginTop:-4,marginBottom:6,
          lineHeight:1.5,fontFamily:F_BODY}}>
          Casino solo sobre GGR. Desafíos es sobre la comisión que
          cobra la casa, no sobre lo apostado.</div>
      )}

      {!esInf&&(
        <>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>Permisos de la agencia</div>
          <select value={permiso} onChange={e=>setPermiso(e.target.value)}
            style={{...inpStyle,color:Q.text}}>
            <option value="solo_agencia">Solo agencia (crea clientes)</option>
            <option value="crea_agencias">+ Crear sub-agencias</option>
            <option value="crea_influencers">+ Crear influencers</option>
            <option value="ambos">Todo (sub-agencias + influencers)</option>
          </select>
        </>
      )}
      {esInf&&(
        <>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>Dónde valen sus links</div>
          <select value={alcance} onChange={e=>setAlcance(e.target.value)}
            style={{...inpStyle,color:alcance?Q.text:Q.muted}}>
            <option value="">— Sin cambios —</option>
            <option value="solo_agencia">Solo esa agencia</option>
            <option value="rama">Toda su rama</option>
            <option value="global">Cualquier lado + Telegram</option>
          </select>
        </>
      )}

      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>
        El % no puede superar el del padre.</div>

      <Btn label={proc?"GUARDANDO...":<><Save size={13}/> Guardar configuración</>} onClick={guardar}
        color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </div>
  );
}

function FichaAgencia({ agencia, adminKey, onCambio, onNoAutorizado }){
  const [ccMonto,setCcMonto]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [ver,setVer]=useState("cc");   // cc | config | editar | anular
  const [resetOpen,setResetOpen]=useState(false);
  const [anulaRama,setAnulaRama]=useState(false);
  const [puedeAnular,setPuedeAnular]=useState(!!agencia?.puede_anular);
  // Cash out: cierra una apuesta antes de tiempo. Achica el margen, así
  // que conviene darlo solo a las agencias que lo manejan bien.
  const [puedeCashout,setPuedeCashout]=useState(agencia?.puede_cashout!==false);

  const guardarPermisoCashout=async(activo)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(
        `${API}/api/admin/agencias/${agencia.code}/permiso-cashout`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo, rama:anulaRama}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setPuedeCashout(activo);
      setMsg({text:anulaRama
        ? `Cash out ${activo?"habilitado":"quitado"} en ${d.agencias_alcanzadas} agencias`
        : `Cash out ${activo?"habilitado":"quitado"}`,ok:true});
      onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  // Permiso de anular apuestas. Con rama, alcanza a esta agencia y a
  // todas las que cuelgan de ella.
  const guardarPermisoAnular=async(activo)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(
        `${API}/api/admin/agencias/${agencia.code}/permiso-anular`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo, rama:anulaRama}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setPuedeAnular(activo);
      setMsg({text:anulaRama
        ? `${activo?"Habilitado":"Quitado"} en ${d.agencias_alcanzadas} agencias`
        : `${activo?"Habilitado":"Quitado"}`,ok:true});
      onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  const cargarCC=async(signo)=>{
    const m=parseFloat(ccMonto);
    if(!m||m<=0){ setMsg({text:"Poné un monto",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/agencias/${agencia.code}/cc`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({monto:m*signo}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Saldo actualizado",ok:true}); setCcMonto(""); onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  const toggleStatus=async()=>{
    const nuevo=agencia.status==="active"?"suspended":"active";
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/agencias/${agencia.code}`,{
        method:"PUT",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({status:nuevo}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      // "🔒 Suspendida" carries no ✅, but the old render already matched
      // either status glyph before deciding green — same as FichaCliente's
      // block confirmation, this screen never had the red-on-success
      // asymmetry Agencia.jsx did.
      setMsg({text:nuevo==="active"?"Reactivada":"🔒 Suspendida",ok:true}); onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  if(!agencia) return null;
  return(
    <GCard style={{padding:SPACING[16]}}>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["cc",<><Icon name="wallet-cards" size={13}/> Saldo</>],["config",<><Icon name="sliders-horizontal" size={13}/> Configurar</>],["editar","Estado"],
          ["anular",<><Ban size={13}/> Anular</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVer(k)} style={{flex:1,
            background:ver===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${ver===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px 4px",cursor:"pointer",color:ver===k?Q.cyan:Q.muted,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
        <button onClick={()=>setResetOpen(true)} style={{flex:1,
          background:`${Q.amber}18`,border:`1px solid ${Q.amber}`,borderRadius:RADII.md,
          padding:"8px 4px",cursor:"pointer",color:Q.amber,
          fontSize:12,fontWeight:700,fontFamily:F_BODY}}><Key size={12}/> Clave</button>
      </div>

      {ver==="anular"&&(
        <div>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:6,
            fontFamily:F_BODY}}>
            Permiso para anular apuestas</div>
          <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
            marginBottom:12,fontFamily:F_BODY}}>
            Sirve para que el cajero corrija un error propio, no para que
            el cliente se arrepienta. Solo se puede dentro de los 5 minutos
            del cobro y con más de 1 hora hasta el evento. El servidor
            valida las dos condiciones aunque el botón esté visible.</div>

          <div style={{background:puedeAnular?`${Q.green}15`:"rgba(255,255,255,0.04)",
            border:`1px solid ${puedeAnular?Q.green:Q.border}`,borderRadius:RADII.md,
            padding:"12px 16px",marginBottom:12}}>
            <div style={{color:puedeAnular?Q.green:Q.muted,fontWeight:700,
              fontSize:12.5,fontFamily:F_BODY}}>
              {puedeAnular ? "Habilitada" : "Sin permiso"}</div>
            <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
              {agencia.code} · {agencia.name}</div>
          </div>

          <button onClick={()=>setAnulaRama(v=>!v)} style={{width:"100%",
            background:anulaRama?`${Q.amber}18`:"rgba(255,255,255,0.04)",
            border:`1px solid ${anulaRama?Q.amber:Q.border}`,borderRadius:RADII.md,
            padding:"12px",cursor:"pointer",marginBottom:10,
            color:anulaRama?Q.amber:Q.muted,fontSize:12,fontWeight:700,
            fontFamily:F_BODY}}>
            {anulaRama
              ? "Aplicar a TODA la rama que cuelga de acá"
              : "Aplicar solo a esta agencia"}</button>

          <div style={{display:"flex",gap:SPACING[8]}}>
            <button disabled={proc} onClick={()=>guardarPermisoAnular(true)}
              style={{flex:1,background:`${Q.green}18`,
              border:`1px solid ${Q.green}`,borderRadius:RADII.md,padding:"12px",
              cursor:"pointer",color:Q.green,fontSize:12.5,fontWeight:700,
              fontFamily:F_BODY}}>Habilitar</button>
            <button disabled={proc} onClick={()=>guardarPermisoAnular(false)}
              style={{flex:1,background:`${Q.red}18`,
              border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"12px",
              cursor:"pointer",color:Q.red,fontSize:12.5,fontWeight:700,
              fontFamily:F_BODY}}>Quitar</button>
          </div>

          {anulaRama&&(
            <div style={{color:Q.amber,fontSize:12,marginTop:10,
              lineHeight:1.5,fontFamily:F_BODY}}>
              Ojo: esto pisa lo que tenga configurado cada agencia de la
              rama, incluidas las que hayas ajustado a mano.</div>
          )}

          {/* Cash out: mismo esquema, el interruptor de rama es compartido */}
          <div style={{borderTop:`1px solid ${Q.border}`,marginTop:18,
            paddingTop:SPACING[16]}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:6,
              fontFamily:F_BODY}}>
              Permiso para hacer cash out</div>
            <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
              marginBottom:12,fontFamily:F_BODY}}>
              Cerrar una apuesta antes de que termine, a un valor calculado.
              Le gusta al cliente pero achica el margen: conviene darlo a
              las agencias que lo manejan bien. Las apuestas con bono nunca
              tienen cash out.</div>

            <div style={{background:puedeCashout?`${Q.green}15`:"rgba(255,255,255,0.04)",
              border:`1px solid ${puedeCashout?Q.green:Q.border}`,borderRadius:RADII.md,
              padding:"12px 16px",marginBottom:12}}>
              <div style={{color:puedeCashout?Q.green:Q.muted,fontWeight:700,
                fontSize:12.5,fontFamily:F_BODY}}>
                {puedeCashout ? "Habilitada" : "Sin permiso"}</div>
            </div>

            <div style={{display:"flex",gap:SPACING[8]}}>
              <button disabled={proc} onClick={()=>guardarPermisoCashout(true)}
                style={{flex:1,background:`${Q.green}18`,
                border:`1px solid ${Q.green}`,borderRadius:RADII.md,padding:"12px",
                cursor:"pointer",color:Q.green,fontSize:12.5,fontWeight:700,
                fontFamily:F_BODY}}>Habilitar</button>
              <button disabled={proc} onClick={()=>guardarPermisoCashout(false)}
                style={{flex:1,background:`${Q.red}18`,
                border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"12px",
                cursor:"pointer",color:Q.red,fontSize:12.5,fontWeight:700,
                fontFamily:F_BODY}}>Quitar</button>
            </div>
            <div style={{color:Q.dim,fontSize:12,marginTop:8,
              fontFamily:F_BODY}}>
              Usa el mismo interruptor de rama de arriba.</div>
          </div>
        </div>
      )}

      {ver==="cc"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
            <span style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>Saldo actual</span>
            {/* El mismo patrón exacto que el cash out de arriba: dibujaba
                `{ars(...)} {agencia.moneda}`, o sea `$500 USD`. El código ya
                estaba suelto, así que va con `{conCodigo:true}` y en pesos
                se lee idéntico a hoy. */}
            <span style={{color:(agencia.saldo_cc||0)>=0?Q.green:Q.red,fontWeight:900,fontSize:18,
              fontFamily:F_BODY}}>
              {money(agencia.saldo_cc, agencia.moneda, {conCodigo:true})}</span>
          </div>
          <input value={ccMonto} onChange={e=>setCcMonto(e.target.value)}
            placeholder="Monto" inputMode="numeric"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,marginBottom:8,
              fontFamily:F_BODY}}/>
          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label="+ Cargar" onClick={()=>cargarCC(1)} color={Q.green} full disabled={proc}/>
            <Btn label="− Descontar" onClick={()=>cargarCC(-1)} color={Q.amber} outline full disabled={proc}/>
          </div>
        </div>
      )}

      {ver==="config"&&(
        <ConfigurarCuenta cuenta={agencia} esAdmin adminKey={adminKey}
          onCambio={onCambio} onNoAutorizado={onNoAutorizado}/>
      )}

      {ver==="editar"&&(
        <div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:10,
            fontFamily:F_BODY}}>
            Estado: <span style={{color:agencia.status==="active"?Q.green:Q.red,fontWeight:700}}>
            {agencia.status==="active"?"Activa":"Suspendida"}</span></div>
          <Btn label={agencia.status==="active"?<><Lock size={13}/> Suspender agencia</>:<><Icon name="circle-check" size={13}/> Reactivar</>}
            onClick={toggleStatus}
            color={agencia.status==="active"?Q.red:Q.green} full disabled={proc}/>
        </div>
      )}

      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      {resetOpen&&<ResetPasswordAdmin adminKey={adminKey} code={agencia.code}
        nombre={agencia.name} onCerrar={()=>setResetOpen(false)}/>}
    </GCard>
  );
}

function ComisionAgencia({ agencia, adminKey, onCambio, onNoAutorizado }){
  const [ggr,setGgr]=useState(String(agencia.pct_ggr||0));
  const [ven,setVen]=useState(String(agencia.pct_ventas||0));
  // El casino se liquida aparte: su propio porcentaje y solo sobre
  // GGR, porque el mismo saldo puede jugarse muchas veces y el
  // volumen no refleja lo que la casa ganó.
  const [ggrCas,setGgrCas]=useState(String(agencia.pct_ggr_casino||0));
  // Los desafíos comisionan sobre el rake, no sobre el GGR: la casa
  // no arriesga nada ahí, solo cobra por juntar a los dos jugadores.
  const [pctDes,setPctDes]=useState(String(agencia.pct_desafios||0));
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  // Qué productos ofrece esta agencia
  const [prods,setProds]=useState(null);
  const [procProd,setProcProd]=useState(false);

  const cargarProds=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/productos`,
        {headers:adminHeaders(adminKey)});
      if(r.ok){
        const d=await r.json();
        const apagados=(d.restricciones||[])
          .filter(x=>x.agencia===agencia.code).map(x=>x.producto);
        setProds({catalogo:(d.catalogo||[]).filter(c=>c.activo), apagados});
      }
    }catch(e){}
  };
  useEffect(()=>{ cargarProds(); /* eslint-disable-next-line */ },[agencia.code]);

  const cambiarProd=async(codigo,activo)=>{
    setProcProd(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/productos/agencia`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({agencia_code:agencia.code,
                             producto:codigo, activo})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:activo?"Producto habilitado":"Producto quitado",ok:true});
      cargarProds();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProcProd(false);
  };

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/agencias/${agencia.code}/comisiones`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({pct_ggr:parseFloat(ggr),pct_ventas:parseFloat(ven)}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }

      // El casino va por su propio camino: es otra comisión y otra
      // cascada, así que se guarda aparte.
      const rd=await fetch(
        `${API}/api/admin/agencias/${agencia.code}/comision-desafios`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({pct_desafios:parseFloat(pctDes)||0})});
      if(!rd.ok){ const e=await rd.json().catch(()=>({}));
        throw new Error(e.detail||"No se pudo guardar el % de desafíos"); }

      const rc=await fetch(
        `${API}/api/admin/agencias/${agencia.code}/comision-casino`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({pct_ggr_casino:parseFloat(ggrCas)||0})});
      if(!rc.ok){ const e=await rc.json().catch(()=>({}));
        throw new Error(e.detail||"No se pudo guardar el % de casino"); }

      setMsg({text:"Comisiones actualizadas",ok:true}); onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message,ok:false}); }
    setProc(false);
  };

  return(
    <div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% GGR</div>
          <input value={ggr} onChange={e=>setGgr(e.target.value)} inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% Ventas</div>
          <input value={ven} onChange={e=>setVen(e.target.value)} inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
        </div>
      </div>

      <div style={{marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          % sobre la comisión de desafíos</div>
        <input value={pctDes} onChange={e=>setPctDes(e.target.value)}
          inputMode="decimal"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:5,lineHeight:1.5,
          fontFamily:F_BODY}}>
          En desafíos la casa no arriesga: cobra comisión por juntar a
          los dos jugadores. Esto es lo que le toca a la agencia de
          esa comisión.</div>
      </div>

      <div style={{marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>% GGR de casino</div>
        <input value={ggrCas} onChange={e=>setGgrCas(e.target.value)}
          inputMode="decimal"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:5,lineHeight:1.5,
          fontFamily:F_BODY}}>
          El casino se liquida aparte y solo sobre GGR. No hay comisión
          por volumen: el mismo saldo puede jugarse muchas veces sin que
          la casa haya ganado nada.</div>
      </div>
      <Btn label={proc?"GUARDANDO...":"Guardar comisiones"} onClick={guardar}
        color={Q.violet} full disabled={proc}/>

      {/* Qué productos puede ofrecer. Lo que se apague acá alcanza
          también a todas las agencias que cuelgan de ella. */}
      {prods&&(
        <div style={{marginTop:16,paddingTop:SPACING[16],
          borderTop:`1px solid ${Q.border}`}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,
            marginBottom:3,fontFamily:F_BODY}}>
            Productos habilitados</div>
          <div style={{color:Q.dim,fontSize:12,marginBottom:9,
            lineHeight:1.45}}>
            Alcanza también a las agencias que cuelgan de ella.</div>

          {prods.catalogo.map(c=>{
            const apagado=prods.apagados.includes(c.codigo);
            return(
              <button key={c.codigo}
                onClick={()=>cambiarProd(c.codigo,apagado)}
                disabled={procProd}
                style={{width:"100%",display:"flex",
                  justifyContent:"space-between",alignItems:"center",
                  background:apagado?`${Q.red}10`:`${Q.green}10`,
                  border:`1px solid ${apagado?`${Q.red}44`:`${Q.green}44`}`,
                  borderRadius:RADII.md,padding:"12px 12px",marginBottom:6,
                  cursor:"pointer",
                  fontFamily:F_BODY}}>
                <span style={{color:apagado?Q.dim:Q.text,fontSize:13,
                  fontWeight:600}}>{c.icono} {c.nombre}</span>
                <span style={{color:apagado?Q.red:Q.green,fontSize:12,
                  fontWeight:700}}>
                  {apagado?"Sin acceso":"Habilitado"}</span>
              </button>
            );
          })}
        </div>
      )}
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </div>
  );
}

function CrearClienteAdmin({ adminKey, agencia, onListo, onCancel, onNoAutorizado }){
  const [nombre,setNombre]=useState("");
  const [tel,setTel]=useState("");
  // Credenciales del cliente para entrar al sitio web
  const [usuario,setUsuario]=useState("");
  const [clave,setClave]=useState("");
  const [msg,setMsg]=useState("");
  const [proc,setProc]=useState(false);

  const crear=async()=>{
    if(!nombre.trim()){ setMsg("Poné el nombre"); return; }
    if(!/^[a-z0-9._-]{3,40}$/.test(usuario.trim().toLowerCase())){
      setMsg("Usuario: letras, números, punto, guion y guion bajo (3 a 40)");
      return;
    }
    if(clave.length<6){ setMsg("La clave debe tener al menos 6 caracteres"); return; }
    setProc(true); setMsg("");
    try{
      const r=await fetch(`${API}/api/admin/clientes`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({nombre,agencia,telefono:tel,
          username:usuario.trim().toLowerCase(), password:clave}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      onListo();
    }catch(e){ setMsg("⚠️ "+e.message); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[12],marginBottom:8}}>
      {!agencia&&<div style={{color:Q.muted,fontSize:12,marginBottom:8,lineHeight:1.4,
        fontFamily:F_BODY}}>
        <Icon name="landmark" size={11}/> Cliente propio del admin (sin agencia).</div>}
      <input value={nombre} onChange={e=>setNombre(e.target.value)}
        placeholder="Nombre del cliente"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <input value={usuario} onChange={e=>setUsuario(e.target.value)}
        placeholder="Usuario para el sitio web"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <input value={clave} onChange={e=>setClave(e.target.value)}
        placeholder="Clave (mínimo 6)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <input value={tel} onChange={e=>setTel(e.target.value)}
        placeholder="Teléfono (opcional)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <div style={{display:"flex",gap:SPACING[8]}}>
        <Btn label="Cancelar" onClick={onCancel} outline color={Q.muted} full/>
        <Btn label={proc?"...":"Crear"} onClick={crear} color={Q.cyan} full disabled={proc}/>
      </div>
      {msg&&<div style={{color:Q.red,fontSize:12,marginTop:8,
        fontFamily:F_BODY}}>{msg}</div>}
    </GCard>
  );
}

function CrearAgenciaAdmin({ adminKey, agencias, onListo, onNoAutorizado }){
  const [form,setForm]=useState({name:"",username:"",password:"",
    parent_code:"",pct_ggr:"",pct_ventas:"",moneda:"ARS",permiso:"ambos"});
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [monedas,setMonedas]=useState([]);
  useEffect(()=>{
    fetch(`${API}/api/monedas`).then(r=>r.ok?r.json():{monedas:[]})
      .then(d=>setMonedas(d.monedas||[])).catch(()=>setMonedas([]));
  },[]);

  const crear=async()=>{
    if(!form.name||!form.username||!form.password){ setMsg({text:"Completá nombre, usuario y clave",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/agencias`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(form),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setMsg({text:`Creada: ${d.code}`,ok:true});
      setTimeout(onListo,700);
    }catch(e){ setMsg({text:e.message,ok:false}); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[16],marginBottom:14}}>
      <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:12,
        fontFamily:F_BODY}}><Icon name="plus" size={14}/> Nueva agencia</div>
      {[["name","Nombre","Agencia Norte"],["username","Usuario","norte1"],
        ["password","Contraseña","••••••••"]].map(([k,l,ph])=>(
        <div key={k} style={{marginBottom:8}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>{l}</div>
          <input value={form[k]} type={k==="password"?"password":"text"}
            onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={ph}
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
        </div>
      ))}
      <div style={{marginBottom:8}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Cuelga de (vacío = raíz)</div>
        <select value={form.parent_code}
          onChange={e=>setForm(f=>({...f,parent_code:e.target.value}))}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
            color:form.parent_code?Q.text:Q.muted,fontSize:15,
            fontFamily:F_BODY}}>
          <option value="">— Raíz (del admin) —</option>
          {agencias.map(a=>(
            <option key={a.code} value={a.code}>{"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
          ))}
        </select>
      </div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% GGR</div>
          <input value={form.pct_ggr} onChange={e=>setForm(f=>({...f,pct_ggr:e.target.value}))}
            placeholder="15" inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>% Ventas</div>
          <input value={form.pct_ventas} onChange={e=>setForm(f=>({...f,pct_ventas:e.target.value}))}
            placeholder="5" inputMode="decimal"
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
              color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
        </div>
      </div>
      {!form.parent_code&&(
        <div style={{marginBottom:10}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Moneda</div>
          <select value={form.moneda}
            onChange={e=>setForm(f=>({...f,moneda:e.target.value}))}
            style={{width:"100%",background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
              color:Q.text,fontSize:15,fontFamily:F_BODY}}>
            {(monedas.length?monedas:[{codigo:"ARS",nombre:"Peso argentino"}]).map(m=>(
              <option key={m.codigo} value={m.codigo}>{m.codigo} — {m.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <div style={{marginBottom:10}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Permisos de la agencia</div>
        <select value={form.permiso}
          onChange={e=>setForm(f=>({...f,permiso:e.target.value}))}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"12px 12px",
            color:Q.text,fontSize:15,fontFamily:F_BODY}}>
          <option value="solo_agencia">Solo agencia (crea clientes)</option>
          <option value="crea_agencias">+ Crear sub-agencias</option>
          <option value="crea_influencers">+ Crear influencers</option>
          <option value="ambos">Todo (sub-agencias + influencers)</option>
        </select>
      </div>
      <Btn label={proc?"CREANDO...":"Crear agencia"} onClick={crear}
        color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </GCard>
  );
}

function TabBilletera({ adminKey, onNoAutorizado }){
  // La billetera del admin = pagos digitales (PSP)
  return <TabPSP adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>;
}
function TabUsuarios({ adminKey, onNoAutorizado }){
  const [query,setQuery]=useState("");
  const [lista,setLista]=useState([]);
  const [cargando,setCargando]=useState(false);
  const [crear,setCrear]=useState(false);
  const [sel,setSel]=useState(null);   // user_id de ficha
  const [msg,setMsg]=useState("");

  const buscar=async(texto)=>{
    setCargando(true);
    try{
      const r=await fetch(`${API}/api/admin/clientes?buscar=${encodeURIComponent(texto||"")}&limite=100`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ const d=await r.json(); setLista(d.clientes||d||[]); }
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ buscar(""); // eslint-disable-next-line
  },[]);

  // `sel` used to swap the whole screen for <FichaCliente/> via an early
  // return, which threw away the list (and whatever the admin had typed
  // in the search box) the moment it closed. FichaCliente already draws
  // itself as a fixed-position sheet with its own backdrop and close
  // button, so it renders as an overlay on top of the list below instead
  // — closing it needs no re-render of the list, the search text was
  // never touched.

  // Misma lista de `/api/admin/clientes`, misma regla: el saldo va con la
  // moneda de su fila y el código aparece sólo si la búsqueda trajo más de
  // una moneda.
  const montoUsr = formatoDeLista(lista);

  return(
    <div>
      <PageHeader icon={<Icon name="users"/>} title="Clientes"
        description="Buscá, dá de alta y administrá los clientes del sistema."
        action={<Btn label={crear?"Cancelar":"+ Nuevo cliente"} onClick={()=>setCrear(c=>!c)}
          color={Q.violet} size="sm"/>}/>

      {crear&&<CrearClienteAdmin adminKey={adminKey} agencia=""
        onListo={()=>{setCrear(false);buscar(query);}} onCancel={()=>setCrear(false)}
        onNoAutorizado={onNoAutorizado}/>}

      <GCard style={{padding:"12px 16px",marginBottom:12,
        display:"flex",alignItems:"center",gap:SPACING[12]}}>
        <span style={{color:Q.muted,fontSize:16}}><Icon name="search" size={16}/></span>
        <input value={query} onChange={e=>{setQuery(e.target.value);buscar(e.target.value);}}
          placeholder="Buscar cliente por nombre..."
          style={{background:"transparent",border:"none",color:Q.text,
            fontSize:14,flex:1,fontFamily:F_BODY}}/>
      </GCard>

      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Buscando...</div>}
      {!cargando&&lista.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
        padding:SPACING[20],fontFamily:F_BODY}}>Sin clientes</div>}
      {/* A side-to-side stacked list read as a spreadsheet on desktop;
          an auto-fit card grid gives each client room for name, agency
          and balance without a horizontal scan, and still stacks to one
          column on a phone with no breakpoint logic. */}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
        gap:SPACING[12]}}>
        {lista.map(u=>(
          <GCard key={u.id} onClick={()=>setSel(u.id)}
            style={{padding:SPACING[12],cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{u.nombre_completo||u.nombre||u.username}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {u.creado_por==="admin"?<><Icon name="landmark" size={11}/> Admin</>:<><Building2 size={11}/> {u.creado_por||"—"}</>}
                  {u.telefono?` · ${u.telefono}`:""}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                {/* La conversión de centavos a pesos la hace el backend.
                    Acá se dividía otra vez, así que un saldo de 2.500 se
                    mostraba como 25. La regla: convertir en un solo lado. */}
                <div style={{color:(u.saldo!=null?u.saldo:u.balance||0)>=0?Q.green:Q.red,
                  fontWeight:700,fontSize:TEXT[15],
                  fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>
                  {montoUsr(u.saldo!=null?u.saldo:(u.balance||0), u.moneda)}</div>
                <div style={{color:Q.muted,fontSize:12}}>saldo ›</div>
              </div>
            </div>
          </GCard>
        ))}
      </div>
      {msg&&<div style={{fontSize:12,marginTop:8,color:Q.green,
        fontFamily:F_BODY}}>{msg}</div>}

      {sel&&<FichaCliente userId={sel} adminKey={adminKey}
        onCerrar={()=>{setSel(null);buscar(query);}} onCambio={()=>buscar(query)}
        onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB CONFIG
// ═══════════════════════════════════════════════════════════════
function TabConfig({ adminKey, onNoAutorizado }){
  const [sub,setSub]=useState("limites");
  const subs=[
    ["limites",<><Icon name="sliders-horizontal" size={13}/> Límites</>],
    ["riesgo",<><Icon name="shield-alert" size={13}/> Riesgo</>],
    ["boost",<><Rocket size={13}/> Potencializador</>],
    ["mejora",<><Icon name="scan-line" size={13}/> Bet Best</>],
    ["flash",<><Zap size={13}/> Combo flash</>],
    ["mensajeria",<><Mail size={13}/> Mensajería</>],
    ["productos",<><Gamepad2 size={13}/> Productos</>],
    ["recompensas",<><Megaphone size={13}/> Compartir</>],
    ["desafios",<><Handshake size={13}/> Desafíos</>],
    ["monedas",<><ArrowLeftRight size={13}/> Monedas</>],
    ["proveedor",<><Icon name="spade" size={13}/> Casino</>],
    ["superbono",<><PartyPopper size={13}/> Súper Bono</>],
    ["responsable",<><Icon name="shield-check" size={13}/> Responsable</>],
    ["banners",<><ImageIcon size={13}/> Banners</>],
    ["bonos",<><Gift size={13}/> Bonos</>],
    ["builder",<><Wrench size={13}/> Bet Builder</>],
    ["general",<><Shield size={13}/> General</>],
  ];
  return(
    <div>
      <PageHeader icon={<Icon name="sliders-horizontal"/>} title="Configuración"
        description="Todo lo configurable del sistema en un solo lugar."/>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:16,flexWrap:"wrap"}}>
        {subs.map(([k,l])=>(
          <button key={k} onClick={()=>setSub(k)} style={{
            background:sub===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${sub===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px 16px",cursor:"pointer",color:sub===k?Q.violet2||Q.violet:Q.muted,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>
      {sub==="limites"&&<TabLimites adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="riesgo"&&<TabRiesgo adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="boost"&&<TabBoost adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="mejora"&&<TabMejora adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="flash"&&<TabFlash adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="mensajeria"&&<TabMensajeria adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="productos"&&<TabProductosPermisos adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="recompensas"&&<TabRecompensas adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="desafios"&&<TabDesafios adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="monedas"&&<TabMonedas adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="proveedor"&&<TabCasinoProveedor adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="superbono"&&<TabSuperBono adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="responsable"&&<TabResponsable adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="banners"&&<TabBanners adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="bonos"&&<TabBonos adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="builder"&&<TabBetBuilder adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="general"&&<ConfigGeneral adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}

function TabBonos({ adminKey, onNoAutorizado }){
  const [bonos,setBonos]=useState([]);
  // ESTA LISTA ESTABA SIEMPRE VACÍA, y era un defecto anterior a la moneda.
  // `setAgencias` no se llamaba en ningún lado de este componente, así que el
  // desplegable de `Rama` y `Agencia` no tenía nada para elegir y desde esta
  // pantalla sólo se podía asignar con alcance `Red` —justo el único que
  // asignaba a todas las agencias sin filtrar la moneda—. Se arregla acá
  // porque un filtro por moneda que no se puede usar no protege nada.
  //
  // `moneda` se guarda junto al código: el desplegable muestra sólo las
  // agencias de la moneda del bono, así el admin no elige una que el backend
  // va a rechazar con 400.
  const [agencias,setAgencias]=useState([]);

  // Lista los bonos definidos. Antes esta función no existía y cada
  // guardado, activación o borrado rompía la pantalla del admin.
  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/bonos`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json().catch(()=>({}));
      setBonos(d.bonos||[]);
    }catch(e){ setMsg({text:"No se pudieron cargar los bonos",ok:false}); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[adminKey]);
  // El árbol de agencias, que ya trae la moneda de cada una. Es la misma
  // fuente que usan las otras tres pantallas que ofrecen elegir agencia.
  useEffect(()=>{
    fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{agencias:[]})
      .then(d=>setAgencias((d.agencias||[]).map(a=>({
        code:a.code,name:a.name||a.code,moneda:a.moneda||"ARS"}))))
      .catch(()=>{});
    /* eslint-disable-next-line */
  },[adminKey]);
  // Las monedas que la casa acepta, de la lista central del backend. El
  // formulario las necesita porque un bono SE DEFINE en una moneda: cuando se
  // crea todavía no hay agencia de la que heredarla.
  const [monedas,setMonedas]=useState([]);
  useEffect(()=>{
    fetch(`${API}/api/monedas`).then(r=>r.ok?r.json():{monedas:[]})
      .then(d=>setMonedas(d.monedas||[])).catch(()=>setMonedas([]));
  },[]);
  const [form,setForm]=useState({nombre:"",tipo:"bienvenida",moneda:"ARS",monto_fijo:"",
    porcentaje:"",tope:"",rollover:"5",deposito_minimo:"",cuota_minima:"1.50",
    requiere_verificacion:true,evento:"primer_deposito",
    stake_max_tipo:"porcentaje",stake_max_valor:"15",cuota_maxima:"",mercados_excluidos:"",
    vigente_desde:"",vigente_hasta:"",dias_semana:"",hora_desde:"",hora_hasta:"",
    // Quién paga el bono cuando se libere. Por defecto la casa.
    pct_paga_casa:"100",pct_paga_agencia:"0"});
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [analisis,setAnalisis]=useState("");
  // Se perdió al reorganizar el analizador: sin esto el componente
  // reventaba al renderizar y la pantalla quedaba negra.
  const [analizando,setAnalizando]=useState(false);
  // A qué agencias se asigna el bono
  const [asignar,setAsignar]=useState({bono:null,alcance:"global",code:""});
  // Lo que implica el bono antes de guardarlo
  const [revision,setRevision]=useState(null);
  const [revProc,setRevProc]=useState(false);

  const revisar=async()=>{
    setRevProc(true);
    try{
      const r=await fetch(`${API}/api/admin/bonos/revisar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(form)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setRevision(await r.json());
    }catch(e){}
    setRevProc(false);
  };

  // Se revisa solo mientras se completa: son reglas, no consumen IA
  useEffect(()=>{
    if(!form.nombre) { setRevision(null); return; }
    const t=setTimeout(revisar,600);
    return()=>clearTimeout(t);
    // eslint-disable-next-line
  },[form]);

  const analizarRiesgo=async()=>{
    setAnalizando(true); setAnalisis("");
    try{
      // Endpoint propio de bonos: trae el margen real de la casa y
      // cuántos bonos se liberaron antes, que es lo que dice si el
      // rollover es alcanzable.
      const r=await fetch(`${API}/api/admin/bonos/analizar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(form),
      });
      const d=await r.json();
      if(r.ok) setAnalisis(d.analisis||"Sin respuesta");
      else setAnalisis("⚠️ "+(d.detail||"Error"));
    }catch(e){ setAnalisis("⚠️ Error al consultar la IA"); }
    setAnalizando(false);
  };

  const guardar=async()=>{
    if(!form.nombre.trim()){ setMsg({text:"Poné un nombre",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/bonos`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(form),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok&&d.ok){ setMsg({text:"Bono guardado",ok:true});
        // LA MONEDA SOBREVIVE AL RESETEO a propósito, y es lo único que
        // sobrevive: el admin que acaba de crear un bono en dólares
        // normalmente va a crear el siguiente también en dólares, y volver a
        // pesos en silencio es la forma de que el segundo nazca en la moneda
        // equivocada sin que nadie lo note hasta que se acredite.
        setForm(f=>({nombre:"",tipo:"bienvenida",moneda:f.moneda,monto_fijo:"",porcentaje:"",tope:"",rollover:"5",
          deposito_minimo:"",cuota_minima:"1.50",requiere_verificacion:true,evento:"primer_deposito",
          stake_max_tipo:"porcentaje",stake_max_valor:"15",cuota_maxima:"",mercados_excluidos:"",
    vigente_desde:"",vigente_hasta:"",dias_semana:"",hora_desde:"",hora_hasta:""}));
        setAnalisis(""); cargar(); }
      else setMsg({text:(d.detail||"Error"),ok:false});
    }catch(e){ setMsg({text:"Error",ok:false}); }
    setProc(false);
  };

  const toggleActivo=async(b)=>{
    // Antes esto no miraba la respuesta ni avisaba si fallaba: si el
    // servidor rechazaba, la pantalla recargaba con el estado viejo y
    // parecía que el botón no hacía nada.
    setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/bonos/${b.id}/activar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo:!b.activo}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json().catch(()=>({}));
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:d.activo?"Bono activado":"Bono desactivado",ok:true});
      await cargar();
    }catch(e){ setMsg({text:e.message,ok:false}); }
  };
  const borrar=async(id)=>{
    if(!window.confirm("¿Borrar este bono? No se puede deshacer.")) return;
    setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/bonos/${id}`,
        {method:"DELETE",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Bono borrado",ok:true});
      await cargar();
    }catch(e){ setMsg({text:e.message,ok:false}); }
  };
  const resetear=async(id)=>{
    if(!window.confirm("¿Resetear este bono? Se borran todos sus otorgamientos y se puede reutilizar desde cero.")) return;
    await fetch(`${API}/api/admin/bonos/${id}/resetear`,{
      method:"POST",headers:adminHeaders(adminKey)});
    cargar();
  };
  const desasignar=async(bonoId, code, nombre)=>{
    if(!window.confirm(
      `¿Quitar este bono de ${nombre||code}? Los clientes que ya lo `+
      `recibieron lo conservan; deja de ofrecerse de acá en adelante.`)) return;
    await fetch(`${API}/api/admin/bonos/${bonoId}/desasignar`,{
      method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
      body:JSON.stringify({agencia_code:code}),
    });
    cargar();
  };

  const hacerAsignar=async()=>{
    if(asignar.alcance!=="global"&&!asignar.code){ return; }
    setMsg(null);
    // LA RESPUESTA SE LEE, y antes se descartaba entera. Ahora el backend
    // puede contestar 400 —cuando la agencia nombrada lleva otra moneda— y
    // puede contestar cuántas agencias dejó afuera el filtro con alcance de
    // red. Con la respuesta en el piso, el admin apretaba "Asignar", la
    // pantalla recargaba igual y el bono no quedaba asignado a nadie.
    try{
      const r=await fetch(`${API}/api/admin/bonos/${asignar.bono}/asignar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({alcance:asignar.alcance,agencia_code:asignar.code}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json().catch(()=>({}));
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:`Asignado a ${d.asignado_a} agencia(s) en ${d.moneda}`+
        (d.salteadas?` · ${d.salteadas} de otra moneda quedaron afuera`:""),ok:true});
      setAsignar({bono:null,alcance:"global",code:""});
    }catch(e){ setMsg({text:e.message,ok:false}); }
    cargar();
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
    borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  return(
    <div>
      <GCard style={{padding:SPACING[16],marginBottom:16}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:10,
          fontFamily:F_BODY}}><Gift size={14}/> Nuevo bono</div>
        <input value={form.nombre} onChange={e=>setForm(f=>({...f,nombre:e.target.value}))}
          placeholder="Nombre del bono" style={inp}/>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
          {[["bienvenida",<><PartyPopper size={13}/> Bienvenida</>],["carga",<><Icon name="wallet-cards" size={13}/> Por carga</>]].map(([k,l])=>(
            <button key={k} onClick={()=>setForm(f=>({...f,tipo:k}))} style={{flex:1,
              background:form.tipo===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${form.tipo===k?Q.violet:Q.border}`,borderRadius:RADII.md,
              padding:"8px 4px",cursor:"pointer",color:form.tipo===k?Q.violet2||Q.violet:Q.muted,
              fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>
        {/* LA MONEDA DEL BONO, y va ARRIBA de los montos porque es lo que los
            rotula. Un bono se define en una sola moneda y sólo se puede
            asignar a agencias de esa moneda: el backend corta el cruce. */}
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Moneda del bono</div>
        <select value={form.moneda}
          onChange={e=>setForm(f=>({...f,moneda:e.target.value}))}
          aria-label="Moneda del bono" style={inp}>
          {monedas.length===0&&<option value={form.moneda}>{form.moneda}</option>}
          {monedas.map(m=>(
            <option key={m.codigo} value={m.codigo}>
              {m.codigo} · {m.nombre}</option>))}
        </select>
        <div style={{color:Q.dim,fontSize:12,marginBottom:8,lineHeight:1.5,
          fontFamily:F_BODY}}>
          Los montos de abajo están en esta moneda. El bono sólo se puede
          asignar a agencias que lleven su libro en {form.moneda}.</div>
        {form.tipo==="bienvenida"?(
          <>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>Monto fijo del bono</div>
            <input value={form.monto_fijo} onChange={e=>setForm(f=>({...f,monto_fijo:e.target.value.replace(/\D/g,"")}))}
              placeholder="Ej: 1000" inputMode="numeric" style={inp}/>
          </>
        ):(
          <>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>Porcentaje sobre la carga (%)</div>
            <input value={form.porcentaje} onChange={e=>setForm(f=>({...f,porcentaje:e.target.value}))}
              placeholder="Ej: 20" inputMode="decimal" style={inp}/>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>Tope del bono (máximo)</div>
            <input value={form.tope} onChange={e=>setForm(f=>({...f,tope:e.target.value.replace(/\D/g,"")}))}
              placeholder="Ej: 5000" inputMode="numeric" style={inp}/>
          </>
        )}
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Rollover (veces a apostar antes de retirar)</div>
        <input value={form.rollover} onChange={e=>setForm(f=>({...f,rollover:e.target.value}))}
          placeholder="Ej: 5" inputMode="decimal" style={inp}/>

        <div style={{borderTop:`1px solid ${Q.border}`,margin:"10px 0",paddingTop:SPACING[12]}}>
          <div style={{color:Q.cyan,fontSize:12,fontWeight:700,marginBottom:8,
            fontFamily:F_BODY}}><Icon name="shield-alert" size={14}/> Condiciones de riesgo</div>

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Depósito mínimo para activarlo</div>
          <input value={form.deposito_minimo} onChange={e=>setForm(f=>({...f,deposito_minimo:e.target.value.replace(/\\D/g,"")}))}
            placeholder="Ej: 500 (0 = sin mínimo)" inputMode="numeric" style={inp}/>

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Cuota mínima para que la apuesta cuente al rollover</div>
          <input value={form.cuota_minima} onChange={e=>setForm(f=>({...f,cuota_minima:e.target.value}))}
            placeholder="Ej: 1.50" inputMode="decimal" style={inp}/>

          {/* Quién paga cuando el bono se libere. Mientras es bono,
              nadie puso nada: el costo aparece al cumplirse el
              rollover, que es cuando la plata pasa a ser del cliente. */}
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Quién paga este bono</div>
          <div style={{display:"flex",gap:SPACING[8],marginBottom:6}}>
            <div style={{flex:1}}>
              <div style={{color:Q.dim,fontSize:12,marginBottom:3}}>
                La casa (%)</div>
              <input value={form.pct_paga_casa} inputMode="decimal"
                onChange={e=>{
                  const v=e.target.value;
                  const n=parseFloat(v);
                  setForm(f=>({...f, pct_paga_casa:v,
                    // El resto lo pone la agencia: así siempre suma 100
                    pct_paga_agencia: isNaN(n)?f.pct_paga_agencia
                                             :String(Math.max(0,100-n))}));
                }} style={inp}/>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.dim,fontSize:12,marginBottom:3}}>
                La agencia (%)</div>
              <input value={form.pct_paga_agencia} inputMode="decimal"
                onChange={e=>{
                  const v=e.target.value;
                  const n=parseFloat(v);
                  setForm(f=>({...f, pct_paga_agencia:v,
                    pct_paga_casa: isNaN(n)?f.pct_paga_casa
                                          :String(Math.max(0,100-n))}));
                }} style={inp}/>
            </div>
          </div>
          <div style={{display:"flex",gap:SPACING[4],marginBottom:10}}>
            {[[100,0,"Todo la casa"],[50,50,"Mitad y mitad"],
              [0,100,"Todo la agencia"]].map(([c,a,l])=>(
              <button key={l} onClick={()=>setForm(f=>({...f,
                  pct_paga_casa:String(c),pct_paga_agencia:String(a)}))}
                style={{flex:1,background:"rgba(255,255,255,0.04)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
                  padding:"8px 4px",cursor:"pointer",color:Q.muted,
                  fontSize:12,
                  fontFamily:F_BODY}}>{l}</button>
            ))}
          </div>

          {/* Qué implica el bono: se calcula con reglas mientras se
              completa el formulario, sin consumir IA.

              Los montos se dibujan con `revision.resumen.moneda`, que es la
              del borrador que se le mandó, y no con la del formulario: así lo
              que se lee es lo que el backend entendió. Si las dos difirieran
              —una moneda que el backend no conoce— el recuadro lo mostraría
              en vez de confirmar la elección de la pantalla con la pantalla
              misma. */}
          {revision&&(
            <GCard glow={revision.graves>0?Q.red:Q.cyan}
              style={{padding:SPACING[12],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                marginBottom:7}}>QUÉ IMPLICA ESTE BONO</div>

              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:12,padding:"4px 0"}}>
                <span style={{color:Q.muted}}>Costo por cada bono</span>
                <span style={{color:Q.text,fontWeight:700,
                  fontFamily:F_BODY}}>
                  {money(revision.resumen.costo_por_bono, revision.resumen.moneda)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:12,padding:"4px 0"}}>
                <span style={{color:Q.muted}}>Pone la casa</span>
                <span style={{color:Q.cyan,
                  fontFamily:F_BODY}}>
                  {money(revision.resumen.reparto.casa, revision.resumen.moneda)}
                  <span style={{color:Q.dim,fontSize:12}}>
                    {" "}({revision.resumen.reparto.pct_casa}%)</span></span>
              </div>
              {revision.resumen.reparto.pct_agencia>0&&(
                <div style={{display:"flex",justifyContent:"space-between",
                  fontSize:12,padding:"4px 0"}}>
                  <span style={{color:Q.muted}}>Pone la agencia</span>
                  <span style={{color:Q.gold,
                    fontFamily:F_BODY}}>
                    {money(revision.resumen.reparto.agencia, revision.resumen.moneda)}
                    <span style={{color:Q.dim,fontSize:12}}>
                      {" "}({revision.resumen.reparto.pct_agencia}%)</span></span>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",
                fontSize:12,padding:"4px 0",marginTop:4,paddingTop:SPACING[8],
                borderTop:`1px solid ${Q.border}`}}>
                <span style={{color:Q.muted}}>El cliente debe apostar</span>
                <span style={{color:Q.text,
                  fontFamily:F_BODY}}>
                  {money(revision.resumen.debe_apostar, revision.resumen.moneda)}</span>
              </div>

              <div style={{color:Q.dim,fontSize:12,marginTop:7,
                lineHeight:1.5}}>{revision.resumen.cuando_se_paga}</div>

              {(revision.avisos||[]).length>0&&(
                <div style={{marginTop:9,paddingTop:SPACING[8],
                  borderTop:`1px solid ${Q.border}`}}>
                  {revision.avisos.map((a,i)=>(
                    <div key={i} style={{
                      color:a.sev==="alta"?Q.red:Q.amber,
                      fontSize:12,lineHeight:1.5,marginBottom:5,
                      fontFamily:F_BODY}}>
                      <Icon name="circle-dot" size={12} color={a.sev==="alta"?Q.red:Q.gold}/> {a.texto}</div>
                  ))}
                </div>
              )}
            </GCard>
          )}

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Evento que dispara el bono</div>
          <select value={form.evento} onChange={e=>setForm(f=>({...f,evento:e.target.value}))} style={inp}>
            <option value="primer_deposito">Primer depósito del cliente</option>
            <option value="cualquier_deposito">Cualquier depósito (sobre el mínimo)</option>
            <option value="registro">Al registrarse (bienvenida)</option>
          </select>

          <label style={{display:"flex",alignItems:"center",gap:SPACING[8],cursor:"pointer",
            marginTop:4,marginBottom:4}}>
            <input type="checkbox" checked={form.requiere_verificacion}
              onChange={e=>setForm(f=>({...f,requiere_verificacion:e.target.checked}))}/>
            <span style={{color:Q.text,fontSize:12,fontFamily:F_BODY}}>
              Requiere verificación de identidad</span>
          </label>

          <div style={{color:Q.gold,fontSize:12,fontWeight:700,marginTop:8,marginBottom:6,
            fontFamily:F_BODY}}><Icon name="triangle-alert" size={14}/> Mitigaciones (recomendadas por IA)</div>

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Stake máximo por apuesta durante rollover</div>
          <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
            <select value={form.stake_max_tipo} onChange={e=>setForm(f=>({...f,stake_max_tipo:e.target.value}))}
              style={{...inp,marginBottom:0,flex:1}}>
              <option value="porcentaje">% del bono</option>
              <option value="fijo">Monto fijo</option>
            </select>
            <input value={form.stake_max_valor} onChange={e=>setForm(f=>({...f,stake_max_valor:e.target.value}))}
              placeholder={form.stake_max_tipo==="porcentaje"?"Ej: 15":"Ej: 1000"} inputMode="decimal"
              style={{...inp,marginBottom:0,flex:1}}/>
          </div>

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Cuota máxima (techo, opcional)</div>
          <input value={form.cuota_maxima} onChange={e=>setForm(f=>({...f,cuota_maxima:e.target.value}))}
            placeholder="Ej: 5.00 (vacío = sin techo)" inputMode="decimal" style={inp}/>

          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>Mercados excluidos (separados por coma)</div>
          <input value={form.mercados_excluidos} onChange={e=>setForm(f=>({...f,mercados_excluidos:e.target.value}))}
            placeholder="Ej: handicap asiatico, over/under" style={inp}/>

          {/* Programación: cuándo está disponible el bono. Todo opcional.
              La hora es la de la casa, una sola para todas las agencias:
              con husos distintos, "viernes de 18 a 22" significaría cosas
              diferentes según el local. */}
          <div style={{borderTop:`1px solid ${Q.border}`,marginTop:14,
            paddingTop:SPACING[12]}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
              fontFamily:F_BODY}}>
              <CalendarDays size={13}/> Cuándo está disponible</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:10,
              lineHeight:1.5,fontFamily:F_BODY}}>
              Dejá todo vacío para que esté siempre. Fuera de su ventana el
              bono se sigue viendo, pero deshabilitado y con el horario, para
              que el cajero le diga al cliente cuándo volver.</div>

            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              Vigencia (opcional)</div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
              <input type="date" value={form.vigente_desde}
                onChange={e=>setForm(f=>({...f,vigente_desde:e.target.value}))}
                style={{...inp,marginBottom:0,flex:1}}/>
              <input type="date" value={form.vigente_hasta}
                onChange={e=>setForm(f=>({...f,vigente_hasta:e.target.value}))}
                style={{...inp,marginBottom:0,flex:1}}/>
            </div>

            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              Días de la semana (ninguno = todos)</div>
            <div style={{display:"flex",gap:SPACING[4],marginBottom:10}}>
              {["Lu","Ma","Mi","Ju","Vi","Sá","Do"].map((d,i)=>{
                const sel=(form.dias_semana||"").split(",")
                  .filter(Boolean).includes(String(i));
                return(
                  <button key={i} onClick={()=>setForm(f=>{
                    const act=(f.dias_semana||"").split(",").filter(Boolean);
                    const nuevo=sel?act.filter(x=>x!==String(i))
                                   :[...act,String(i)];
                    return {...f,dias_semana:nuevo.sort().join(",")};
                  })} style={{flex:1,background:sel?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                    border:`1px solid ${sel?Q.violet:Q.border}`,borderRadius:RADII.md,
                    padding:"8px 4px",cursor:"pointer",fontSize:12,
                    fontWeight:sel?700:400,color:sel?Q.cyan:Q.muted,
                    fontFamily:F_BODY}}>{d}</button>
                );
              })}
            </div>

            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              Franja horaria (vacío = todo el día)</div>
            <div style={{display:"flex",gap:SPACING[8],alignItems:"center"}}>
              <input value={form.hora_desde} inputMode="numeric"
                onChange={e=>setForm(f=>({...f,hora_desde:e.target.value.replace(/\D/g,"").slice(0,2)}))}
                placeholder="18" style={{...inp,marginBottom:0,flex:1,
                  textAlign:"center"}}/>
              <span style={{color:Q.muted,fontSize:12}}>a</span>
              <input value={form.hora_hasta} inputMode="numeric"
                onChange={e=>setForm(f=>({...f,hora_hasta:e.target.value.replace(/\D/g,"").slice(0,2)}))}
                placeholder="22" style={{...inp,marginBottom:0,flex:1,
                  textAlign:"center"}}/>
              <span style={{color:Q.muted,fontSize:12}}>hs</span>
            </div>
            <div style={{color:Q.dim,fontSize:12,marginTop:6}}>
              Se puede cruzar la medianoche: de 22 a 2 son las cuatro horas
              de la noche.</div>
          </div>
        </div>

        <button onClick={analizarRiesgo} disabled={analizando} style={{width:"100%",
          background:`${Q.cyan}18`,border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"12px",
          color:Q.cyan,fontWeight:700,fontSize:13,cursor:"pointer",marginBottom:8,
          fontFamily:F_BODY}}>
          {analizando?"Analizando...":<><Bot size={13}/> Analizar riesgo con IA</>}</button>
        {analisis&&(
          <div style={{background:`${Q.cyan}0C`,border:`1px solid ${Q.cyan}44`,borderRadius:RADII.md,
            padding:"12px",marginBottom:10,color:Q.text,fontSize:12,lineHeight:1.6,
            whiteSpace:"pre-wrap",fontFamily:F_BODY}}>{analisis}</div>
        )}
        <button onClick={guardar} disabled={proc} style={{width:"100%",
          background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,border:"none",
          borderRadius:RADII.md,padding:"12px",color:"#fff",fontWeight:700,fontSize:14,
          cursor:"pointer",fontFamily:F_BODY}}>
          {proc?"Guardando...":"Guardar bono"}</button>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>

      <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
        marginBottom:8,marginLeft:4,fontFamily:F_BODY}}>Bonos creados</div>
      {bonos.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Sin bonos todavía</div>}
      {bonos.map(b=>(
        <GCard key={b.id} style={{padding:"12px 16px",marginBottom:8,
          opacity:b.activo?1:0.55}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{b.nombre}
                <span style={{color:Q.muted,fontWeight:400,fontSize:12}}> · {b.tipo}</span>
                {/* LA MONEDA, VISIBLE EN LA TARJETA. Esta lista mezcla bonos
                    de monedas distintas, y cuatro monedas comparten el `$`:
                    sin el código, dos tarjetas con `$10.000` pueden ser dos
                    cosas con 1400× de diferencia. */}
                <span style={{color:Q.cyan,fontWeight:700,fontSize:12}}>
                  {" · "}{b.moneda||"ARS"}</span></div>
              <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
                {b.tipo==="bienvenida"?`${money(b.monto_fijo||0, b.moneda)}`:`${b.porcentaje||0}% (máx ${money(b.tope||0, b.moneda)})`}
                {" · rollover x"+(b.rollover||0)}</div>
            </div>
            <div style={{display:"flex",gap:SPACING[8]}}>
              <button onClick={()=>toggleActivo(b)} style={{background:"transparent",
                border:`1px solid ${b.activo?Q.green:Q.muted}`,borderRadius:RADII.md,padding:"4px 8px",
                color:b.activo?Q.green:Q.muted,fontSize:12,cursor:"pointer"}}>
                {b.activo?"Activo":"Inactivo"}</button>
              <button onClick={()=>resetear(b.id)} style={{background:"transparent",
                border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,padding:"4px 8px",
                color:Q.cyan,fontSize:12,cursor:"pointer"}}><RotateCcw size={12} role="img" aria-label="Resetear"/></button>
              <button onClick={()=>borrar(b.id)} style={{background:"transparent",
                border:`1px solid ${Q.red}55`,borderRadius:RADII.md,padding:"4px 8px",
                color:Q.red,fontSize:12,cursor:"pointer"}}>✕</button>
            </div>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4],marginBottom:6}}>
            {/* Cada asignación con su × para quitarla. El endpoint
                /desasignar existía desde siempre, pero no había botón:
                se podía asignar un bono y no revertirlo. */}
            {(b.asignaciones||[]).map((a,i)=>{
              // LAS ASIGNACIONES QUE CRUZAN MONEDAS SE PINTAN EN ROJO. El
              // backend ya no deja crear ninguna nueva, pero las filas que
              // estaban en `bonos_agencias` se escribieron antes de que el
              // filtro existiera, así que pueden cruzar. El bono no se le va
              // a otorgar a esa agencia —los dos caminos de otorgamiento
              // filtran por moneda—, y el admin es el único que las puede
              // sacar: tiene que poder verlas.
              const cruza = codigoDeMoneda(a.agencia_moneda)!==codigoDeMoneda(b.moneda);
              return (
              <span key={i} style={{background:cruza?`${Q.red}18`:`${Q.violet}18`,
                border:`1px solid ${cruza?Q.red:Q.violet}44`,
                borderRadius:RADII.md,padding:"4px 4px 4px 8px",
                color:cruza?Q.red:(Q.violet2||Q.violet),fontSize:12,
                display:"inline-flex",alignItems:"center",gap:SPACING[4],
                fontFamily:F_BODY}}
                title={cruza
                  ? `${a.agencia_nombre||a.agencia_code} lleva su libro en `+
                    `${a.agencia_moneda} y el bono está en ${b.moneda||"ARS"}: `+
                    `no se le va a otorgar. Quitala.`
                  : (a.agencia_nombre||a.agencia_code)}>
                {cruza&&<Icon name="triangle-alert" size={11}/>}
                {a.agencia_code}
                {cruza&&<span style={{fontWeight:700}}>{a.agencia_moneda}</span>}
                <button onClick={()=>desasignar(b.id,a.agencia_code,a.agencia_nombre)}
                  aria-label={`Quitar de ${a.agencia_code}`}
                  style={{background:"transparent",border:"none",color:Q.red,
                    fontSize:12,lineHeight:1,cursor:"pointer",padding:"0 4px"}}>×</button>
              </span>
            );})}
            {(b.asignaciones||[]).length===0&&<span style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY}}>Sin asignar</span>}
          </div>
          {asignar.bono===b.id?(
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:RADII.md,padding:SPACING[12]}}>
              {/* "Red" dejó de ser "todas las agencias": es todas las de la
                  moneda del bono. Decirlo acá es lo que evita que el alcance
                  se lea como antes. */}
              <div style={{color:Q.dim,fontSize:12,marginBottom:6,lineHeight:1.5,
                fontFamily:F_BODY}}>
                Sólo agencias en {b.moneda||"ARS"}, la moneda del bono.</div>
              <div style={{display:"flex",gap:SPACING[8],marginBottom:6}}>
                {[["global","Red"],["rama","Rama"],["agencia","Agencia"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setAsignar(a=>({...a,alcance:k}))} style={{flex:1,
                    background:asignar.alcance===k?`${Q.cyan}22`:"rgba(255,255,255,0.04)",
                    border:`1px solid ${asignar.alcance===k?Q.cyan:Q.border}`,borderRadius:RADII.md,
                    padding:"8px 4px",cursor:"pointer",color:asignar.alcance===k?Q.cyan:Q.muted,
                    fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
                ))}
              </div>
              {asignar.alcance!=="global"&&(()=>{
                // Sólo las de la moneda del bono: el backend rechaza el cruce
                // con 400, así que ofrecer una que no coincide es ofrecer un
                // error. Con `rama` se elige la cabeza y las hijas heredan su
                // moneda, así que filtrar por la cabeza alcanza.
                const elegibles=agencias.filter(a=>
                  codigoDeMoneda(a.moneda)===codigoDeMoneda(b.moneda));
                if(!elegibles.length) return (
                  <div style={{color:Q.amber,fontSize:12,marginBottom:6,
                    fontFamily:F_BODY}}>No hay ninguna agencia en {codigoDeMoneda(b.moneda)},
                    así que este bono todavía no se le puede asignar a nadie.</div>
                );
                return (
                  <select value={asignar.code} onChange={e=>setAsignar(a=>({...a,code:e.target.value}))}
                    style={{...inp,marginBottom:6}}>
                    <option value="">Elegí agencia</option>
                    {elegibles.map(a=>(<option key={a.code} value={a.code}>{a.code} · {a.name}</option>))}
                  </select>
                );
              })()}
              <div style={{display:"flex",gap:SPACING[8]}}>
                <button onClick={()=>setAsignar({bono:null,alcance:"global",code:""})}
                  style={{flex:1,background:"transparent",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.md,padding:"8px",color:Q.muted,fontSize:12,cursor:"pointer"}}>Cancelar</button>
                <button onClick={hacerAsignar}
                  style={{flex:1,background:`${Q.cyan}33`,border:`1px solid ${Q.cyan}`,
                    borderRadius:RADII.md,padding:"8px",color:Q.cyan,fontSize:12,fontWeight:700,cursor:"pointer"}}>Asignar</button>
              </div>
            </div>
          ):(
            <button onClick={()=>setAsignar({bono:b.id,alcance:"global",code:""})}
              style={{width:"100%",background:"transparent",border:`1px solid ${Q.border}`,
                borderRadius:RADII.md,padding:"8px",color:Q.muted,fontSize:12,cursor:"pointer",
                fontFamily:F_BODY}}>+ Asignar a agencias</button>
          )}
        </GCard>
      ))}
    </div>
  );
}

function TabPSP({ adminKey, onNoAutorizado }){
  const [cfg,setCfg]=useState(null);
  const [saldo,setSaldo]=useState(null);
  const [retiros,setRetiros]=useState([]);
  const [msg,setMsg]=useState("");
  const [agencias,setAgencias]=useState([]);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/psp/config`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setCfg(await r.json());
      const rr=await fetch(`${API}/api/admin/psp/retiros?estado=solicitado`,{headers:adminHeaders(adminKey)});
      if(rr.ok) setRetiros((await rr.json()).retiros||[]);
      const ra=await fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)});
      if(ra.ok){ const d=await ra.json();
        setAgencias((d.agencias||[]).map(a=>({code:a.code,name:a.name||a.code}))); }
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const guardarCfg=async(patch)=>{
    const nuevo={...cfg,...patch};
    setCfg(nuevo);
    await fetch(`${API}/api/admin/psp/config`,{
      method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
      body:JSON.stringify({activo:nuevo.activo,retiro_modo:nuevo.retiro_modo,ramas:nuevo.ramas||[]}),
    });
  };

  const verSaldo=async()=>{
    setMsg("Consultando...");
    try{
      const r=await fetch(`${API}/api/admin/psp/saldo`,{headers:adminHeaders(adminKey)});
      const d=await r.json();
      if(r.ok){ setSaldo(d.saldo); setMsg(""); }
      else setMsg("⚠️ "+(d.detail||"Error"));
    }catch(e){ setMsg("⚠️ Error"); }
  };

  const resolver=async(id,aprobar)=>{
    await fetch(`${API}/api/admin/psp/retiros/${id}/aprobar`,{
      method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
      body:JSON.stringify({aprobar}),
    });
    cargar();
  };

  if(!cfg) return <div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
    fontFamily:F_BODY}}>Cargando...</div>;

  // Es la pantalla donde se aprieta "Aprobar y pagar": el monto que se lee
  // es el que sale. `psp_retiros` no tiene columna `moneda`, así que el
  // borde la deriva del jugador y la manda por fila.
  const montoRet = formatoDeLista(retiros);

  return(
    <div>
      <PageHeader icon={<Icon name="landmark"/>} title="Pagos digitales (PSP)"
        description={<>Carga y retiro automáticos por transferencia.
        {!cfg.configurada&&(<> <Icon name="triangle-alert" size={11}/> Falta cargar la PSP_API_KEY en el servidor.</>)}</>}/>

      <GCard glow={cfg.activo?Q.green:Q.violet} style={{padding:SPACING[16],marginBottom:12}}>
        <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          cursor:"pointer",marginBottom:12}}>
          <span style={{color:Q.text,fontWeight:700,fontSize:14,
            fontFamily:F_BODY}}>PSP activa</span>
          <div onClick={()=>guardarCfg({activo:!cfg.activo})} style={{
            width:48,height:26,borderRadius:RADII.lg,background:cfg.activo?Q.green:Q.border,
            position:"relative",transition:"all .2s",cursor:"pointer"}}>
            <div style={{width:20,height:20,borderRadius:RADII.md,background:"#fff",position:"absolute",
              top:3,left:cfg.activo?25:3,transition:"all .2s"}}/>
          </div>
        </label>

        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>Modo de retiro</div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
          {[["manual",<><Hand size={13}/> Manual (aprobás vos)</>],["automatico",<><Zap size={13}/> Automático</>]].map(([k,l])=>(
            <button key={k} onClick={()=>guardarCfg({retiro_modo:k})} style={{flex:1,
              background:cfg.retiro_modo===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${cfg.retiro_modo===k?Q.violet:Q.border}`,borderRadius:RADII.md,
              padding:"8px 4px",cursor:"pointer",color:cfg.retiro_modo===k?Q.violet2||Q.violet:Q.muted,
              fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>

        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>Ramas habilitadas (vacío = todas)</div>
        <select value="" onChange={e=>{
          if(e.target.value&&!(cfg.ramas||[]).includes(e.target.value))
            guardarCfg({ramas:[...(cfg.ramas||[]),e.target.value]});
        }} style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px",color:Q.text,
          fontSize:13,marginBottom:8,fontFamily:F_BODY}}>
          <option value="">+ Agregar rama...</option>
          {agencias.map(a=>(<option key={a.code} value={a.code}>{a.code} · {a.name}</option>))}
        </select>
        <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
          {(cfg.ramas||[]).length===0&&<span style={{color:Q.dim,fontSize:12,
            fontFamily:F_BODY}}>Activa para todas las agencias</span>}
          {(cfg.ramas||[]).map(rc=>(
            <span key={rc} onClick={()=>guardarCfg({ramas:cfg.ramas.filter(x=>x!==rc)})}
              style={{background:`${Q.violet}22`,border:`1px solid ${Q.violet}55`,borderRadius:RADII.md,
              padding:"4px 12px",color:Q.violet2||Q.violet,fontSize:12,cursor:"pointer",
              fontFamily:F_BODY}}>{rc} ✕</span>
          ))}
        </div>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              fontFamily:F_BODY}}>Saldo recaudador</div>
            <div style={{color:Q.green,fontWeight:800,fontSize:20,
              fontFamily:F_BODY}}>{saldo!=null?ars(saldo):"—"}</div>
          </div>
          <Btn label="Consultar" onClick={verSaldo} outline color={Q.cyan}/>
        </div>
        {msg&&<div style={{color:Q.red,fontSize:12,marginTop:6,
          fontFamily:F_BODY}}>{msg}</div>}
      </GCard>

      <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
        marginBottom:8,marginLeft:4,fontFamily:F_BODY}}>
        Retiros pendientes de aprobar</div>
      {retiros.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Sin retiros pendientes</div>}
      {retiros.map(r=>(
        <GCard key={r.id} glow={Q.amber} style={{padding:SPACING[12],marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <div>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{r.cliente}</div>
              <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
                {r.agencia} · {fechaHora(r.fecha)}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:2,
                fontFamily:F_BODY}}>CVU: {r.destino}</div>
            </div>
            <div style={{color:Q.gold,fontWeight:800,fontSize:16,
              fontFamily:F_BODY}}>{montoRet(r.monto,r.moneda)}</div>
          </div>
          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label="Rechazar" onClick={()=>resolver(r.id,false)} outline color={Q.red} full/>
            <Btn label="Aprobar y pagar" onClick={()=>resolver(r.id,true)} color={Q.green} full/>
          </div>
        </GCard>
      ))}
    </div>
  );
}

function TabBetBuilder({ adminKey, onNoAutorizado }){
  const [cfg,setCfg]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [analisis,setAnalisis]=useState("");
  const [analizando,setAnalizando]=useState(false);

  const [agencias,setAgencias]=useState([]);
  useEffect(()=>{
    fetch(`${API}/api/admin/bet-builder/config`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){ onNoAutorizado(); return null; } return r.ok?r.json():null; })
      .then(d=>{ if(d) setCfg(d); }).catch(()=>{});
    fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():{agencias:[]})
      .then(d=>setAgencias((d.agencias||[]).map(a=>({code:a.code,name:a.name||a.code}))))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  // Acepta un estado puntual: al tocar una etiqueta se guarda en el
  // momento, sin esperar a que aprieten "Guardar". Antes el cambio
  // quedaba solo en pantalla y parecía que no funcionaba.
  const guardar=async(estado)=>{
    setMsg(null);
    const c = estado || cfg;
    try{
      const r=await fetch(`${API}/api/admin/bet-builder/config`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({...c, excluidas:c.excluidas||[]}),
      });
      if(r.ok) setMsg({text:"Guardado",ok:true}); else setMsg({text:"Error",ok:false});
    }catch(e){ setMsg({text:"Error",ok:false}); }
  };

  const analizar=async()=>{
    setAnalizando(true); setAnalisis("");
    try{
      const r=await fetch(`${API}/api/admin/bet-builder/analizar`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({config:cfg}),
      });
      const d=await r.json();
      setAnalisis(r.ok?(d.analisis||"Sin respuesta"):"⚠️ "+(d.detail||"Error"));
    }catch(e){ setAnalisis("⚠️ Error al consultar IA"); }
    setAnalizando(false);
  };

  if(!cfg) return <div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
    fontFamily:F_BODY}}>Cargando...</div>;

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
    borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Wrench size={16}/> Bet Builder (mismo partido)</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,
        fontFamily:F_BODY}}>
        Combinar mercados del mismo partido. El margen de correlación protege a la casa
        del riesgo de eventos correlacionados.</div>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          cursor:"pointer",marginBottom:12}}>
          <span style={{color:Q.text,fontWeight:700,fontSize:14,
            fontFamily:F_BODY}}>Bet Builder activo</span>
          <div onClick={()=>setCfg({...cfg,activo:!cfg.activo})} style={{width:48,height:26,
            borderRadius:RADII.lg,background:cfg.activo?Q.green:Q.border,position:"relative",cursor:"pointer"}}>
            <div style={{width:20,height:20,borderRadius:RADII.md,background:"#fff",position:"absolute",
              top:3,left:cfg.activo?25:3,transition:"all .2s"}}/>
          </div>
        </label>

        <div style={{color:Q.muted,fontSize:12,marginBottom:6,marginTop:4,
          fontFamily:F_BODY}}>Ramas habilitadas (vacío = todo el sistema)</div>
        <select value="" onChange={e=>{
          if(e.target.value&&!(cfg.ramas||[]).includes(e.target.value)){
            const n={...cfg,ramas:[...(cfg.ramas||[]),e.target.value]};
            setCfg(n); guardar(n);
          }
        }} style={inp}>
          <option value="">+ Agregar rama/agencia...</option>
          {agencias.map(a=>(<option key={a.code} value={a.code}>{a.code} · {a.name}</option>))}
        </select>
        <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4],marginBottom:10}}>
          {(cfg.ramas||[]).length===0&&<span style={{color:Q.dim,fontSize:12,
            fontFamily:F_BODY}}>Activo para todo el sistema</span>}
          {(cfg.ramas||[]).map(rc=>(
            <span key={rc} onClick={()=>{
                const n={...cfg,ramas:cfg.ramas.filter(x=>x!==rc)};
                setCfg(n); guardar(n);
              }}
              style={{background:`${Q.violet}22`,border:`1px solid ${Q.violet}55`,borderRadius:RADII.md,
              padding:"4px 12px",color:Q.violet2||Q.violet,fontSize:12,cursor:"pointer",
              fontFamily:F_BODY}}>{rc} ✕</span>
          ))}
        </div>

        {/* Excluir agencias puntuales. Antes, para sacárselo a una
            había que listar a todas las demás como habilitadas. */}
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Quitárselo a una agencia</div>
        <select value="" onChange={e=>{
            if(e.target.value&&!(cfg.excluidas||[]).includes(e.target.value)){
              const n={...cfg,excluidas:[...(cfg.excluidas||[]),e.target.value]};
              setCfg(n); guardar(n);
            }
          }} style={inp}>
          <option value="">Elegí la agencia a excluir…</option>
          {agencias.map(a=>(
            <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
          ))}
        </select>
        <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4],marginBottom:6}}>
          {(cfg.excluidas||[]).length===0&&(
            <span style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY}}>
              Ninguna excluida</span>
          )}
          {(cfg.excluidas||[]).map(rc=>(
            <span key={rc} onClick={()=>{
                const n={...cfg,excluidas:cfg.excluidas.filter(x=>x!==rc)};
                setCfg(n); guardar(n);
              }}
              style={{background:`${Q.red}18`,border:`1px solid ${Q.red}55`,
              borderRadius:RADII.md,padding:"4px 12px",color:Q.red,fontSize:12,
              cursor:"pointer",
              fontFamily:F_BODY}}>{rc} ✕</span>
          ))}
        </div>
        {(cfg.excluidas||[]).length>0&&(
          <div style={{color:Q.dim,fontSize:12,marginBottom:10,
            lineHeight:1.5,fontFamily:F_BODY}}>
            La exclusión alcanza también a las agencias que cuelgan de
            ellas, y gana sobre cualquier habilitación de arriba.</div>
        )}

        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Margen de correlación (%) — descuento sobre la cuota</div>
        <input type="number" value={cfg.margen_correlacion}
          onChange={e=>setCfg({...cfg,margen_correlacion:parseFloat(e.target.value)||0})}
          placeholder="Ej: 15" style={inp}/>

        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Stake máximo por apuesta del mismo partido</div>
        <input type="number" value={cfg.stake_max}
          onChange={e=>setCfg({...cfg,stake_max:parseInt(e.target.value)||0})}
          placeholder="Ej: 20000" style={inp}/>

        <div style={{display:"flex",gap:SPACING[8]}}>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>Mín. selecciones</div>
            <input type="number" value={cfg.min_picks}
              onChange={e=>setCfg({...cfg,min_picks:parseInt(e.target.value)||2})}
              placeholder="2" style={inp}/>
          </div>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>Máx. selecciones</div>
            <input type="number" value={cfg.max_picks}
              onChange={e=>setCfg({...cfg,max_picks:parseInt(e.target.value)||4})}
              placeholder="4" style={inp}/>
          </div>
        </div>

        <div style={{background:`${Q.gold}0C`,border:`1px solid ${Q.gold}44`,borderRadius:RADII.md,
          padding:"12px 12px",marginBottom:8}}>
          <div style={{color:Q.gold,fontSize:12,fontWeight:700,marginBottom:6,
            fontFamily:F_BODY}}><Icon name="chart-no-axes-combined" size={14}/> Margen escalonado</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:8,lineHeight:1.4,
            fontFamily:F_BODY}}>
            Suma margen extra por cada selección de más (más picks = más correlación).</div>
          <div style={{display:"flex",gap:SPACING[8]}}>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:3,
                fontFamily:F_BODY}}>+% por pick extra</div>
              <input type="number" value={cfg.margen_escalon}
                onChange={e=>setCfg({...cfg,margen_escalon:parseFloat(e.target.value)||0})}
                placeholder="3" style={{...inp,marginBottom:0}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:3,
                fontFamily:F_BODY}}>Desde N picks</div>
              <input type="number" value={cfg.escalon_desde}
                onChange={e=>setCfg({...cfg,escalon_desde:parseInt(e.target.value)||3})}
                placeholder="3" style={{...inp,marginBottom:0}}/>
            </div>
          </div>
          <div style={{color:Q.dim,fontSize:12,marginTop:6,
            fontFamily:F_BODY}}>
            Ej: base {cfg.margen_correlacion}% + {cfg.margen_escalon}% por cada pick sobre {cfg.escalon_desde}.
            Con {(cfg.escalon_desde||3)+1} picks → {(parseFloat(cfg.margen_correlacion)||0)+(parseFloat(cfg.margen_escalon)||0)}%.</div>
        </div>

        <button onClick={analizar} disabled={analizando} style={{width:"100%",
          background:`${Q.cyan}18`,border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"12px",
          color:Q.cyan,fontWeight:700,fontSize:13,cursor:"pointer",marginBottom:8,
          fontFamily:F_BODY}}>
          {analizando?"Analizando...":<><Bot size={13}/> Analizar riesgo con IA</>}</button>
        {analisis&&<div style={{background:`${Q.cyan}0C`,border:`1px solid ${Q.cyan}44`,
          borderRadius:RADII.md,padding:"12px",marginBottom:10,color:Q.text,fontSize:12,lineHeight:1.6,
          whiteSpace:"pre-wrap",fontFamily:F_BODY}}>{analisis}</div>}

        <button onClick={guardar} style={{width:"100%",
          background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,border:"none",borderRadius:RADII.md,
          padding:"12px",color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer",
          fontFamily:F_BODY}}>Guardar configuración</button>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>

      <div style={{color:Q.dim,fontSize:12,textAlign:"center",lineHeight:1.5,
        fontFamily:F_BODY}}>
        Ejemplo: con margen 15%, si las cuotas multiplican 3.00 (ganancia 2.00),
        el cliente cobra a cuota 2.70 (ganancia 1.70). La diferencia protege del riesgo
        de correlación.</div>

      {cfg.agencias_activas&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}>
            <Building2 size={13}/> Agencias con Bet Builder activo · {cfg.total_activas||0}</div>
          {(cfg.agencias_activas||[]).length===0
            ? <div style={{color:Q.muted,fontSize:12,
                fontFamily:F_BODY}}>Ninguna agencia activa</div>
            : <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {cfg.agencias_activas.map(a=>(
                  <span key={a.code} style={{background:`${Q.green}18`,
                    border:`1px solid ${Q.green}55`,borderRadius:RADII.md,padding:"4px 8px",
                    fontSize:12,color:Q.green,fontFamily:F_BODY}}>
                    {a.code} · {a.name}</span>
                ))}
              </div>}
        </GCard>
      )}
    </div>
  );
}

function ConfigGeneral({ adminKey, onNoAutorizado }){
  // El sonido lo configura quien recibe las alertas
  // El control de riesgo del sistema vive acá: es lo que mira el
  // operador cuando quiere saber si algo raro está pasando.
  return(
    <div>
      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <SelectorSonido/>
      </GCard>
      <TabRiesgoSistema adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
    </div>
  );
}
function TabDiag({ adminKey, onNoAutorizado }){
  const [creditos,setCreditos]=useState(null);
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [liqGR8,setLiqGR8]=useState(null);
  const [liqGR8Loading,setLiqGR8Loading]=useState(false);
  const liquidarGR8=async()=>{
    if(liqGR8Loading) return;
    setLiqGR8Loading(true); setLiqGR8(null);
    try{
      const r=await fetch(`${API}/api/admin/gr8/liquidar`,{
        method:"POST",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setLiqGR8(await r.json());
    }catch(e){ setLiqGR8({error:"Sin conexión"}); }
    setLiqGR8Loading(false);
  };

  // Acá se pedían DOS diagnósticos. El segundo, `/api/_diag/live`, medía por
  // qué un partido en curso no cruzaba con el feed de cuotas, y ese cruce era
  // `_armar_live`: la pantalla en vivo de la cadena vieja. Sin el cruce no hay
  // nada que medir, y la ruta se fue con él —ver `casino_api.py`—. Con ella se
  // fueron las dos tarjetas que la dibujaban, "En vivo sin cuotas" y "Nombres
  // en el feed de cuotas", que informaban en VERDE ("todos los partidos en
  // curso tienen cuota") justamente porque no medían nada.
  const cargar=async()=>{
    setLoading(true); setErr("");
    try {
      const rc=await fetch(`${API}/api/_diag/creditos`,
        {headers:adminHeaders(adminKey)});
      if(rc.status===401){ onNoAutorizado(); return; }
      if(rc.ok) setCreditos(await rc.json());
      else setErr(`Error ${rc.status}`);
    } catch(e){
      setErr("Sin conexión con el servidor");
    }
    setLoading(false);
  };

  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const rest = creditos?.remaining!=null ? Number(creditos.remaining) : null;
  const colorCred = rest==null ? Q.muted : rest<1000 ? Q.red : rest<5000 ? Q.amber : Q.green;

  return(
    <div>
      <PageHeader title="Diagnóstico"
        description="El estado del feed de cuotas: los créditos disponibles, y la liquidación por la clave de GR8."
        action={<Btn label={loading?"...":"↻ Actualizar"} onClick={cargar}
          outline color={Q.muted} size="sm"/>}/>

      {err&&<div style={{color:Q.red,fontSize:12,marginBottom:12}}>{err}</div>}

      {/* Créditos de The Odds API */}
      <GCard glow={colorCred} style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:10,
          fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Créditos The Odds API</div>
        {creditos?.remaining!=null?(
          <>
            <div style={{color:colorCred,fontWeight:900,fontSize:30,
              fontFamily:F_BODY}}>
              {Number(creditos.remaining).toLocaleString("es-AR")}
            </div>
            <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
              restantes · usados: {creditos.used??"—"} · al {creditos.last_check??"—"}
            </div>
            {rest!=null&&rest<5000&&(
              <div style={{color:colorCred,fontSize:12,marginTop:8,
                fontFamily:F_BODY}}>
                {rest<1000?<><Icon name="triangle-alert" size={11}/> Saldo crítico — bajá sports_limit o sacá btts</>
                          :"Saldo bajo — conviene revisar la configuración"}
              </div>
            )}
          </>
        ):(
          <div style={{color:Q.muted,fontSize:12}}>
            Todavía sin datos. Se llena la primera vez que la API consulta cuotas.
          </div>
        )}
        <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
          {[["Mercados",creditos?.markets_configurados],
            ["Límite deportes",creditos?.sports_limit],
            ["Caché prematch",creditos?.ttl_prematch_seg?`${creditos.ttl_prematch_seg}s`:null],
          ].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
              <span style={{color:Q.muted,fontSize:12}}>{l}</span>
              <span style={{color:Q.text,fontSize:12,
                fontFamily:F_BODY}}>{v??"—"}</span>
            </div>
          ))}
        </div>
      </GCard>

      {/* Liquidación por la clave de GR8 — la que paga de verdad */}
      {/* El borde de color sólo cuando hay algo trabado: boletos que no se
          pueden liquidar solos y hay que liquidar a mano. Un borde fijo en una
          tarjeta que es nada más una tarjeta no ordena nada. */}
      <GCard glow={(liqGR8&&(liqGR8.sin_clave||0)>0)?Q.amber:null}
        style={{padding:SPACING[16],marginTop:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:4,
          fontFamily:F_BODY}}><Scale size={14}/> Liquidar con GR8</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:10}}>
          Liquida los boletos de toda la casa cruzando por la clave con la que
          GR8 liquida. El premio de una apuesta hecha desde el saldo del jugador
          se acredita acá; el boleto vendido por una agencia se marca y lo cobra
          el cajero por su caja.
        </div>
        <Btn label={liqGR8Loading?"Liquidando...":<><Scale size={13}/> Liquidar apuestas con GR8</>}
          onClick={liquidarGR8} color={Q.green} full disabled={liqGR8Loading}/>
        {liqGR8&&liqGR8.error&&(
          <div style={{color:Q.red,fontSize:12,marginTop:8,
            fontFamily:F_BODY}}><Icon name="x" size={13}/> {liqGR8.error}</div>
        )}
        {liqGR8&&!liqGR8.error&&(
          <div style={{marginTop:8,fontSize:12,fontFamily:F_BODY}}>
            <div style={{color:Q.green,fontWeight:700}}>
              {(liqGR8.ganadas||0)+(liqGR8.perdidas||0)+(liqGR8.devueltas||0)}{" "}
              liquidadas · {liqGR8.ganadas||0} ganadas ·{" "}
              {liqGR8.perdidas||0} perdidas · {liqGR8.devueltas||0} devueltas
            </div>
            <div style={{color:Q.muted,marginTop:2}}>
              {liqGR8.esperando||0} esperando el resultado de GR8
            </div>
            {(liqGR8.sin_clave||0)>0&&(
              <div style={{color:Q.amber,marginTop:2}}>
                {liqGR8.sin_clave} sin clave de liquidación: esas van a mano
              </div>
            )}
            {(liqGR8.en_revision||0)>0&&(
              <div style={{color:Q.amber,marginTop:2}}>
                {liqGR8.en_revision} marcadas para revisar: GR8 retiró el
                resultado después de pagarlas
              </div>
            )}
            <div style={{color:Q.cyan,marginTop:2}}>{liqGR8.mensaje}</div>
          </div>
        )}
      </GCard>
      {/* Las pruebas del sistema van acá: es el mismo lugar donde se
          viene a ver si algo anda mal. */}
      <Rendimiento adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>

      <div style={{marginTop:22,paddingTop:SPACING[20],
        borderTop:`1px solid ${Q.border}`}}>
        <TabTester adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      </div>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════
const TABS=[
  {k:"global",   i:<Globe size={17}/>, l:"Global"},
  {k:"cierre",   i:<Icon name="chart-no-axes-combined" size={17}/>, l:"Cierre"},
  {k:"combos",   i:<Zap size={17}/>, l:"Combos"},
  {k:"agencias", i:<Building2 size={17}/>, l:"Agencias"},
  {k:"influencers", i:<Star size={17}/>, l:"Influencers"},
  {k:"eventos",  i:<Calendar size={17}/>, l:"Eventos"},
  {k:"billetera",i:<Icon name="wallet-cards" size={17}/>, l:"Billetera"},
  {k:"usuarios", i:<Icon name="users" size={17}/>, l:"Usuarios"},
  {k:"config",   i:<Icon name="sliders-horizontal" size={17}/>, l:"Config"},
  {k:"diag",     i:<Stethoscope size={17}/>, l:"Diag"},
  {k:"chat",     i:<Icon name="message-circle" size={17}/>, l:"Consultas"},
];

// Desktop-only grouping of AdminPanel's TABS for the sidebar menu.
// Approved grouping: odd/tasks/sidebar-groups.md. Every key here must
// also be a key in TABS above (sidebarGroupKeysMatchTabs.test.js guards
// that pairing). Below 1024px the fixed bottom bar still renders straight
// from the flat TABS array, unaffected by this grouping.
const TAB_GROUPS = [
  { label: "Operación", keys: ["global", "eventos", "combos"] },
  { label: "Red", keys: ["agencias", "influencers", "usuarios"] },
  { label: "Dinero", keys: ["billetera", "cierre"] },
  { label: "Sistema", keys: ["config", "diag", "chat"] },
];

// Agrupa el asistente y los mensajes con agencias en una sola
// pestaña: son las dos cosas que hace un operador cuando necesita
// comunicarse o entender algo.
function PanelComunicacion({ adminKey, onNoAutorizado }){
  const [modo,setModo]=useState("mensajes");
  const [pend,setPend]=useState(0);

  useEffect(()=>{
    const traer=()=>fetch(`${API}/api/admin/mensajes`,
        {headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setPend(d.total_sin_leer||0)).catch(()=>{});
    traer();
    const t=setInterval(traer,60000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[]);

  return(
    <div>
      {/* No PageHeader here, for the same reason TabGlobal has none: the
          three buttons below already name the three things a header would
          describe, and this tab's sub-views are chat viewports sized from
          the viewport height. A header would cost them a permanent strip
          of conversation on a phone to restate the buttons under it. */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["mensajes",<><Mail size={13}/> Agencias</>],["soporte",<><Headphones size={13}/> Soporte</>],
          ["asistente",<><MessageSquare size={13}/> Asistente</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setModo(k)} style={{flex:1,
            background:modo===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${modo===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"12px 8px",cursor:"pointer",
            color:modo===k?Q.cyan:Q.muted,fontSize:12.5,
            fontWeight:modo===k?700:400,
            fontFamily:F_BODY}}>
            {l}
            {k==="mensajes"&&pend>0&&(
              <span style={{background:Q.red,color:"#fff",borderRadius:RADII.md,
                padding:"4px 8px",fontSize:12,marginLeft:5}}>{pend}</span>
            )}
          </button>
        ))}
      </div>
      {modo==="mensajes"&&
        <TabMensajes adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {modo==="soporte"&&
        <TabSoporte adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {modo==="asistente"&&
        <ChatOperador adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}

// Sonido de las alertas. Lo configura quien lo recibe, no quien lo
// manda: si el ruido molesta, la gente silencia el navegador entero
// y se pierde también lo que sí importa.
//
// Se genera con el propio navegador en vez de cargar un archivo: no
// suma peso ni depende de que un audio esté disponible.
const SONIDO_KEY = "qp_sonido_alertas";

function nivelSonido(){
  try{ return localStorage.getItem(SONIDO_KEY) || "suave"; }
  catch(e){ return "suave"; }
}

function guardarNivelSonido(v){
  try{ localStorage.setItem(SONIDO_KEY, v); }catch(e){}
}

function sonarAlerta(urgente){
  const nivel = nivelSonido();
  if(nivel === "off") return;
  // 'urgente' solo suena para lo crítico; el resto queda en silencio
  if(nivel === "urgente" && !urgente) return;
  try{
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if(!Ctx) return;
    const ctx = new Ctx();
    // Dos tonos para lo urgente, uno solo para lo normal
    const notas = urgente ? [880, 660] : [660];
    notas.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      // Sube y baja suave: un tono seco resulta molesto
      const t0 = ctx.currentTime + i * 0.22;
      vol.gain.setValueAtTime(0, t0);
      vol.gain.linearRampToValueAtTime(0.14, t0 + 0.04);
      vol.gain.linearRampToValueAtTime(0, t0 + 0.2);
      osc.connect(vol); vol.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.22);
    });
    setTimeout(()=>{ try{ ctx.close(); }catch(e){} }, 900);
  }catch(e){}
}

// Selector para poner donde haga falta
function SelectorSonido(){
  const [nivel,setNivel]=useState(nivelSonido());
  const cambiar=(v)=>{
    setNivel(v); guardarNivelSonido(v);
    if(v!=="off") sonarAlerta(v==="urgente");
  };
  return(
    <div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:5,
        fontFamily:F_BODY}}>Sonido de alertas</div>
      <div style={{display:"flex",gap:SPACING[8]}}>
        {[["off",<><VolumeX size={13}/> Sin sonido</>],["urgente",<><Bell size={13}/> Solo urgentes</>],
          ["suave",<><Volume2 size={13}/> Todas</>]].map(([k,l])=>(
          <button key={k} onClick={()=>cambiar(k)} style={{flex:1,
            background:nivel===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${nivel===k?Q.violet:Q.border}`,
            borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
            color:nivel===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:nivel===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>
      <div style={{color:Q.dim,fontSize:12,marginTop:5,lineHeight:1.4}}>
        Tocá una opción para escucharla.</div>
    </div>
  );
}

// ── DESAFÍOS EN EL ADMIN ──────────────────────────────────────
// El producto completo: configuración, cotizaciones de IACOIN,
// disputas y moderación del muro.
function TabDesafios({ adminKey, onNoAutorizado }){
  const [sub,setSub]=useState("config");
  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Handshake size={14}/> Desafíos</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Apuestas entre jugadores. La casa no arriesga: cobra comisión
        sobre lo que pone cada uno.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:14,
        overflowX:"auto"}}>
        {[["config",<><Icon name="sliders-horizontal" size={13}/> Config</>],["iacoin",<><Coins size={13}/> IACOIN</>],
          ["disputas",<><Scale size={13}/> Disputas</>],["muro",<><Icon name="shield-alert" size={13}/> Muro</>],
          ["reporte",<><Icon name="chart-no-axes-combined" size={13}/> Reporte</>],["cc",<><Icon name="wallet-cards" size={13}/> Cuenta cte.</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setSub(k)}
            style={{background:sub===k?`${Q.violet}33`:"transparent",
              border:`1px solid ${sub===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:sub===k?Q.cyan:Q.muted,fontSize:12,
              fontWeight:sub===k?700:400,whiteSpace:"nowrap",
              fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {sub==="config"  &&<DesafiosConfig adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="iacoin"  &&<IacoinPanel adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="disputas"&&<DisputasPanel adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="muro"    &&<ModeracionPanel adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="reporte" &&<ReporteDesafios adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
      {sub==="cc"      &&<CCDesafios adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>}
    </div>
  );
}


function DesafiosConfig({ adminKey, onNoAutorizado }){
  const [c,setC]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=()=>{
    fetch(`${API}/api/admin/desafios/config`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(d=>d&&setC(d)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const guardar=async(over)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/desafios/config`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({...c, ...(over||{})})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"Error");
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!c) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:16,fontWeight:700,textAlign:"center",
    fontFamily:F_BODY};
  const campo=(label,clave,ayuda)=>(
    <GCard style={{padding:SPACING[16],marginBottom:10}}>
      <div style={{color:Q.muted,fontSize:12,marginBottom:4,
        fontFamily:F_BODY}}>{label}</div>
      <input value={c[clave]} inputMode="decimal"
        onChange={e=>setC({...c,[clave]:e.target.value})} style={inp}/>
      {ayuda&&<div style={{color:Q.dim,fontSize:12,marginTop:6,
        lineHeight:1.5}}>{ayuda}</div>}
    </GCard>
  );

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <button onClick={()=>{const v=!c.activo; setC({...c,activo:v});
          guardar({activo:v});}}
        style={{width:"100%",
          background:c.activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
          border:`1px solid ${c.activo?Q.green:Q.border}`,borderRadius:RADII.md,
          padding:"12px",cursor:"pointer",marginBottom:12,
          color:c.activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
          fontFamily:F_BODY}}>
        {c.activo?<><Icon name="circle-check" size={13}/> Producto activo</>:<><CircleOff size={13}/> Producto apagado</>}</button>

      {campo("Comisión (%)","comision_pct",
        "Se cobra sobre lo que pone cada jugador, no sobre el premio. "+
        "Si los dos ponen 10.000 con 10%, el pozo queda en 18.000. "+
        "En empate o devolución no se cobra nada.")}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <div style={{flex:1}}>
          {campo("Mínimo","monto_min")}
        </div>
        <div style={{flex:1}}>
          {campo("Máximo","monto_max")}
        </div>
      </div>

      {campo("Proporción máxima entre montos","ratio_max",
        "10.000 contra 100 no es una apuesta entre pares: es pasarle "+
        "plata a alguien con apariencia de juego. Este tope lo evita.")}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <div style={{flex:1}}>{campo("Abiertos por jugador","max_abiertas")}</div>
        <div style={{flex:1}}>{campo("Por día","max_dia")}</div>
      </div>

      {campo("Horas de vigencia","horas_vigencia",
        "Si nadie lo toma en ese tiempo, se libera el saldo retenido.")}

      <Btn label={proc?"Guardando…":"Guardar"} onClick={()=>guardar()}
        color={Q.violet} full disabled={proc}/>
    </div>
  );
}


// Cotizaciones de IACOIN. La IA sugiere, un humano confirma: si un
// valor equivocado entra en vigencia, se compran IACOIN baratos y la
// casa pierde plata real.
function IacoinPanel({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [sug,setSug]=useState(null);
  const [analisis,setAnalisis]=useState("");
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [editando,setEditando]=useState(null);
  const [precio,setPrecio]=useState("");
  const [spread,setSpread]=useState("3");

  const cargar=()=>{
    fetch(`${API}/api/admin/iacoin/cotizaciones`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const sugerir=async()=>{
    setProc(true); setMsg(null); setAnalisis("");
    try{
      const r=await fetch(`${API}/api/admin/iacoin/sugerir`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setSug(x.sugerencias||{}); setAnalisis(x.analisis||"");
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const fijar=async(moneda,valor)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/iacoin/cotizacion`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({moneda, precio_compra:parseFloat(valor),
          spread_pct:parseFloat(spread)||3,
          fuente:sug&&sug[moneda]?"ia":"manual",
          sugerido_ia:sug?sug[moneda]:null})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:`${moneda}: compra ${x.compra} · venta ${x.venta}`, ok:true});
      setEditando(null); setPrecio(""); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  return(
    <div>
      <div style={{background:`${Q.amber}12`,
        border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
        padding:"12px 12px",marginBottom:12,color:Q.amber,fontSize:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        La IA sugiere, vos confirmás. Si un valor equivocado entra en
        vigencia, se compran IACOIN baratos y la casa pierde plata
        real. Verificá contra una fuente actual antes de aplicar.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center",lineHeight:1.45}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"flex",gap:SPACING[12]}}>
          <div style={{flex:1}}>
            {/* ── POR QUÉ EL IACOIN VA CON `money()` Y CON DECIMALES ──
                La forma ya estaba elegida en este archivo: `money(x,
                "IACOIN")`, que escribe el código ADELANTE porque el IACOIN
                no tiene símbolo (ver el comentario de `Liquidacion`, arriba
                de las dos tarjetas de totales). `moneda.js` nombra una
                moneda que no conoce en vez de disfrazarla de pesos, y eso
                es justo lo que hace falta acá: un número sin unidad, al
                lado de números que sí la tienen, es el defecto.

                LOS DOS DECIMALES NO SON RUIDO, son la escala real de la
                unidad: `users.saldo_iacoin` es `numeric(16,4)`,
                `p2p_apuestas.comision_monto` también desde
                `20261026130000_los_decimales_del_desafio.sql`, y esa
                migración mide que se llega con datos comunes —un monto de
                700,01 por un 8,03%—. El backend redondea el rake a dos
                (`bot/casino_api.py:8950`). Así que `IACOIN 1.234,00` dice
                la precisión que el dato tiene, no una inventada. */}
            <div style={{color:Q.muted,fontSize:12}}>En circulación</div>
            <div style={{color:Q.gold,fontWeight:800,fontSize:18,
              fontFamily:F_BODY}}>
              {money(d.en_circulacion, "IACOIN")}</div>
          </div>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12}}>Jugadores</div>
            <div style={{color:Q.text,fontWeight:800,fontSize:18,
              fontFamily:F_BODY}}>
              {d.jugadores_con_saldo}</div>
          </div>
        </div>
      </GCard>

      <Btn label={proc?"Consultando…":<><Bot size={13}/> Pedirle sugerencias a la IA</>}
        onClick={sugerir} color={Q.cyan} full disabled={proc}/>

      {analisis&&(
        <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
          <div style={{color:Q.muted,fontSize:12.5,lineHeight:1.6,
            whiteSpace:"pre-wrap",
            fontFamily:F_BODY}}>{analisis}</div>
        </GCard>
      )}

      <div style={{color:Q.muted,fontSize:12,margin:"14px 0 6px",
        fontFamily:F_BODY}}>
        Diferencia entre compra y venta (%)</div>
      <input value={spread} inputMode="decimal"
        onChange={e=>setSpread(e.target.value)}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px",
          color:Q.text,fontSize:15,fontWeight:700,textAlign:"center",
          marginBottom:6,fontFamily:F_BODY}}/>
      <div style={{color:Q.dim,fontSize:12,marginBottom:14,
        lineHeight:1.5}}>
        Sin diferencia, alguien compra hoy y vuelve a su moneda después
        de una devaluación habiendo ganado sin apostar.</div>

      {(d.cotizaciones||[]).map(c=>(
        <GCard key={c.moneda} style={{padding:SPACING[12],marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center"}}>
            <div>
              <div style={{color:Q.text,fontWeight:700,fontSize:14,
                fontFamily:F_BODY}}>{c.moneda}</div>
              {/* ESTO NO ES PLATA EN IACOIN, ES EL PRECIO DE UN IACOIN: lo
                  que cuesta en la moneda de la fila, que es `c.moneda` —la
                  misma clave del `GCard`, escrita arriba como título—. Iba
                  con `toLocaleString` pelado, o sea un número sin unidad
                  abajo del nombre de una moneda. Sin `conCodigo`: el título
                  de la tarjeta ya es el código. */}
              <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                compra {money(c.compra, c.moneda)} · vende{" "}
                {money(c.venta, c.moneda)}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                {fechaHora(c.desde)} · {c.fuente==="ia"?"sugerido por IA":"manual"}</div>
            </div>
            <button onClick={()=>{setEditando(c.moneda);
                setPrecio(String(sug?.[c.moneda]||c.compra));}}
              style={{background:`${Q.violet}22`,
                border:`1px solid ${Q.violet}66`,borderRadius:RADII.md,
                padding:"8px 12px",color:Q.cyan,fontSize:12,
                cursor:"pointer",flexShrink:0}}>Cambiar</button>
          </div>
          {sug&&sug[c.moneda]&&Math.abs(sug[c.moneda]-c.compra)>0.01&&(
            <div style={{color:Q.amber,fontSize:12,marginTop:7,
              lineHeight:1.45}}>
              La IA sugiere {money(sug[c.moneda], c.moneda)}</div>
          )}
          {editando===c.moneda&&(
            <div style={{marginTop:10,paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <input value={precio} inputMode="decimal"
                onChange={e=>setPrecio(e.target.value)}
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"12px",color:Q.text,fontSize:16,
                  fontWeight:700,textAlign:"center",marginBottom:7,
                  fontFamily:F_BODY}}/>
              <div style={{display:"flex",gap:SPACING[8]}}>
                <button onClick={()=>fijar(c.moneda,precio)} disabled={proc}
                  style={{flex:1,background:`${Q.green}22`,
                    border:`1px solid ${Q.green}`,borderRadius:RADII.md,
                    padding:"8px",color:Q.green,fontSize:12,
                    fontWeight:700,cursor:"pointer"}}>Confirmar</button>
                <button onClick={()=>setEditando(null)}
                  style={{flex:1,background:"transparent",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                    padding:"8px",color:Q.muted,fontSize:12,
                    cursor:"pointer"}}>Cancelar</button>
              </div>
            </div>
          )}
        </GCard>
      ))}

      {(d.monedas_faltantes||[]).length>0&&(
        <GCard glow={Q.red} style={{padding:SPACING[12],marginTop:10}}>
          <div style={{color:Q.red,fontSize:12,fontWeight:700,
            marginBottom:6,fontFamily:F_BODY}}>
            Sin cotización</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:9,
            lineHeight:1.5}}>
            Los jugadores de estas monedas no pueden comprar IACOIN.</div>
          {d.monedas_faltantes.map(m=>(
            <div key={m} style={{display:"flex",
              justifyContent:"space-between",alignItems:"center",
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text,fontSize:13,fontWeight:600}}>
                {m}
                {sug&&sug[m]&&(
                  <span style={{color:Q.amber,fontSize:12,
                    marginLeft:7,fontWeight:400}}>
                    {/* `m` ES la moneda que itera el map: el precio
                        sugerido está en ella. */}
                    IA sugiere {money(sug[m], m)}</span>
                )}</span>
              {editando===m?(
                <div style={{display:"flex",gap:SPACING[4]}}>
                  <input value={precio} inputMode="decimal"
                    onChange={e=>setPrecio(e.target.value)}
                    style={{width:90,background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
                      padding:"8px",color:Q.text,fontSize:13,
                      textAlign:"center"}}/>
                  <button onClick={()=>fijar(m,precio)} disabled={proc}
                    style={{background:`${Q.green}22`,
                      border:`1px solid ${Q.green}`,borderRadius:RADII.sm,
                      padding:"8px 12px",color:Q.green,fontSize:12,
                      cursor:"pointer"}}>OK</button>
                </div>
              ):(
                <button onClick={()=>{setEditando(m);
                    setPrecio(String(sug?.[m]||""));}}
                  style={{background:`${Q.violet}22`,
                    border:`1px solid ${Q.violet}66`,borderRadius:RADII.sm,
                    padding:"8px 12px",color:Q.cyan,fontSize:12,
                    cursor:"pointer"}}>Fijar</button>
              )}
            </div>
          ))}
        </GCard>
      )}
    </div>
  );
}

// Disputas: la IA arma el caso y el equipo decide. Con plata de por
// medio, la decisión final tiene que ser de una persona.
function DisputasPanel({ adminKey, onNoAutorizado }){
  const [lista,setLista]=useState(null);
  const [abierta,setAbierta]=useState(null);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [nota,setNota]=useState("");

  const cargar=()=>{
    fetch(`${API}/api/admin/desafios/disputas`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(d=>setLista(d?.disputas||[])).catch(()=>setLista([]));
  };
  useEffect(cargar,[]);

  const analizar=async(id)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(
        `${API}/api/admin/desafios/disputas/${id}/analizar`,{
        method:"POST",headers:adminHeaders(adminKey)});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"Error");
      setLista(l=>l.map(x=>x.id===id
        ?{...x, analisis_ia:d.analisis, sugerencia_ia:d.sugerencia}:x));
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const resolver=async(id,ganador)=>{
    const q={creador:"el que propuso",aceptador:"el que aceptó",
      empate:"empate (se devuelve a los dos)"}[ganador];
    if(!window.confirm(`¿Resolver a favor de ${q}? Se paga al instante.`))
      return;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(
        `${API}/api/admin/desafios/disputas/${id}/resolver`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({ganador, nota})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"Error");
      setMsg({text:"Resuelta y pagada", ok:true}); setAbierta(null); setNota("");
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(lista===null) return <div style={{color:Q.muted,
    textAlign:"center",padding:SPACING[20]}}>Cargando…</div>;

  if(!lista.length) return(
    <GCard style={{padding:SPACING[24],textAlign:"center"}}>
      <div style={{color:Q.green,fontSize:26,marginBottom:8}}>✓</div>
      <div style={{color:Q.muted,fontSize:13,lineHeight:1.55,
        fontFamily:F_BODY}}>
        No hay disputas abiertas.</div>
    </GCard>
  );

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        {lista.length} {lista.length===1?"disputa":"disputas"} esperando.
        El saldo de los jugadores sigue retenido hasta que se resuelva.</div>

      {lista.map(d=>(
        <GCard key={d.id} glow={Q.amber} style={{padding:SPACING[16],marginBottom:10}}>
          <div style={{color:Q.text,fontSize:13.5,fontWeight:700,
            lineHeight:1.4,fontFamily:F_BODY}}>
            {d.titulo}</div>
          {d.descripcion&&(
            <div style={{color:Q.muted,fontSize:12,marginTop:4,
              lineHeight:1.5}}>{d.descripcion}</div>
          )}
          <div style={{color:Q.dim,fontSize:12,marginTop:5}}>
            {/* El código estaba suelto DESPUÉS del número; `money` lo pone
                adelante, que es la forma del archivo, así que el suelto se
                va para no decirlo dos veces. */}
            Pozo {money(d.pozo, "IACOIN")} · {d.hace}</div>

          <div style={{marginTop:10,paddingTop:SPACING[8],
            borderTop:`1px solid ${Q.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,padding:"4px 0"}}>
              <span style={{color:Q.muted}}>{d.creador} dice que ganó</span>
              <span style={{color:Q.text,fontWeight:600}}>
                {d.dice_creador==="creador"?"él"
                  :d.dice_creador==="aceptador"?"el otro":"empate"}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,padding:"4px 0"}}>
              <span style={{color:Q.muted}}>{d.aceptador} dice que ganó</span>
              <span style={{color:Q.text,fontWeight:600}}>
                {d.dice_aceptador==="aceptador"?"él"
                  :d.dice_aceptador==="creador"?"el otro":"empate"}</span>
            </div>
          </div>

          {d.analisis_ia&&(
            <div style={{marginTop:10,padding:"12px 12px",
              background:`${Q.cyan}0D`,border:`1px solid ${Q.cyan}33`,
              borderRadius:RADII.md}}>
              <div style={{color:Q.cyan,fontSize:12,letterSpacing:0.5,
                marginBottom:5}}>LECTURA DE LA IA</div>
              <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
                whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                {d.analisis_ia}</div>
              {d.sugerencia_ia&&(
                <div style={{color:Q.amber,fontSize:12,marginTop:7,
                  fontWeight:600}}>
                  Sugiere: {{creador:"a favor del que propuso",
                    aceptador:"a favor del que aceptó",
                    empate:"devolver a los dos",
                    revisar:"revisarlo con más datos"}[d.sugerencia_ia]}</div>
              )}
              <div style={{color:Q.dim,fontSize:12,marginTop:6,
                lineHeight:1.45}}>
                Es una lectura para ayudarte, no una resolución.</div>
            </div>
          )}

          {!d.analisis_ia&&(
            <button onClick={()=>analizar(d.id)} disabled={proc}
              style={{width:"100%",marginTop:10,background:`${Q.cyan}18`,
                border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,
                padding:"12px",color:Q.cyan,fontSize:12.5,
                fontWeight:700,cursor:"pointer",
                fontFamily:F_BODY}}>
              {proc?"Analizando…":<><Bot size={13}/> Que la IA arme el caso</>}</button>
          )}

          {abierta===d.id?(
            <div style={{marginTop:10,paddingTop:SPACING[12],
              borderTop:`1px solid ${Q.border}`}}>
              <input value={nota} onChange={e=>setNota(e.target.value)}
                placeholder="Por qué decidiste esto (queda registrado)"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"8px 12px",color:Q.text,fontSize:12,
                  marginBottom:8,
                  fontFamily:F_BODY}}/>
              <div style={{display:"flex",gap:SPACING[8]}}>
                <button onClick={()=>resolver(d.id,"creador")}
                  disabled={proc} style={{flex:1,background:`${Q.green}18`,
                    border:`1px solid ${Q.green}66`,borderRadius:RADII.md,
                    padding:"12px 8px",color:Q.green,fontSize:12,
                    fontWeight:700,cursor:"pointer",lineHeight:1.3}}>
                  Gana<br/>{d.creador}</button>
                <button onClick={()=>resolver(d.id,"aceptador")}
                  disabled={proc} style={{flex:1,background:`${Q.green}18`,
                    border:`1px solid ${Q.green}66`,borderRadius:RADII.md,
                    padding:"12px 8px",color:Q.green,fontSize:12,
                    fontWeight:700,cursor:"pointer",lineHeight:1.3}}>
                  Gana<br/>{d.aceptador}</button>
                <button onClick={()=>resolver(d.id,"empate")}
                  disabled={proc} style={{flex:1,background:"transparent",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                    padding:"12px 8px",color:Q.muted,fontSize:12,
                    cursor:"pointer",lineHeight:1.3}}>
                  Devolver<br/>a los dos</button>
              </div>
              <button onClick={()=>setAbierta(null)}
                style={{width:"100%",marginTop:6,background:"transparent",
                  border:"none",color:Q.dim,fontSize:12,
                  cursor:"pointer"}}>Cancelar</button>
            </div>
          ):(
            <button onClick={()=>setAbierta(d.id)}
              style={{width:"100%",marginTop:8,
                background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
                border:"none",borderRadius:RADII.md,padding:"12px",
                color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",
                fontFamily:F_BODY}}>
              Resolver</button>
          )}
        </GCard>
      ))}
    </div>
  );
}


function ModeracionPanel({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text

  const cargar=()=>{
    fetch(`${API}/api/admin/muro/moderacion`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const moderar=async(postId,accion)=>{
    try{
      await fetch(`${API}/api/admin/muro/${postId}/moderar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({accion})});
      setMsg({text:accion==="ocultar"?"Ocultado":"Restaurado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {d.denunciados.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center",marginBottom:12}}>
          <div style={{color:Q.green,fontSize:24,marginBottom:6}}>✓</div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            Nada denunciado pendiente</div>
        </GCard>
      )}

      {d.denunciados.map(p=>(
        <GCard key={p.post_id} glow={Q.red}
          style={{padding:SPACING[12],marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"baseline",marginBottom:5}}>
            <span style={{color:Q.cyan,fontSize:12,fontWeight:700}}>
              {p.autor}</span>
            <span style={{color:Q.red,fontSize:12}}>
              {p.denuncias} {p.denuncias===1?"denuncia":"denuncias"}
              {p.oculto&&" · oculto"}</span>
          </div>
          <div style={{color:Q.text,fontSize:12.5,lineHeight:1.5,
            fontFamily:F_BODY}}>{p.texto}</div>
          <div style={{display:"flex",gap:SPACING[8],marginTop:9}}>
            <button onClick={()=>moderar(p.post_id,
                p.oculto?"restaurar":"ocultar")}
              style={{flex:1,
                background:p.oculto?`${Q.green}18`:`${Q.red}18`,
                border:`1px solid ${p.oculto?Q.green:Q.red}66`,
                borderRadius:RADII.md,padding:"8px",
                color:p.oculto?Q.green:Q.red,fontSize:12,
                fontWeight:700,cursor:"pointer"}}>
              {p.oculto?"Restaurar":"Ocultar"}</button>
          </div>
        </GCard>
      ))}

      {(d.reincidentes||[]).length>0&&(
        <GCard glow={Q.amber} style={{padding:SPACING[12],marginTop:12}}>
          <div style={{color:Q.amber,fontSize:12,fontWeight:700,
            marginBottom:4,fontFamily:F_BODY}}>
            Cuentas con varios rechazos</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:8,
            lineHeight:1.5}}>
            Tres o más en el último mes. Vale la pena mirarlas.</div>
          {d.reincidentes.map(r=>(
            <div key={r.user_id} style={{display:"flex",
              justifyContent:"space-between",fontSize:12,padding:"4px 0",
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text}}>Cliente #{r.user_id}</span>
              <span style={{color:Q.amber}}>{r.rechazos}</span>
            </div>
          ))}
        </GCard>
      )}

      {(d.rechazos_recientes||[]).length>0&&(
        <GCard style={{padding:SPACING[12],marginTop:12}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.5,
            marginBottom:7}}>RECHAZADO ESTA SEMANA</div>
          {d.rechazos_recientes.slice(0,12).map((r,i)=>(
            <div key={i} style={{fontSize:12,padding:"4px 0",
              borderTop:i?`1px solid ${Q.border}`:"none"}}>
              <div style={{color:Q.muted}}>
                {r.autor||"—"}
                <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                  {fechaHora(r.cuando)} · {r.por==="ia"?"IA":r.por}</span></div>
              <div style={{color:Q.dim,fontSize:12,lineHeight:1.4}}>
                {r.motivo}</div>
            </div>
          ))}
        </GCard>
      )}
    </div>
  );
}


// Todo lo jugado, filtrable por cliente y por agencia. Cuando alguien
// reclama, acá está lo que pasó con fecha y hora.
function HistorialAdmin({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace7=new Date(Date.now()-7*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace7);
  const [hasta,setHasta]=useState(hoy);
  const [usuario,setUsuario]=useState("");
  const [agencia,setAgencia]=useState("");
  const [tipo,setTipo]=useState("");
  const [ags,setAgs]=useState([]);
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);

  useEffect(()=>{
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(x=>x&&setAgs(Array.isArray(x)?x:(x.agencias||[])))
      .catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const cargar=async()=>{
    setCargando(true);
    try{
      const q=new URLSearchParams({desde,hasta,tipo,
        usuario:usuario.trim(), agencia, limite:"150"});
      const r=await fetch(`${API}/api/admin/historial?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,minWidth:0,
    fontFamily:F_BODY};

  const ICONO={deportivas:<Icon name="trophy" size={13}/>,casino:<Icon name="spade" size={13}/>,casino_vivo:<Video size={13}/>};

  // ── EL HISTORIAL MEZCLA DOS TABLAS Y TODAS LAS AGENCIAS ──────
  //
  // Cada renglón trae su moneda del borde, y la cadena que la resolvió
  // depende de la FUENTE: un boleto la deriva del jugador y de las ramas
  // que lo vendieron y lo cobraron; una ronda de casino arranca por la
  // sesión. Acá abajo las dos llegan ya resueltas como `m.moneda`, y lo
  // que importa es que el renglón se dibuje con la suya y no con la del
  // archivo.
  //
  // El TOTAL es otra cosa: sin filtro de agencia suma TODAS las ramas, así
  // que viene agrupado. `Jugadas` es una cuenta y queda plana.
  //
  // La guarda con `Array.isArray` no es defensiva por costumbre:
  // `por_moneda` puede faltar con una respuesta vieja de caché o con el
  // frontend desfasado del backend, y entonces no se dibuja plata en vez
  // de reventar la pestaña con un `.map` sobre `undefined`.
  const histFilas = Array.isArray(d?.total?.por_moneda)
    ? d.total.por_moneda : [];
  const variasHist = histFilas.length > 1;
  const rotHist = (texto, m) => variasHist ? `${texto} · ${m.moneda}` : texto;
  const montoMovHist = formatoDeLista(d?.movimientos);

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}>
        <Icon name="clipboard-list" size={14}/> Historial de juegos</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Todo lo jugado con fecha y hora. Cuando alguien reclama, acá
        está lo que pasó.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)}
          style={{...inp,flex:1}}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)}
          style={{...inp,flex:1}}/>
      </div>

      <input value={usuario} onChange={e=>setUsuario(e.target.value)}
        onKeyDown={e=>{ if(e.key==="Enter") cargar(); }}
        placeholder="Cliente: nombre, usuario, documento o número"
        style={{...inp,width:"100%",marginBottom:8}}/>

      <select value={agencia} onChange={e=>setAgencia(e.target.value)}
        style={{...inp,width:"100%",marginBottom:8}}>
        <option value="">Todas las agencias</option>
        {ags.map(a=>(
          <option key={a.code} value={a.code}>
            {"— ".repeat(a.nivel||0)}{a.name}</option>
        ))}
      </select>

      <div style={{display:"flex",gap:SPACING[4],marginBottom:10,
        overflowX:"auto"}}>
        {[["","Todo"],["deportivas",<><Icon name="trophy" size={12}/> Deportivas</>],
          ["casino",<><Icon name="spade" size={12}/> Casino</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setTipo(k)}
            style={{background:tipo===k?`${Q.violet}28`:"transparent",
              border:`1px solid ${tipo===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:tipo===k?Q.cyan:Q.muted,fontSize:12,
              whiteSpace:"nowrap",flexShrink:0,
              fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      <Btn label={cargando?"Buscando…":"Buscar"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {d?.aviso&&(
        <div style={{color:Q.amber,fontSize:12,marginTop:10,
          textAlign:"center",lineHeight:1.45,
          fontFamily:F_BODY}}>{d.aviso}</div>
      )}

      {d&&d.total&&(
        <>
          {/* `Jugadas` viaja en el renglón de la PRIMERA moneda y no en
              una fila propia: con una sola moneda —el caso de hoy— la
              tarjeta queda exactamente igual que antes, tres columnas en
              una línea, que es la garantía de que las pantallas en pesos
              no cambian. Con dos monedas, la cuenta no se repite abajo
              porque una jugada es una jugada en cualquiera de las dos. */}
          <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
            {histFilas.length===0&&(
              <div style={{display:"flex",gap:SPACING[12]}}>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Jugadas</div>
                  <div style={{color:Q.text,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {d.total.jugadas}</div>
                </div>
              </div>
            )}
            {histFilas.map((m,i)=>(
              <div key={m.moneda} style={{display:"flex",gap:SPACING[12],
                marginTop:i?SPACING[8]:0}}>
                {i===0&&(
                  <div style={{flex:1}}>
                    <div style={{color:Q.muted,fontSize:12}}>Jugadas</div>
                    <div style={{color:Q.text,fontWeight:800,fontSize:17,
                      fontFamily:F_BODY}}>
                      {d.total.jugadas}</div>
                  </div>
                )}
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {rotHist("Apostado",m)}</div>
                  <div style={{color:Q.gold,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(m.apostado,m.moneda)}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {rotHist("GGR",m)}</div>
                  <div style={{color:m.ggr>=0?Q.green:Q.red,
                    fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(m.ggr,m.moneda)}</div>
                </div>
              </div>
            ))}
          </GCard>

          {(d.movimientos||[]).length===0&&(
            <div style={{color:Q.muted,textAlign:"center",padding:SPACING[24],
              fontSize:12.5,
              fontFamily:F_BODY}}>
              Sin movimientos con esos filtros</div>
          )}

          {(d.movimientos||[]).map((m,i)=>(
            <div key={i} style={{display:"flex",gap:SPACING[8],padding:"8px 0",
              borderTop:`1px solid ${Q.border}`,alignItems:"flex-start"}}>
              <span style={{fontSize:14,flexShrink:0,marginTop:1}}>
                {ICONO[m.tipo]||"·"}</span>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12,lineHeight:1.35,
                  overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap",
                  fontFamily:F_BODY}}>
                  {m.titulo}</div>
                <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                  {m.cliente}{m.agencia&&` · ${m.agencia}`}
                  {" · "}{fechaHora(m.fecha)}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}>
                  {montoMovHist(m.apostado,m.moneda)}</div>
                {m.cobrado>0&&(
                  <div style={{color:Q.green,fontSize:12,fontWeight:700,
                    fontFamily:F_BODY}}>
                    +{montoMovHist(m.cobrado,m.moneda)}</div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// El número final por agencia, cruzando todo: deportivas, casino,
// desafíos y lo que ya venía debiendo.
//
// Sin esto hay que abrir tres pantallas y sumar a mano, y ahí es
// donde aparecen las diferencias con lo que la agencia dice que le
// corresponde.
function Liquidacion({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);
  const [abierta,setAbierta]=useState(null);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API}/api/admin/liquidacion-consolidada?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  // El equivalente de la comisión de desafíos va en la moneda de CADA
  // agencia, así que esta lista puede cruzar varias. Misma regla que el resto
  // del archivo: el código de moneda aparece cuando hay con qué
  // confundirse, y la unidad de decisión es la lista entera. La moneda vive
  // anidada en el bloque de desafíos, así que se le pasa a `formatoDeLista`
  // una lista plana con ese campo en lugar de duplicar la regla acá.
  const fmtEquiv=formatoDeLista(
    (d?.agencias||[]).map(a=>({moneda:a?.desafios?.equivalente_hoy_moneda})));

  // Los dos totales de la casa en moneda local bajaron a una lista. Sumaban
  // el `total_liquidar_pesos` de TODAS las agencias, y cada uno está en la
  // moneda de SU libro: el nombre decía "pesos" y adentro había pesos más
  // dólares. Las dos tarjetas de IACOIN no se tocan: son otra unidad y ya
  // estaban separadas.
  const liqTotales=Array.isArray(d?.por_moneda)?d.por_moneda:[];
  const variasLiq=liqTotales.length>1;

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}>
        <Icon name="wallet-cards" size={14}/> Liquidación</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Cuánto se le paga a cada agencia cruzando todos los productos.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:9}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Calculando…":"Calcular"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {d&&(
        <>
          {/* ── DOS ESCALAS, DOS NÚMEROS POR TARJETA ──────────────
              El total de la casa sumaba los `total_liquidar` de todas las
              agencias, y cada uno ya traía la comisión de desafíos —que está
              en IACOIN— adentro de un número en pesos: la mezcla se componía
              una vez por agencia. El backend ahora manda los cuatro totales
              separados y acá se dibujan separados.

              El renglón de IACOIN se dibuja con `money(..., "IACOIN")`, que
              escribe el código adelante porque no es una moneda con símbolo.
              No es un `ars()` nuevo: la pata de pesos sigue con el atajo que
              espera a M4-M6 y la de IACOIN no lo usa, porque su unidad no
              depende de la agencia. */}
          <div style={{display:"flex",gap:SPACING[8],margin:"12px 0"}}>
            <GCard style={{flex:1,padding:SPACING[16]}} glow={Q.red}>
              <div style={{color:Q.muted,fontSize:12}}>A pagar</div>
              {liqTotales.map(m=>(
                <div key={m.moneda} style={{color:Q.red,fontWeight:800,
                  fontSize:18,fontFamily:F_BODY}}>
                  {money(m.total_a_pagar,m.moneda,{conCodigo:variasLiq})}</div>
              ))}
              <div style={{color:Q.red,fontWeight:700,fontSize:13,marginTop:3,
                fontFamily:F_BODY}}>
                {money(d.total_a_pagar_iacoin, "IACOIN")}</div>
            </GCard>
            <GCard style={{flex:1,padding:SPACING[16]}} glow={Q.green}>
              <div style={{color:Q.muted,fontSize:12}}>A cobrar</div>
              {liqTotales.map(m=>(
                <div key={m.moneda} style={{color:Q.green,fontWeight:800,
                  fontSize:18,fontFamily:F_BODY}}>
                  {money(m.total_a_cobrar,m.moneda,{conCodigo:variasLiq})}</div>
              ))}
              <div style={{color:Q.green,fontWeight:700,fontSize:13,
                marginTop:3,fontFamily:F_BODY}}>
                {money(d.total_a_cobrar_iacoin, "IACOIN")}</div>
            </GCard>
          </div>

          {(d.agencias||[]).length===0&&(
            <GCard style={{padding:SPACING[20],textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:12.5,
                fontFamily:F_BODY}}>
                Ninguna agencia tiene movimiento en ese período</div>
            </GCard>
          )}

          {/* ── CADA FILA ES UNA SOLA AGENCIA, Y POR LO TANTO UNA SOLA
              MONEDA ──
              `a.moneda` es la del LIBRO de esa agencia, y con ella se
              dibujan sus siete cifras en moneda local. No se lee de
              `a.desafios.equivalente_hoy_moneda`: ése es el rótulo de la
              conversión del IACOIN y atar la unidad de la plata de la
              agencia a ese sub-objeto es el "campo moneda vecino" que este
              archivo viene a sacar. Las dos tarjetas de arriba son otra
              cosa: suman TODAS las agencias y por eso no se rotulan. */}
          {(d.agencias||[]).map(a=>(
            <div key={a.code} style={{borderTop:`1px solid ${Q.border}`}}>
              <div onClick={()=>setAbierta(abierta===a.code?null:a.code)}
                style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",gap:SPACING[8],padding:"12px 0",
                  cursor:"pointer"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontSize:12.5,fontWeight:600,
                    overflow:"hidden",textOverflow:"ellipsis",
                    whiteSpace:"nowrap",
                    fontFamily:F_BODY}}>
                    {a.nombre}</div>
                  <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                    {a.deportivas.comision>0&&
                      `dep ${money(a.deportivas.comision, a.moneda)}`}
                    {a.casino.comision>0&&
                      ` · casino ${money(a.casino.comision, a.moneda)}`}
                    {/* Los desafíos van con su unidad escrita: en este
                        renglón conviven con dos cifras en moneda local y sin
                        el código se leen como si fueran lo mismo. */}
                    {a.desafios.total_iacoin!==0&&
                      ` · desafíos ${money(a.desafios.total_iacoin,
                                           a.desafios.moneda||"IACOIN")}`}</div>
                </div>
                {/* El total de la agencia, también en dos renglones. El de
                    IACOIN sólo aparece si hay movimiento: una agencia que no
                    juega desafíos no tiene por qué ver un cero. */}
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:a.total_liquidar_pesos>=0?Q.red:Q.green,
                    fontSize:15,fontWeight:800,
                    fontFamily:F_BODY}}>
                    {money(Math.abs(a.total_liquidar_pesos), a.moneda)}</div>
                  <div style={{color:Q.dim,fontSize:12}}>
                    {a.total_liquidar_pesos>=0?"le pagamos":"nos debe"}</div>
                  {a.desafios.total_iacoin!==0&&(
                    <div style={{color:a.desafios.total_iacoin>=0?Q.red:Q.green,
                      fontSize:12.5,fontWeight:700,marginTop:3,
                      fontFamily:F_BODY}}>
                      {money(Math.abs(a.desafios.total_iacoin),
                             a.desafios.moneda||"IACOIN")}
                      <span style={{color:Q.dim,fontWeight:400}}>
                        {" "}{a.desafios.total_iacoin>=0
                                ?"le pagamos":"nos debe"}</span></div>
                  )}
                </div>
              </div>

              {abierta===a.code&&(
                <div style={{paddingBottom:SPACING[12],paddingLeft:SPACING[8]}}>
                  {/* El detalle va en DOS listas. Antes era una sola con las
                      cinco filas juntas y las dos de desafíos dibujadas con
                      `ars()`: el IACOIN salía con el símbolo de la moneda
                      local y el que miraba la columna sumaba de arriba abajo.
                      Cada lista lleva su propio total, y no hay ninguno que
                      sume las dos. */}
                  {[["Deportivas",a.deportivas.comision,
                     `GGR ${money(a.deportivas.ggr, a.moneda)}`],
                    ["Casino",a.casino.comision,
                     `GGR ${money(a.casino.ggr, a.moneda)}`],
                    ["Cuenta corriente anterior",a.cuenta_corriente,""],
                  ].filter(([,v])=>v!==0).map(([l,v,ay])=>(
                    <div key={l} style={{display:"flex",
                      justifyContent:"space-between",fontSize:12,
                      padding:"4px 0",
                      fontFamily:F_BODY}}>
                      <span style={{color:Q.muted,minWidth:0,flex:1}}>
                        {l}
                        {ay&&<span style={{color:Q.dim,fontSize:12,
                          display:"block",lineHeight:1.3}}>{ay}</span>}</span>
                      <span style={{color:v>=0?Q.text:Q.amber,
                        flexShrink:0,marginLeft:8}}>
                        {v>=0?"+":""}{money(v, a.moneda)}</span>
                    </div>
                  ))}
                  <div style={{display:"flex",
                    justifyContent:"space-between",fontSize:12.5,
                    paddingTop:SPACING[8],marginTop:5,
                    borderTop:`1px solid ${Q.border}`,
                    fontFamily:F_BODY}}>
                    <span style={{color:Q.text,fontWeight:700}}>
                      Total en moneda local</span>
                    <span style={{color:a.total_liquidar_pesos>=0
                                          ?Q.red:Q.green,
                      fontWeight:800}}>
                      {money(a.total_liquidar_pesos, a.moneda)}</span>
                  </div>

                  {a.desafios.total_iacoin!==0&&(
                    <div style={{marginTop:SPACING[12]}}>
                      <div style={{color:Q.muted,fontSize:12,
                        letterSpacing:0.6,marginBottom:3,
                        fontFamily:F_BODY}}>
                        DESAFÍOS · {a.desafios.moneda||"IACOIN"}</div>
                      {[["Comisión",a.desafios.comision,""],
                        ["Movimiento",a.desafios.saldo_movimiento,
                         "lo que cobró de sus jugadores contra lo que paga"],
                      ].filter(([,v])=>v!==0).map(([l,v,ay])=>(
                        <div key={l} style={{display:"flex",
                          justifyContent:"space-between",fontSize:12,
                          padding:"4px 0",
                          fontFamily:F_BODY}}>
                          <span style={{color:Q.muted,minWidth:0,flex:1}}>
                            {l}
                            {ay&&<span style={{color:Q.dim,fontSize:12,
                              display:"block",lineHeight:1.3}}>{ay}</span>}
                            </span>
                          <span style={{color:v>=0?Q.text:Q.amber,
                            flexShrink:0,marginLeft:8}}>
                            {v>=0?"+":""}{money(v,
                              a.desafios.moneda||"IACOIN")}</span>
                        </div>
                      ))}
                      <div style={{display:"flex",
                        justifyContent:"space-between",fontSize:12.5,
                        paddingTop:SPACING[8],marginTop:5,
                        borderTop:`1px solid ${Q.border}`,
                        fontFamily:F_BODY}}>
                        <span style={{color:Q.text,fontWeight:700}}>
                          Total en {a.desafios.moneda||"IACOIN"}</span>
                        <span style={{color:a.desafios.total_iacoin>=0
                                              ?Q.red:Q.green,
                          fontWeight:800}}>
                          {money(a.desafios.total_iacoin,
                                 a.desafios.moneda||"IACOIN")}</span>
                      </div>
                      {/* ── Y CUÁNTO SON ESOS IACOIN HOY ────────────
                          Desde el 2026-10-27 la comisión de desafíos se le
                          paga a la agencia en SU moneda, a la cotización del
                          día. El rótulo dice "hoy" porque la cotización la
                          fija un humano y cambia: un valor convertido sin
                          fecha miente mañana.

                          UN TERCER RENGLÓN, NO UN SUMANDO. No se suma al
                          total en moneda local de arriba ni al de IACOIN de
                          acá: son tres cosas distintas y juntarlas es el
                          defecto que esta pantalla ya arregló una vez.

                          Y VIVE ACÁ ADENTRO Y NO EN EL RENGLÓN PLEGADO, a
                          propósito. Arriba conviven `dep $500` y
                          `casino $500`, los dos en moneda local: un tercer
                          monto en moneda local en esa misma fila se lee como
                          otro sumando de la columna. Acá está debajo del
                          rótulo DESAFÍOS y pegado al número del que sale.

                          Y NO LLEVA `ars()`: la moneda la manda el backend
                          por fila, desde `agencias.moneda`. Esta lista cruza
                          agencias en monedas distintas, así que el código va
                          cuando hay con qué confundirse, con la misma regla
                          de `formatoDeLista` que el resto del archivo. */}
                      {typeof a.desafios.equivalente_hoy==="number"&&(
                        <div style={{display:"flex",
                          justifyContent:"space-between",fontSize:12,
                          paddingTop:SPACING[8],marginTop:5,
                          borderTop:`1px solid ${Q.border}`,
                          fontFamily:F_BODY}}>
                          <span style={{color:Q.muted,minWidth:0,flex:1}}>
                            Hoy son
                            {a.desafios.cotizacion_de_hoy?(
                              <span style={{color:Q.dim,fontSize:12,
                                display:"block",lineHeight:1.3}}>
                                1 IACOIN = {money(
                                  a.desafios.cotizacion_de_hoy,
                                  a.desafios.equivalente_hoy_moneda)}</span>
                            ):null}</span>
                          <span style={{color:Q.text,fontWeight:700,
                            flexShrink:0,marginLeft:8}}>
                            {fmtEquiv(a.desafios.equivalente_hoy,
                                      a.desafios.equivalente_hoy_moneda)}
                            </span>
                        </div>
                      )}
                      {/* Sin cotización para esa moneda el backend manda
                          `null` y el motivo. No se dibuja un cero: se vería
                          igual que "no se le debe nada". */}
                      {typeof a.desafios.equivalente_hoy!=="number"
                        &&a.desafios.equivalente_hoy_error&&(
                        <div style={{color:Q.amber,fontSize:12,marginTop:6,
                          lineHeight:1.4,fontFamily:F_BODY}}>
                          Sin convertir a {a.desafios.equivalente_hoy_moneda}:
                          {" "}{a.desafios.equivalente_hoy_error}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {d.sin_movimiento>0&&(
            <div style={{color:Q.dim,fontSize:12,marginTop:10,
              fontFamily:F_BODY}}>
              {d.sin_movimiento} agencias sin movimiento, no se listan.</div>
          )}

          <div style={{color:Q.dim,fontSize:12,marginTop:10,
            lineHeight:1.5,fontFamily:F_BODY}}>
            {d.nota}</div>
        </>
      )}
    </div>
  );
}

// Quién le debe a quién por los desafíos.
//
// La agencia cobra el efectivo de su jugador. Si su jugador perdió,
// esa plata es del sistema menos su rake. Si ganó, tiene que pagarle
// el pozo y el sistema le transfiere la diferencia.
function CCDesafios({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API}/api/admin/desafios/cuenta-corriente?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:13.5,
        marginBottom:4,fontFamily:F_BODY}}>
        Cuenta corriente</div>
      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Cada agencia cobró el efectivo de su jugador. Acá se ve qué
        parte es del sistema y qué parte hay que transferirle.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:9}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Cargando…":"Ver"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {d&&(
        <>
          {/* Toda la cuenta corriente de desafíos está en IACOIN y ninguno
              de estos números lo decía. Va con `money(x,"IACOIN")`, la
              forma que este archivo ya usa en `Liquidacion`. */}
          <div style={{display:"flex",gap:SPACING[8],margin:"12px 0"}}>
            <GCard style={{flex:1,padding:SPACING[12]}}>
              <div style={{color:Q.muted,fontSize:12}}>El sistema debe</div>
              <div style={{color:Q.red,fontWeight:800,fontSize:17,
                fontFamily:F_BODY}}>
                {money(d.sistema_debe, "IACOIN")}</div>
            </GCard>
            <GCard style={{flex:1,padding:SPACING[12]}}>
              <div style={{color:Q.muted,fontSize:12}}>El sistema cobra</div>
              <div style={{color:Q.green,fontWeight:800,fontSize:17,
                fontFamily:F_BODY}}>
                {money(d.sistema_cobra, "IACOIN")}</div>
            </GCard>
          </div>

          <GCard glow={d.neto_sistema>=0?Q.green:Q.amber}
            style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center"}}>
              <span style={{color:Q.text,fontSize:13,fontWeight:700,
                fontFamily:F_BODY}}>
                Neto para la casa</span>
              <span style={{color:d.neto_sistema>=0?Q.green:Q.amber,
                fontWeight:900,fontSize:19,
                fontFamily:F_BODY}}>
                {money(d.neto_sistema, "IACOIN")}</span>
            </div>
            {d.neto_sistema<0&&(
              <div style={{color:Q.amber,fontSize:12,marginTop:6,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Está en negativo: el rake se reparte entero entre las
                agencias. Bajá su porcentaje si querés que la casa se
                quede con una parte.</div>
            )}
          </GCard>

          {(d.cuentas||[]).length===0&&(
            <GCard style={{padding:SPACING[20],textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:12.5,
                fontFamily:F_BODY}}>
                No hubo desafíos en ese período</div>
            </GCard>
          )}

          {(d.cuentas||[]).map(c=>(
            <div key={c.code||"sis"} style={{padding:"12px 0",
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{display:"flex",
                justifyContent:"space-between",alignItems:"baseline",
                gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontSize:12.5,
                    fontWeight:600,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap",
                    fontFamily:F_BODY}}>
                    {c.nombre}</div>
                  <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                    {c.desafios} desafíos · cobró{" "}
                    {money(c.cobrado_a_jugadores, "IACOIN")}
                    {c.premios_a_pagar>0&&
                      ` · paga ${money(c.premios_a_pagar, "IACOIN")}`}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:c.saldo>=0?Q.green:Q.red,
                    fontSize:14,fontWeight:800,
                    fontFamily:F_BODY}}>
                    {c.saldo>=0?"+":""}{money(c.saldo, "IACOIN")}</div>
                  <div style={{color:Q.dim,fontSize:12}}>
                    {c.saldo>=0?"le transferimos":"nos debe"}</div>
                </div>
              </div>
              {c.rake>0&&(
                <div style={{color:Q.gold,fontSize:12,marginTop:3,
                  fontFamily:F_BODY}}>
                  ganó {money(c.rake, "IACOIN")} de comisión</div>
              )}
            </div>
          ))}

          <div style={{color:Q.dim,fontSize:12,marginTop:12,
            lineHeight:1.5,fontFamily:F_BODY}}>
            {d.nota}</div>
        </>
      )}
    </div>
  );
}

function ReporteDesafios({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API}/api/admin/desafios/reporte?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Cargando…":"Ver"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {d&&(
        <>
          <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
            <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
              marginBottom:9}}>EL PRODUCTO EN IACOIN</div>
            <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Desafíos</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:18,
                  fontFamily:F_BODY}}>
                  {d.total.desafios}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Jugadores</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:18,
                  fontFamily:F_BODY}}>
                  {d.total.jugadores}</div>
              </div>
              {/* "Desafíos" y "Jugadores" de al lado son CANTIDADES y no
                  llevan unidad; "Jugado" es plata en IACOIN y en la misma
                  fila se leía igual que ellas. Lleva la unidad por eso.

                  Acá el número SÍ cambia: iba con
                  `{maximumFractionDigits:0}` y `money` dibuja los dos
                  decimales de la unidad. Es el precio de que la columna
                  diga en qué está. */}
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Jugado</div>
                <div style={{color:Q.gold,fontWeight:800,fontSize:18,
                  fontFamily:F_BODY}}>
                  {money(d.total.jugado, "IACOIN")}</div>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>
                Comisión
                {d.total.rake_pct!=null&&(
                  <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                    {d.total.rake_pct}% de lo jugado</span>
                )}</span>
              <span style={{color:Q.green,fontWeight:900,fontSize:20,
                fontFamily:F_BODY}}>
                {money(d.total.comision, "IACOIN")}</span>
            </div>
          </GCard>

          <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
            <GCard style={{flex:1,padding:SPACING[12],textAlign:"center"}}>
              <div style={{color:Q.gold,fontWeight:800,fontSize:17,
                fontFamily:F_BODY}}>
                {money(d.iacoin_comprados, "IACOIN")}</div>
              {/* El rótulo decía "IACOIN comprados" y la unidad ahora viaja
                  con el número, así que se retira del rótulo para no
                  escribirla dos veces en la misma tarjeta. */}
              <div style={{color:Q.muted,fontSize:12}}>comprados</div>
            </GCard>
            <GCard style={{flex:1,padding:SPACING[12],textAlign:"center"}}
              glow={d.disputas_abiertas>0?Q.amber:null}>
              <div style={{color:d.disputas_abiertas>0?Q.amber:Q.text,
                fontWeight:800,fontSize:17,
                fontFamily:F_BODY}}>
                {d.disputas_abiertas}</div>
              <div style={{color:Q.muted,fontSize:12}}>disputas abiertas</div>
            </GCard>
          </div>

          {Object.keys(d.estados||{}).length>0&&(
            <GCard style={{padding:SPACING[12],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.5,
                marginBottom:7}}>ESTADO DE LOS DESAFÍOS</div>
              {Object.entries(d.estados).map(([k,v])=>(
                <div key={k} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,
                  padding:"4px 0"}}>
                  <span style={{color:Q.muted}}>
                    {{abierta:"Esperando que alguien los tome",
                      tomada:"En juego",resuelta:"Terminados",
                      anulada:"Cancelados",
                      vencida:"Vencidos sin tomar"}[k]||k}</span>
                  <span style={{color:Q.text}}>{v}</span>
                </div>
              ))}
            </GCard>
          )}

          {(d.por_agencia||[]).length>0&&(
            <GCard style={{padding:SPACING[12]}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.5,
                marginBottom:8}}>POR AGENCIA</div>
              {d.por_agencia.map(a=>(
                <div key={a.code} style={{display:"flex",
                  justifyContent:"space-between",alignItems:"baseline",
                  padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{color:Q.text,fontSize:12.5}}>
                      {a.nombre||a.code}</div>
                    <div style={{color:Q.dim,fontSize:12}}>
                      {a.desafios} desafíos · {a.jugadores} jugadores</div>
                  </div>
                  <span style={{color:Q.green,fontSize:13,fontWeight:700,
                    flexShrink:0,
                    fontFamily:F_BODY}}>
                    {money(a.comision, "IACOIN")}</span>
                </div>
              ))}
            </GCard>
          )}

          <div style={{color:Q.dim,fontSize:12,marginTop:12,
            lineHeight:1.5,fontFamily:F_BODY}}>
            {d.nota}</div>
        </>
      )}
    </div>
  );
}

// Qué juego generó cada peso. Sin este desglose el reporte solo
// puede decir cuánto dejó el casino en total, y no cuáles conviene
// mostrar primero ni cuáles no trabajan.
function ReporteJuegos({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API}/api/admin/casino/reporte-juegos?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  // La plata del total viene agrupada por moneda: un juego se le ofrece a
  // todas las agencias, así que su apostado y su GGR sumaban pesos con
  // dólares. La guarda es por si contesta una respuesta vieja de caché o si
  // el frontend queda desfasado del backend en un despliegue: mejor sin
  // plata que una pestaña que revienta por un `.map` sobre `undefined`.
  const monedasTot=Array.isArray(d?.total?.por_moneda)?d.total.por_moneda:[];
  // Con UNA sola moneda esta pantalla tiene que verse IGUAL que hoy: mismo
  // KPI, misma línea de GGR, sin rótulo nuevo. El código aparece sólo cuando
  // hay con qué confundirse: ARS, CLP, COP y MXN comparten el signo.
  const variasMonedas=monedasTot.length>1;
  const rotulo=(texto,m)=>variasMonedas?`${texto} · ${m.moneda}`:texto;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:13.5,
        marginBottom:8,fontFamily:F_BODY}}>
        Por juego</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:9}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Cargando…":"Ver"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {d&&(
        <>
          <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
            {/* Las cuentas son planas —una jugada es una jugada en
                cualquier moneda— y la plata se repite por moneda. */}
            <div style={{display:"flex",gap:SPACING[12],marginBottom:9,
              flexWrap:"wrap"}}>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Jugadas</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:17,
                  fontFamily:F_BODY}}>
                  {d.total.jugadas.toLocaleString("es-AR")}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Jugadores</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:17,
                  fontFamily:F_BODY}}>
                  {d.total.jugadores}</div>
              </div>
              {monedasTot.map(m=>(
                <div key={m.moneda} style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {rotulo("Apostado",m)}</div>
                  <div style={{color:Q.gold,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(m.apostado,m.moneda)}</div>
                </div>
              ))}
            </div>
            {monedasTot.map(m=>(
            <div key={m.moneda} style={{display:"flex",
              justifyContent:"space-between",
              alignItems:"center",paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>
                {rotulo("GGR",m)}
                {m.margen_pct!=null&&(
                  <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                    margen {m.margen_pct}%</span>
                )}</span>
              <span style={{color:m.ggr>=0?Q.green:Q.red,
                fontWeight:900,fontSize:20,
                fontFamily:F_BODY}}>
                {money(m.ggr,m.moneda)}</span>
            </div>
            ))}
          </GCard>

          {d.aviso&&(
            <div style={{color:Q.amber,fontSize:12,marginBottom:10,
              lineHeight:1.5,fontFamily:F_BODY}}>
              <Icon name="triangle-alert" size={12}/> {d.aviso}</div>
          )}

          {d.juegos.length===0&&(
            <GCard style={{padding:SPACING[20],textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:12.5,
                fontFamily:F_BODY}}>
                Todavía no hay jugadas en ese período</div>
            </GCard>
          )}

          {/* La clave lleva la moneda: con dos monedas el MISMO `game_id`
              vuelve en dos filas, y una clave repetida le hace a React
              reusar la fila equivocada. */}
          {d.juegos.map((j,i)=>(
            <div key={`${j.game_id||i}-${j.moneda}`} style={{display:"flex",
              justifyContent:"space-between",alignItems:"baseline",
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12.5,
                  overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap",
                  fontFamily:F_BODY}}>
                  {j.juego}</div>
                <div style={{color:Q.dim,fontSize:12}}>
                  {j.jugadas.toLocaleString("es-AR")} jugadas ·{" "}
                  {j.jugadores} jugadores ·{" "}
                  {money(j.apostado,j.moneda)}
                  {j.margen_pct!=null&&` · ${j.margen_pct}%`}</div>
              </div>
              {/* Cada fila es un juego EN UNA MONEDA, y con varias en
                  juego el mismo juego aparece dos veces: ahí el código no
                  es decorado, es lo único que distingue las dos filas. */}
              <span style={{color:j.ggr>=0?Q.green:Q.red,fontSize:13.5,
                fontWeight:700,flexShrink:0,marginLeft:8,
                fontFamily:F_BODY}}>
                {money(j.ggr,j.moneda,{conCodigo:variasMonedas})}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Asignar el logo de cada proveedor.
//
// Los nombres de archivo no siguen ninguna regla —espacios,
// mayúsculas, abreviaturas, algunos llamados "logo 01"— así que
// derivarlos del nombre de la marca falla en la mayoría. Acá se
// asigna a mano una vez y queda guardado.
function LogosCasino({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [editando,setEditando]=useState(null);
  const [archivo,setArchivo]=useState("");

  const cargar=()=>{
    fetch(`${API}/api/admin/casino/logos`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const automatico=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/casino/logos-automatico`,{
        method:"POST",headers:adminHeaders(adminKey)});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:`${x.asignados} asignados`+
        (x.faltan?.length?` · faltan ${x.faltan.length}: `+
          x.faltan.slice(0,6).join(", "):""), ok:true});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const guardar=async(marca,arch)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/casino/logos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({marca, archivo:arch})});
      if(!r.ok) throw new Error("No se pudo");
      setMsg({text:arch?marca:`Quitado de ${marca}`, ok:true});
      setEditando(null); setArchivo(""); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:4,
        fontFamily:F_BODY}}>
        Logos de proveedores</div>
      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        lineHeight:1.5,fontFamily:F_BODY}}>
        {d.con_logo} de {d.total} asignados. Escribí el nombre del
        archivo tal cual está en el repositorio, con espacios y
        mayúsculas si los tiene.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:9,
        textAlign:"center",lineHeight:1.45}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {/* Las que se pueden deducir por el nombre, de una vez. Los
          archivos "logo 01" a "logo 09" no dicen de qué proveedor
          son, así que esos quedan para asignar a mano. */}
      <button onClick={automatico} disabled={proc}
        style={{width:"100%",background:`${Q.cyan}18`,
          border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,
          padding:"12px",color:Q.cyan,fontSize:12.5,fontWeight:700,
          cursor:"pointer",marginBottom:12,
          fontFamily:F_BODY}}>
        <Zap size={13}/> Asignar los que coinciden</button>

      {(d.marcas||[]).map(m=>(
        <div key={m.marca} style={{padding:"8px 0",
          borderTop:`1px solid ${Q.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",gap:SPACING[8]}}>
            {m.archivo&&(
              <img src={`/${m.archivo}`} alt="" loading="lazy"
                onError={e=>{e.target.style.opacity="0.15";}}
                style={{width:32,height:32,objectFit:"contain",
                  flexShrink:0}}/>
            )}
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontSize:12.5,
                fontFamily:F_BODY}}>{m.marca}</div>
              <div style={{color:m.archivo?Q.green:Q.dim,fontSize:12,
                overflow:"hidden",textOverflow:"ellipsis",
                whiteSpace:"nowrap"}}>
                {m.archivo||"sin logo"}</div>
            </div>
            <button onClick={()=>{
                setEditando(editando===m.marca?null:m.marca);
                setArchivo(m.archivo||""); }}
              style={{background:`${Q.violet}22`,
                border:`1px solid ${Q.violet}66`,borderRadius:RADII.sm,
                padding:"8px 12px",color:Q.cyan,fontSize:12,
                cursor:"pointer",flexShrink:0}}>
              {m.archivo?"Cambiar":"Asignar"}</button>
          </div>

          {editando===m.marca&&(
            <div style={{marginTop:8,paddingLeft:SPACING[4]}}>
              <input value={archivo}
                onChange={e=>setArchivo(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter")
                  guardar(m.marca, archivo.trim()); }}
                placeholder="ej: real dealer.png"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"8px 12px",color:Q.text,fontSize:12.5,
                  marginBottom:6,
                  fontFamily:F_BODY}}/>
              {archivo.trim()&&(
                <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
                  marginBottom:6}}>
                  <img src={`/${archivo.trim()}`} alt=""
                    onError={e=>{e.target.style.display="none";
                      e.target.nextSibling.style.display="block";}}
                    onLoad={e=>{e.target.style.display="block";
                      e.target.nextSibling.style.display="none";}}
                    style={{height:30,maxWidth:90,objectFit:"contain"}}/>
                  <span style={{color:Q.red,fontSize:12,display:"none"}}>
                    ese archivo no existe</span>
                </div>
              )}
              <div style={{display:"flex",gap:SPACING[8]}}>
                <button onClick={()=>guardar(m.marca, archivo.trim())}
                  disabled={proc}
                  style={{flex:1,background:`${Q.green}18`,
                    border:`1px solid ${Q.green}66`,borderRadius:RADII.md,
                    padding:"8px",color:Q.green,fontSize:12,
                    fontWeight:700,cursor:"pointer"}}>Guardar</button>
                {m.archivo&&(
                  <button onClick={()=>guardar(m.marca,"")}
                    disabled={proc}
                    style={{background:"transparent",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",color:Q.muted,fontSize:12,
                      cursor:"pointer"}}>Quitar</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Las marcas del catálogo, con lo que dejó cada una. Apagar una la
// saca de todos los locales: por eso conviene mirar el margen y el
// volumen antes, no decidir a ojo.
function ProveedoresCasino({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [filtro,setFiltro]=useState("todos");
  const [busq,setBusq]=useState("");
  // Para apagar en una agencia puntual en vez de globalmente
  const [agSel,setAgSel]=useState("");
  const [ags,setAgs]=useState([]);
  const [porAgencia,setPorAgencia]=useState([]);

  useEffect(()=>{
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAgs(Array.isArray(d)?d:(d.agencias||[])))
      .catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const cargar=()=>{
    fetch(`${API}/api/admin/casino/proveedores`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const uno=async(marca,activo)=>{
    let motivo=null;
    if(!activo){
      motivo=window.prompt(`¿Por qué se apaga ${marca}? Queda registrado.`);
      if(motivo===null) return;
    }
    setProc(true);
    try{
      await fetch(
        `${API}/api/admin/casino/proveedores/${encodeURIComponent(marca)}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({activo, motivo})});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  // Apagar para una agencia puntual y su rama
  const porAg=async(marca)=>{
    const nom=ags.find(a=>a.code===agSel)?.name||agSel;
    const motivo=window.prompt(
      `¿Apagar ${marca} solo para ${nom} y su rama? Escribí el motivo.`);
    if(motivo===null) return;
    setProc(true);
    try{
      const r=await fetch(`${API}/api/admin/casino/proveedores-agencia`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({agencia_code:agSel, marca,
                             activo:false, motivo})});
      if(!r.ok) throw new Error("No se pudo");
      setMsg({text:`${marca} apagado en ${nom}`, ok:true});
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  // Prender o apagar solo una superficie, sin tocar el proveedor
  const superficie=async(marca,campo,valor)=>{
    setProc(true);
    try{
      const r=await fetch(
        `${API}/api/admin/casino/proveedores/${encodeURIComponent(marca)}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({[campo]:valor})});
      if(!r.ok) throw new Error("No se pudo");
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const masivo=async(alcance,activo)=>{
    const q={todos:"todos los proveedores",vivo:"los de crupier en vivo",
      slots:"las tragamonedas"}[alcance];
    if(!window.confirm(
      `¿${activo?"Prender":"Apagar"} ${q}? Afecta a todos los locales.`))
      return;
    let motivo=null;
    if(!activo){
      motivo=window.prompt("Motivo (queda registrado)");
      if(motivo===null) return;
    }
    setProc(true);
    try{
      const r=await fetch(`${API}/api/admin/casino/proveedores-masivo`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({alcance, activo, motivo})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:`${x.cambiados} proveedores`, ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const lista=(d.proveedores||[]).filter(p=>{
    if(filtro==="vivo"&&!p.es_vivo) return false;
    if(filtro==="slots"&&p.es_vivo) return false;
    if(filtro==="apagados"&&p.activo) return false;
    if(busq.trim()&&!p.marca.toLowerCase()
        .includes(busq.toLowerCase().trim())) return false;
    return true;
  });

  // Un proveedor se le ofrece a TODAS las agencias, así que lo que dejó en
  // 30 días viene agrupado por moneda: sumarlo era pesos más dólares.
  //
  // Si hay más de una moneda en toda la LISTA, los montos llevan el código,
  // aunque una fila tenga una sola: el riesgo acá no es confundir dos
  // monedas dentro de un proveedor, es leer el `$` de un proveedor en pesos
  // al lado del `$` de otro en dólares, en la misma columna. Con una sola
  // moneda la pantalla se ve igual que hoy.
  const monedasDeLaLista=new Set();
  for(const p of (d.proveedores||[]))
    for(const m of (p.por_moneda_30d||[])) monedasDeLaLista.add(m.moneda);
  const variasMonedas=monedasDeLaLista.size>1;

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <div style={{color:Q.muted,fontSize:12,marginBottom:10,
        lineHeight:1.5,fontFamily:F_BODY}}>
        {d.total} proveedores
        {d.apagados>0&&`, ${d.apagados} apagados`}. Apagar uno lo saca
        de todos los locales.</div>

      {/* Apagar por agencia o rama, no solo global. Un local puede
          tener condiciones distintas con un proveedor. */}
      <GCard style={{padding:SPACING[12],marginBottom:12}}>
        <div style={{color:Q.text,fontSize:12.5,fontWeight:700,
          marginBottom:7,fontFamily:F_BODY}}>
          Para una agencia en particular</div>
        <select value={agSel} onChange={e=>setAgSel(e.target.value)}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"8px 12px",color:Q.text,fontSize:12.5,
            marginBottom:agSel?8:0,
            fontFamily:F_BODY}}>
          <option value="">Elegí una agencia…</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>
              {"— ".repeat(a.nivel||0)}{a.name}</option>
          ))}
        </select>
        {agSel&&(
          <div style={{color:Q.dim,fontSize:12,lineHeight:1.5}}>
            Tocá un proveedor de la lista para apagarlo solo en{" "}
            {ags.find(a=>a.code===agSel)?.name} y su rama.</div>
        )}
      </GCard>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <button onClick={()=>masivo("todos",true)} disabled={proc}
          style={{flex:1,background:`${Q.green}14`,
            border:`1px solid ${Q.green}55`,borderRadius:RADII.md,
            padding:"8px 8px",color:Q.green,fontSize:12,
            cursor:"pointer",
            fontFamily:F_BODY}}>Prender todos</button>
        <button onClick={()=>masivo("slots",false)} disabled={proc}
          style={{flex:1,background:"transparent",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"8px 8px",color:Q.muted,fontSize:12,
            cursor:"pointer",
            fontFamily:F_BODY}}>Apagar slots</button>
        <button onClick={()=>masivo("vivo",false)} disabled={proc}
          style={{flex:1,background:"transparent",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"8px 8px",color:Q.muted,fontSize:12,
            cursor:"pointer",
            fontFamily:F_BODY}}>Apagar vivo</button>
      </div>

      <input value={busq} onChange={e=>setBusq(e.target.value)}
        placeholder="Buscar proveedor…"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,
          padding:"12px 12px",color:Q.text,fontSize:13,marginBottom:8,
          fontFamily:F_BODY}}/>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,
        overflowX:"auto"}}>
        {[["todos","Todos"],["slots",<><Icon name="spade" size={12}/> Slots</>],["vivo",<><Video size={12}/> En vivo</>],
          ["apagados","Apagados"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFiltro(k)}
            style={{background:filtro===k?`${Q.violet}33`:"transparent",
              border:`1px solid ${filtro===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:filtro===k?Q.cyan:Q.muted,fontSize:12,
              whiteSpace:"nowrap",flexShrink:0,
              fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {lista.length===0&&(
        <div style={{color:Q.muted,textAlign:"center",padding:SPACING[24],
          fontSize:12.5,fontFamily:F_BODY}}>
          No hay proveedores con ese filtro</div>
      )}

      {/* Todo junto: apagar, dónde se muestra y forzar. Antes eran
          dos listas con los mismos proveedores y había que buscar el
          mismo nombre dos veces para hacer dos cosas relacionadas. */}
      {lista.map(p=>(
        <div key={p.marca} style={{padding:"12px 0",
          borderTop:`1px solid ${Q.border}`,
          opacity:p.activo?1:0.55}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",gap:SPACING[8]}}>
            {/* El logo si existe. Si el archivo no está, se esconde
                solo y queda el nombre, que es lo que había antes. */}
            {p.logo&&(
              <img src={p.logo} alt="" loading="lazy"
                onError={e=>{e.target.style.display="none";}}
                style={{width:34,height:34,objectFit:"contain",
                  borderRadius:RADII.sm,flexShrink:0,
                  opacity:p.activo?1:0.4}}/>
            )}
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:p.activo?Q.text:Q.dim,fontSize:12.5,
                fontWeight:600,
                fontFamily:F_BODY}}>
                {p.es_vivo&&<Video size={11}/>} {p.marca}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                {p.juegos} juegos
                {p.jugadas_30d>0&&` · ${p.jugadas_30d.toLocaleString("es-AR")} jugadas`}
                {(p.por_moneda_30d||[]).map(m=>m.margen==null?""
                  :` · margen ${m.margen}%`
                    +(variasMonedas?` ${m.moneda}`:"")).join("")}</div>
              {!p.activo&&p.motivo&&(
                <div style={{color:Q.red,fontSize:12,marginTop:2,
                  lineHeight:1.4}}>{p.motivo}</div>
              )}
            </div>
            {/* El GGR, uno por moneda. Un proveedor sin jugadas en 30 días
                trae la lista vacía y no dibuja nada, que es lo mismo que
                hacía el `!==0` de antes. */}
            {(p.por_moneda_30d||[]).filter(m=>m.ggr!==0).map(m=>(
              <span key={m.moneda}
                style={{color:m.ggr>=0?Q.green:Q.red,fontSize:12,
                fontWeight:700,flexShrink:0,
                fontFamily:F_BODY}}>
                {money(m.ggr,m.moneda,{conCodigo:variasMonedas})}</span>
            ))}
            <button onClick={()=>agSel?porAg(p.marca):uno(p.marca,!p.activo)}
              disabled={proc}
              style={{background:p.activo?"transparent":`${Q.green}18`,
                border:`1px solid ${p.activo?Q.border:Q.green}66`,
                borderRadius:RADII.sm,padding:"8px 12px",
                color:p.activo?Q.muted:Q.green,fontSize:12,
                cursor:"pointer",flexShrink:0}}>
              {p.activo?"Apagar":"Prender"}</button>
          </div>

          {/* Dónde se muestra. Solo si está prendido: apagado no hay
              nada que decidir. Y no aparece con una agencia elegida,
              porque la superficie es una decisión global. */}
          {p.activo&&!agSel&&(
            <div style={{display:"flex",gap:SPACING[4],marginTop:8,
              paddingLeft:SPACING[4]}}>
              <button onClick={()=>superficie(p.marca,"en_app",!p.en_app)}
                disabled={proc}
                style={{background:p.en_app?`${Q.green}14`:"transparent",
                  border:`1px solid ${p.en_app?`${Q.green}55`:Q.border}`,
                  borderRadius:RADII.sm,padding:"4px 12px",
                  color:p.en_app?Q.green:Q.dim,fontSize:12,
                  cursor:"pointer",
                  fontFamily:F_BODY}}>App</button>
              <button onClick={()=>superficie(p.marca,"en_web",!p.en_web)}
                disabled={proc}
                style={{background:p.en_web?`${Q.green}14`:"transparent",
                  border:`1px solid ${p.en_web?`${Q.green}55`:Q.border}`,
                  borderRadius:RADII.sm,padding:"4px 12px",
                  color:p.en_web?Q.green:Q.dim,fontSize:12,
                  cursor:"pointer",
                  fontFamily:F_BODY}}>Web</button>
              {p.es_vivo ? (
                <span style={{color:Q.dim,fontSize:12,alignSelf:"center",
                  marginLeft:4,fontFamily:F_BODY}}>
                  las mesas van a la app igual</span>
              ) : (
                <button onClick={()=>superficie(p.marca,"forzar_movil",
                    !p.forzar_movil)}
                  disabled={proc}
                  title="Mostrar en la app aunque el proveedor no lo marque para móvil"
                  style={{background:p.forzar_movil?`${Q.amber}14`:"transparent",
                    border:`1px solid ${p.forzar_movil?`${Q.amber}55`:Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 12px",
                    color:p.forzar_movil?Q.amber:Q.dim,fontSize:12,
                    cursor:"pointer",
                    fontFamily:F_BODY}}>Forzar</button>
              )}
            </div>
          )}
        </div>
      ))}


    </div>
  );
}

// Los proveedores conectados. Se pueden tener varios: cuando dos
// ofrecen el mismo juego, gana el de menor prioridad. Así se mueve
// el volumen sin desconectar a nadie.
function Integraciones({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [editando,setEditando]=useState(null);
  const [form,setForm]=useState({});
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [est,setEst]=useState(()=>estadoInicial());

  const cargar=()=>{
    fetch(`${API}/api/admin/casino/integraciones`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>{ if(x){ setD(x); setEst(s=>recargar(s,x.integraciones)); } })
      .catch(()=>{});
  };
  useEffect(cargar,[]);

  // El interruptor. Apagar pide confirmación porque quita los juegos a los
  // jugadores; encender no. Si el servidor no confirma, la máquina de
  // `proveedoresApi` devuelve el interruptor a donde estaba y deja el motivo
  // en la fila: un interruptor que muestra lo que el servidor no hizo es
  // peor que no tenerlo.
  const cambiar=async(codigo,activa)=>{
    const actual=est.lista.find(p=>p.codigo===codigo);
    if(!actual) return;
    if(requiereConfirmacion(actual.activa,activa)
      && !window.confirm(textoConfirmacionApagado(actual))) return;
    const paso=iniciarCambio(est,codigo,activa);
    if(!paso.enviar) return;
    setEst(paso.estado); setMsg(null);
    const r=await cambiarActiva({api:API,adminKey,codigo,activa});
    if(r.noAutorizado){
      setEst(s=>cambioFallido(s,codigo,paso.anterior,""));
      onNoAutorizado(); return;
    }
    if(!r.ok){
      setEst(s=>cambioFallido(s,codigo,paso.anterior,r.mensaje)); return;
    }
    setEst(s=>cambioConfirmado(s,codigo));
    // Se relee: lo que queda en pantalla es lo que dice la base.
    cargar();
  };

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/casino/integraciones`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(form)});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:"Guardado", ok:true}); setEditando(null); setForm({}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  // Desactiva, no borra: las jugadas del histórico apuntan a esta
  // integración y perderla dejaría el reporte sin nombres de juego.
  const quitar=async(codigo,nombre)=>{
    if(!window.confirm(
      `¿Quitar ${nombre}? Sus juegos dejan de aparecer, pero el `+
      `histórico se conserva. Se puede volver a activar después.`))
      return;
    setProc(true);
    try{
      const r=await fetch(
        `${API}/api/admin/casino/integraciones/${codigo}`,{
        method:"DELETE",headers:adminHeaders(adminKey)});
      if(!r.ok) throw new Error("No se pudo");
      setMsg({text:`${nombre} quitada`, ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const sincronizar=async(codigo)=>{
    setProc(true); setMsg({text:"Trayendo el catálogo…", ok:true});
    try{
      const r=await fetch(`${API}/api/admin/casino/sincronizar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify(codigo?{integracion:codigo}:{})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      const ok=(x.resultados||[]).filter(y=>y.ok);
      const mal=(x.resultados||[]).filter(y=>!y.ok);
      // A batch result: each item's own outcome (✅/⚠️) is content, not
      // decoration — it says which integration succeeded and which
      // didn't, information the outer ok:mal.length===0 can't carry by
      // itself. Only the batch-level prefix moves to colour; the
      // per-item glyphs inside this summary stay.
      setMsg({text:ok.map(y=>`✅ ${y.integracion}: ${y.juegos} juegos`)
        .concat(mal.map(y=>`⚠️ ${y.integracion}: ${y.error}`)).join(" · "),
        ok:mal.length===0});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:13.5,marginBottom:8,
    fontFamily:F_BODY};

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:10,
        textAlign:"center",lineHeight:1.45}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <Btn label={proc?"Sincronizando…":<><RefreshCw size={13}/> Traer catálogos</>}
        onClick={()=>sincronizar(null)} color={Q.cyan} full
        disabled={proc}/>

      {d.juegos_repetidos>0&&(
        <div style={{color:Q.amber,fontSize:12,marginTop:9,
          lineHeight:1.5,fontFamily:F_BODY}}>
          {d.juegos_repetidos} juegos están en más de un proveedor. Se
          muestra el de menor prioridad.</div>
      )}

      <div style={{height:14}}/>

      {est.lista.map(p=>(
        <FilaProveedor key={p.codigo} proveedor={p}
          ocupado={p.codigo in est.pendientes}
          error={est.errores[p.codigo]}
          deshabilitado={proc}
          onCambiar={(activa)=>cambiar(p.codigo,activa)}
          onEditar={()=>{ setEditando(p.codigo);
            setForm({codigo:p.codigo,nombre:p.nombre,
              activa:p.activa,url:p.url,monedas:p.monedas,
              prioridad:p.prioridad,notas:p.notas}); }}
          onTraer={()=>sincronizar(p.codigo)}
          onQuitar={()=>quitar(p.codigo,p.nombre)}/>
      ))}

      <div style={{height:8}}/>
      <Btn label={editando==="nueva"?"Cancelar":"+ Agregar proveedor"}
        onClick={()=>{
          if(editando==="nueva"){ setEditando(null); setForm({}); }
          else { setEditando("nueva");
            setForm({activa:false,monedas:"ARS",prioridad:100}); }
        }} color={Q.violet} outline full/>

      {editando&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,
            marginBottom:9,fontFamily:F_BODY}}>
            {editando==="nueva"?"Nuevo proveedor":`Editar ${form.nombre}`}</div>

          {editando==="nueva"&&(
            <>
              <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
                Código corto (sin espacios)</div>
              <input value={form.codigo||""}
                onChange={e=>setForm({...form,
                  codigo:e.target.value.toLowerCase().replace(/\s/g,"")})}
                placeholder="softswiss" style={inp}/>
            </>
          )}

          <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
            Nombre</div>
          <input value={form.nombre||""}
            onChange={e=>setForm({...form,nombre:e.target.value})}
            style={inp}/>

          <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
            Dirección de su API</div>
          <input value={form.url||""}
            onChange={e=>setForm({...form,url:e.target.value})}
            placeholder="https://proveedor.com" style={inp}/>

          <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
            X-Code</div>
          <input value={form.api_code||""}
            onChange={e=>setForm({...form,api_code:e.target.value})}
            placeholder={editando==="nueva"?"":"(se conserva el actual)"}
            style={inp}/>

          <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
            Clave secreta</div>
          <input value={form.api_secret||""} type="password"
            onChange={e=>setForm({...form,api_secret:e.target.value})}
            placeholder={editando==="nueva"?"":"(se conserva la actual)"}
            style={inp}/>

          <div style={{display:"flex",gap:SPACING[8]}}>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
                Monedas</div>
              <input value={form.monedas||""}
                onChange={e=>setForm({...form,
                  monedas:e.target.value.toUpperCase()})}
                placeholder="ARS,USD" style={inp}/>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
                Prioridad</div>
              <input value={form.prioridad??100} inputMode="numeric"
                onChange={e=>setForm({...form,
                  prioridad:e.target.value.replace(/\D/g,"")})}
                style={inp}/>
            </div>
          </div>
          <div style={{color:Q.dim,fontSize:12,marginTop:-4,
            marginBottom:9,lineHeight:1.5}}>
            Menor número gana cuando dos proveedores tienen el mismo
            juego.</div>

          <button onClick={()=>setForm({...form,activa:!form.activa})}
            style={{width:"100%",
              background:form.activa?`${Q.green}18`:"rgba(255,255,255,0.04)",
              border:`1px solid ${form.activa?Q.green:Q.border}`,
              borderRadius:RADII.md,padding:"12px",cursor:"pointer",
              color:form.activa?Q.green:Q.muted,fontSize:12.5,
              fontWeight:700,marginBottom:10,
              fontFamily:F_BODY}}>
            {form.activa?<><Icon name="circle-check" size={13}/> Activa</>:<><CircleOff size={13}/> Apagada</>}</button>

          <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
            color={Q.violet} full disabled={proc||!form.codigo}/>
        </GCard>
      )}
    </div>
  );
}


// Riesgo del casino. Busca proveedores que pagan más de lo que
// recaudan y jugadores con rachas. La IA lee el caso y recomienda,
// pero apagar un proveedor lo decide una persona: deja sin juegos a
// todos los locales.
function RiesgoCasino({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text

  const cargar=async(revisar)=>{
    setProc(true);
    try{
      const r=await fetch(
        `${API}/api/admin/casino/riesgo${revisar?"?revisar=1":""}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setProc(false);
  };
  useEffect(()=>{ cargar(false); /* eslint-disable-next-line */ },[]);

  const analizar=async(id)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(
        `${API}/api/admin/casino/riesgo/${id}/analizar`,{
        method:"POST",headers:adminHeaders(adminKey)});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setD(v=>({...v, alertas:v.alertas.map(a=>a.id===id
        ?{...a, analisis_ia:x.analisis, sugerencia:x.sugerencia}:a)}));
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const apagar=async(marca)=>{
    const motivo=window.prompt(
      `¿Por qué se apaga ${marca}? Queda registrado.`);
    if(motivo===null) return;
    setProc(true);
    try{
      await fetch(`${API}/api/admin/casino/proveedores/${encodeURIComponent(marca)}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({activo:false, motivo})});
      setMsg({text:`${marca} apagado`, ok:true}); cargar(false);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const prender=async(marca)=>{
    setProc(true);
    try{
      await fetch(`${API}/api/admin/casino/proveedores/${encodeURIComponent(marca)}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({activo:true})});
      setMsg({text:`${marca} disponible`, ok:true}); cargar(false);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const cerrar=async(id)=>{
    const nota=window.prompt("Nota (opcional)");
    if(nota===null) return;
    try{
      await fetch(`${API}/api/admin/casino/riesgo/${id}/cerrar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({nota})});
      cargar(false);
    }catch(e){}
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const COLOR={alta:Q.red,media:Q.amber,baja:Q.muted};

  return(
    <div>
      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <Btn label={proc?"Revisando…":<><Icon name="search" size={13}/> Revisar ahora</>}
        onClick={()=>cargar(true)} color={Q.cyan} full disabled={proc}/>

      <div style={{color:Q.dim,fontSize:12,margin:"8px 0 14px",
        lineHeight:1.55,fontFamily:F_BODY}}>
        Un GGR negativo con pocas jugadas suele ser un premio grande
        que con volumen se acomoda. Por eso las alertas exigen un
        mínimo de {d.umbrales?.jugadas_min||50} jugadas.</div>

      {(d.proveedores_apagados||[]).length>0&&(
        <GCard glow={Q.red} style={{padding:SPACING[12],marginBottom:12}}>
          <div style={{color:Q.red,fontSize:12,fontWeight:700,
            marginBottom:7,fontFamily:F_BODY}}>
            Proveedores apagados</div>
          {d.proveedores_apagados.map(p=>(
            <div key={p.marca} style={{display:"flex",
              justifyContent:"space-between",alignItems:"center",
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12.5}}>{p.marca}</div>
                {p.motivo&&(
                  <div style={{color:Q.dim,fontSize:12,lineHeight:1.4}}>
                    {p.motivo}{p.cuando&&` · ${fechaHora(p.cuando)}`}</div>
                )}
              </div>
              <button onClick={()=>prender(p.marca)} disabled={proc}
                style={{background:`${Q.green}18`,
                  border:`1px solid ${Q.green}66`,borderRadius:RADII.sm,
                  padding:"8px 12px",color:Q.green,fontSize:12,
                  cursor:"pointer",flexShrink:0}}>Prender</button>
            </div>
          ))}
        </GCard>
      )}

      {d.alertas.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{color:Q.green,fontSize:24,marginBottom:6}}>✓</div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            Sin alertas abiertas</div>
        </GCard>
      )}

      {d.alertas.map(a=>(
        <GCard key={a.id} glow={COLOR[a.severidad]}
          style={{padding:SPACING[16],marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:SPACING[8],marginBottom:5}}>
            <span style={{color:Q.text,fontSize:13,fontWeight:700,
              lineHeight:1.4,fontFamily:F_BODY}}>
              {a.titulo}</span>
            <span style={{background:`${COLOR[a.severidad]}22`,
              border:`1px solid ${COLOR[a.severidad]}66`,borderRadius:RADII.lg,
              padding:"4px 8px",fontSize:12,color:COLOR[a.severidad],
              flexShrink:0,fontWeight:700}}>{a.severidad}</span>
          </div>

          {a.detalle&&(
            <div style={{color:Q.muted,fontSize:12,lineHeight:1.5,
              fontFamily:F_BODY}}>{a.detalle}</div>
          )}

          {a.analisis_ia&&(
            <div style={{marginTop:10,padding:"12px 12px",
              background:`${Q.cyan}0D`,border:`1px solid ${Q.cyan}33`,
              borderRadius:RADII.md}}>
              <div style={{color:Q.cyan,fontSize:12,letterSpacing:0.5,
                marginBottom:5}}>LECTURA DE LA IA</div>
              <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
                whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                {a.analisis_ia}</div>
              {a.sugerencia&&(
                <div style={{color:Q.amber,fontSize:12,marginTop:7,
                  fontWeight:600}}>
                  Sugiere: {{observar:"seguir mirándolo",
                    apagar:"apagar el proveedor",
                    revisar_jugador:"revisar al jugador",
                    normal:"está dentro de lo normal"}[a.sugerencia]}</div>
              )}
            </div>
          )}

          <div style={{display:"flex",gap:SPACING[8],marginTop:10}}>
            {!a.analisis_ia&&(
              <button onClick={()=>analizar(a.id)} disabled={proc}
                style={{flex:1,background:`${Q.cyan}18`,
                  border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,
                  padding:"8px",color:Q.cyan,fontSize:12,
                  fontWeight:700,cursor:"pointer"}}><Bot size={12}/> Analizar</button>
            )}
            {a.marca&&(
              <button onClick={()=>apagar(a.marca)} disabled={proc}
                style={{flex:1,background:`${Q.red}12`,
                  border:`1px solid ${Q.red}55`,borderRadius:RADII.md,
                  padding:"8px",color:Q.red,fontSize:12,
                  cursor:"pointer"}}>Apagar {a.marca}</button>
            )}
            <button onClick={()=>cerrar(a.id)}
              style={{flex:1,background:"transparent",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"8px",color:Q.muted,fontSize:12,
                cursor:"pointer"}}>Cerrar</button>
          </div>
        </GCard>
      ))}
    </div>
  );
}

// Estado de la conexión con el proveedor de casino. Sin esto hay que
// adivinar entre credenciales mal cargadas, firma incorrecta y reloj
// desfasado, que dan errores parecidos.
// El interruptor del sportsbook de Content360.
//
// Apagarlo esconde la entrada de menú y no deja abrir apuestas nuevas (quien
// ya apostó ahí la ve apagada, con un aviso): las apuestas que ya hizo se
// siguen liquidando y pagando. Una apuesta deportiva se
// resuelve días después, y quitarle el premio a quien apostó ayer porque
// hoy se apagó la entrada sería perder plata de un jugador. Por eso el
// cartel lo dice acá, donde quien lo apaga lo lee antes de tocarlo.
function InterruptorSportsbook({ adminKey, onNoAutorizado }){
  const [activo,setActivo]=useState(null);
  const [guardando,setGuardando]=useState(false);
  const [msg,setMsg]=useState("");

  useEffect(()=>{
    let vigente=true;
    fetch(`${API}/api/admin/sportsbook-c360`,{headers:adminHeaders(adminKey)})
      .then(r=>{
        if(r.status===401){ onNoAutorizado(); return null; }
        return r.ok?r.json():null;
      })
      .then(d=>{
        if(!vigente) return;
        if(d) setActivo(!!d.activo);
        else setMsg("No se pudo leer el estado del sportsbook.");
      })
      .catch(()=>{ if(vigente) setMsg("No se pudo leer el estado del sportsbook."); });
    return()=>{ vigente=false; };
    // eslint-disable-next-line
  },[adminKey]);

  const cambiar=async()=>{
    if(activo===null||guardando) return;
    const nuevo=!activo;
    setGuardando(true); setMsg("");
    try{
      const r=await fetch(`${API}/api/admin/sportsbook-c360`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo:nuevo})});
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok) throw new Error();
      // Se muestra lo que el servidor guardó, no lo que se pidió.
      const d=await r.json();
      setActivo(!!d.activo);
    }catch(e){ setMsg("No se pudo guardar. El interruptor sigue como estaba."); }
    setGuardando(false);
  };

  if(activo===null&&!msg) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20],fontFamily:F_BODY}}>Cargando...</div>;

  return(
    <div>
      <GCard glow={activo?Q.green:Q.violet} style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",
          justifyContent:"space-between",gap:SPACING[12]}}>
          <div>
            <div style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>Sportsbook de Content360</div>
            <div style={{color:Q.muted,fontSize:12,marginTop:2,lineHeight:1.5,
              fontFamily:F_BODY}}>
              {activo
                ?"Prendido: los jugadores ven la entrada Sportsbook en el menú."
                :"Apagado: solo ven la entrada, apagada, quienes ya apostaron. Arranca apagado hasta que lo verifiques."}
            </div>
          </div>
          <button role="switch" aria-checked={!!activo}
            aria-label="Sportsbook de Content360"
            disabled={activo===null||guardando} onClick={cambiar}
            style={{flexShrink:0,width:48,height:26,borderRadius:RADII.lg,
              border:"none",padding:0,position:"relative",
              background:activo?Q.green:Q.border,
              cursor:guardando?"wait":"pointer",transition:"all .2s"}}>
            <span style={{display:"block",width:20,height:20,
              borderRadius:RADII.md,background:"#fff",position:"absolute",
              top:3,left:activo?25:3,transition:"all .2s"}}/>
          </button>
        </div>
        {msg&&<div style={{color:Q.red,fontSize:12,marginTop:8,
          fontFamily:F_BODY}}>{msg}</div>}
      </GCard>
      <div style={{color:Q.muted,fontSize:12,lineHeight:1.6,
        fontFamily:F_BODY}}>
        Apagarlo esconde la entrada y no deja abrir apuestas nuevas; quien
        ya apostó la sigue viendo, apagada, con un aviso. Las apuestas ya
        hechas se siguen liquidando y pagando: un jugador que apostó ayer
        cobra hoy aunque lo apagues.</div>
    </div>
  );
}

// El interruptor del cash out para los jugadores SIN agencia.
//
// El cash out se habilita por agencia (`puede_cashout`), pero el jugador que
// se registró en la web sin código de referido no tiene ninguna: es de la
// casa. Es justo el que más margen deja, porque no hay comisión de agencia de
// por medio, y también el que más cuesta si el cash out se maneja mal. Por eso
// arranca apagado y lo prende el dueño a conciencia.
// NO toca a los jugadores que sí tienen agencia: siguen dependiendo del
// permiso de la suya, esté como esté este.
function InterruptorCashoutCasa({ adminKey, onNoAutorizado }){
  const [activo,setActivo]=useState(null);
  const [guardando,setGuardando]=useState(false);
  const [msg,setMsg]=useState("");

  useEffect(()=>{
    let vigente=true;
    fetch(`${API}/api/admin/cashout-casa`,{headers:adminHeaders(adminKey)})
      .then(r=>{
        if(r.status===401){ onNoAutorizado(); return null; }
        return r.ok?r.json():null;
      })
      .then(d=>{
        if(!vigente) return;
        if(d) setActivo(!!d.activo);
        else setMsg("No se pudo leer el estado del cash out de la casa.");
      })
      .catch(()=>{ if(vigente) setMsg("No se pudo leer el estado del cash out de la casa."); });
    return()=>{ vigente=false; };
    // eslint-disable-next-line
  },[adminKey]);

  const cambiar=async()=>{
    if(activo===null||guardando) return;
    setGuardando(true); setMsg("");
    try{
      const r=await fetch(`${API}/api/admin/cashout-casa`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo:!activo})});
      if(r.status===401){ onNoAutorizado(); return; }
      if(!r.ok) throw new Error();
      // Se muestra lo que el servidor guardó, no lo que se pidió.
      const d=await r.json();
      setActivo(!!d.activo);
    }catch(e){ setMsg("No se pudo guardar. El interruptor sigue como estaba."); }
    setGuardando(false);
  };

  if(activo===null&&!msg) return null;

  return(
    <GCard glow={activo?Q.green:Q.violet} style={{padding:SPACING[16],marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",
        justifyContent:"space-between",gap:SPACING[12]}}>
        <div>
          <div style={{color:Q.text,fontWeight:700,fontSize:14,
            fontFamily:F_BODY}}>Cash out para jugadores sin agencia</div>
          <div style={{color:Q.muted,fontSize:12,marginTop:2,lineHeight:1.5,
            fontFamily:F_BODY}}>
            {activo
              ?"Prendido: los jugadores que se registraron sin código de referido pueden hacer cash out."
              :"Apagado: los jugadores que se registraron sin código de referido no pueden hacer cash out. Arranca apagado."}
            {" "}Los jugadores con agencia siguen dependiendo del permiso de su agencia.
          </div>
        </div>
        <button role="switch" aria-checked={!!activo}
          aria-label="Cash out para jugadores sin agencia"
          disabled={activo===null||guardando} onClick={cambiar}
          style={{flexShrink:0,width:48,height:26,borderRadius:RADII.lg,
            border:"none",padding:0,position:"relative",
            background:activo?Q.green:Q.border,
            cursor:guardando?"wait":"pointer",transition:"all .2s"}}>
          <span style={{display:"block",width:20,height:20,
            borderRadius:RADII.md,background:"#fff",position:"absolute",
            top:3,left:activo?25:3,transition:"all .2s"}}/>
        </button>
      </div>
      {msg&&<div style={{color:Q.red,fontSize:12,marginTop:8,
        fontFamily:F_BODY}}>{msg}</div>}
    </GCard>
  );
}

function TabCasinoProveedor({ adminKey, onNoAutorizado }){
  const [solapa,setSolapa]=useState("conexion");
  const [d,setD]=useState(null);
  const [proc,setProc]=useState(false);

  const probar=async()=>{
    setProc(true); setD(null);
    try{
      const r=await fetch(`${API}/api/admin/casino/diagnostico`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setD(await r.json());
    }catch(e){ setD({diagnostico:"No se pudo consultar: "+e.message}); }
    setProc(false);
  };
  useEffect(()=>{ probar(); /* eslint-disable-next-line */ },[]);

  const fila=(label,ok)=>(
    <div style={{display:"flex",justifyContent:"space-between",
      fontSize:12.5,padding:"4px 0",
      borderTop:`1px solid ${Q.border}`}}>
      <span style={{color:Q.muted}}>{label}</span>
      <span style={{color:ok?Q.green:Q.red,fontWeight:700}}>
        {ok?"✓":"falta"}</span>
    </div>
  );

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="spade" size={14}/> Proveedor de casino</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Conexión con 44neoluck para traer los juegos y abrir partidas.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:14,
        overflowX:"auto"}}>
        {[["conexion",<><Plug size={13}/> Diagnóstico</>],["integraciones",<><Link size={13}/> Integraciones</>],
          ["marcas",<><Dices size={13}/> Proveedores</>],["logos",<><ImageIcon size={13}/> Logos</>],
          ["riesgo",<><Icon name="shield-alert" size={13}/> Riesgo</>],
          ["juegos",<><Icon name="chart-no-axes-combined" size={13}/> Por juego</>],
          ["sportsbook",<><Icon name="trophy" size={13}/> Sportsbook</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setSolapa(k)}
            style={{background:solapa===k?`${Q.violet}33`:"transparent",
              border:`1px solid ${solapa===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:solapa===k?Q.cyan:Q.muted,fontSize:12,
              fontWeight:solapa===k?700:400,whiteSpace:"nowrap",
              fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {solapa==="conexion"&&(
        <>
          <Btn label={proc?"Probando…":<><Plug size={13}/> Probar la conexión</>}
            onClick={probar} color={Q.cyan} full disabled={proc}/>

      {d&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}
          glow={d.juegos_en_cache>0?Q.green:Q.amber}>
          {fila("Dirección del proveedor", d.url_configurada)}
          {fila("Código de cliente", d.code_configurado)}
          {fila("Clave secreta", d.secret_configurado)}

          {d.prueba&&(
            <div style={{marginTop:10,paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.5,
                marginBottom:4}}>RESPUESTA</div>
              <div style={{color:Q.text,fontSize:12.5,lineHeight:1.5,
                fontFamily:F_BODY}}>{d.prueba}</div>
            </div>
          )}

          {d.diagnostico&&(
            <div style={{color:d.juegos_en_cache>0?Q.green:Q.amber,
              fontSize:12,marginTop:9,lineHeight:1.55,
              fontFamily:F_BODY}}>
              {d.diagnostico}</div>
          )}

          {/* El desfase de reloj da un error idéntico al de firma mal
              calculada, y es lo primero que hay que descartar. */}
          {d.prueba&&String(d.prueba).includes("405")&&(
            <div style={{color:Q.dim,fontSize:12,marginTop:8,
              lineHeight:1.5,fontFamily:F_BODY}}>
              El 405 puede ser la clave o el reloj del servidor: ellos
              aceptan pedidos con hasta 30 segundos de diferencia.</div>
          )}
        </GCard>
      )}

        </>
      )}

      {solapa==="integraciones"&&(
        <Integraciones adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}
      {solapa==="marcas"&&(
        <ProveedoresCasino adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}
      {solapa==="logos"&&(
        <LogosCasino adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}
      {solapa==="riesgo"&&(
        <RiesgoCasino adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}
      {solapa==="juegos"&&(
        <ReporteJuegos adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}

      {solapa==="sportsbook"&&(
        <InterruptorSportsbook adminKey={adminKey} onNoAutorizado={onNoAutorizado}/>
      )}
    </div>
  );
}

// Dónde está el tiempo del sistema.
//
// Lo que importa no es el promedio general sino el PESO: un endpoint
// de 500ms llamado mil veces consume más que uno de 3 segundos
// llamado una vez. Optimizar sin esto es adivinar.
function Rendimiento({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(`${API}/api/admin/rendimiento`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const [carga,setCarga]=useState(null);

  const correrCarga=async(conc,vueltas)=>{
    setCargando(true); setCarga(null);
    try{
      const r=await fetch(`${API}/api/admin/carga`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({concurrencia:conc, vueltas})});
      const x=await r.json();
      if(r.ok){ setCarga(x); cargar(); }
    }catch(e){}
    setCargando(false);
  };

  const reiniciar=async()=>{
    if(!window.confirm("¿Borrar lo medido y arrancar de cero?")) return;
    try{
      await fetch(`${API}/api/admin/rendimiento/reiniciar`,{
        method:"POST",headers:adminHeaders(adminKey)});
      cargar();
    }catch(e){}
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Midiendo…</div>;

  const p=d.pool||{};
  // Cuánto del pool está en uso. Se llama distinto del estado
  // "carga" de la prueba: son dos cosas y el nombre chocaba.
  const pctPool=p.maximo?(p.usadas/p.maximo*100):0;

  return(
    <div style={{marginTop:22,paddingTop:SPACING[20],
      borderTop:`1px solid ${Q.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"baseline",marginBottom:4}}>
        <span style={{color:Q.text,fontWeight:800,fontSize:15,
          fontFamily:F_BODY}}><Icon name="chart-no-axes-combined" size={14}/> Rendimiento</span>
        <button onClick={reiniciar}
          style={{background:"transparent",border:"none",color:Q.dim,
            fontSize:12,cursor:"pointer"}}>reiniciar</button>
      </div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.5,fontFamily:F_BODY}}>
        {d.total_llamadas.toLocaleString("es-AR")} pedidos medidos desde
        el último reinicio del servidor.</div>

      {(d.avisos||[]).length>0&&(
        <GCard glow={Q.amber} style={{padding:SPACING[12],marginBottom:12}}>
          <div style={{color:Q.amber,fontSize:12,fontWeight:700,
            marginBottom:6,fontFamily:F_BODY}}>
            Atención</div>
          {d.avisos.map((a,i)=>(
            <div key={i} style={{color:Q.muted,fontSize:12,
              lineHeight:1.5,padding:"4px 0",
              fontFamily:F_BODY}}>· {a}</div>
          ))}
        </GCard>
      )}

      {/* El pool: si se llena, los pedidos hacen cola y todo se
          vuelve lento aunque el servidor esté libre. */}
      <GCard glow={pctPool>80?Q.red:(pctPool>50?Q.amber:Q.green)}
        style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"baseline",marginBottom:6}}>
          <span style={{color:Q.text,fontSize:12.5,fontWeight:700,
            fontFamily:F_BODY}}>
            Conexiones a la base</span>
          <span style={{color:pctPool>80?Q.red:Q.text,fontSize:14,
            fontWeight:800,
            fontFamily:F_BODY}}>
            {p.usadas ?? "—"} / {p.maximo ?? "—"}</span>
        </div>
        <div style={{height:5,background:"rgba(255,255,255,.07)",
          borderRadius:RADII.sm,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${Math.min(100,pctPool)}%`,
            background:pctPool>80?Q.red:(pctPool>50?Q.amber:Q.green),
            transition:"width .3s"}}/>
        </div>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,
          lineHeight:1.45,fontFamily:F_BODY}}>
          Cuando se llenan, los pedidos hacen cola y todo se vuelve
          lento aunque el servidor esté libre.</div>
      </GCard>

      {/* Sin tráfico real no hay nada que medir. Esto lo genera:
          golpea las consultas que más se usan con muchos pedidos a
          la vez, que es cuando aparecen las esperas por conexión. */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <button onClick={()=>correrCarga(30,4)} disabled={cargando}
          style={{flex:1,background:`${Q.cyan}18`,
            border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,
            padding:"12px 8px",color:Q.cyan,fontSize:12,
            fontWeight:700,cursor:"pointer",
            fontFamily:F_BODY}}>
          <Zap size={13}/> Rápida</button>
        <button onClick={()=>correrCarga(80,8)} disabled={cargando}
          style={{flex:1,background:`${Q.amber}14`,
            border:`1px solid ${Q.amber}55`,borderRadius:RADII.md,
            padding:"12px 8px",color:Q.amber,fontSize:12,
            fontWeight:700,cursor:"pointer",
            fontFamily:F_BODY}}>
          <Flame size={13}/> Exigente</button>
      </div>

      {carga&&(
        <GCard glow={(carga.avisos||[]).length?Q.amber:Q.green}
          style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.text,fontSize:12.5,fontWeight:700,
            marginBottom:8,fontFamily:F_BODY}}>
            Prueba de carga · {carga.simultaneos_reales
              ||carga.concurrencia} conexiones a la vez</div>

          {(carga.resultados||[]).map(r=>(
            <div key={r.prueba} style={{padding:"8px 0",
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{display:"flex",
                justifyContent:"space-between",alignItems:"baseline",
                gap:SPACING[8]}}>
                <span style={{color:Q.text,fontSize:12,
                  fontFamily:F_BODY}}>
                  {r.prueba}</span>
                <span style={{color:r.p95_ms>2000?Q.red
                    :(r.p95_ms>800?Q.amber:Q.green),
                  fontSize:12,fontWeight:700,flexShrink:0,
                  fontFamily:F_BODY}}>
                  {r.por_segundo}/s</span>
              </div>
              <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                mediana {r.mediana_ms}ms · peor 5% {r.p95_ms}ms ·
                máximo {r.peor_ms}ms
                {r.errores>0&&(
                  <span style={{color:Q.red}}> · {r.errores} fallaron</span>
                )}</div>
              {/* El motivo del primer fallo: sin esto hay que
                  adivinar si es carga o un error de la consulta. */}
              {r.motivo&&(
                <div style={{color:Q.red,fontSize:12,marginTop:2,
                  lineHeight:1.4,wordBreak:"break-word",
                  fontFamily:F_BODY}}>
                  {r.motivo}</div>
              )}
            </div>
          ))}

          {carga.capacidad&&(
            <div style={{color:Q.cyan,fontSize:12,marginTop:9,
              paddingTop:SPACING[8],borderTop:`1px solid ${Q.border}`,
              lineHeight:1.5,
              fontFamily:F_BODY}}>
              {carga.capacidad}</div>
          )}

          {(carga.avisos||[]).map((a,i)=>(
            <div key={i} style={{color:Q.amber,fontSize:12,marginTop:6,
              lineHeight:1.5,
              fontFamily:F_BODY}}>· {a}</div>
          ))}

          <div style={{color:Q.dim,fontSize:12,marginTop:8,
            lineHeight:1.45,
            fontFamily:F_BODY}}>
            {carga.nota}</div>
        </GCard>
      )}

      <div style={{color:Q.muted,fontSize:12,letterSpacing:0.6,
        marginBottom:7,fontFamily:F_BODY}}>
        LO QUE MÁS TIEMPO CONSUME</div>

      {(d.endpoints||[]).slice(0,12).map((e,i)=>(
        <div key={i} style={{padding:"8px 0",
          borderTop:i?`1px solid ${Q.border}`:"none"}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"baseline",gap:SPACING[8]}}>
            <span style={{color:Q.text,fontSize:12,minWidth:0,flex:1,
              overflow:"hidden",textOverflow:"ellipsis",
              whiteSpace:"nowrap",
              fontFamily:F_BODY}}>
              {e.ruta}</span>
            <span style={{color:e.promedio_ms>800?Q.red
                :(e.promedio_ms>300?Q.amber:Q.muted),
              fontSize:12,fontWeight:700,flexShrink:0,
              fontFamily:F_BODY}}>
              {e.promedio_ms}ms</span>
          </div>
          <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
            {e.llamadas.toLocaleString("es-AR")} llamadas · pico{" "}
            {e.max_ms}ms · consume {e.peso}s
            {e.errores>0&&(
              <span style={{color:Q.red}}> · {e.errores} errores</span>
            )}</div>
        </div>
      ))}

      {(d.sin_indice||[]).length>0&&(
        <GCard glow={Q.red} style={{padding:SPACING[12],marginTop:12}}>
          <div style={{color:Q.red,fontSize:12,fontWeight:700,
            marginBottom:4,fontFamily:F_BODY}}>
            Tablas sin índice</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:8,
            lineHeight:1.5}}>
            Se recorren enteras en cada consulta. Con pocas filas no
            se nota; con muchas, cada consulta tarda más.</div>
          {d.sin_indice.map(t=>(
            <div key={t.tabla} style={{display:"flex",
              justifyContent:"space-between",fontSize:12,padding:"4px 0",
              fontFamily:F_BODY}}>
              <span style={{color:Q.text}}>{t.tabla}</span>
              <span style={{color:Q.red}}>{t.pct}% completos</span>
            </div>
          ))}
        </GCard>
      )}

      {(d.tablas||[]).length>0&&(
        <div style={{marginTop:12}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.6,
            marginBottom:6,fontFamily:F_BODY}}>
            TABLAS MÁS GRANDES</div>
          {d.tablas.slice(0,6).map(t=>(
            <div key={t.tabla} style={{display:"flex",
              justifyContent:"space-between",fontSize:12,padding:"4px 0",
              fontFamily:F_BODY}}>
              <span style={{color:Q.muted}}>{t.tabla}</span>
              <span style={{color:Q.dim}}>
                {t.filas.toLocaleString("es-AR")} filas</span>
            </div>
          ))}
        </div>
      )}

      <div style={{color:Q.dim,fontSize:12,marginTop:12,
        lineHeight:1.5,fontFamily:F_BODY}}>
        {d.nota}</div>
    </div>
  );
}

// Panel de pruebas. Recorre el sistema y verifica que responda y que
// los números den.
//
// En producción las pruebas que crean datos piden confirmación
// escrita: ensucian reportes y cierres, y un botón que crea plata en
// la base real es cuestión de tiempo hasta que alguien lo aprieta
// sin querer.
function TabTester({ adminKey, onNoAutorizado }){
  const [est,setEst]=useState(null);
  const [res,setRes]=useState(null);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [conf,setConf]=useState("");
  const [conEscritura,setConEscritura]=useState(false);

  const cargar=()=>{
    fetch(`${API}/api/admin/test/estado`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setEst(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const correr=async()=>{
    setProc(true); setMsg(null); setRes(null);
    try{
      const r=await fetch(`${API}/api/admin/test/correr`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({escritura:conEscritura, confirmacion:conf})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setRes(x); cargar();
      if(x.bloqueado) setMsg({text:x.bloqueado, ok:false});
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const limpiar=async()=>{
    const c=window.prompt(
      "Vas a borrar TODOS los datos marcados como prueba.\n"+
      "No se puede deshacer.\n\nEscribí BORRAR para confirmar:");
    if(!c) return;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/test/limpiar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({confirmacion:c})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      const det=Object.entries(x.borrado||{})
        .map(([k,v])=>`${v} ${k}`).join(", ");
      setMsg({text:x.total?`Borrado: ${det}`:"No había nada que borrar", ok:true});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!est) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const bloque=(titulo,items)=>items.length>0&&(
    <div style={{marginBottom:14}}>
      <div style={{color:Q.muted,fontSize:12,letterSpacing:0.6,
        marginBottom:7,fontFamily:F_BODY}}>
        {titulo}</div>
      {items.map((x,i)=>(
        <div key={i} style={{display:"flex",gap:SPACING[8],padding:"8px 0",
          borderTop:i?`1px solid ${Q.border}`:"none",
          alignItems:"flex-start"}}>
          <span style={{color:x.ok?Q.green:Q.red,fontSize:12,
            flexShrink:0,fontWeight:700}}>{x.ok?"✓":"✗"}</span>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:x.ok?Q.muted:Q.text,fontSize:12,
              lineHeight:1.4,
              fontFamily:F_BODY}}>{x.nombre}</div>
            {!x.ok&&x.detalle&&(
              <div style={{color:Q.red,fontSize:12,marginTop:2,
                lineHeight:1.4}}>{x.detalle}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><FlaskConical size={16}/> Pruebas</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Recorre el sistema y verifica que responda y que los números
        den bien.</div>

      {est.produccion&&(
        <div style={{background:`${Q.amber}12`,
          border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
          padding:"12px 12px",marginBottom:12,color:Q.amber,
          fontSize:12,lineHeight:1.55,
          fontFamily:F_BODY}}>
          Estás en producción. Las pruebas de lectura no tocan nada.
          Las que crean datos piden confirmación escrita.</div>
      )}

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center",lineHeight:1.45}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {est.hay_datos&&(
        <GCard glow={Q.amber} style={{padding:SPACING[12],marginBottom:12}}>
          <div style={{color:Q.amber,fontSize:12,fontWeight:700,
            marginBottom:5,fontFamily:F_BODY}}>
            Hay datos de prueba en la base</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:9,
            lineHeight:1.5}}>
            {est.agencias} agencias · {est.usuarios} clientes ·{" "}
            {est.desafios} desafíos. Están ensuciando los reportes.</div>
          <button onClick={limpiar} disabled={proc}
            style={{width:"100%",background:`${Q.red}14`,
              border:`1px solid ${Q.red}55`,borderRadius:RADII.md,
              padding:"12px",color:Q.red,fontSize:12.5,fontWeight:700,
              cursor:"pointer",
              fontFamily:F_BODY}}>
            <Trash2 size={13}/> Borrar datos de prueba</button>
        </GCard>
      )}

      <button onClick={()=>setConEscritura(v=>!v)}
        style={{width:"100%",
          background:conEscritura?`${Q.amber}14`:"rgba(255,255,255,0.04)",
          border:`1px solid ${conEscritura?Q.amber:Q.border}`,
          borderRadius:RADII.md,padding:"12px",cursor:"pointer",marginBottom:8,
          color:conEscritura?Q.amber:Q.muted,fontSize:12.5,
          fontWeight:conEscritura?700:400,textAlign:"left",
          fontFamily:F_BODY}}>
        {conEscritura?<><Icon name="triangle-alert" size={12}/> Con creación de datos</>:"Solo lectura"}
        <div style={{color:Q.dim,fontSize:12,marginTop:3,
          fontWeight:400,lineHeight:1.4}}>
          {conEscritura
            ? "Crea agencias, clientes y desafíos marcados como prueba"
            : "No toca nada: solo verifica cálculos y configuración"}</div>
      </button>

      {conEscritura&&est.produccion&&(
        <input value={conf} onChange={e=>setConf(e.target.value)}
          placeholder="Escribí CREAR DATOS para confirmar"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${conf.toUpperCase()==="CREAR DATOS"
              ?Q.amber:Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
            color:Q.text,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}/>
      )}

      <Btn label={proc?"Probando…":<><Icon name="play" size={13}/> Correr las pruebas</>}
        onClick={correr} color={Q.violet} full disabled={proc}/>

      {res&&(
        <>
          <GCard glow={res.mal===0?Q.green:Q.red}
            style={{padding:SPACING[16],margin:"14px 0"}}>
            <div style={{display:"flex",gap:SPACING[16],alignItems:"baseline"}}>
              <div>
                <div style={{color:Q.green,fontWeight:900,fontSize:26,
                  fontFamily:F_BODY}}>
                  {res.bien}</div>
                <div style={{color:Q.muted,fontSize:12}}>bien</div>
              </div>
              <div>
                <div style={{color:res.mal?Q.red:Q.dim,fontWeight:900,
                  fontSize:26,fontFamily:F_BODY}}>
                  {res.mal}</div>
                <div style={{color:Q.muted,fontSize:12}}>mal</div>
              </div>
              <div style={{marginLeft:"auto",textAlign:"right"}}>
                <div style={{color:res.mal===0?Q.green:Q.red,
                  fontSize:12.5,fontWeight:700,
                  fontFamily:F_BODY}}>
                  {res.mal===0?"Todo en orden":"Hay cosas rotas"}</div>
              </div>
            </div>
          </GCard>

          {bloque("VERIFICACIONES", res.lectura||[])}
          {bloque("CIRCUITO COMPLETO", res.escritura||[])}

          {res.creado&&(
            <div style={{color:Q.dim,fontSize:12,marginTop:10,
              lineHeight:1.5,fontFamily:F_BODY}}>
              Se crearon {(res.creado.agencias||[]).length} agencias y{" "}
              {(res.creado.usuarios||[]).length} clientes de prueba.
              Borralos con el botón de arriba cuando termines.</div>
          )}
        </>
      )}
    </div>
  );
}

// Juego responsable y auditoría. Lo que pide cualquier jurisdicción
// regulada, y lo primero que mira un certificador.
function TabResponsable({ adminKey, onNoAutorizado }){
  const [solapa,setSolapa]=useState("config");
  const [d,setD]=useState(null);
  const [aud,setAud]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=()=>{
    fetch(`${API}/api/admin/juego-responsable`,
      {headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const cargarAud=()=>{
    fetch(`${API}/api/admin/auditoria?limite=80`,
      {headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(x=>x&&setAud(x)).catch(()=>{});
  };
  useEffect(()=>{ if(solapa==="auditoria"&&!aud) cargarAud();
    /* eslint-disable-next-line */ },[solapa]);

  const guardar=async(over)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/juego-responsable`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({...d, ...(over||{})})});
      if(!r.ok) throw new Error("Error");
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const toggle=(clave,label,ayuda)=>(
    <GCard key={clave} style={{padding:SPACING[16],marginBottom:9}}>
      <button onClick={()=>{const v=!d[clave];
          setD({...d,[clave]:v}); guardar({[clave]:v});}}
        style={{width:"100%",display:"flex",
          justifyContent:"space-between",alignItems:"center",
          background:"transparent",border:"none",cursor:"pointer",
          padding:0,fontFamily:F_BODY}}>
        <span style={{color:Q.text,fontSize:13,fontWeight:600,
          textAlign:"left"}}>{label}</span>
        <span style={{fontSize:12,fontWeight:700,flexShrink:0,
          color:d[clave]?Q.green:Q.dim}}>
          {d[clave]?<><Icon name="circle-check" size={12}/> Activo</>:<><CircleOff size={12}/> Apagado</>}</span>
      </button>
      {ayuda&&<div style={{color:Q.dim,fontSize:12,marginTop:7,
        lineHeight:1.5,fontFamily:F_BODY}}>
        {ayuda}</div>}
    </GCard>
  );

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}>
        <Icon name="shield-check" size={14}/> Juego responsable</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Lo que exige cualquier jurisdicción regulada. Es lo primero
        que mira un certificador.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
        {[["config",<><Icon name="sliders-horizontal" size={12}/> Config</>],["auditoria",<><Icon name="clipboard-list" size={12}/> Auditoría</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setSolapa(k)}
            style={{background:solapa===k?`${Q.violet}33`:"transparent",
              border:`1px solid ${solapa===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:solapa===k?Q.cyan:Q.muted,fontSize:12,
              fontWeight:solapa===k?700:400,
              fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {solapa==="config"&&(
        <>
          {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
            textAlign:"center"}}>
            <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

          <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
            <GCard style={{flex:1,padding:SPACING[12],textAlign:"center"}}>
              <div style={{color:Q.cyan,fontWeight:800,fontSize:19,
                fontFamily:F_BODY}}>
                {d.jugadores_con_limites}</div>
              <div style={{color:Q.muted,fontSize:12}}>con límites</div>
            </GCard>
            <GCard style={{flex:1,padding:SPACING[12],textAlign:"center"}}
              glow={d.autoexcluidos>0?Q.amber:null}>
              <div style={{color:d.autoexcluidos>0?Q.amber:Q.text,
                fontWeight:800,fontSize:19,
                fontFamily:F_BODY}}>
                {d.autoexcluidos}</div>
              <div style={{color:Q.muted,fontSize:12}}>autoexcluidos</div>
            </GCard>
          </div>

          {toggle("activo","Juego responsable",
            "Apaga o prende toda la sección para los jugadores.")}
          {toggle("limites","Límites del jugador",
            "Cada uno se pone su tope de carga y de apuesta. Bajarlo "+
            "vale ya; subirlo espera.")}
          {toggle("autoexclusion","Autoexclusión",
            "El jugador cierra su cuenta por un plazo. Apagar esto no "+
            "libera a quien ya se excluyó: seguiría excluido hasta que "+
            "venza, porque lo contrario sería romper la promesa que se "+
            "le hizo.")}
          {toggle("aviso_tiempo","Aviso de tiempo de juego",
            "Le recuerda cuánto lleva jugando.")}

          <GCard style={{padding:SPACING[16],marginBottom:9}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:4,
              fontFamily:F_BODY}}>
              Horas de espera para subir un límite</div>
            <input value={d.horas_para_subir} inputMode="numeric"
              onChange={e=>setD({...d,
                horas_para_subir:e.target.value.replace(/\D/g,"")})}
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"12px",color:Q.text,fontSize:16,fontWeight:700,
                textAlign:"center",
                fontFamily:F_BODY}}/>
            <div style={{color:Q.dim,fontSize:12,marginTop:6,
              lineHeight:1.5}}>
              Sin esta espera, alguien que se puso un tope lo levanta
              cuando quiere jugar más y el límite no sirve. La mayoría
              de las jurisdicciones pide 24 horas.</div>
          </GCard>

          <Btn label={proc?"Guardando…":"Guardar"} onClick={()=>guardar()}
            color={Q.violet} full disabled={proc}/>
        </>
      )}

      {solapa==="auditoria"&&(
        <>
          {!aud&&<div style={{color:Q.muted,textAlign:"center",
            padding:SPACING[20]}}>Cargando…</div>}

          {aud&&(
            <>
              <GCard glow={aud.cadena_intacta?null:Q.red}
                style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{display:"flex",
                  justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{color:Q.text,fontSize:13,fontWeight:700,
                    fontFamily:F_BODY}}>
                    Cadena de registros</span>
                  <span style={{color:aud.cadena_intacta?Q.green:Q.red,
                    fontSize:13,fontWeight:800}}>
                    {aud.cadena_intacta?"✓ íntegra":"✗ rota"}</span>
                </div>
                <div style={{color:Q.dim,fontSize:12,marginTop:7,
                  lineHeight:1.5,
                  fontFamily:F_BODY}}>
                  {aud.total} registros. Cada uno incluye el hash del
                  anterior: si alguien edita o borra una fila, la
                  cadena se rompe y queda a la vista.</div>
                {aud.aviso&&(
                  <div style={{color:Q.red,fontSize:12,marginTop:8,
                    lineHeight:1.5}}><Icon name="triangle-alert" size={12}/> {aud.aviso}</div>
                )}
              </GCard>

              {(aud.eventos||[]).map(e=>(
                <div key={e.id} style={{padding:"8px 0",
                  borderTop:`1px solid ${Q.border}`}}>
                  <div style={{display:"flex",
                    justifyContent:"space-between",alignItems:"baseline",
                    gap:SPACING[8]}}>
                    <span style={{color:Q.text,fontSize:12,
                      fontFamily:F_BODY}}>
                      {e.evento}</span>
                    <span style={{color:Q.dim,fontSize:12,
                      flexShrink:0}}>{fechaHora(e.cuando)}</span>
                  </div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                    {e.tipo}{e.actor&&` · ${e.actor}`}
                    {e.objetivo&&` → ${e.objetivo}`}</div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

// El Súper Bono: un porcentaje del GGR se acumula y después se
// reparte entre jugadores conectados.
//
// EL REPARTO NO ES AUTOMÁTICO a propósito. Elegir el momento es una
// decisión comercial: conviene cuando hay gente adentro, no a las
// cuatro de la mañana con tres conectados.
function TabSuperBono({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [form,setForm]=useState(null);

  const cargar=()=>{
    fetch(`${API}/api/admin/superbono`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>{ if(x){ setD(x);
        if(!form) setForm({activo:x.activo,pct:String(x.pct),
          objetivo:String(x.objetivo),ganadores:String(x.ganadores),
          minutos_activo:String(x.minutos_activo),
          min_jugadas:String(x.min_jugadas),
          // La moneda del pozo viene en la RAÍZ de la respuesta y no dentro
          // de `pozo`: el endpoint devuelve `**cfg`.
          moneda:x.moneda||"ARS"}); } })
      .catch(()=>{});
  };
  useEffect(cargar,[]);

  // Las monedas que la casa acepta, de la lista central del backend. El
  // formulario las necesita porque el pozo corre en UNA sola: una lista de
  // opciones escrita a mano acá se desincroniza de `MONEDAS` y el síntoma es
  // un 400 al guardar en una moneda que el resto del sistema sí acepta.
  const [monedas,setMonedas]=useState([]);
  useEffect(()=>{
    fetch(`${API}/api/monedas`).then(r=>r.ok?r.json():{monedas:[]})
      .then(x=>setMonedas(x.monedas||[])).catch(()=>setMonedas([]));
  },[]);

  const guardar=async(over)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/superbono/config`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({...form,
          pct:parseFloat(form.pct)||0,
          objetivo:parseFloat(form.objetivo)||0,
          ganadores:parseInt(form.ganadores)||1,
          minutos_activo:parseInt(form.minutos_activo)||15,
          min_jugadas:parseInt(form.min_jugadas)||0, ...(over||{})})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const acumular=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/superbono/acumular`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({dias:1})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:`+${money(x.aporte,d.moneda)} al pozo `+
        `(GGR ${money(x.ggr_periodo,d.moneda)})`, ok:true});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const repartir=async()=>{
    // LA CONFIRMACIÓN YA NO DICE "entre N conectados", y es a propósito:
    // `d.conectados` cuenta a todos los que están adentro, de cualquier
    // moneda, y el reparto sólo alcanza a los de la moneda del pozo. Ese
    // número daba de más. Cuántos son de verdad no está en la respuesta, así
    // que acá se nombra la moneda y el backend contesta 400 nombrándola si no
    // hay ninguno.
    if(!window.confirm(
      `¿Repartir ${money(d.pozo.acumulado,d.moneda)} entre los `+
      `conectados en ${d.moneda}? Se acredita al instante y no se `+
      `puede deshacer.`))
      return;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/superbono/repartir`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:`🎉 ${x.ganadores} ganadores · `+
        `${money(x.monto_c_u,d.moneda)} cada uno`, ok:true});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d||!form) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:16,fontWeight:700,textAlign:"center",
    fontFamily:F_BODY};

  // El GGR sin tomar, una fila por moneda. La guarda es para una respuesta
  // vieja de caché o un frontend desfasado del backend en un despliegue.
  const ggrSinTomar=Array.isArray(d.ggr_sin_tomar_por_moneda)
    ?d.ggr_sin_tomar_por_moneda:[];
  const variasMonedas=ggrSinTomar.length>1;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><PartyPopper size={17}/> Súper Bono</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Un porcentaje del GGR del casino se acumula y se reparte entre
        jugadores conectados.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center",lineHeight:1.45}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <button onClick={()=>{const v=!form.activo;
          setForm({...form,activo:v}); guardar({activo:v});}}
        style={{width:"100%",
          background:form.activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
          border:`1px solid ${form.activo?Q.green:Q.border}`,
          borderRadius:RADII.md,padding:"12px",cursor:"pointer",marginBottom:12,
          color:form.activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
          fontFamily:F_BODY}}>
        {form.activo?<><Icon name="circle-check" size={13}/> Activo</>:<><CircleOff size={13}/> Apagado</>}</button>

      <GCard glow={d.pozo.listo?Q.gold:Q.cyan}
        style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
          marginBottom:6}}>POZO ACTUAL · {d.moneda}</div>
        <div style={{color:Q.gold,fontWeight:900,fontSize:30,
          fontFamily:F_BODY}}>
          {money(d.pozo.acumulado,d.moneda)}</div>

        <div style={{height:6,background:"rgba(255,255,255,.07)",
          borderRadius:RADII.sm,marginTop:10,overflow:"hidden"}}>
          <div style={{height:"100%",
            width:`${Math.min(100,d.pozo.pct_completo)}%`,
            background:d.pozo.listo
              ?`linear-gradient(90deg,${Q.gold},#FFD54F)`
              :`linear-gradient(90deg,${Q.violet},${Q.cyan})`,
            transition:"width .4s"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",
          marginTop:6}}>
          <span style={{color:Q.dim,fontSize:12}}>
            {d.pozo.pct_completo}% de {money(d.pozo.objetivo,d.moneda)}</span>
          <span style={{color:Q.dim,fontSize:12}}>
            {d.pozo.aportes} aportes</span>
        </div>

        <div style={{display:"flex",gap:SPACING[12],marginTop:12,paddingTop:SPACING[12],
          borderTop:`1px solid ${Q.border}`}}>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12}}>Conectados ahora</div>
            <div style={{color:d.conectados>0?Q.green:Q.dim,
              fontWeight:800,fontSize:19,
              fontFamily:F_BODY}}>
              {d.conectados}</div>
          </div>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12}}>GGR sin tomar</div>
            {/* El GGR sin tomar viene agrupado por moneda, y sigue viniendo
                así aunque el pozo ya sea de una sola: es lo que deja ver que
                hay movimiento en una moneda que el pozo NO toma, que es la
                única forma de notar que la configuración apunta a la moneda
                equivocada. El pozo de arriba sí es un número solo, y ahora
                dice de qué moneda. */}
            {ggrSinTomar.map(m=>(
              <div key={m.moneda} style={{color:Q.text,fontWeight:800,
                fontSize:19,fontFamily:F_BODY}}>
                {money(m.ggr,m.moneda,{conCodigo:variasMonedas})}</div>
            ))}
          </div>
        </div>
      </GCard>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
        <button onClick={acumular} disabled={proc||!form.activo}
          style={{flex:1,background:`${Q.cyan}18`,
            border:`1px solid ${Q.cyan}55`,borderRadius:RADII.md,
            padding:"12px",color:Q.cyan,fontSize:12.5,fontWeight:700,
            cursor:"pointer",
            fontFamily:F_BODY}}>
          ↑ Sumar el GGR de ayer</button>
        <button onClick={repartir}
          disabled={proc||d.pozo.acumulado<=0||d.conectados===0}
          style={{flex:1,
            background:(d.pozo.acumulado>0&&d.conectados>0)
              ?`linear-gradient(135deg,${Q.gold},#c9a227)`
              :"rgba(255,255,255,0.05)",
            border:"none",borderRadius:RADII.md,padding:"12px",
            color:(d.pozo.acumulado>0&&d.conectados>0)?"#1a1200":Q.dim,
            fontSize:12.5,fontWeight:800,cursor:"pointer",
            fontFamily:F_BODY}}>
          <PartyPopper size={13}/> Repartir</button>
      </div>

      {d.conectados===0&&d.pozo.acumulado>0&&(
        <div style={{color:Q.amber,fontSize:12,marginBottom:12,
          lineHeight:1.5,fontFamily:F_BODY}}>
          No hay nadie conectado. Repartir ahora no le llegaría a
          nadie: esperá a que haya movimiento.</div>
      )}

      {/* LA MONEDA DEL POZO, y va ARRIBA de los montos porque es lo que los
          rotula y lo que decide quién puede ganar. Sólo aporta el GGR de esta
          moneda y sólo entran al sorteo los jugadores que la llevan: antes el
          pozo sumaba el GGR de todas y le acreditaba el mismo número a cada
          ganador en la moneda de cada uno. */}
      <GCard style={{padding:SPACING[16],marginBottom:9}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Moneda del pozo</div>
        <select value={form.moneda}
          onChange={e=>setForm({...form,moneda:e.target.value})}
          aria-label="Moneda del pozo" style={inp}>
          {monedas.length===0&&<option value={form.moneda}>{form.moneda}</option>}
          {monedas.map(m=>(
            <option key={m.codigo} value={m.codigo}>
              {m.codigo} · {m.nombre}</option>))}
        </select>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          El objetivo de abajo está en esta moneda, y sólo pueden ganar los
          jugadores que la llevan. Con el pozo ya acumulado no se puede
          cambiar: hay que repartirlo o vaciarlo antes.</div>
      </GCard>

      {[["Porcentaje del GGR (%)","pct",
         "Cuánto de lo que gana el casino va al pozo. No se le "+
         "descuenta nada al jugador: cobra siempre lo que el juego "+
         "le paga."],
        ["Objetivo del pozo","objetivo",
         "Cuando llega a este monto, la barra se completa. Podés "+
         "repartir antes igual."],
        ["Cuántos ganadores","ganadores",
         "El pozo se divide en partes iguales entre ellos."],
        ["Minutos para contar como conectado","minutos_activo",
         "La app avisa cada dos minutos que el jugador está adentro."],
      ].map(([label,clave,ayuda])=>(
        <GCard key={clave} style={{padding:SPACING[16],marginBottom:9}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4,
            fontFamily:F_BODY}}>{label}</div>
          <input value={form[clave]} inputMode="decimal"
            onChange={e=>setForm({...form,[clave]:e.target.value})}
            style={inp}/>
          <div style={{color:Q.dim,fontSize:12,marginTop:6,
            lineHeight:1.5}}>{ayuda}</div>
        </GCard>
      ))}

      <Btn label={proc?"Guardando…":"Guardar"} onClick={()=>guardar()}
        color={Q.violet} full disabled={proc}/>

      {(d.historial||[]).length>0&&(
        <GCard style={{padding:SPACING[16],marginTop:14}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.5,
            marginBottom:8}}>REPARTOS ANTERIORES</div>
          {/* LA MONEDA SALE DE CADA FILA, no de la configuración de hoy.
              `superbono_pozo` no tiene columna `moneda`, así que el backend la
              deriva de los ganadores de ESE pozo, que son los que cobraron.
              Rotular la fila con `d.moneda` haría que el reparto de octubre
              dijera la moneda de noviembre en cuanto alguien la cambie.

              Y CUANDO LA FILA VIENE SIN MONEDA SE DIBUJA SIN SÍMBOLO. Un pozo
              repartido antes de este cambio puede tener ganadores de monedas
              distintas: el backend manda `moneda: null` y la lista de las que
              hay. Elegir una para mostrar sería tapar la única fila que prueba
              que el defecto existió. */}
          {d.historial.map(h=>{
            const mezclado=!h.moneda;
            return(
            <div key={h.id} style={{display:"flex",
              justifyContent:"space-between",alignItems:"baseline",
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <div>
                <div style={{color:Q.text,fontSize:12.5,
                  fontFamily:F_BODY}}>
                  {mezclado
                    ?Number(h.monto||0).toLocaleString("es-AR")
                    :money(h.monto,h.moneda,{conCodigo:true})}</div>
                <div style={{color:Q.dim,fontSize:12}}>
                  {h.ganadores} ganadores · {mezclado
                    ?Number(h.c_u||0).toLocaleString("es-AR")
                    :money(h.c_u,h.moneda)} c/u</div>
                {mezclado&&(
                  <div style={{color:Q.amber,fontSize:12,marginTop:4,
                    lineHeight:1.45,fontFamily:F_BODY}}>
                    Este pozo se repartió entre jugadores de varias monedas
                    ({(h.monedas||[]).join(", ")}), así que el monto no tiene
                    una sola: cada ganador cobró ese número en la suya.</div>
                )}
              </div>
              <span style={{color:Q.dim,fontSize:12}}>{fechaHora(h.cuando)}</span>
            </div>
            );
          })}
        </GCard>
      )}
    </div>
  );
}

// Qué monedas se le ofrecen a un cliente que entra por su cuenta.
// Antes se adivinaba por la IP y fallaba: alguien en Argentina con
// un Telegram de otro país terminaba con la moneda equivocada.
function TabMonedas({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [sel,setSel]=useState([]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=()=>{
    fetch(`${API}/api/admin/monedas`,{headers:adminHeaders(adminKey)})
      .then(r=>{ if(r.status===401){onNoAutorizado();return null;}
        return r.ok?r.json():null; })
      .then(x=>{ if(x){ setD(x); setSel(x.activas||[]); } })
      .catch(()=>{});
  };
  useEffect(cargar,[]);

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/monedas`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({monedas:sel})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return <div style={{color:Q.muted,textAlign:"center",
    padding:SPACING[20]}}>Cargando…</div>;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><ArrowLeftRight size={16}/> Monedas</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        Entre cuáles puede elegir alguien que se registra por su
        cuenta, sin que lo dé de alta una agencia.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {d.todas.map(m=>{
        const on=sel.includes(m.codigo);
        const usan=d.en_uso?.[m.codigo]||0;
        return(
          <button key={m.codigo}
            onClick={()=>setSel(s=>on?s.filter(x=>x!==m.codigo)
                                     :[...s,m.codigo])}
            style={{width:"100%",display:"flex",
              justifyContent:"space-between",alignItems:"center",
              background:on?`${Q.green}10`:"rgba(255,255,255,0.03)",
              border:`1px solid ${on?`${Q.green}55`:Q.border}`,
              borderRadius:RADII.md,padding:"12px 12px",marginBottom:6,
              cursor:"pointer",
              fontFamily:F_BODY}}>
            <span style={{color:on?Q.text:Q.dim,fontSize:13,
              fontWeight:600}}>
              {m.simbolo} {m.nombre}
              <span style={{color:Q.dim,fontSize:12,marginLeft:6,
                fontWeight:400}}>{m.codigo}</span></span>
            <span style={{fontSize:12,fontWeight:700,
              color:on?Q.green:Q.muted}}>
              {usan>0&&(
                <span style={{color:Q.dim,fontWeight:400,marginRight:7}}>
                  {usan} {usan===1?"cliente":"clientes"}</span>
              )}
              {on?"✓":"—"}</span>
          </button>
        );
      })}

      {sel.length===0&&(
        <div style={{color:Q.red,fontSize:12,marginTop:8,
          lineHeight:1.5,fontFamily:F_BODY}}>
          Sin ninguna moneda nadie puede registrarse.</div>
      )}

      {/* Apagar una moneda que ya usan clientes los deja sin poder
          operar: conviene avisarlo antes de guardar. */}
      {d.todas.some(m=>!sel.includes(m.codigo)&&(d.en_uso?.[m.codigo]||0)>0)&&(
        <div style={{color:Q.amber,fontSize:12,marginTop:8,
          lineHeight:1.55,fontFamily:F_BODY}}>
          Estás dejando afuera monedas que ya usan clientes. Los que
          las tengan siguen operando, pero nadie nuevo va a poder
          elegirlas.</div>
      )}

      <div style={{height:12}}/>
      <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
        color={Q.violet} full disabled={proc||sel.length===0}/>
    </div>
  );
}

// ── Recompensa por compartir ──────────────────────────────────
// Se paga por visitas reales al enlace, no por tocar compartir: el
// teléfono no avisa si la persona compartió o canceló, así que pagar
// el toque sería regalar plata a quien abra y cierre el menú.
function TabRecompensas({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [activo,setActivo]=useState(false);
  const [monto,setMonto]=useState("0.10");
  const [maxAp,setMaxAp]=useState("5");
  const [maxDia,setMaxDia]=useState("20");
  const [roll,setRoll]=useState("1");
  const [pctAg,setPctAg]=useState("0");
  // Dónde y a quiénes se ofrece
  const [destinos,setDestinos]=useState(["app","web"]);
  const [ramas,setRamas]=useState([]);
  const [excluidas,setExcluidas]=useState([]);
  const [ags,setAgs]=useState([]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  useEffect(()=>{
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAgs(Array.isArray(d)?d:(d.agencias||[]))).catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/recompensas`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const x=await r.json();
        setD(x); setActivo(!!x.activo);
        setMonto(String(x.monto_usd)); setMaxAp(String(x.max_por_apuesta));
        setMaxDia(String(x.max_por_dia)); setRoll(String(x.rollover));
        setPctAg(String(x.pct_agencia||0));
        setDestinos(x.destinos?.length?x.destinos:["app","web"]);
        setRamas(x.ramas||[]); setExcluidas(x.excluidas||[]);
      }
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const guardar=async(over)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/recompensas`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({activo, monto_usd:parseFloat(monto)||0,
          max_por_apuesta:parseInt(maxAp)||5,
          max_por_dia:parseInt(maxDia)||20,
          rollover:parseFloat(roll)||1,
          pct_agencia:parseFloat(pctAg)||0,
          destinos, ramas, excluidas, ...(over||{})})});
      if(r.status===401){ onNoAutorizado(); return; }
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||`Error ${r.status}`);
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:16,fontWeight:700,textAlign:"center",
    fontFamily:F_BODY};

  // "QUIÉNES MÁS GANARON" mezcla clientes de monedas distintas: el backend
  // agrupa por `(user_id, moneda)` justamente para que cada fila sea un
  // cliente EN una moneda (`bot/casino_api.py:12544`), y el `ganado` iba en
  // pesos clavados. Un mismo cliente puede aparecer dos veces, una por
  // moneda, y sin el código las dos filas se leían iguales.
  const montoTop = formatoDeLista(d?.top);

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}>
        <Megaphone size={16}/> Recompensa por compartir</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.55,fontFamily:F_BODY}}>
        El cliente comparte su jugada y gana por cada persona distinta
        que entre con su enlace.</div>

      <div style={{background:`${Q.cyan}0F`,border:`1px solid ${Q.cyan}44`,
        borderRadius:RADII.md,padding:"12px 12px",marginBottom:12,fontSize:12,
        color:Q.text,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Se paga por <b>visitas reales</b>, no por tocar compartir. El
        teléfono no avisa si la persona compartió o canceló, así que
        pagar el toque sería regalarle plata a cualquiera que abra y
        cierre el menú cinco veces.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <button onClick={()=>{const v=!activo; setActivo(v); guardar({activo:v});}}
        style={{width:"100%",
        background:activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
        border:`1px solid ${activo?Q.green:Q.border}`,borderRadius:RADII.md,
        padding:"12px",cursor:"pointer",marginBottom:12,
        color:activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
        fontFamily:F_BODY}}>
        {activo?<><Icon name="circle-check" size={13}/> Activo</>:<><CircleOff size={13}/> Apagado</>}</button>

      {d&&d.visitas_pagadas>0&&(
        <GCard style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
            marginBottom:8}}>ÚLTIMOS 30 DÍAS</div>
          <div style={{display:"flex",gap:SPACING[12],marginBottom:8}}>
            <div style={{flex:1}}>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>
                {d.visitas_pagadas}</div>
              <div style={{color:Q.muted,fontSize:12}}>visitas pagadas</div>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>{d.clientes}</div>
              <div style={{color:Q.muted,fontSize:12}}>clientes</div>
            </div>
            {/* El costo del programa viene agrupado por moneda: corre sobre
                todas las agencias, así que un total plano sumaría pesos con
                dólares. Con UNA sola moneda esto se dibuja igual que antes
                —una celda, el rótulo "costo" sin agregado—; el código
                aparece sólo cuando hay con qué confundirse, porque ARS, CLP,
                COP y MXN comparten el signo `$`.

                La guarda no es defensiva por costumbre: `por_moneda` puede
                faltar si contesta una respuesta vieja de caché o si el
                frontend queda desfasado del backend en un despliegue, y es
                mejor no dibujar el costo que reventar la pestaña con un
                `.map` sobre `undefined`. */}
            {(Array.isArray(d.por_moneda)?d.por_moneda:[]).map((m,_i,todas)=>(
              <div key={m.moneda} style={{flex:1}}>
                <div style={{color:Q.amber,fontWeight:800,fontSize:18,
                  fontFamily:F_BODY}}>
                  {money(m.costo_total,m.moneda)}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {todas.length>1?`costo · ${m.moneda}`:"costo"}</div>
              </div>
            ))}
          </div>

          {(d.rechazos||[]).length>0&&(
            <div style={{paddingTop:SPACING[8],borderTop:`1px solid ${Q.border}`}}>
              <div style={{color:Q.dim,fontSize:12,marginBottom:4}}>
                VISITAS NO PAGADAS</div>
              {d.rechazos.map(r=>(
                <div key={r.motivo} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,
                  padding:"4px 0",color:Q.muted}}>
                  <span>{{misma_ip:"Misma conexión que el que compartió",
                    mismo_device:"Mismo dispositivo",
                    repetida:"Ya había entrado",
                    tope:"Llegó al tope"}[r.motivo]||r.motivo}</span>
                  <span style={{color:Q.dim}}>{r.cantidad}</span>
                </div>
              ))}
            </div>
          )}
        </GCard>
      )}

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Por visita, en dólares</div>
        <input value={monto} inputMode="decimal"
          onChange={e=>setMonto(e.target.value)} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          Se convierte a la moneda de cada cliente. Con 0.10 son unos
          100 en pesos, 400 en pesos colombianos.</div>
      </GCard>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <GCard style={{flex:1,padding:SPACING[16]}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
            Tope por apuesta</div>
          <input value={maxAp} inputMode="numeric"
            onChange={e=>setMaxAp(e.target.value.replace(/\D/g,""))}
            style={inp}/>
        </GCard>
        <GCard style={{flex:1,padding:SPACING[16]}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
            Tope por día</div>
          <input value={maxDia} inputMode="numeric"
            onChange={e=>setMaxDia(e.target.value.replace(/\D/g,""))}
            style={inp}/>
        </GCard>
      </div>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Cuánto tiene que apostarlo antes de retirar</div>
        <input value={roll} inputMode="decimal"
          onChange={e=>setRoll(e.target.value)} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          Entra como saldo de bono. Con 1x tiene que apostarlo una vez
          para poder retirarlo. Menos de 1 no se acepta: sería plata
          retirable sin jugar.</div>
      </GCard>

      {/* Dónde se ofrece y a quiénes. Si una agencia queda afuera,
          sus clientes no ven el botón: prometer un premio que no se
          va a pagar es peor que no ofrecerlo. */}
      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
          fontFamily:F_BODY}}>
          Dónde se ofrece</div>

        <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
          {[["app",<><Smartphone size={13}/> App</>],["web",<><Globe size={13}/> Sitio</>]].map(([k,l])=>{
            const on=destinos.includes(k);
            return(
              <button key={k} onClick={()=>{
                  const n=on?destinos.filter(x=>x!==k):[...destinos,k];
                  setDestinos(n); guardar({destinos:n});
                }} style={{flex:1,
                background:on?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                border:`1px solid ${on?Q.violet:Q.border}`,borderRadius:RADII.md,
                padding:"12px 8px",cursor:"pointer",
                color:on?Q.cyan:Q.muted,fontSize:12,
                fontWeight:on?700:400,
                fontFamily:F_BODY}}>{l}</button>
            );
          })}
        </div>

        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Solo estas agencias (vacío = todas)</div>
        <select value="" onChange={e=>{
            if(e.target.value&&!ramas.includes(e.target.value)){
              const n=[...ramas,e.target.value];
              setRamas(n); guardar({ramas:n});
            }
          }} style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"12px 12px",color:Q.text,fontSize:13,marginBottom:6,
            fontFamily:F_BODY}}>
          <option value="">+ Agregar agencia o rama…</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>
              {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
          ))}
        </select>
        <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4],marginBottom:12}}>
          {ramas.length===0&&(
            <span style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY}}>
              Todas las agencias</span>
          )}
          {ramas.map(rc=>(
            <span key={rc} onClick={()=>{
                const n=ramas.filter(x=>x!==rc);
                setRamas(n); guardar({ramas:n});
              }}
              style={{background:`${Q.violet}22`,
                border:`1px solid ${Q.violet}55`,borderRadius:RADII.md,
                padding:"4px 12px",color:Q.cyan,fontSize:12,
                cursor:"pointer",
                fontFamily:F_BODY}}>{rc} ✕</span>
          ))}
        </div>

        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Quitárselo a una agencia</div>
        <select value="" onChange={e=>{
            if(e.target.value&&!excluidas.includes(e.target.value)){
              const n=[...excluidas,e.target.value];
              setExcluidas(n); guardar({excluidas:n});
            }
          }} style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"12px 12px",color:Q.text,fontSize:13,marginBottom:6,
            fontFamily:F_BODY}}>
          <option value="">Elegí la agencia a excluir…</option>
          {ags.map(a=>(
            <option key={a.code} value={a.code}>
              {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
          ))}
        </select>
        <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
          {excluidas.length===0&&(
            <span style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY}}>
              Ninguna excluida</span>
          )}
          {excluidas.map(rc=>(
            <span key={rc} onClick={()=>{
                const n=excluidas.filter(x=>x!==rc);
                setExcluidas(n); guardar({excluidas:n});
              }}
              style={{background:`${Q.red}18`,border:`1px solid ${Q.red}55`,
                borderRadius:RADII.md,padding:"4px 12px",color:Q.red,fontSize:12,
                cursor:"pointer",
                fontFamily:F_BODY}}>{rc} ✕</span>
          ))}
        </div>
        <div style={{color:Q.dim,fontSize:12,marginTop:7,lineHeight:1.5,
          fontFamily:F_BODY}}>
          La exclusión alcanza también a las agencias que cuelgan de
          ella. Sus clientes no ven el botón de compartir.</div>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Cuánto paga la agencia (%)</div>
        <input value={pctAg} inputMode="decimal"
          onChange={e=>setPctAg(e.target.value)} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          El resto lo pone la casa. Aparece restando en el cierre de
          cada uno.</div>
      </GCard>

      <Btn label={proc?"Guardando…":"Guardar"} onClick={()=>guardar()}
        color={Q.violet} full disabled={proc}/>

      {d&&(d.top||[]).length>0&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
            marginBottom:7}}>QUIÉNES MÁS GANARON</div>
          {d.top.map(t=>(
            <div key={t.user_id} style={{display:"flex",
              justifyContent:"space-between",fontSize:12,padding:"4px 0",
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text}}>{t.nombre}
                <span style={{color:Q.dim,fontSize:12,marginLeft:5}}>
                  {t.visitas} visitas</span></span>
              <span style={{color:Q.gold,
                fontFamily:F_BODY}}>
                {montoTop(t.ganado,t.moneda)}</span>
            </div>
          ))}
        </GCard>
      )}
    </div>
  );
}

// ── Qué productos ofrece cada agencia ─────────────────────────
// El permiso baja por el árbol y nadie puede ampliarlo: si acá se
// apaga casino para una rama, ninguna agencia de esa rama lo tiene
// aunque su padre intente habilitarlo.
function TabProductosPermisos({ adminKey, onNoAutorizado }){
  const [datos,setDatos]=useState(null);
  const [ags,setAgs]=useState([]);
  const [sel,setSel]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/productos`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setDatos(await r.json());
    }catch(e){}
  };
  useEffect(()=>{
    cargar();
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAgs(Array.isArray(d)?d:(d.agencias||[]))).catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const global_=async(codigo,activo)=>{
    setProc(true);
    try{
      const r=await fetch(`${API}/api/admin/productos/catalogo`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({codigo,activo})});
      if(r.ok){ setMsg({text:"Guardado", ok:true}); cargar(); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const porAgencia=async(agencia_code,producto,activo)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/productos/agencia`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({agencia_code,producto,activo})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:activo?"Habilitado":"Deshabilitado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const restriccionesDe=(code)=>(datos?.restricciones||[])
    .filter(r=>r.agencia===code).map(r=>r.producto);

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Gamepad2 size={17}/> Productos</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Qué puede ofrecer cada agencia. Lo que apagues acá no lo puede
        volver a encender ninguna agencia de esa rama.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {!datos&&<div style={{color:Q.muted,textAlign:"center",
        padding:SPACING[20]}}>Cargando…</div>}

      {datos&&(
        <>
          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,
              marginBottom:3,fontFamily:F_BODY}}>
              En todo el sistema</div>
            <div style={{color:Q.dim,fontSize:12,marginBottom:10,
              lineHeight:1.45}}>
              Apagar uno acá lo saca de todas las agencias.</div>
            {datos.catalogo.map(c=>(
              <button key={c.codigo} onClick={()=>global_(c.codigo,!c.activo)}
                disabled={proc}
                style={{width:"100%",display:"flex",
                  justifyContent:"space-between",alignItems:"center",
                  background:c.activo?`${Q.green}12`:"rgba(255,255,255,0.03)",
                  border:`1px solid ${c.activo?`${Q.green}55`:Q.border}`,
                  borderRadius:RADII.md,padding:"12px 12px",marginBottom:6,
                  cursor:"pointer",
                  fontFamily:F_BODY}}>
                <span style={{color:c.activo?Q.text:Q.dim,fontSize:13,
                  fontWeight:600}}>{c.icono} {c.nombre}</span>
                <span style={{color:c.activo?Q.green:Q.muted,fontSize:12,
                  fontWeight:700}}>
                  {c.activo?"Activo":"Apagado"}</span>
              </button>
            ))}
          </GCard>

          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,
              marginBottom:8,fontFamily:F_BODY}}>
              Por agencia</div>
            <select value={sel} onChange={e=>setSel(e.target.value)}
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"12px 12px",color:Q.text,fontSize:13,
                marginBottom:sel?10:0,
                fontFamily:F_BODY}}>
              <option value="">Elegí una agencia…</option>
              {ags.map(a=>(
                <option key={a.code} value={a.code}>
                  {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
              ))}
            </select>

            {sel&&datos.catalogo.filter(c=>c.activo).map(c=>{
              const apagado=restriccionesDe(sel).includes(c.codigo);
              return(
                <button key={c.codigo}
                  onClick={()=>porAgencia(sel,c.codigo,apagado)}
                  disabled={proc}
                  style={{width:"100%",display:"flex",
                    justifyContent:"space-between",alignItems:"center",
                    background:apagado?`${Q.red}12`:`${Q.green}12`,
                    border:`1px solid ${apagado?`${Q.red}55`:`${Q.green}55`}`,
                    borderRadius:RADII.md,padding:"12px 12px",marginBottom:6,
                    cursor:"pointer",
                    fontFamily:F_BODY}}>
                  <span style={{color:apagado?Q.dim:Q.text,fontSize:13,
                    fontWeight:600}}>{c.icono} {c.nombre}</span>
                  <span style={{color:apagado?Q.red:Q.green,fontSize:12,
                    fontWeight:700}}>
                    {apagado?"No lo tiene":"Lo tiene"}</span>
                </button>
              );
            })}
            {sel&&(
              <div style={{color:Q.dim,fontSize:12,marginTop:6,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Alcanza también a todas las agencias que cuelgan de ella.</div>
            )}
          </GCard>

          {(datos.restricciones||[]).length>0&&(
            <GCard glow={Q.amber} style={{padding:SPACING[16]}}>
              <div style={{color:Q.amber,fontWeight:700,fontSize:12.5,
                marginBottom:8,fontFamily:F_BODY}}>
                Restricciones activas</div>
              {datos.restricciones.map((r,i)=>(
                <div key={i} style={{display:"flex",
                  justifyContent:"space-between",alignItems:"center",
                  fontSize:12,padding:"4px 0",
                  borderTop:i?`1px solid ${Q.border}`:"none"}}>
                  <span style={{color:Q.text}}>
                    {r.agencia_nombre||r.agencia}</span>
                  <span style={{color:Q.red,fontSize:12}}>
                    sin {r.producto}
                    {r.definido_por&&r.definido_por!=="admin"&&(
                      <span style={{color:Q.dim}}> · por {r.definido_por}</span>
                    )}</span>
                </div>
              ))}
            </GCard>
          )}
        </>
      )}
    </div>
  );
}

// ── Reportes por producto ─────────────────────────────────────
// Deportivas, casino y ruleta juntos y desglosados. Cada producto
// guarda lo suyo con su propia forma, así que el backend los traduce
// a una vista común antes de sumarlos.
function TabProductos({ adminKey, onNoAutorizado }){
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [datos,setDatos]=useState(null);
  const [abierto,setAbierto]=useState(null);
  const [cargando,setCargando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API}/api/admin/reportes/productos?desde=${desde}&hasta=${hasta}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setDatos(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const ICONO={deportivas:<Icon name="trophy" size={13}/>, casino:<Icon name="spade" size={13}/>, ruleta:<Disc size={13}/>};
  const NOMBRE={deportivas:"Deportivas", casino:"Casino", ruleta:"Ruleta"};

  // ── ESTA PESTAÑA LLAMA AL ENDPOINT SIN FILTRO DE AGENCIA ─────
  //
  // `cargar()` pega a `/api/admin/reportes/productos` sin el parámetro
  // `agencia`, o sea que el `total` suma TODAS las agencias de la casa. Por
  // eso el endpoint lo devuelve agrupado y las tres claves planas de plata se
  // retiraron: ese número era pesos más dólares junto.
  //
  // La guarda con `Array.isArray` no es defensiva por costumbre: `por_moneda`
  // puede faltar si contesta una respuesta vieja de caché o si el frontend
  // queda desfasado del backend en un despliegue. Entonces no se dibuja
  // plata, que es mejor que reventar la pestaña con un `.map` sobre
  // `undefined`.
  //
  // `jugadas` queda plana y se muestra una sola vez: es una CUENTA y una
  // jugada es una jugada en cualquier moneda.
  const totMonedas = Array.isArray(datos?.total?.por_moneda)
    ? datos.total.por_moneda : [];
  const variasTot = totMonedas.length > 1;
  const rotTot = (texto, m) => variasTot ? `${texto} · ${m.moneda}` : texto;
  // La lista de productos mezcla monedas renglón a renglón: cada monto se
  // formatea con la moneda de SU fila, y el código aparece sólo cuando hay
  // con qué confundirse. Con una sola moneda la pantalla se ve igual que hoy.
  const montoProd = formatoDeLista(datos?.productos);
  // Con `por_moneda=True` la clave del agrupado es `(producto, moneda)`: el
  // casino en pesos y el casino en dólares son dos renglones, así que el
  // `key` de React y el acordeón tienen que distinguirlos o los dos se abren
  // y se cierran juntos.
  const idProd = (p) => `${p.producto}|${p.moneda || ""}`;
  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="chart-no-axes-combined" size={14}/> Por producto</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Todo lo jugado, separado por producto y sumado. El GGR es lo
        apostado menos lo pagado en premios.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Cargando…":"Ver"} onClick={cargar}
        color={Q.violet} full disabled={cargando}/>

      {datos&&(
        <>
          {totMonedas.map(m=>(
          <GCard key={m.moneda} style={{padding:SPACING[16],margin:"12px 0"}}>
            <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
              marginBottom:8}}>{rotTot("TOTAL DEL PERÍODO",m)}</div>
            <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Apostado</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:17,
                  fontFamily:F_BODY}}>
                  {money(m.apostado,m.moneda)}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Pagado</div>
                <div style={{color:Q.muted,fontWeight:700,fontSize:17,
                  fontFamily:F_BODY}}>
                  {money(m.pagado,m.moneda)}</div>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"baseline",paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text,fontWeight:700,fontSize:13}}>
                GGR
                {m.margen_pct!=null&&(
                  <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                    margen {m.margen_pct}%</span>
                )}
              </span>
              <span style={{color:m.ggr>=0?Q.green:Q.red,
                fontWeight:900,fontSize:22,
                fontFamily:F_BODY}}>
                {money(m.ggr,m.moneda)}</span>
            </div>
          </GCard>
          ))}

          {datos.productos.length===0&&(
            <GCard style={{padding:SPACING[24],textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:13,
                fontFamily:F_BODY}}>
                No hubo actividad en ese período</div>
            </GCard>
          )}

          {datos.productos.map(p=>(
            <GCard key={idProd(p)} style={{padding:SPACING[16],marginBottom:8}}>
              <div onClick={()=>setAbierto(abierto===idProd(p)?null:idProd(p))}
                style={{cursor:p.detalle.length?"pointer":"default",
                  display:"flex",justifyContent:"space-between",
                  alignItems:"center",gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:13.5,
                    fontFamily:F_BODY}}>
                    {ICONO[p.producto]||"•"} {NOMBRE[p.producto]||p.producto}
                    {variasTot&&p.moneda&&(
                      <span style={{color:Q.dim,fontSize:12,marginLeft:5}}>
                        {p.moneda}</span>
                    )}
                    {p.detalle.length>0&&(
                      <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                        {abierto===idProd(p)?"▲":"▼"}</span>
                    )}
                  </div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                    {p.jugadas.toLocaleString("es-AR")} jugadas ·{" "}
                    {p.jugadores} jugadores ·{" "}
                    {montoProd(p.apostado,p.moneda)} apostado
                    {p.margen_pct!=null?` · margen ${p.margen_pct}%`:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:p.ggr>=0?Q.green:Q.red,fontWeight:800,
                    fontSize:16,
                    fontFamily:F_BODY}}>
                    {montoProd(p.ggr,p.moneda)}</div>
                  <div style={{color:Q.dim,fontSize:12}}>GGR</div>
                </div>
              </div>

              {abierto===idProd(p)&&p.detalle.length>0&&(
                <div style={{marginTop:10,paddingTop:SPACING[12],
                  borderTop:`1px solid ${Q.border}`}}>
                  {p.detalle.map(d=>(
                    <div key={d.nombre} style={{display:"flex",
                      justifyContent:"space-between",fontSize:12,
                      padding:"4px 0",color:Q.muted}}>
                      <span style={{minWidth:0,flex:1,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {d.nombre}
                        <span style={{color:Q.dim,fontSize:12,
                          marginLeft:5}}>{d.jugadas}</span></span>
                      {/* El detalle es de SU producto, así que hereda la
                          moneda de la fila que lo contiene: `v_actividad`
                          agrupa por `(producto, detalle, moneda)`. */}
                      <span style={{color:d.ggr>=0?Q.green:Q.red,
                        flexShrink:0,
                        fontFamily:F_BODY}}>
                        {montoProd(d.ggr,p.moneda)}</span>
                    </div>
                  ))}
                </div>
              )}
            </GCard>
          ))}
        </>
      )}
    </div>
  );
}

// ── Soporte a los jugadores ───────────────────────────────────
// El admin ve todos los tickets, de todas las agencias. Los que la
// IA derivó aparecen primero, y dentro de esos los sensibles: son
// los que no pueden esperar.
function TabSoporte({ adminKey, onNoAutorizado }){
  const [estado,setEstado]=useState("abierto");
  const [datos,setDatos]=useState(null);
  const [abierto,setAbierto]=useState(null);
  const [detalle,setDetalle]=useState(null);
  const [texto,setTexto]=useState("");
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState("");
  const finRef=useRef(null);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/soporte?estado=${estado}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setDatos(await r.json());
    }catch(e){ setDatos({tickets:[]}); }
  };
  useEffect(()=>{
    cargar();
    const t=setInterval(cargar,45000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[estado]);

  const abrir=async(id)=>{
    setAbierto(id); setDetalle(null);
    try{
      const r=await fetch(`${API}/api/admin/soporte/${id}`,
        {headers:adminHeaders(adminKey)});
      if(r.ok) setDetalle(await r.json());
    }catch(e){}
  };

  useEffect(()=>{ finRef.current?.scrollIntoView({behavior:"smooth"}); },[detalle]);

  const responder=async(cerrar)=>{
    const t=texto.trim();
    if(!t&&!cerrar) return;
    setProc(true);
    try{
      const r=await fetch(`${API}/api/admin/soporte/${abierto}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({texto:t,cerrar})});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        setTexto("");
        if(cerrar){ setAbierto(null); cargar(); }
        else abrir(abierto);
      }
    }catch(e){ setMsg("⚠️ "+e.message); }
    setProc(false);
  };

  const AUTOR={cliente:"Cliente", ia:"Asistente",
               agencia:"Agencia", admin:"Vos"};

  // ── Un ticket abierto ──
  if(abierto){
    return(
      <div style={{display:"flex",flexDirection:"column",
        height:"calc(100dvh - 210px)",minHeight:340}}>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
          marginBottom:10}}>
          <button onClick={()=>{setAbierto(null);setDetalle(null);}}
            style={{background:"transparent",border:"none",color:Q.cyan,
              fontSize:20,cursor:"pointer",padding:0}}>‹</button>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>
              {detalle?.cliente||"Cargando…"}</div>
            {/* El detalle del ticket trae `moneda` del cliente
                (`bot/casino_api.py:14283`) y el saldo iba en pesos
                clavados. Es un cliente solo, no una lista, así que lleva su
                moneda y nada más. */}
            <div style={{color:Q.muted,fontSize:12}}>
              {detalle?.agencia||""}
              {detalle?` · saldo ${money(detalle.saldo, detalle.moneda)}`:""}</div>
          </div>
        </div>

        {detalle?.motivo==="sensible"&&(
          <div style={{background:`${Q.red}14`,
            border:`1px solid ${Q.red}55`,borderRadius:RADII.md,
            padding:"12px 12px",marginBottom:10,fontSize:12,
            color:Q.red,lineHeight:1.5,
            fontFamily:F_BODY}}>
            El cliente mencionó algo que sugiere un problema con el
            juego. Respondé con cuidado y ofrecele limitar o cerrar
            la cuenta si lo pide. No le sugieras seguir jugando.</div>
        )}

        <div style={{flex:1,overflowY:"auto",marginBottom:10}}>
          {!detalle&&<div style={{color:Q.muted,textAlign:"center",
            padding:SPACING[20]}}>Cargando…</div>}
          {(detalle?.mensajes||[]).map((m,i)=>(
            <div key={i} style={{display:"flex",
              justifyContent:m.autor==="cliente"?"flex-start":"flex-end",
              marginBottom:8}}>
              <div style={{maxWidth:"84%",
                background:m.autor==="cliente"?"rgba(255,255,255,0.05)"
                  :(m.autor==="ia"?`${Q.cyan}14`:`${Q.violet}28`),
                border:`1px solid ${m.autor==="cliente"?Q.border
                  :(m.autor==="ia"?`${Q.cyan}44`:Q.violet)}`,
                borderRadius:RADII.lg,padding:"8px 12px",color:Q.text,
                fontSize:12.5,lineHeight:1.55,whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                <div style={{color:m.autor==="ia"?Q.cyan:Q.dim,
                  fontSize:12,fontWeight:700,marginBottom:3}}>
                  {AUTOR[m.autor]||m.autor}</div>
                {m.texto}
                <div style={{color:Q.dim,fontSize:12,marginTop:4}}>
                  {fechaHora(m.fecha)}</div>
              </div>
            </div>
          ))}
          <div ref={finRef}/>
        </div>

        <div style={{display:"flex",gap:SPACING[8],marginBottom:7}}>
          <input value={texto} onChange={e=>setTexto(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&responder(false)}
            placeholder="Escribí tu respuesta…"
            style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,
              padding:"12px 16px",color:Q.text,fontSize:14,
              fontFamily:F_BODY}}/>
          <button onClick={()=>responder(false)}
            disabled={proc||!texto.trim()}
            style={{background:texto.trim()?Q.violet:"rgba(255,255,255,0.05)",
              border:"none",borderRadius:RADII.md,padding:"0 20px",
              cursor:texto.trim()?"pointer":"default",
              color:texto.trim()?"#fff":Q.dim,fontSize:14,fontWeight:700,
              flexShrink:0}}>↑</button>
        </div>
        <button onClick={()=>responder(true)} disabled={proc}
          style={{background:"transparent",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.muted,
            fontSize:12,
            fontFamily:F_BODY}}>
          Marcar como resuelto</button>
      </div>
    );
  }

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Headphones size={17}/> Soporte</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Consultas de los jugadores. La IA resuelve lo simple; acá
        aparecen las que necesitan una persona.</div>

      {datos&&(datos.derivados>0||datos.sensibles>0)&&(
        <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
          <GCard glow={datos.derivados?Q.gold:null}
            style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
            <div style={{color:datos.derivados?Q.gold:Q.dim,
              fontWeight:900,fontSize:20,
              fontFamily:F_BODY}}>
              {datos.derivados}</div>
            <div style={{color:Q.muted,fontSize:12}}>esperan respuesta</div>
          </GCard>
          <GCard glow={datos.sensibles?Q.red:null}
            style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
            <div style={{color:datos.sensibles?Q.red:Q.dim,
              fontWeight:900,fontSize:20,
              fontFamily:F_BODY}}>
              {datos.sensibles}</div>
            <div style={{color:Q.muted,fontSize:12}}>caso sensible</div>
          </GCard>
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["abierto","Abiertos"],["atendido","Atendidos"],
          ["todos","Todos"]].map(([k,l])=>(
          <button key={k} onClick={()=>setEstado(k)} style={{flex:1,
            background:estado===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${estado===k?Q.violet:Q.border}`,
            borderRadius:RADII.md,padding:"8px 8px",cursor:"pointer",
            color:estado===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:estado===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {msg&&<div style={{color:Q.muted,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>{msg}</div>}

      {!datos&&<div style={{color:Q.muted,textAlign:"center",
        padding:SPACING[20]}}>Cargando…</div>}

      {datos&&datos.tickets.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{color:Q.green,fontSize:13,
            fontFamily:F_BODY}}>
            Sin consultas pendientes</div>
        </GCard>
      )}

      {(datos?.tickets||[]).map(t=>(
        <GCard key={t.id} onClick={()=>abrir(t.id)}
          glow={t.motivo==="sensible"?Q.red:(t.derivado?Q.gold:null)}
          style={{padding:"12px 12px",marginBottom:7,cursor:"pointer"}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                fontFamily:F_BODY}}>
                {t.motivo==="sensible"&&(
                  <span style={{color:Q.red,marginRight:4}}><Icon name="circle-dot" size={11}/></span>
                )}
                {t.derivado&&t.motivo!=="sensible"&&(
                  <span style={{color:Q.gold,marginRight:4}}><Icon name="circle-dot" size={11}/></span>
                )}
                {t.cliente}</div>
              <div style={{color:Q.muted,fontSize:12,marginTop:2,
                overflow:"hidden",textOverflow:"ellipsis",
                whiteSpace:"nowrap"}}>{t.ultimo}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                {t.agencia} · {fechaHora(t.fecha)}
                {t.estado!=="abierto"?` · ${t.estado}`:""}</div>
            </div>
          </div>
        </GCard>
      ))}
    </div>
  );
}

// ── Mensajes con las agencias y avisos en cascada ─────────────
// Un hilo por agencia, más avisos que bajan por el árbol. No hay
// chat entre agencias: cada una ve solo su rama, y un canal directo
// entre ellas rompería ese aislamiento.
function TabMensajes({ adminKey, onNoAutorizado }){
  const [vista,setVista]=useState("hilos");
  const [hilos,setHilos]=useState(null);
  const [abierto,setAbierto]=useState(null);
  const [msgs,setMsgs]=useState(null);
  const [texto,setTexto]=useState("");
  const [proc,setProc]=useState(false);
  const [avisos,setAvisos]=useState(null);
  const [nuevo,setNuevo]=useState({titulo:"",cuerpo:"",nivel:"info",
    alcance:"todas",agencia_code:"",horas:"",destinos:["agencia"]});
  const [ags,setAgs]=useState([]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const finRef=useRef(null);

  const cargarHilos=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/mensajes`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setHilos(await r.json());
    }catch(e){}
  };

  const cargarAvisos=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/avisos`,
        {headers:adminHeaders(adminKey)});
      if(r.ok) setAvisos((await r.json()).avisos||[]);
    }catch(e){}
  };

  const abrirHilo=async(code)=>{
    setAbierto(code); setMsgs(null);
    try{
      const r=await fetch(`${API}/api/admin/mensajes/${code}`,
        {headers:adminHeaders(adminKey)});
      if(r.ok){ setMsgs((await r.json()).mensajes||[]); cargarHilos(); }
    }catch(e){}
  };

  const enviar=async()=>{
    const t=texto.trim();
    if(!t||!abierto||proc) return;
    setProc(true); setTexto("");
    try{
      const r=await fetch(`${API}/api/admin/mensajes/${abierto}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({texto:t})});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ abrirHilo(abierto); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const crearAviso=async()=>{
    if(!nuevo.titulo.trim()){ setMsg({text:"Poné un título", ok:false}); return; }
    if(nuevo.alcance!=="todas"&&!nuevo.agencia_code){
      setMsg({text:"Elegí la agencia", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/avisos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(nuevo)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Aviso publicado", ok:true});
      setNuevo({titulo:"",cuerpo:"",nivel:"info",alcance:"todas",
                agencia_code:"",horas:"",destinos:["agencia"]});
      cargarAvisos();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const borrarAviso=async(id)=>{
    if(!window.confirm("¿Quitar este aviso?")) return;
    try{
      const r=await fetch(`${API}/api/admin/avisos/${id}`,
        {method:"DELETE",headers:adminHeaders(adminKey)});
      if(r.ok) cargarAvisos();
    }catch(e){}
  };

  useEffect(()=>{
    cargarHilos(); cargarAvisos();
    // Se refresca solo: sin esto había que recargar la pantalla para
    // ver un mensaje nuevo o un aviso recién publicado.
    // Cada 20 segundos: una agencia que escribe espera respuesta,
    // y 45 se sienten como que el sistema no anda.
    const t=setInterval(()=>{ cargarHilos(); cargarAvisos(); },20000);
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAgs(Array.isArray(d)?d:(d.agencias||[]))).catch(()=>{});
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[]);

  useEffect(()=>{ finRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs]);

  // El hilo abierto también se refresca, para ver lo que responden
  // sin tener que cerrarlo y volver a entrar.
  useEffect(()=>{
    if(!abierto) return;
    const t=setInterval(()=>{
      fetch(`${API}/api/admin/mensajes/${abierto}`,
        {headers:adminHeaders(adminKey)})
        .then(r=>r.ok?r.json():null)
        .then(d=>d&&setMsgs(d.mensajes||[])).catch(()=>{});
    },10000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[abierto]);

  const NIVEL={info:Q.cyan, aviso:Q.gold, urgente:Q.red};
  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,marginBottom:9,
    fontFamily:F_BODY};

  // ── Un hilo abierto ──
  if(abierto){
    const h=(hilos?.hilos||[]).find(x=>x.code===abierto);
    return(
      <div style={{display:"flex",flexDirection:"column",
        height:"calc(100dvh - 210px)",minHeight:340}}>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
          marginBottom:10}}>
          <button onClick={()=>{setAbierto(null);setMsgs(null);}}
            style={{background:"transparent",border:"none",color:Q.cyan,
              fontSize:20,cursor:"pointer",padding:0}}>‹</button>
          <div>
            <div style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>
              {h?.nombre||abierto}</div>
            <div style={{color:Q.muted,fontSize:12}}>{abierto}</div>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",marginBottom:10}}>
          {!msgs&&<div style={{color:Q.muted,textAlign:"center",
            padding:SPACING[20]}}>Cargando…</div>}
          {msgs&&msgs.length===0&&(
            <div style={{color:Q.dim,textAlign:"center",padding:SPACING[20],
              fontSize:12.5}}>Todavía no hay mensajes con esta agencia.</div>
          )}
          {(msgs||[]).map(m=>(
            <div key={m.id} style={{display:"flex",
              justifyContent:m.mio?"flex-end":"flex-start",marginBottom:8}}>
              <div style={{maxWidth:"84%",
                background:m.mio?`${Q.violet}28`:"rgba(255,255,255,0.05)",
                border:`1px solid ${m.mio?Q.violet:Q.border}`,
                borderRadius:RADII.lg,padding:"8px 12px",color:Q.text,
                fontSize:12.5,lineHeight:1.55,whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                {m.texto}
                <div style={{color:Q.dim,fontSize:12,marginTop:4}}>
                  {fechaHora(m.fecha)}</div>
              </div>
            </div>
          ))}
          <div ref={finRef}/>
        </div>

        <div style={{display:"flex",gap:SPACING[8]}}>
          <input value={texto} onChange={e=>setTexto(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&enviar()}
            placeholder="Escribí un mensaje…" style={{...inp,marginBottom:0,
              flex:1,minWidth:0}}/>
          <button onClick={enviar} disabled={proc||!texto.trim()}
            style={{background:texto.trim()?Q.violet:"rgba(255,255,255,0.05)",
              border:"none",borderRadius:RADII.md,padding:"0 20px",
              cursor:texto.trim()?"pointer":"default",
              color:texto.trim()?"#fff":Q.dim,fontSize:14,fontWeight:700,
              flexShrink:0}}>↑</button>
        </div>
      </div>
    );
  }

  return(
    <div>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["hilos",<><MessageSquare size={13}/> Agencias</>],["avisos",<><Megaphone size={13}/> Avisos</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"12px 8px",cursor:"pointer",
            color:vista===k?Q.cyan:Q.muted,fontSize:12.5,
            fontWeight:vista===k?700:400,
            fontFamily:F_BODY}}>
            {l}
            {k==="hilos"&&hilos?.total_sin_leer>0&&(
              <span style={{background:Q.red,color:"#fff",borderRadius:RADII.md,
                padding:"4px 8px",fontSize:12,marginLeft:5}}>
                {hilos.total_sin_leer}</span>
            )}
          </button>
        ))}
      </div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {vista==="hilos"&&(
        <>
          {!hilos&&<div style={{color:Q.muted,textAlign:"center",
            padding:SPACING[20]}}>Cargando…</div>}
          {(hilos?.hilos||[]).map(h=>(
            <GCard key={h.code} glow={h.sin_leer>0?Q.cyan:null}
              onClick={()=>abrirHilo(h.code)}
              style={{padding:"12px 12px",marginBottom:7,cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                    fontFamily:F_BODY}}>
                    {h.nombre}
                    {h.sin_leer>0&&(
                      <span style={{background:Q.red,color:"#fff",
                        borderRadius:RADII.md,padding:"4px 8px",fontSize:12,
                        marginLeft:6}}>{h.sin_leer}</span>
                    )}
                  </div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2,
                    overflow:"hidden",textOverflow:"ellipsis",
                    whiteSpace:"nowrap"}}>
                    {h.ultimo||"Sin mensajes"}</div>
                </div>
                <span style={{color:Q.dim,fontSize:12,flexShrink:0}}>
                  {fechaHora(h.fecha)}</span>
              </div>
            </GCard>
          ))}
        </>
      )}

      {vista==="avisos"&&(
        <>
          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,
              marginBottom:8,fontFamily:F_BODY}}>
              Publicar un aviso</div>

            <input value={nuevo.titulo}
              onChange={e=>setNuevo(n=>({...n,titulo:e.target.value}))}
              placeholder="Título" style={inp}/>
            <textarea value={nuevo.cuerpo}
              onChange={e=>setNuevo(n=>({...n,cuerpo:e.target.value}))}
              placeholder="Detalle (opcional)" rows={3}
              style={{...inp,resize:"vertical"}}/>

            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              Importancia</div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
              {[["info","Informativo"],["aviso","Importante"],
                ["urgente","Urgente"]].map(([k,l])=>(
                <button key={k} onClick={()=>setNuevo(n=>({...n,nivel:k}))}
                  style={{flex:1,
                  background:nuevo.nivel===k?`${NIVEL[k]}22`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${nuevo.nivel===k?NIVEL[k]:Q.border}`,
                  borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                  color:nuevo.nivel===k?NIVEL[k]:Q.muted,fontSize:12,
                  fontWeight:nuevo.nivel===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>

            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              A quién</div>
            <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
              {[["todas","Todas"],["rama","Una rama"],
                ["agencia","Una agencia"]].map(([k,l])=>(
                <button key={k} onClick={()=>setNuevo(n=>({...n,alcance:k}))}
                  style={{flex:1,
                  background:nuevo.alcance===k?`${Q.violet}33`
                    :"rgba(255,255,255,0.04)",
                  border:`1px solid ${nuevo.alcance===k?Q.violet:Q.border}`,
                  borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                  color:nuevo.alcance===k?Q.cyan:Q.muted,fontSize:12,
                  fontWeight:nuevo.alcance===k?700:400,
                  fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>

            {nuevo.alcance!=="todas"&&(
              <select value={nuevo.agencia_code}
                onChange={e=>setNuevo(n=>({...n,agencia_code:e.target.value}))}
                style={inp}>
                <option value="">Elegí la agencia…</option>
                {ags.map(a=>(
                  <option key={a.code} value={a.code}>
                    {"— ".repeat(a.nivel||0)}{a.name} ({a.code})</option>
                ))}
              </select>
            )}

            {/* Dónde se muestra. Un aviso operativo va solo a las
                agencias; una promoción, también a los clientes. */}
            <div style={{color:Q.muted,fontSize:12,marginBottom:4}}>
              Dónde se muestra</div>
            <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",
              marginBottom:10}}>
              {[["agencia",<><Building2 size={13}/> Agencias</>],["box",<><Monitor size={13}/> Terminales</>],
                ["app",<><Smartphone size={13}/> App</>],["web",<><Globe size={13}/> Sitio</>]].map(([k,l])=>{
                const on=(nuevo.destinos||[]).includes(k);
                return(
                  <button key={k} onClick={()=>setNuevo(n=>({...n,
                      destinos:on?n.destinos.filter(x=>x!==k)
                                 :[...(n.destinos||[]),k]}))}
                    style={{background:on?`${Q.violet}33`
                      :"rgba(255,255,255,0.04)",
                    border:`1px solid ${on?Q.violet:Q.border}`,
                    borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
                    color:on?Q.cyan:Q.muted,fontSize:12,
                    fontWeight:on?700:400,
                    fontFamily:F_BODY}}>{l}</button>
                );
              })}
            </div>
            {(nuevo.destinos||[]).some(d=>d!=="agencia")&&(
              <div style={{color:Q.amber,fontSize:12,marginBottom:10,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Los clientes van a ver este aviso. Revisá que el texto
                esté pensado para ellos y no para uso interno.</div>
            )}

            <input value={nuevo.horas} inputMode="numeric"
              onChange={e=>setNuevo(n=>({...n,
                horas:e.target.value.replace(/\D/g,"")}))}
              placeholder="Vence en (horas, vacío = sin vencimiento)"
              style={inp}/>

            <Btn label={proc?"Publicando…":<><Megaphone size={13}/> Publicar</>} onClick={crearAviso}
              color={Q.violet} full disabled={proc}/>
          </GCard>

          {(avisos||[]).map(a=>(
            <GCard key={a.id} glow={a.vigente?NIVEL[a.nivel]:null}
              style={{padding:"12px 12px",marginBottom:7,
                opacity:a.vigente?1:0.55}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",gap:SPACING[8]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                    fontFamily:F_BODY}}>
                    <span style={{color:NIVEL[a.nivel]}}>
                      <Icon name="circle-dot" size={12}/>
                    </span> {a.titulo}</div>
                  {a.cuerpo&&(
                    <div style={{color:Q.muted,fontSize:12,marginTop:3,
                      lineHeight:1.45}}>{a.cuerpo}</div>
                  )}
                  {/* Dónde se ve, que es distinto de a quién alcanza */}
                  <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",
                    marginTop:4}}>
                    {(a.destinos||["agencia"]).map(dst=>(
                      <span key={dst} style={{fontSize:12,
                        border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
                        padding:"4px 8px",color:Q.cyan,
                        fontFamily:F_BODY}}>
                        {{agencia:<><Building2 size={11}/> Agencias</>,box:<><Monitor size={11}/> Terminales</>,
                          app:<><Smartphone size={11}/> App</>,web:<><Globe size={11}/> Sitio</>}[dst]||dst}</span>
                    ))}
                  </div>
                  <div style={{color:Q.dim,fontSize:12,marginTop:3}}>
                    {a.alcance==="todas"?"Toda la red"
                      :`${a.agencia_nombre||a.agencia}${
                        a.alcance==="rama"?" y su rama":""}`}
                    {" · "}{a.vistos} vieron
                    {a.vence?` · vence ${fechaHora(a.vence)}`:""}
                    {!a.vigente?" · VENCIDO":""}</div>
                </div>
                <button onClick={()=>borrarAviso(a.id)} style={{
                  background:"transparent",border:"none",color:Q.red,
                  fontSize:16,cursor:"pointer",flexShrink:0,padding:0}}>×</button>
              </div>
            </GCard>
          ))}
        </>
      )}
    </div>
  );
}

// ── Chat interno del operador ─────────────────────────────────
// Un asistente que conoce el sistema: dónde está cada cosa, qué
// significa cada concepto, y los números del día cuando se los pide.
//
// Está pensado para que el operador no tenga que preguntarle a otro
// dónde se hace algo. Las preguntas de "cómo hago" se responden con
// el manual y salen baratas; solo las que piden números consultan la
// base, que es lo que encarece cada consulta.
function ChatOperador({ adminKey, onNoAutorizado }){
  const [mensajes,setMensajes]=useState([]);
  const [texto,setTexto]=useState("");
  const [proc,setProc]=useState(false);
  const finRef=useRef(null);

  useEffect(()=>{
    finRef.current?.scrollIntoView({behavior:"smooth"});
  },[mensajes,proc]);

  const enviar=async(pregunta)=>{
    const q=(pregunta||texto).trim();
    if(!q||proc) return;
    setTexto("");
    const nuevos=[...mensajes,{rol:"user",texto:q}];
    setMensajes(nuevos);
    setProc(true);
    try{
      const r=await fetch(`${API}/api/admin/chat`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({mensaje:q,
          // Solo los últimos: mandar todo el historial encarece sin
          // agregar mucho a la respuesta.
          historial:nuevos.slice(-6)})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMensajes(m=>[...m,{rol:"bot",texto:d.respuesta,
                            datos:d.consulto_datos}]);
    }catch(e){
      setMensajes(m=>[...m,{rol:"bot",texto:"No pude responder: "+e.message,
                            error:true}]);
    }
    setProc(false);
  };

  const sugerencias=[
    "¿Cómo bloqueo un evento?",
    "¿Cuánto se jugó hoy?",
    "¿Qué es el GGR?",
    "¿Dónde habilito el cash out?",
    "¿Hay alertas de riesgo abiertas?",
    "¿Cómo anulo una apuesta?",
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",
      height:"calc(100dvh - 210px)",minHeight:340}}>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:3,
        fontFamily:F_BODY}}><Icon name="message-circle" size={14}/> Consultas</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:10,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Preguntá sobre el sistema: dónde está cada cosa, qué significa
        un concepto, o los números del día.</div>

      <div style={{flex:1,overflowY:"auto",marginBottom:10}}>
        {mensajes.length===0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8]}}>
            {sugerencias.map(x=>(
              <button key={x} onClick={()=>enviar(x)} style={{
                background:"rgba(255,255,255,0.04)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"8px 12px",cursor:"pointer",color:Q.muted,
                fontSize:12,textAlign:"left",
                fontFamily:F_BODY}}>{x}</button>
            ))}
          </div>
        )}

        {mensajes.map((m,i)=>(
          <div key={i} style={{display:"flex",
            justifyContent:m.rol==="user"?"flex-end":"flex-start",
            marginBottom:9}}>
            <div style={{maxWidth:"86%",
              background:m.rol==="user"?`${Q.violet}28`
                :(m.error?`${Q.red}14`:"rgba(255,255,255,0.05)"),
              border:`1px solid ${m.rol==="user"?Q.violet
                :(m.error?`${Q.red}55`:Q.border)}`,
              borderRadius:RADII.lg,padding:"12px 12px",
              color:m.error?Q.red:Q.text,fontSize:12.5,lineHeight:1.6,
              whiteSpace:"pre-wrap",
              fontFamily:F_BODY}}>
              {m.texto}
              {m.datos&&(
                <div style={{color:Q.dim,fontSize:12,marginTop:6}}>
                  con datos del sistema</div>
              )}
            </div>
          </div>
        ))}

        {proc&&(
          <div style={{color:Q.muted,fontSize:12,padding:"8px 4px",
            fontFamily:F_BODY}}>Pensando…</div>
        )}
        <div ref={finRef}/>
      </div>

      <div style={{display:"flex",gap:SPACING[8]}}>
        <input value={texto} onChange={e=>setTexto(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&enviar()}
          placeholder="Escribí tu consulta…"
          style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"12px 16px",color:Q.text,fontSize:14,
            fontFamily:F_BODY}}/>
        <button onClick={()=>enviar()} disabled={proc||!texto.trim()}
          style={{background:texto.trim()?Q.violet:"rgba(255,255,255,0.05)",
            border:"none",borderRadius:RADII.md,padding:"0 20px",
            cursor:texto.trim()?"pointer":"default",
            color:texto.trim()?"#fff":Q.dim,fontSize:14,fontWeight:700,
            flexShrink:0,fontFamily:F_BODY}}>↑</button>
      </div>

      {mensajes.length>0&&(
        <button onClick={()=>setMensajes([])} style={{background:"transparent",
          border:"none",color:Q.dim,fontSize:12,cursor:"pointer",
          marginTop:7,fontFamily:F_BODY}}>
          Limpiar conversación</button>
      )}
    </div>
  );
}

// ── Control de riesgo del sistema ─────────────────────────────
// Detecta los patrones con los que alguien puede sacarle ventaja
// sistemática a la casa: contrapartida entre cuentas vinculadas,
// multicuenta, escaleras de cuota baja, especialistas de nicho.
//
// El motor avisa, no bloquea. Una detección puede ser un falso
// positivo, y limitarle la cuenta a un cliente legítimo cuesta más
// que la pérdida que se evita.
function TabRiesgoSistema({ adminKey, onNoAutorizado }){
  const [alertas,setAlertas]=useState(null);
  const [resumen,setResumen]=useState({});
  const [filtro,setFiltro]=useState("abierta");
  const [tipoF,setTipoF]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [escaneando,setEscaneando]=useState(false);
  const [analisis,setAnalisis]=useState("");
  const [anaProc,setAnaProc]=useState(false);
  const [abierta,setAbierta]=useState(null);
  // Tres vistas: las alertas del motor, el registro de IPs y el
  // panorama por agencia. La IP se mira cuando algo no llegó a
  // dispararse como alerta pero igual llama la atención.
  const [vista,setVista]=useState("alertas");
  const [ips,setIps]=useState(null);
  const [ipsAbierta,setIpsAbierta]=useState(null);
  const [ags,setAgs]=useState(null);
  const [ficha,setFicha]=useState(null);
  // Auditoría de cuotas y vigía general
  const [cuotas,setCuotas]=useState(null);
  const [salud,setSalud]=useState(null);
  const [vigProc,setVigProc]=useState(false);
  // Avisos por Telegram: a quién y desde qué severidad
  const [avisos,setAvisos]=useState(null);
  const [avProc,setAvProc]=useState(false);

  const cargarAvisos=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/riesgo/avisos`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setAvisos(await r.json());
    }catch(e){}
  };

  const guardarAvisos=async(nuevo)=>{
    setAvProc(true);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/avisos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(nuevo)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Avisos guardados", ok:true}); cargarAvisos();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAvProc(false);
  };

  const probarAvisos=async()=>{
    setAvProc(true);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/avisos/probar`,
        {method:"POST",headers:adminHeaders(adminKey)});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:`Enviado a ${d.enviados} de ${d.destinos}`, ok:true});
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAvProc(false);
  };

  const [bloqProc,setBloqProc]=useState(false);
  const [bloqueadas,setBloqueadas]=useState(new Set());

  // Bloquear desde acá evita tener que ir a buscar el evento a mano.
  const bloquearCuota=async(eventIds,umbral)=>{
    setBloqProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/cuotas/bloquear`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify(eventIds?{event_ids:eventIds,umbral:"alta"}
                                    :{umbral:umbral||"critica"})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      if(eventIds) setBloqueadas(b=>new Set([...b,...eventIds]));
      setMsg({text:d.bloqueados>0
        ? `${d.bloqueados} ${d.bloqueados===1?"evento bloqueado"
            :"eventos bloqueados"}`
        : "Ya estaban bloqueados", ok:true});
      // Se relee siempre: así el estado sale de la base y no de lo
      // que la pantalla recuerde.
      auditarCuotas();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setBloqProc(false);
  };

  // Desbloquear desde acá: había que ir a Eventos a buscarlo, y si el
  // bloqueo era de un mercado ni siquiera aparecía en el listado.
  const desbloquearCuota=async(eventId)=>{
    setBloqProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/cuotas/desbloquear`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({event_id:eventId})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setBloqueadas(x=>{ const n=new Set(x); n.delete(eventId); return n; });
      setMsg({text:d.quitados>0?"Habilitado":"No estaba bloqueado", ok:true});
      auditarCuotas();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setBloqProc(false);
  };

  const auditarCuotas=async()=>{
    setCuotas({cargando:true});
    try{
      const r=await fetch(`${API}/api/admin/riesgo/cuotas`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setCuotas(r.ok?await r.json():{error:`Error ${r.status}`});
      // Se marca lo que ya está bloqueado, para que el botón de
      // habilitar aparezca aunque el bloqueo sea de otra sesión.
      const rb=await fetch(`${API}/api/admin/eventos/bloqueos`,
        {headers:adminHeaders(adminKey)});
      if(rb.ok){
        const d=await rb.json();
        setBloqueadas(new Set((d.bloqueos||[])
          .map(b=>b.event_id).filter(Boolean)));
      }
    }catch(e){ setCuotas({error:e.message}); }
  };

  const correrVigia=async()=>{
    setVigProc(true); setSalud(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/vigia`,
        {method:"POST",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setSalud(r.ok?await r.json():{error:`Error ${r.status}`});
    }catch(e){ setSalud({error:e.message}); }
    setVigProc(false);
  };

  // minimo=1 lista todas; 2 solo las compartidas, que son las
  // sospechosas. Se puede alternar según lo que se esté buscando.
  const [soloCompartidas,setSoloCompartidas]=useState(true);
  const [detalleIp,setDetalleIp]=useState(null);
  const [diag,setDiag]=useState(null);

  // Comprueba si el proxy está pasando la IP real del jugador
  const diagnosticar=async()=>{
    setDiag({cargando:true});
    try{
      const r=await fetch(`${API}/api/admin/riesgo/diagnostico-ip`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setDiag(r.ok?await r.json():{error:`Error ${r.status}`});
    }catch(e){ setDiag({error:e.message}); }
  };

  const verDetalleIp=async(ip)=>{
    setDetalleIp({cargando:true, ip});
    try{
      const r=await fetch(
        `${API}/api/admin/riesgo/ip/${encodeURIComponent(ip)}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setDetalleIp(r.ok?await r.json():{error:`Error ${r.status}`, ip});
    }catch(e){ setDetalleIp({error:e.message, ip}); }
  };

  const cargarIps=async()=>{
    setIps(null);
    try{
      const r=await fetch(
        `${API}/api/admin/riesgo/ips?dias=30&minimo=${soloCompartidas?2:1}&limite=120`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setIps(await r.json());
      else setIps({error:`Error ${r.status}`});
    }catch(e){ setIps({error:e.message}); }
  };

  const cargarAgencias=async()=>{
    setAgs(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/agencias?dias=30`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setAgs(await r.json());
      else setAgs({error:`Error ${r.status}`});
    }catch(e){ setAgs({error:e.message}); }
  };

  const verFicha=async(userId)=>{
    setFicha({cargando:true});
    try{
      const r=await fetch(`${API}/api/admin/riesgo/cliente/${userId}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setFicha(await r.json());
      else setFicha({error:`Error ${r.status}`});
    }catch(e){ setFicha({error:e.message}); }
  };

  useEffect(()=>{
    if(vista==="ips") cargarIps();
    // eslint-disable-next-line
  },[vista,soloCompartidas]);

  useEffect(()=>{
    if(vista==="agencias") cargarAgencias();
    // eslint-disable-next-line
    if(vista==="cuotas") auditarCuotas();
    if(vista==="vigia"&&!salud) correrVigia();
    if(vista==="avisos"&&!avisos) cargarAvisos();
    // eslint-disable-next-line
  },[vista]);

  const cargar=async()=>{
    try{
      const q=new URLSearchParams({estado:filtro});
      if(tipoF) q.set("tipo",tipoF);
      const r=await fetch(`${API}/api/admin/riesgo/alertas?${q}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const d=await r.json();
        setAlertas(d.alertas||[]); setResumen(d.resumen||{});
      }else{
        const e=await r.json().catch(()=>({}));
        setAlertas([]); setMsg({text:e.detail||`Error ${r.status}`, ok:false});
      }
    }catch(e){ setAlertas([]); setMsg({text:e.message, ok:false}); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[filtro,tipoF]);

  const escanear=async()=>{
    setEscaneando(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/escanear`,
        {method:"POST",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      const total=Object.values(d.nuevas||{}).reduce((a,b)=>a+b,0);
      setMsg({text:total>0?`${total} alertas nuevas`:"Sin hallazgos nuevos", ok:true});
      // Appends a partial-failure caveat onto the just-set success text
      // without touching its colour: the scan itself succeeded, some
      // rules just couldn't run.
      if(d.reglas_con_error?.length)
        setMsg(m=>m&&({text:m.text+` · no pudieron correr: ${d.reglas_con_error.join(", ")}`, ok:m.ok}));
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setEscaneando(false);
  };

  const analizar=async()=>{
    setAnaProc(true); setAnalisis("");
    try{
      const r=await fetch(`${API}/api/admin/riesgo/alertas/analizar`,
        {method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:"{}"});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok) setAnalisis(d.analisis||"");
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAnaProc(false);
  };

  const marcar=async(id,estado)=>{
    try{
      const r=await fetch(`${API}/api/admin/riesgo/alertas/${id}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify({estado})});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ setAbierta(null); cargar(); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
  };

  const marcarUsuario=async(userId,nivel)=>{
    if(!window.confirm(nivel==="normal"
      ? "¿Sacar a este cliente de observación?"
      : `¿Marcar al cliente como ${nivel}? No se lo bloquea: seguí `+
        `revisando antes de tomar una decisión definitiva.`)) return;
    try{
      const r=await fetch(`${API}/api/admin/riesgo/marcar-usuario`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},
        body:JSON.stringify({user_id:userId,nivel})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:`Cliente marcado como ${nivel}`, ok:true});
    }catch(e){ setMsg({text:e.message, ok:false}); }
  };

  const COLOR={critica:Q.red,alta:"#FF8A3D",media:Q.gold,baja:Q.muted};
  const NOMBRE={
    multicuenta:"Multicuenta", contrapartida:"Contrapartida",
    cuota_baja:"Escalera / cuota baja", especialista:"Especialista",
    monto_anomalo:"Monto fuera de patrón", live_abuso:"Abuso de en vivo",
    bonus_abuse:"Abuso de bonos",
  };

  // El `monto` de una alerta sale del jugador o, cuando la alerta es de la
  // agencia, de su libro: el borde lo resuelve y lo manda por fila.
  //
  // ESTO NO ARREGLA LA ESCALA, y no hay que leerlo como si lo hiciera:
  // `riesgo_alertas.monto` fue una columna contradictoria hasta el
  // 2026-10-21 y las filas anteriores siguen en las dos escalas. Decir en
  // qué moneda está un número no dice que su magnitud sea correcta.
  const montoAlerta = formatoDeLista(alertas);

  // ── EL DETALLE DE UNA IP: TRES LECTURAS, TRES REGLAS ─────────
  //
  // UNA IP CRUZA CUENTAS Y AGENCIAS por definición —es lo que esta pantalla
  // busca—, así que es el lugar del panel donde más fácil conviven pesos y
  // dólares, y cada una de las tres lecturas necesita una regla distinta.
  //
  // `resumen` es un TOTAL de toda la conexión: viene agrupado en
  // `por_moneda` y se dibuja con el patrón de lista que ya usan la caja y
  // el cierre. Las cuatro cuentas de arriba —apuestas, cuentas, equipos y
  // ganadas— siguen planas.
  //
  // `cuentas` y `apuestas` son listas POR FILA: cada renglón trae su
  // moneda y se formatea con ella. El código aparece sólo si la lista
  // entera tiene más de una, así que una pantalla en pesos se ve idéntica.
  //
  // Las dos guardas con `Array.isArray` no son defensivas por costumbre:
  // `por_moneda` puede faltar si contesta una respuesta vieja de caché o si
  // el frontend queda desfasado del backend en un despliegue, y entonces no
  // se dibuja plata en vez de reventar el modal con un `.map`.
  const ipFilas = Array.isArray(detalleIp?.resumen?.por_moneda)
    ? detalleIp.resumen.por_moneda : [];
  const variasIp = ipFilas.length > 1;
  const rotIp = (texto, m) => variasIp ? `${texto} · ${m.moneda}` : texto;
  const montoCuentaIp = formatoDeLista(detalleIp?.cuentas);
  const montoApuestaIp = formatoDeLista(detalleIp?.apuestas);

  // LA FOTO DEL VIGÍA: cinco renglones por moneda y cinco planos.
  //
  // Lo apostado en 24h, el promedio de la semana previa y el saldo de los
  // jugadores sumaban toda la casa sin mirar la moneda. Los DOS COCIENTES
  // bajan con ellos: un margen que divide la suma de los GGR por la suma de
  // lo apostado no es el margen de ninguna de las dos monedas.
  //
  // Las CUENTAS —apuestas, sin liquidar, reservas, agencias en rojo— y la
  // cobertura de IP, que es un cociente de cuentas, quedan planas.
  const saludFilas = Array.isArray(salud?.foto?.por_moneda)
    ? salud.foto.por_moneda : [];
  const variasSalud = saludFilas.length > 1;
  const rotSalud = (texto, m) => variasSalud ? `${texto} · ${m.moneda}` : texto;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="shield-alert" size={14}/> Control de riesgo</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Busca patrones de explotación: cuentas vinculadas apostando a
        lados opuestos, escaleras de cuota baja, especialistas de nicho.
        El sistema avisa pero no bloquea: revisá antes de actuar.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center",fontFamily:F_BODY}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {/* Semáforo */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["critica","Críticas"],["alta","Altas"],["media","Medias"]].map(([k,l])=>(
          <GCard key={k} glow={resumen[k]?COLOR[k]:null}
            style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
            <div style={{color:resumen[k]?COLOR[k]:Q.dim,fontWeight:900,
              fontSize:22,fontFamily:F_BODY}}>
              {resumen[k]||0}</div>
            <div style={{color:Q.muted,fontSize:12}}>{l}</div>
          </GCard>
        ))}
      </div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["alertas",<><Icon name="shield-alert" size={13}/> Alertas</>],["ips",<><Globe size={13}/> IPs</>],
          ["agencias",<><Building2 size={13}/> Agencias</>],["cuotas",<><TrendingDown size={13}/> Cuotas</>],
          ["vigia",<><Eye size={13}/> Vigía</>],["avisos",<><Bell size={13}/> Avisos</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px 8px",cursor:"pointer",
            color:vista===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:vista===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {vista==="alertas"&&(
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <Btn label={escaneando?"Buscando…":<><Icon name="search" size={13}/> Escanear ahora</>}
          onClick={escanear} color={Q.cyan} full disabled={escaneando}/>
        <Btn label={anaProc?"…":<><Bot size={13}/> Priorizar</>} onClick={analizar}
          color={Q.violet} outline full disabled={anaProc}/>
      </div>
      )}

      {vista==="alertas"&&analisis&&(
        <GCard style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.violet2||Q.violet,fontWeight:700,fontSize:12,
            marginBottom:6,fontFamily:F_BODY}}>
            <Bot size={12}/> Por dónde empezar</div>
          <div style={{color:Q.text,fontSize:12.5,lineHeight:1.6,
            whiteSpace:"pre-wrap",
            fontFamily:F_BODY}}>{analisis}</div>
        </GCard>
      )}

      {vista==="alertas"&&
      <div style={{display:"flex",gap:SPACING[8],marginBottom:10,overflowX:"auto"}}>
        {[["abierta","Abiertas"],["revisada","Revisadas"],
          ["descartada","Descartadas"],["todas","Todas"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFiltro(k)} style={{
            background:filtro===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${filtro===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px 12px",cursor:"pointer",whiteSpace:"nowrap",
            color:filtro===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:filtro===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>}

      {vista==="alertas"&&!alertas&&<div style={{color:Q.muted,
        textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}

      {vista==="alertas"&&alertas&&alertas.length===0&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.green,fontSize:13,
            fontFamily:F_BODY}}>
            Sin alertas en este filtro</div>
          <div style={{color:Q.dim,fontSize:12,marginTop:5}}>
            Probá "Escanear ahora" para buscar patrones nuevos.</div>
        </GCard>
      )}

      {vista==="alertas"&&(alertas||[]).map(a=>(
        <GCard key={a.id} glow={COLOR[a.severidad]}
          style={{padding:SPACING[16],marginBottom:9}}>
          <div onClick={()=>setAbierta(abierta===a.id?null:a.id)}
            style={{cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
                  marginBottom:3}}>
                  <span style={{color:COLOR[a.severidad],fontSize:12,
                    fontWeight:800,border:`1px solid ${COLOR[a.severidad]}66`,
                    borderRadius:RADII.sm,padding:"4px 8px",letterSpacing:0.5,
                    textTransform:"uppercase"}}>{a.severidad}</span>
                  <span style={{color:Q.muted,fontSize:12}}>
                    {NOMBRE[a.tipo]||a.tipo}</span>
                </div>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{a.titulo}</div>
                <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                  {fechaHora(a.fecha)}{a.cliente?` · ${a.cliente}`:""}
                  {a.agencia?` · ${a.agencia}`:""}</div>
              </div>
              {a.monto>0&&(
                <div style={{color:COLOR[a.severidad],fontWeight:800,
                  fontSize:13,flexShrink:0,
                  fontFamily:F_BODY}}>{montoAlerta(a.monto,a.moneda)}</div>
              )}
            </div>
          </div>

          {abierta===a.id&&(
            <div style={{marginTop:10,paddingTop:SPACING[12],
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{color:Q.text,fontSize:12,lineHeight:1.6,
                marginBottom:10,
                fontFamily:F_BODY}}>{a.detalle}</div>

              {a.evidencia&&Object.keys(a.evidencia).length>0&&(
                <div style={{background:"rgba(255,255,255,0.03)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:SPACING[12],
                  marginBottom:10}}>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:5,
                    letterSpacing:0.8}}>EVIDENCIA</div>
                  {Object.entries(a.evidencia)
                    .filter(([k,v])=>typeof v!=="object")
                    .map(([k,v])=>(
                    <div key={k} style={{display:"flex",
                      justifyContent:"space-between",fontSize:12,
                      padding:"4px 0",color:Q.muted}}>
                      <span>{k.replace(/_/g," ")}</span>
                      <span style={{color:Q.text,
                        fontFamily:F_BODY}}>
                        {String(v)}</span>
                    </div>
                  ))}
                  {Array.isArray(a.evidencia.usuarios)&&(
                    <div style={{marginTop:6,paddingTop:SPACING[8],
                      borderTop:`1px solid ${Q.border}`}}>
                      <div style={{color:Q.muted,fontSize:12,marginBottom:3}}>
                        CUENTAS INVOLUCRADAS</div>
                      {a.evidencia.usuarios.map((u,i)=>(
                        <div key={i} style={{color:Q.text,fontSize:12,
                          padding:"4px 0"}}>
                          · {u.nombre||u.nombre_completo||u.username}
                          {u.creado_por?` (${u.creado_por})`:""}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {a.estado==="abierta"&&(
                <div style={{display:"flex",gap:SPACING[8],flexWrap:"wrap"}}>
                  <button onClick={()=>marcar(a.id,"revisada")} style={{
                    flex:1,minWidth:100,background:`${Q.green}18`,
                    border:`1px solid ${Q.green}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.green,fontSize:12,
                    fontWeight:700,fontFamily:F_BODY}}>
                    Revisada</button>
                  <button onClick={()=>marcar(a.id,"descartada")} style={{
                    flex:1,minWidth:100,background:"transparent",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.muted,fontSize:12,
                    fontFamily:F_BODY}}>
                    Falso positivo</button>
                  {a.user_id&&(
                    <button onClick={()=>marcarUsuario(a.user_id,"observado")}
                      style={{flex:1,minWidth:100,background:`${Q.gold}18`,
                      border:`1px solid ${Q.gold}`,borderRadius:RADII.md,
                      padding:"8px",cursor:"pointer",color:Q.gold,fontSize:12,
                      fontWeight:700,fontFamily:F_BODY}}>
                      Observar cliente</button>
                  )}
                </div>
              )}
              {a.estado!=="abierta"&&(
                <div style={{color:Q.dim,fontSize:12,
                  fontFamily:F_BODY}}>
                  Marcada como {a.estado}
                  {a.nota?` · ${a.nota}`:""}</div>
              )}
            </div>
          )}
        </GCard>
      ))}

      {/* ── REGISTRO DE IPs ── */}
      {vista==="ips"&&(
        <>
          {!ips&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
            fontFamily:F_BODY}}>Cargando...</div>}
          {ips?.error&&<GCard glow={Q.red} style={{padding:SPACING[16],
            textAlign:"center"}}>
            <div style={{color:Q.red,fontSize:13}}>{ips.error}</div></GCard>}

          {ips&&!ips.error&&(
            <>
              {/* Sin cobertura de IP el motor trabaja a ciegas: conviene
                  saberlo antes de confiar en que "no hay hallazgos". */}
              <GCard glow={ips.cobertura.pct<50?Q.amber:null}
                style={{padding:SPACING[12],marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline"}}>
                  <span style={{color:Q.muted,fontSize:12,
                    fontFamily:F_BODY}}>
                    Apuestas con IP registrada</span>
                  <span style={{color:ips.cobertura.pct<50?Q.amber:Q.green,
                    fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {ips.cobertura.pct}%</span>
                </div>
                <div style={{color:Q.dim,fontSize:12,marginTop:3}}>
                  {ips.cobertura.con_ip} de {ips.cobertura.apuestas} en{" "}
                  {ips.dias} días
                  {ips.cobertura.pct<50&&" · las apuestas anteriores al "+
                    "cambio no tienen IP"}</div>

                <button onClick={diagnosticar} style={{width:"100%",
                  marginTop:9,background:"transparent",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"8px",cursor:"pointer",color:Q.cyan,fontSize:12,
                  fontFamily:F_BODY}}>
                  <Wrench size={12}/> ¿Llega la IP real del jugador?</button>

                {diag?.cargando&&(
                  <div style={{color:Q.muted,fontSize:12,marginTop:8,
                    textAlign:"center"}}>Revisando…</div>
                )}

                {diag&&!diag.cargando&&!diag.error&&(
                  <div style={{marginTop:10,paddingTop:SPACING[12],
                    borderTop:`1px solid ${Q.border}`}}>
                    {diag.alerta?(
                      <div style={{color:Q.amber,fontSize:12,lineHeight:1.5,
                        marginBottom:8,
                        fontFamily:F_BODY}}>
                        <Icon name="triangle-alert" size={12}/> {diag.alerta}</div>
                    ):(
                      <div style={{color:Q.green,fontSize:12,marginBottom:8,
                        fontFamily:F_BODY}}>
                        <Icon name="circle-check" size={12}/> Se registran {diag.ips_registradas.length} IP
                        distintas: el dato llega bien.</div>
                    )}
                    <div style={{color:Q.dim,fontSize:12,lineHeight:1.6,
                      fontFamily:F_BODY}}>
                      Tu IP ahora: {diag.ip_que_veo_ahora}<br/>
                      {Object.entries(diag.cabeceras||{})
                        .filter(([k])=>k.toLowerCase().includes("forward")
                          ||k.toLowerCase().includes("real-ip"))
                        .map(([k,v])=>(
                          <span key={k}>{k}: {String(v).slice(0,60)}<br/></span>
                        ))}
                    </div>
                  </div>
                )}
              </GCard>

              <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
                {[[true,"Compartidas"],[false,"Todas"]].map(([v,l])=>(
                  <button key={String(v)} onClick={()=>setSoloCompartidas(v)}
                    style={{flex:1,
                    background:soloCompartidas===v?`${Q.violet}33`
                      :"rgba(255,255,255,0.04)",
                    border:`1px solid ${soloCompartidas===v?Q.violet:Q.border}`,
                    borderRadius:RADII.md,padding:"8px 8px",cursor:"pointer",
                    color:soloCompartidas===v?Q.cyan:Q.muted,fontSize:12,
                    fontWeight:soloCompartidas===v?700:400,
                    fontFamily:F_BODY}}>{l}</button>
                ))}
              </div>

              {ips.ips.length===0&&(
                <GCard style={{padding:SPACING[20],textAlign:"center"}}>
                  <div style={{color:Q.green,fontSize:13,
                    fontFamily:F_BODY}}>
                    {soloCompartidas
                      ? "Ninguna IP con más de una cuenta"
                      : "Todavía no hay apuestas con IP registrada"}</div>
                  {soloCompartidas&&(
                    <div style={{color:Q.dim,fontSize:12,marginTop:6}}>
                      Probá "Todas" para ver el registro completo.</div>
                  )}
                </GCard>
              )}

              {ips.ips.map(x=>(
                <GCard key={x.ip} glow={x.cuentas>=5?Q.red
                  :(x.cuentas>=3?Q.gold:null)}
                  style={{padding:SPACING[12],marginBottom:8}}>
                  <div onClick={()=>setIpsAbierta(ipsAbierta===x.ip?null:x.ip)}
                    style={{cursor:"pointer",display:"flex",
                      justifyContent:"space-between",alignItems:"center",
                      gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:700,fontSize:13,
                        fontFamily:F_BODY}}>{x.ip}</div>
                      {/* LO APOSTADO VIENE AGRUPADO POR MONEDA, y acá el
                          agrupado es el punto: una IP cruza cuentas y
                          agencias —es lo que esta pantalla busca—, así que
                          un total solo mezclaba monedas. La fila sigue
                          siendo la IP: el conteo de cuentas es lo que hace
                          sospechosa a la conexión y no se parte.

                          El código de moneda aparece cuando ESTA IP tiene
                          más de una, que además es el hallazgo: dos cuentas
                          de países distintos detrás de la misma conexión. */}
                      <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                        {x.apuestas} apuestas ·{" "}
                        {(x.por_moneda||[]).map(m=>money(
                          m.apostado,m.moneda,
                          {conCodigo:(x.por_moneda||[]).length>1})
                        ).join(" + ")||money(0,"ARS")} ·{" "}
                        última {fechaHora(x.ultima)}</div>
                      <button onClick={e=>{e.stopPropagation();
                          verDetalleIp(x.ip);}}
                        style={{background:"transparent",border:"none",
                          color:Q.cyan,fontSize:12,cursor:"pointer",
                          padding:"4px 0 0",
                          fontFamily:F_BODY}}>
                        Ver todo el detalle ›</button>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:x.cuentas>=5?Q.red
                        :(x.cuentas>=3?Q.gold:Q.cyan),
                        fontWeight:900,fontSize:20,
                        fontFamily:F_BODY}}>
                        {x.cuentas}</div>
                      <div style={{color:Q.dim,fontSize:12}}>cuentas</div>
                    </div>
                  </div>

                  {ipsAbierta===x.ip&&(
                    <div style={{marginTop:10,paddingTop:SPACING[12],
                      borderTop:`1px solid ${Q.border}`}}>
                      {x.usuarios.map(u=>(
                        <div key={u.id} onClick={()=>verFicha(u.id)}
                          style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",padding:"8px 0",gap:SPACING[8],
                            cursor:"pointer",
                            borderBottom:`1px solid ${Q.border}`}}>
                          <div style={{minWidth:0,flex:1}}>
                            <div style={{color:Q.text,fontSize:12}}>
                              {u.nombre}
                              {u.nivel!=="normal"&&(
                                <span style={{color:Q.gold,fontSize:12,
                                  marginLeft:5}}>({u.nivel})</span>
                              )}
                            </div>
                            <div style={{color:Q.dim,fontSize:12}}>
                              {u.agencia_nombre||u.agencia||"Sin agencia"}</div>
                          </div>
                          {/* El saldo es de UNA cuenta, así que el monto ya
                              era válido y lo único que faltaba era el
                              rótulo. La unidad de decisión es la lista de
                              cuentas de esta IP: el riesgo es leer el `$` de
                              una cuenta en pesos al lado del de otra en
                              dólares, en la misma columna. */}
                          <div style={{color:Q.green,fontSize:12,
                            flexShrink:0,
                            fontFamily:F_BODY}}>
                            {formatoDeLista(x.usuarios)(u.saldo,u.moneda)} ›</div>
                        </div>
                      ))}
                      <div style={{color:Q.dim,fontSize:12,marginTop:8,
                        lineHeight:1.5}}>
                        Compartir conexión no es delito: puede ser una
                        familia, un local o una wifi pública. Mirá si además
                        apuestan al mismo evento en lados opuestos.</div>
                    </div>
                  )}
                </GCard>
              ))}
            </>
          )}
        </>
      )}

      {/* ── AGENCIAS ── */}
      {vista==="agencias"&&(
        <>
          {!ags&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
            fontFamily:F_BODY}}>Cargando...</div>}
          {ags?.error&&<GCard glow={Q.red} style={{padding:SPACING[16],
            textAlign:"center"}}>
            <div style={{color:Q.red,fontSize:13}}>{ags.error}</div></GCard>}

          {ags&&!ags.error&&(
            <>
              <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Margen de la casa por agencia en {ags.dias} días. Un margen
                negativo sostenido puede ser mala suerte, pero también un
                cajero cargando apuestas con el resultado conocido.</div>

              {ags.agencias.filter(a=>a.apuestas>0).map(a=>(
                <GCard key={a.code}
                  glow={a.margen_pct!=null&&a.margen_pct<0?Q.red
                        :(a.alertas>0?Q.gold:null)}
                  style={{padding:SPACING[12],marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:700,fontSize:13,
                        fontFamily:F_BODY}}>
                        {a.name}
                        {a.alertas>0&&(
                          <span style={{color:Q.gold,fontSize:12,marginLeft:6,
                            border:`1px solid ${Q.gold}55`,borderRadius:RADII.sm,
                            padding:"4px 4px"}}>{a.alertas} alertas</span>
                        )}
                      </div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                        {a.code} · {a.clientes} clientes · {a.apuestas} apuestas
                        {a.moneda&&a.moneda!=="ARS"?` · ${a.moneda}`:""}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      {/* LA MISMA FILA SE CONTRADECÍA: el subtítulo de arriba
                          ya escribe ` · USD` cuando la moneda no es ARS, y
                          dos líneas abajo el GGR se dibujaba en pesos con
                          `ars()`. `riesgo/agencias` manda `a.moneda` por fila
                          (`bot/casino_api.py:16720`) desde antes de esto.

                          Sin `conCodigo` y sin `formatoDeLista`: el código ya
                          lo pone el subtítulo de la fila, así que agregarlo
                          acá lo escribiría dos veces en la misma tarjeta. En
                          pesos se lee idéntico a hoy. */}
                      <div style={{color:a.ggr>=0?Q.green:Q.red,
                        fontWeight:800,fontSize:14,
                        fontFamily:F_BODY}}>
                        {money(a.ggr, a.moneda)}</div>
                      {a.margen_pct!=null&&(
                        <div style={{color:a.margen_pct<0?Q.red:Q.dim,
                          fontSize:12}}>margen {a.margen_pct}%</div>
                      )}
                    </div>
                  </div>
                </GCard>
              ))}
            </>
          )}
        </>
      )}

      {/* ── AUDITORÍA DE CUOTAS ── */}
      {vista==="cuotas"&&(
        <>
          <div style={{color:Q.muted,fontSize:12,marginBottom:10,
            lineHeight:1.55,fontFamily:F_BODY}}>
            Se suman las probabilidades implícitas de cada evento
            (100 ÷ cuota). Por encima de 100 está el margen de la casa;
            por debajo, el jugador gana pase lo que pase y no hace falta
            que sepa nada de fútbol para verlo.</div>

          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label={cuotas?.cargando?"Revisando…":<><Icon name="search" size={13}/> Revisar ahora</>}
              onClick={auditarCuotas} color={Q.cyan} full
              disabled={cuotas?.cargando}/>
            {cuotas?.problemas?.some(p=>p.severidad==="critica")&&(
              <Btn label={bloqProc?"…":<><Ban size={13}/> Bloquear críticas</>}
                onClick={()=>{
                  if(window.confirm(
                    "¿Bloquear todos los eventos donde la casa pierde con "+
                    "cualquier resultado? Se bloquean para toda la red y "+
                    "quedan así hasta que los habilites.")) 
                    bloquearCuota(null,"critica");
                }} color={Q.red} full disabled={bloqProc}/>
            )}
          </div>

          {cuotas?.error&&(
            <GCard glow={Q.red} style={{padding:SPACING[16],marginTop:12,
              textAlign:"center"}}>
              <div style={{color:Q.red,fontSize:13}}>{cuotas.error}</div>
            </GCard>
          )}

          {cuotas&&!cuotas.error&&!cuotas.cargando&&(
            <>
              <div style={{color:Q.dim,fontSize:12,margin:"12px 0 8px",
                textAlign:"center",
                fontFamily:F_BODY}}>
                {cuotas.revisados} eventos revisados ·{" "}
                {cuotas.problemas.length} con problemas</div>

              {cuotas.problemas.length===0&&(
                <GCard style={{padding:SPACING[20],textAlign:"center"}}>
                  <div style={{color:Q.green,fontSize:13,
                    fontFamily:F_BODY}}>
                    Todas las cuotas dejan margen para la casa</div>
                </GCard>
              )}

              {cuotas.problemas.map((p,i)=>(
                <GCard key={i}
                  glow={p.severidad==="critica"?Q.red
                        :(p.severidad==="alta"?"#FF8A3D":Q.gold)}
                  style={{padding:SPACING[12],marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"flex-start",gap:SPACING[8],marginBottom:6}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                        fontFamily:F_BODY}}>
                        {p.evento}</div>
                      <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                        {p.deporte}
                        {p.mercado&&p.mercado!=="h2h"&&(
                          <span style={{color:Q.amber,marginLeft:5}}>
                            · mercado {p.mercado}</span>
                        )}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:p.margen_pct<0?Q.red:Q.gold,
                        fontWeight:900,fontSize:17,
                        fontFamily:F_BODY}}>
                        {p.margen_pct>0?"+":""}{p.margen_pct}%</div>
                      <div style={{color:Q.dim,fontSize:12}}>margen</div>
                    </div>
                  </div>
                  <div style={{color:p.margen_pct<0?Q.red:Q.muted,fontSize:12,
                    lineHeight:1.5,marginBottom:6,
                    fontFamily:F_BODY}}>{p.motivo}</div>
                  <div style={{display:"flex",gap:SPACING[8],fontSize:12}}>
                    {["L","E","V"].map(k=>p.cuotas[k]&&(
                      <span key={k} style={{color:Q.muted}}>
                        {k}: <span style={{color:Q.gold,fontWeight:700,
                          fontFamily:F_BODY}}>
                          {p.cuotas[k]}</span></span>
                    ))}
                    <span style={{marginLeft:"auto",color:Q.dim}}>
                      suma {p.suma_prob}%</span>
                  </div>

                  {p.event_id&&(
                    (p.bloqueado||bloqueadas.has(p.event_id))?(
                      <button onClick={()=>desbloquearCuota(p.event_id)}
                        disabled={bloqProc}
                        style={{width:"100%",marginTop:8,
                          background:`${Q.green}18`,
                          border:`1px solid ${Q.green}66`,borderRadius:RADII.md,
                          padding:"8px",cursor:"pointer",color:Q.green,
                          fontSize:12,fontWeight:700,
                          fontFamily:F_BODY}}>
                        <Icon name="circle-check" size={12}/> Bloqueado · tocá para habilitar</button>
                    ):(
                      <button onClick={()=>bloquearCuota([p.event_id])}
                        disabled={bloqProc}
                        style={{width:"100%",marginTop:8,
                          background:`${Q.red}18`,
                          border:`1px solid ${Q.red}66`,borderRadius:RADII.md,
                          padding:"8px",cursor:"pointer",color:Q.red,
                          fontSize:12,fontWeight:700,
                          fontFamily:F_BODY}}>
                        <Ban size={12}/> Bloquear {p.mercado&&p.mercado!=="h2h"
                          ? `mercado ${p.mercado}` : "este evento"}</button>
                    )
                  )}
                </GCard>
              ))}
            </>
          )}
        </>
      )}

      {/* ── VIGÍA DEL SISTEMA ── */}
      {vista==="vigia"&&(
        <>
          <div style={{color:Q.muted,fontSize:12,marginBottom:10,
            lineHeight:1.55,fontFamily:F_BODY}}>
            Un chequeo general: volumen, margen, boletos colgados,
            cobertura de datos. A diferencia de las alertas, que buscan
            patrones conocidos, acá se mira lo que no encaja aunque no
            haya una regla escrita para eso.</div>

          <Btn label={vigProc?"Analizando…":<><Eye size={13}/> Revisar el sistema</>}
            onClick={correrVigia} color={Q.violet} full disabled={vigProc}/>

          {salud?.error&&(
            <GCard glow={Q.red} style={{padding:SPACING[16],marginTop:12,
              textAlign:"center"}}>
              <div style={{color:Q.red,fontSize:13}}>{salud.error}</div>
            </GCard>
          )}

          {salud?.analisis&&(
            <GCard style={{padding:SPACING[16],marginTop:12,
              marginBottom:12}}>
              <div style={{color:Q.violet2||Q.violet,fontWeight:700,
                fontSize:12,marginBottom:6,
                fontFamily:F_BODY}}>
                <Eye size={12}/> Lectura del vigía</div>
              <div style={{color:Q.text,fontSize:12.5,lineHeight:1.65,
                whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                {salud.analisis}</div>
            </GCard>
          )}

          {salud?.foto&&(
            <GCard style={{padding:SPACING[16]}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                marginBottom:8}}>LOS NÚMEROS</div>
              {[
                ["Apuestas en 24h", salud.foto.apuestas_24h],
                ...saludFilas.flatMap(m=>[
                  [rotSalud("Apostado en 24h",m),
                   money(m.apostado_24h,m.moneda,{conCodigo:variasSalud})],
                  [rotSalud("Promedio diario previo",m),
                   money(m.promedio_diario_previo,m.moneda,
                         {conCodigo:variasSalud})],
                  [rotSalud("Variación",m), m.variacion_pct!=null
                    ? `${m.variacion_pct>0?"+":""}${m.variacion_pct}%`
                    : "—"],
                  [rotSalud("Margen de la casa",m), m.margen_24h!=null
                    ? `${m.margen_24h}%` : "—"],
                  [rotSalud("Saldo de jugadores",m),
                   money(m.saldo_jugadores,m.moneda,{conCodigo:variasSalud})],
                ]),
                ["Sin liquidar (+3 días)", salud.foto.sin_liquidar],
                ["Reservas vencidas", salud.foto.reservas_vencidas],
                ["Cobertura de IP", salud.foto.cobertura_ip_pct!=null
                  ? `${salud.foto.cobertura_ip_pct}%` : "—"],
                ["Agencias en rojo", salud.foto.agencias_en_rojo],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",
                  justifyContent:"space-between",padding:"4px 0",fontSize:12,
                  borderBottom:`1px solid ${Q.border}`}}>
                  <span style={{color:Q.muted}}>{l}</span>
                  <span style={{color:Q.text,fontWeight:600,
                    fontFamily:F_BODY}}>{v}</span>
                </div>
              ))}
              {/* El aviso de margen negativo también es por moneda: una casa
                  puede estar en rojo en dólares y en verde en pesos, y un
                  solo cartel no diría en cuál. */}
              {saludFilas.filter(m=>m.margen_24h!=null&&m.margen_24h<0)
                .map(m=>(
                <div key={m.moneda} style={{color:Q.red,fontSize:12,
                  marginTop:10,lineHeight:1.5,fontFamily:F_BODY}}>
                  El margen de las últimas 24 horas{variasSalud
                    ? ` en ${m.moneda}` : ""} es negativo: la casa
                  pagó más de lo que recibió. Con pocas apuestas puede ser
                  varianza normal.</div>
              ))}
            </GCard>
          )}
        </>
      )}

      {/* ── AVISOS POR TELEGRAM ── */}
      {vista==="avisos"&&(
        <>
          <div style={{color:Q.muted,fontSize:12,marginBottom:12,
            lineHeight:1.55,fontFamily:F_BODY}}>
            Una alerta que nadie mira no sirve: para detectar
            contrapartida hay que enterarse mientras el evento todavía no
            se jugó. Acá se define a quién avisar y desde qué nivel.</div>

          {!avisos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
            fontFamily:F_BODY}}>Cargando...</div>}

          {avisos&&(
            <>
              {!avisos.bot_configurado&&(
                <GCard glow={Q.amber} style={{padding:SPACING[16],marginBottom:12}}>
                  <div style={{color:Q.amber,fontSize:12,lineHeight:1.5,
                    fontFamily:F_BODY}}>
                    El bot de Telegram no está configurado en el servidor.
                    Sin eso los avisos no salen.</div>
                </GCard>
              )}

              <button onClick={()=>guardarAvisos({...avisos,
                  activo:!avisos.activo})} style={{width:"100%",
                background:avisos.activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
                border:`1px solid ${avisos.activo?Q.green:Q.border}`,
                borderRadius:RADII.md,padding:"12px",cursor:"pointer",
                marginBottom:12,color:avisos.activo?Q.green:Q.muted,
                fontSize:13,fontWeight:700,
                fontFamily:F_BODY}}>
                {avisos.activo?<><Icon name="circle-check" size={13}/> Avisos activos</>:<><CircleOff size={13}/> Avisos apagados</>}</button>

              <GCard style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  marginBottom:4,fontFamily:F_BODY}}>
                  Desde qué nivel avisar</div>
                <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                  lineHeight:1.5}}>
                  Si suena el teléfono por cada alerta media, en una semana
                  lo silencian y volvemos al punto de partida.</div>
                <div style={{display:"flex",gap:SPACING[8]}}>
                  {[["critica","Solo críticas"],["alta","Altas y críticas"],
                    ["media","Medias en adelante"]].map(([k,l])=>(
                    <button key={k} onClick={()=>guardarAvisos({...avisos,
                        minimo:k})} style={{flex:1,
                      background:avisos.minimo===k?`${Q.violet}33`
                        :"rgba(255,255,255,0.04)",
                      border:`1px solid ${avisos.minimo===k?Q.violet:Q.border}`,
                      borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                      color:avisos.minimo===k?Q.cyan:Q.muted,fontSize:12,
                      fontWeight:avisos.minimo===k?700:400,
                      fontFamily:F_BODY}}>{l}</button>
                  ))}
                </div>
              </GCard>

              <GCard style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  marginBottom:4,fontFamily:F_BODY}}>
                  A quién avisar</div>
                <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                  lineHeight:1.5}}>
                  El chat ID lo da @userinfobot en Telegram: le escribís y
                  te devuelve tu número. Para un grupo, agregá el bot al
                  grupo y usá el ID del grupo (empieza con guion).</div>

                {(avisos.destinos||[]).map((d,i)=>(
                  <div key={i} style={{display:"flex",gap:SPACING[8],marginBottom:7,
                    alignItems:"center"}}>
                    <input value={d.nombre} placeholder="Nombre"
                      onChange={e=>setAvisos(a=>({...a,
                        destinos:a.destinos.map((x,j)=>j===i
                          ?{...x,nombre:e.target.value}:x)}))}
                      style={{flex:1,minWidth:0,
                        background:"rgba(255,255,255,0.05)",
                        border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                        padding:"8px 12px",color:Q.text,fontSize:13,
                        fontFamily:F_BODY}}/>
                    <input value={d.chat_id} placeholder="123456789"
                      inputMode="numeric"
                      onChange={e=>setAvisos(a=>({...a,
                        destinos:a.destinos.map((x,j)=>j===i
                          ?{...x,chat_id:e.target.value}:x)}))}
                      style={{width:120,flexShrink:0,
                        background:"rgba(255,255,255,0.05)",
                        border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                        padding:"8px 12px",color:Q.text,fontSize:13,
                        fontFamily:F_BODY}}/>
                    <button onClick={()=>setAvisos(a=>({...a,
                        destinos:a.destinos.filter((_,j)=>j!==i)}))}
                      style={{background:"transparent",border:"none",
                        color:Q.red,fontSize:17,cursor:"pointer",
                        padding:"0 4px",flexShrink:0}}>×</button>
                  </div>
                ))}

                <button onClick={()=>setAvisos(a=>({...a,
                    destinos:[...(a.destinos||[]),{nombre:"",chat_id:""}]}))}
                  style={{width:"100%",background:"transparent",
                    border:`1px dashed ${Q.border}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.cyan,fontSize:12,
                    marginTop:4,
                    fontFamily:F_BODY}}>
                  + Agregar destinatario</button>
              </GCard>

              <div style={{display:"flex",gap:SPACING[8]}}>
                <Btn label={avProc?"…":"Probar"} onClick={probarAvisos}
                  color={Q.cyan} outline full disabled={avProc}/>
                <Btn label={avProc?"Guardando…":"Guardar"}
                  onClick={()=>guardarAvisos(avisos)} color={Q.violet} full
                  disabled={avProc}/>
              </div>
            </>
          )}
        </>
      )}

      {/* ── DETALLE DE UNA IP ── */}
      {detalleIp&&(
        <div onClick={()=>setDetalleIp(null)} style={{position:"fixed",
          inset:0,zIndex:400,background:"rgba(2,2,8,.92)",display:"flex",
          alignItems:"center",justifyContent:"center",padding:SPACING[16]}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",
            maxWidth:460,maxHeight:"88dvh",overflowY:"auto",
            background:Q.deep||"#080814",border:`1px solid ${Q.border}`,
            borderRadius:RADII.lg,padding:SPACING[16]}}>

            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",marginBottom:12}}>
              <div>
                <div style={{color:Q.text,fontWeight:800,fontSize:16,
                  fontFamily:F_BODY}}>
                  {detalleIp.ip}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  Registro completo de esta conexión</div>
              </div>
              <button onClick={()=>setDetalleIp(null)} style={{
                background:"transparent",border:"none",color:Q.muted,
                fontSize:22,cursor:"pointer",padding:0}}>×</button>
            </div>

            {detalleIp.cargando&&<div style={{color:Q.muted,
              textAlign:"center",padding:SPACING[20]}}>Cargando…</div>}
            {detalleIp.error&&<div style={{color:Q.red,textAlign:"center",
              padding:SPACING[20]}}>{detalleIp.error}</div>}

            {detalleIp.resumen&&(
              <>
                <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
                  {[["Apuestas",detalleIp.resumen.apuestas],
                    ["Cuentas",detalleIp.resumen.cuentas],
                    ["Equipos",detalleIp.resumen.dispositivos]].map(([l,v])=>(
                    <GCard key={l} style={{flex:1,padding:"8px 8px",
                      textAlign:"center"}}>
                      <div style={{color:Q.text,fontWeight:800,fontSize:16,
                        fontFamily:F_BODY}}>{v}</div>
                      <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                    </GCard>
                  ))}
                </div>

                {ipFilas.map(m=>(
                  <div key={m.moneda} style={{display:"flex",
                    justifyContent:"space-between",
                    fontSize:12,padding:"8px 0",
                    borderTop:`1px solid ${Q.border}`,
                    borderBottom:`1px solid ${Q.border}`,marginBottom:12}}>
                    <span style={{color:Q.muted}}>
                      {rotIp("Resultado para la casa",m)}</span>
                    <span style={{color:m.resultado_casa>=0
                      ?Q.green:Q.red,fontWeight:700,
                      fontFamily:F_BODY}}>
                      {money(m.resultado_casa,m.moneda)}</span>
                  </div>
                ))}

                <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                  marginBottom:5}}>
                  CUENTAS ({(detalleIp.cuentas||[]).length})</div>
                {(detalleIp.cuentas||[]).map(c=>(
                  <div key={c.id} onClick={()=>{setDetalleIp(null);
                      verFicha(c.id);}}
                    style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",padding:"8px 0",cursor:"pointer",
                      borderBottom:`1px solid ${Q.border}`,gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:12}}>
                        {c.nombre}
                        {c.nivel!=="normal"&&(
                          <span style={{color:Q.gold,fontSize:12,
                            marginLeft:5}}>({c.nivel})</span>
                        )}
                        {c.registrado_aca&&(
                          <span style={{color:Q.cyan,fontSize:12,
                            marginLeft:5}}>· se registró acá</span>
                        )}
                      </div>
                      <div style={{color:Q.dim,fontSize:12}}>
                        {c.agencia||"Sin agencia"} · alta {fechaHora(c.alta)}</div>
                    </div>
                    <span style={{color:Q.green,fontSize:12,flexShrink:0,
                      fontFamily:F_BODY}}>
                      {montoCuentaIp(c.saldo,c.moneda)} ›</span>
                  </div>
                ))}

                <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                  margin:"14px 0 5px"}}>
                  APUESTAS ({(detalleIp.apuestas||[]).length})</div>
                {(detalleIp.apuestas||[]).map(a=>(
                  <div key={a.code} style={{padding:"8px 0",
                    borderBottom:`1px solid ${Q.border}`}}>
                    <div style={{display:"flex",
                      justifyContent:"space-between",alignItems:"baseline",
                      gap:SPACING[8]}}>
                      <span style={{color:Q.gold,fontSize:12,fontWeight:700,
                        fontFamily:F_BODY}}>
                        {a.code}</span>
                      <span style={{color:Q.text,fontSize:12,
                        fontFamily:F_BODY}}>
                        {montoApuestaIp(a.stake,a.moneda)} @ {a.cuota.toFixed(2)}</span>
                    </div>
                    <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                      {fechaHora(a.fecha)} · {a.cliente} · {a.agencia}
                      {a.live?" · EN VIVO":""} · {a.picks}{" "}
                      {a.picks===1?"pick":"picks"} · {a.estado}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── FICHA DE RIESGO DE UN CLIENTE ── */}
      {ficha&&(
        <div onClick={()=>setFicha(null)} style={{position:"fixed",inset:0,
          zIndex:400,background:"rgba(2,2,8,.92)",display:"flex",
          alignItems:"center",justifyContent:"center",padding:SPACING[16]}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",
            maxWidth:440,maxHeight:"88dvh",overflowY:"auto",
            background:Q.deep||"#080814",border:`1px solid ${Q.border}`,
            borderRadius:RADII.lg,padding:SPACING[16]}}>

            {ficha.cargando&&<div style={{color:Q.muted,textAlign:"center",
              padding:SPACING[20]}}>Cargando...</div>}
            {ficha.error&&<div style={{color:Q.red,textAlign:"center",
              padding:SPACING[20]}}>{ficha.error}</div>}

            {ficha.cliente&&(
              <>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"flex-start",marginBottom:12}}>
                  <div>
                    <div style={{color:Q.text,fontWeight:800,fontSize:16,
                      fontFamily:F_BODY}}>
                      {ficha.cliente.nombre}</div>
                    <div style={{color:Q.muted,fontSize:12}}>
                      {ficha.cliente.agencia_nombre||ficha.cliente.agencia}
                      {" · alta "}{fechaHora(ficha.cliente.alta)}</div>
                  </div>
                  <button onClick={()=>setFicha(null)} style={{
                    background:"transparent",border:"none",color:Q.muted,
                    fontSize:22,cursor:"pointer",padding:0}}>×</button>
                </div>

                <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
                  <GCard style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
                    <div style={{color:Q.green,fontWeight:800,fontSize:16,
                      fontFamily:F_BODY}}>
                      {/* La ficha es de UN cliente y toda su plata está en
                          la moneda de esa cuenta, que ahora viene en la
                          respuesta. Con `ars` una cuenta en dólares se
                          dibujaba con `$` y se leía mil veces más chica. */}
                      {money(ficha.cliente.saldo,ficha.cliente.moneda)}</div>
                    <div style={{color:Q.muted,fontSize:12}}>saldo</div>
                  </GCard>
                  <GCard style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
                    <div style={{color:ficha.actividad.resultado_jugador>0
                      ?Q.red:Q.text,fontWeight:800,fontSize:16,
                      fontFamily:F_BODY}}>
                      {ficha.actividad.roi_pct>0?"+":""}
                      {ficha.actividad.roi_pct}%</div>
                    <div style={{color:Q.muted,fontSize:12}}>retorno</div>
                  </GCard>
                  <GCard style={{flex:1,padding:"12px 8px",textAlign:"center"}}>
                    <div style={{color:Q.text,fontWeight:800,fontSize:16,
                      fontFamily:F_BODY}}>
                      {ficha.actividad.apuestas}</div>
                    <div style={{color:Q.muted,fontSize:12}}>apuestas</div>
                  </GCard>
                </div>

                {ficha.actividad.resultado_jugador>0&&(
                  <div style={{background:`${Q.red}14`,
                    border:`1px solid ${Q.red}44`,borderRadius:RADII.md,
                    padding:"8px 12px",marginBottom:12,fontSize:12,
                    color:Q.red,fontFamily:F_BODY}}>
                    La casa lleva perdidos{" "}
                    {money(ficha.actividad.resultado_jugador,
                           ficha.cliente.moneda)}
                    {" "}con este jugador.</div>
                )}

                <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                  marginBottom:5}}>DESDE DÓNDE JUEGA</div>
                {ficha.ips.length===0&&(
                  <div style={{color:Q.dim,fontSize:12,marginBottom:10}}>
                    Todavía no hay apuestas con IP registrada.</div>
                )}
                {ficha.ips.map(x=>(
                  <div key={x.ip} style={{display:"flex",
                    justifyContent:"space-between",fontSize:12,
                    padding:"4px 0",color:Q.text,
                    borderBottom:`1px solid ${Q.border}`}}>
                    <span style={{fontFamily:F_BODY}}>
                      {x.ip}</span>
                    <span style={{color:Q.muted}}>
                      {x.apuestas} · {fechaHora(x.ultima)}</span>
                  </div>
                ))}

                {ficha.comparten_ip.length>0&&(
                  <>
                    <div style={{color:Q.amber,fontSize:12,letterSpacing:0.8,
                      margin:"12px 0 5px"}}>
                      COMPARTEN CONEXIÓN ({ficha.comparten_ip.length})</div>
                    {ficha.comparten_ip.map(v=>(
                      <div key={v.id} onClick={()=>verFicha(v.id)}
                        style={{display:"flex",justifyContent:"space-between",
                          fontSize:12,padding:"4px 0",cursor:"pointer",
                          color:Q.text,
                          borderBottom:`1px solid ${Q.border}`}}>
                        <span>{v.nombre}</span>
                        <span style={{color:Q.dim,fontSize:12}}>
                          {v.agencia||"—"} ›</span>
                      </div>
                    ))}
                  </>
                )}

                {ficha.alertas.length>0&&(
                  <>
                    <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                      margin:"12px 0 5px"}}>ALERTAS</div>
                    {ficha.alertas.map(a=>(
                      <div key={a.id} style={{fontSize:12,padding:"4px 0",
                        color:a.estado==="abierta"?Q.text:Q.dim}}>
                        · [{a.severidad}] {a.titulo}
                        <span style={{color:Q.dim}}> — {fechaHora(a.fecha)}</span>
                      </div>
                    ))}
                  </>
                )}

                <div style={{display:"flex",gap:SPACING[8],marginTop:14}}>
                  {ficha.cliente.nivel==="normal"?(
                    <button onClick={()=>{
                        marcarUsuario(ficha.cliente.id,"observado");
                        setFicha(null);
                      }} style={{flex:1,background:`${Q.gold}18`,
                      border:`1px solid ${Q.gold}`,borderRadius:RADII.md,
                      padding:"12px",cursor:"pointer",color:Q.gold,
                      fontSize:12,fontWeight:700,
                      fontFamily:F_BODY}}>
                      Poner en observación</button>
                  ):(
                    <button onClick={()=>{
                        marcarUsuario(ficha.cliente.id,"normal");
                        setFicha(null);
                      }} style={{flex:1,background:"transparent",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                      padding:"12px",cursor:"pointer",color:Q.muted,
                      fontSize:12,
                      fontFamily:F_BODY}}>
                      Sacar de observación</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Combo flash de Telegram ───────────────────────────────────
// El combo exclusivo del chat, con un extra sobre el premio. Ese
// extra sale del margen de la casa, así que tiene que poder mirarse
// y apagarse sin tocar el servidor.
function TabFlash({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null);
  const [activo,setActivo]=useState(true);
  const [ttl,setTtl]=useState("90");
  const [extra,setExtra]=useState("10");
  const [tope,setTope]=useState("0");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/flash`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const x=await r.json();
        setD(x); setActivo(!!x.activo);
        setTtl(String(x.ttl_min)); setExtra(String(x.extra_pct));
        setTope(String(x.tope_apuesta||0));
      }
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const guardar=async(over)=>{
    setProc(true); setMsg(null);
    try{
      const cuerpo={activo, ttl_min:parseInt(ttl)||90,
        extra_pct:parseFloat(extra)||0, tope_apuesta:parseInt(tope)||0,
        ...(over||{})};
      const r=await fetch(`${API}/api/admin/flash`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...adminHeaders(adminKey)},body:JSON.stringify(cuerpo)});
      if(r.status===401){ onNoAutorizado(); return; }
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||`Error ${r.status}`);
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:16,fontWeight:700,textAlign:"center",
    fontFamily:F_BODY};

  // El combo flash lo ofrece el bot a cualquier cliente de cualquier agencia,
  // así que el volumen, el extra y el resultado vienen agrupados por moneda.
  //
  // El `resultado` era el peor de los tres: es lo apostado menos lo pagado, y
  // restar dólares de pesos daba el número que la tarjeta pinta en verde o en
  // rojo. Con una sola moneda la tarjeta se ve igual que hoy —el borde manda
  // una fila `ARS` en cero cuando no hubo movimiento, para que no quede en
  // blanco donde hoy muestra "$0"—.
  const monedasFlash=d?.por_moneda||[];
  const variasMonedas=monedasFlash.length>1;
  const rotulo=(texto,m)=>variasMonedas?`${texto} · ${m.moneda}`:texto;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Zap size={14}/> Combo flash</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        La combinada que solo existe en el chat de Telegram, con un extra
        sobre el premio y vencimiento corto. Es lo que le da al bot una
        razón de ser que un enlace a la web no tiene.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <button onClick={()=>{const v=!activo; setActivo(v); guardar({activo:v});}}
        style={{width:"100%",
        background:activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
        border:`1px solid ${activo?Q.green:Q.border}`,borderRadius:RADII.md,
        padding:"12px",cursor:"pointer",marginBottom:12,
        color:activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
        fontFamily:F_BODY}}>
        {activo?<><Icon name="circle-check" size={13}/> Activo · se ofrece en el chat</>
               :<><CircleOff size={13}/> Apagado · el bot no ofrece combos</>}</button>

      {d&&(
        <GCard glow={monedasFlash.some(m=>m.extra_pagado>0)?Q.amber:Q.cyan}
          style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}>
            Últimos {d.dias} días</div>
          <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Apostados</div>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>{d.apostados}</div>
              <div style={{color:Q.dim,fontSize:12}}>
                de {d.generados} generados</div>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Volumen</div>
              {monedasFlash.map(m=>(
                <div key={m.moneda} style={{color:Q.green,fontWeight:800,
                  fontSize:18,fontFamily:F_BODY}}>
                  {money(m.volumen,m.moneda,{conCodigo:variasMonedas})}</div>
              ))}
            </div>
          </div>

          {monedasFlash.map(m=>(
          <div key={m.moneda}
            style={{borderTop:`1px solid ${Q.border}`,paddingTop:SPACING[8]}}>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,marginBottom:3}}>
              <span style={{color:Q.muted}}>
                {rotulo("Extra comprometido",m)}</span>
              <span style={{color:Q.dim,
                fontFamily:F_BODY}}>
                {money(m.extra_comprometido,m.moneda,{conCodigo:variasMonedas})}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,marginBottom:3}}>
              <span style={{color:Q.muted}}>
                {rotulo("Extra realmente pagado",m)}</span>
              <span style={{color:Q.amber,fontWeight:700,
                fontFamily:F_BODY}}>
                {money(m.extra_pagado,m.moneda,{conCodigo:variasMonedas})}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              fontSize:12,paddingTop:SPACING[8],marginTop:6,
              borderTop:`1px solid ${Q.border}`}}>
              <span style={{color:Q.text,fontWeight:700}}>
                {rotulo("Resultado",m)}</span>
              <span style={{color:m.resultado>=0?Q.green:Q.red,
                fontWeight:800,fontSize:15,
                fontFamily:F_BODY}}>
                {money(m.resultado,m.moneda,{conCodigo:variasMonedas})}</span>
            </div>
          </div>
          ))}

          <div style={{color:Q.dim,fontSize:12,marginTop:9,lineHeight:1.5}}>
            El extra se paga solo si el cliente acierta. "Comprometido"
            es lo que se pagaría si ganaran todos.</div>
        </GCard>
      )}

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Extra sobre el premio (%)</div>
        <input value={extra} inputMode="decimal"
          onChange={e=>setExtra(e.target.value)} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          Sale de tu margen. Más alto atrae más pero cuesta más cuando
          alguien acierta. Máximo 50%.</div>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Vigencia (minutos)</div>
        <input value={ttl} inputMode="numeric"
          onChange={e=>setTtl(e.target.value.replace(/\D/g,""))} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          Cuánto dura cada combo antes de vencer. Corto genera urgencia;
          muy corto y el cliente no llega a verlo.</div>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Tope por apuesta (0 = sin tope)</div>
        <input value={tope} inputMode="numeric"
          onChange={e=>setTope(e.target.value.replace(/\D/g,""))} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,lineHeight:1.5}}>
          Limita cuánto se puede apostar al flash desde el chat. Para
          montos mayores, el cliente usa la app.</div>
      </GCard>

      <Btn label={proc?"Guardando…":"Guardar"} onClick={()=>guardar()}
        color={Q.violet} full disabled={proc}/>
    </div>
  );
}

// ── Mensajería: credenciales de SMS y correo ────────────────────
// Ver odd/tasks/credenciales-mensajeria-admin.md. La lógica de red y de
// validación vive en mensajeriaApi.js, probada aparte; acá solo hay forma.
function TabMensajeria({ adminKey, onNoAutorizado }){
  const [d,setD]=useState(null); // {proveedores, claveMaestra} | null mientras carga
  const [cargando,setCargando]=useState(true);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  // Un valor nuevo por campo, nunca precargado con la máscara: si se
  // precargara, guardar sin tocar nada guardaría la máscara como si fuera
  // la clave real.
  const [nuevos,setNuevos]=useState({});
  const [procesando,setProcesando]=useState({});
  // La lista de Latam de `GET /api/paises`: la sección de remitentes la
  // usa para el checklist de países, y "Probar el envío" para elegir con
  // qué país simular la resolución real del remitente. Se carga acá y no
  // adentro de cada sección para no pedirla dos veces.
  const [paises,setPaises]=useState([]);

  const cargar=async()=>{
    setCargando(true);
    const [r,p]=await Promise.all([
      listarMensajeria({adminKey, api:API, fetchImpl:fetch}),
      listarPaises({api:API, fetchImpl:fetch}),
    ]);
    if(r.noAutorizado){ onNoAutorizado(); return; }
    if(r.ok) setD(r);
    else setMsg({text:r.mensaje||"No se pudo cargar", ok:false});
    if(p.ok) setPaises(p.paises);
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const claveCampo=(ambito,clave)=>`${ambito}:${clave}`;

  const guardarCampo=async(ambito,clave,etiqueta)=>{
    const k=claveCampo(ambito,clave);
    setProcesando(p=>({...p,[k]:true})); setMsg(null);
    const r=await guardarCredencial({ambito, clave, valor:nuevos[k],
      adminKey, api:API, fetchImpl:fetch});
    if(r.noAutorizado){ onNoAutorizado(); return; }
    setProcesando(p=>({...p,[k]:false}));
    if(r.ok){
      setMsg({text:`${etiqueta} guardado`, ok:true});
      setNuevos(n=>({...n,[k]:""}));
      cargar();
    } else setMsg({text:r.mensaje||"Error", ok:false});
  };

  const borrarCampo=async(ambito,clave,etiqueta)=>{
    if(!window.confirm(
      `¿Quitar ${etiqueta}? Vuelve a usar la variable de entorno, sin reiniciar nada.`
    )) return;
    setMsg(null);
    const r=await borrarCredencial({ambito, clave, adminKey, api:API, fetchImpl:fetch});
    if(r.noAutorizado){ onNoAutorizado(); return; }
    if(r.ok){ setMsg({text:`${etiqueta} quitado`, ok:true}); cargar(); }
    else setMsg({text:r.mensaje||"Error", ok:false});
  };

  const [pruebaAmbito,setPruebaAmbito]=useState(AMBITO_SMS);
  const [destino,setDestino]=useState("");
  const [pruebaPais,setPruebaPais]=useState("");
  const [enviandoPrueba,setEnviandoPrueba]=useState(false);
  const [pruebaMsg,setPruebaMsg]=useState(null);
  const [pruebaResp,setPruebaResp]=useState(null);
  const [pruebaStatus,setPruebaStatus]=useState(null);
  const [pruebaRemitente,setPruebaRemitente]=useState(null);

  const enviarPrueba=async()=>{
    if(!window.confirm(
      `¿Mandar un mensaje real a ${destino||"este destino"}? Tiene costo y no se puede deshacer.`
    )) return;
    setEnviandoPrueba(true); setPruebaMsg(null); setPruebaResp(null);
    setPruebaStatus(null); setPruebaRemitente(null);
    const r=await probarEnvio({ambito:pruebaAmbito, destino, pais:pruebaPais,
      adminKey, api:API, fetchImpl:fetch});
    if(r.noAutorizado){ onNoAutorizado(); return; }
    setEnviandoPrueba(false);
    setPruebaMsg({text:r.mensaje, ok:r.ok});
    setPruebaResp(r.respuestaProveedor);
    setPruebaStatus(r.statusProveedor);
    setPruebaRemitente(r.remitenteUsado);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,fontFamily:F_BODY};

  const proveedores=d?.proveedores||[];
  const claveMaestra=!!d?.claveMaestra;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Mail size={14}/> Mensajería</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Las credenciales de SMS y correo que usa el sistema para mandar
        códigos de verificación. El valor completo nunca vuelve acá: solo
        una vista tapada, de dónde sale hoy y quién lo cambió.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {!cargando&&!claveMaestra&&(
        <GCard glow={Q.red} style={{padding:SPACING[16],marginBottom:16}}>
          <div style={{color:Q.red,fontWeight:700,fontSize:13,marginBottom:4,
            fontFamily:F_BODY}}>
            <Icon name="triangle-alert" size={13}/> Guardar está desactivado</div>
          <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
            fontFamily:F_BODY}}>
            El servidor no tiene configurada la clave maestra
            (<span style={{fontFamily:F_MONO}}>SECRETOS_CLAVE</span>). Sin
            ella no hay con qué cifrar lo que se guarde acá, así que la
            pantalla no escribe nada hasta que se cargue esa variable en el
            entorno. Probar un envío sigue funcionando: usa la credencial
            que el sistema ya tiene, de la base o del entorno.</div>
        </GCard>
      )}

      {cargando&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
        padding:SPACING[16],fontFamily:F_BODY}}>Cargando…</div>}

      {!cargando&&proveedores.map(prov=>(
        <GCard key={prov.ambito} style={{padding:SPACING[16],marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:12,
            display:"flex",alignItems:"center",gap:SPACING[8],
            fontFamily:F_BODY}}>
            {prov.ambito===AMBITO_CORREO?<Mail size={14}/>:<Smartphone size={14}/>}
            {prov.nombre||NOMBRE_PROVEEDOR[prov.ambito]||prov.ambito}</div>

          {(prov.campos||[]).map(campo=>{
            const etiqueta=campo.etiqueta;
            const k=claveCampo(prov.ambito,campo.clave);
            const enBase=campo.origen==="base";
            // Tres estados, no dos: una fila en la base, la variable de
            // entorno como respaldo, o nada en ninguno de los dos lados.
            // Confundir "sin configurar" con "entorno" haría pensar que
            // hay un respaldo activo cuando no hay con qué mandar nada.
            const colorOrigen=enBase?Q.cyan:campo.origen==="entorno"?Q.dim:Q.amber;
            return(
              <div key={campo.clave} style={{marginBottom:16,
                paddingBottom:16,borderBottom:`1px solid ${Q.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",gap:SPACING[8],marginBottom:6}}>
                  <span style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>{etiqueta}</span>
                  <HBadge label={etiquetaOrigen(campo.origen)} color={colorOrigen}/>
                </div>

                <div style={{color:campo.mascara?Q.muted:Q.dim,
                  fontSize:13,marginBottom:4,fontFamily:F_MONO}}>
                  {campo.mascara||"Sin configurar"}</div>
                {enBase&&(campo.actualizadoPor||campo.actualizadoAt)&&(
                  <div style={{color:Q.dim,fontSize:12,marginBottom:10,
                    fontFamily:F_BODY}}>
                    Cambiado por {campo.actualizadoPor||"—"}
                    {campo.actualizadoAt?` · ${campo.actualizadoAt}`:""}</div>
                )}
                {!enBase&&<div style={{marginBottom:10}}/>}

                <input type={campo.secreto?"password":"text"} autoComplete="off"
                  value={nuevos[k]||""}
                  onChange={e=>setNuevos(n=>({...n,[k]:e.target.value}))}
                  placeholder={campo.secreto
                    ?`Pegar un valor nuevo para ${etiqueta.toLowerCase()}`
                    :`Escribir ${etiqueta.toLowerCase()}`}
                  style={{...inp,marginBottom:8}}/>

                <div style={{display:"flex",gap:SPACING[8]}}>
                  <Btn label={procesando[k]?"Guardando…":"Guardar"}
                    onClick={()=>guardarCampo(prov.ambito,campo.clave,etiqueta)}
                    color={Q.violet} size="sm"
                    disabled={!claveMaestra||!(nuevos[k]||"").trim()||!!procesando[k]}/>
                  {enBase&&(
                    <button onClick={()=>borrarCampo(prov.ambito,campo.clave,etiqueta)}
                      style={{background:"transparent",border:`1px solid ${Q.red}55`,
                        borderRadius:RADII.md,padding:"0 16px",color:Q.red,
                        fontSize:12,cursor:"pointer",fontFamily:F_BODY}}>
                      Quitar</button>
                  )}
                </div>
              </div>
            );
          })}
        </GCard>
      ))}

      {!cargando&&<SeccionRemitentes adminKey={adminKey} onNoAutorizado={onNoAutorizado}
        paises={paises} proveedores={proveedores}/>}

      <GCard style={{padding:SPACING[16]}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:4,
          display:"flex",alignItems:"center",gap:SPACING[8],
          fontFamily:F_BODY}}>
          <Icon name="scan-search" size={14}/> Probar el envío</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:12,lineHeight:1.55,
          fontFamily:F_BODY}}>
          Manda un mensaje real, ahora mismo, con la credencial que el
          sistema está usando. Tiene costo: no se dispara solo, hay que
          apretar el botón.</div>

        <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
          {[[AMBITO_SMS,<><Smartphone size={13}/> SMS</>],
            [AMBITO_CORREO,<><Mail size={13}/> Correo</>]].map(([k,l])=>(
            <button key={k} onClick={()=>{setPruebaAmbito(k); setDestino(""); setPruebaPais("");}}
              style={{flex:1,
              background:pruebaAmbito===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${pruebaAmbito===k?Q.violet:Q.border}`,
              borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
              color:pruebaAmbito===k?Q.violet2||Q.violet:Q.muted,
              fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>

        <input value={destino} onChange={e=>setDestino(e.target.value)}
          placeholder={pruebaAmbito===AMBITO_CORREO
            ? "correo@dominio.com" : "+54 9 11 2233-4455"}
          style={{...inp,marginBottom:10}}/>

        {pruebaAmbito===AMBITO_SMS&&(
          <>
            <select value={pruebaPais} onChange={e=>setPruebaPais(e.target.value)}
              style={{...inp,marginBottom:4}}>
              <option value="">Remitente por defecto (sin elegir país)</option>
              {paises.map(p=>(
                <option key={p.codigo} value={p.codigo}>{p.nombre}</option>
              ))}
            </select>
            <div style={{color:Q.dim,fontSize:12,marginBottom:10,lineHeight:1.5,
              fontFamily:F_BODY}}>
              Con un país, la prueba elige el remitente igual que un envío
              real: es la única forma de ver si ese país está cubierto.</div>
          </>
        )}

        <Btn label={enviandoPrueba?"Enviando…":"Enviar prueba"}
          onClick={enviarPrueba} color={Q.amber} full
          disabled={enviandoPrueba||!destino.trim()}/>

        {pruebaMsg&&<div style={{color:pruebaMsg.ok?Q.green:Q.red,fontSize:12.5,
          marginTop:10,textAlign:"center",fontFamily:F_BODY}}>
          <Icon name={pruebaMsg.ok?"circle-check":"triangle-alert"} size={13}/> {pruebaMsg.text}</div>}

        {pruebaRemitente&&<div style={{color:Q.text,fontSize:12.5,marginTop:6,
          textAlign:"center",fontFamily:F_BODY}}>
          Remitente usado: <strong>{pruebaRemitente}</strong></div>}

        {pruebaResp&&(
          <>
            {pruebaStatus!=null&&<div style={{color:Q.dim,fontSize:12,
              marginTop:10,fontFamily:F_BODY}}>
              Estado del proveedor: {pruebaStatus}</div>}
            <pre style={{marginTop:6,background:"rgba(0,0,0,0.3)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,
              padding:"8px 12px",color:Q.muted,fontSize:12,fontFamily:F_MONO,
              whiteSpace:"pre-wrap",wordBreak:"break-word",maxHeight:200,
              overflow:"auto"}}>
              {JSON.stringify(pruebaResp,null,2)}</pre>
          </>
        )}
      </GCard>
    </div>
  );
}

// ── Remitentes por país: agregar, editar y quitar ─────────────────
// Debajo de las credenciales, dentro de la misma pestaña Mensajería. Ver
// odd/tasks/remitentes-por-pais-y-whatsapp.md: un remitente no es un
// secreto (el jugador lo ve en su teléfono), así que esta sección no
// enmascara nada. La de arriba tampoco lo enmascara ya: solo esconde los
// campos que el servidor marca con `secreto`, que son las dos claves de
// API y nada más.
function SeccionRemitentes({ adminKey, onNoAutorizado, paises, proveedores }){
  const [remitentes,setRemitentes]=useState({[AMBITO_SMS]:[],[CANAL_WHATSAPP]:[]});
  const [cargando,setCargando]=useState(true);
  const [msg,setMsg]=useState(null);
  // El formulario sirve para alta y edición: `modo` decide si el nombre
  // del remitente se puede tocar. En edición no puede, porque el POST es
  // un upsert por (canal, remitente) — cambiar el nombre ahí crearía una
  // fila nueva y dejaría la vieja huérfana en vez de editarla.
  const [form,setForm]=useState(null);
  const [guardando,setGuardando]=useState(false);

  const cargar=async()=>{
    setCargando(true);
    const r=await listarRemitentes({adminKey, api:API, fetchImpl:fetch});
    if(r.noAutorizado){ onNoAutorizado(); return; }
    if(r.ok) setRemitentes(r.remitentes);
    else setMsg({text:r.mensaje||"No se pudo cargar", ok:false});
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const nombrePais=codigo=>paises.find(p=>p.codigo===codigo)?.nombre||codigo;

  const abrirNuevo=canal=>{ setMsg(null); setForm({
    modo:"nuevo", canal, id:null, remitenteOriginal:"", remitente:"",
    paisesElegidos:new Set(), activo:true,
  }); };
  const abrirEditar=(canal,r)=>{ setMsg(null); setForm({
    modo:"editar", canal, id:r.id, remitenteOriginal:r.remitente, remitente:r.remitente,
    paisesElegidos:new Set(r.paises), activo:r.activo,
  }); };
  const cerrarForm=()=>setForm(null);

  const alternarPais=codigo=>setForm(f=>{
    const s=new Set(f.paisesElegidos);
    s.has(codigo)?s.delete(codigo):s.add(codigo);
    return {...f, paisesElegidos:s};
  });

  const guardarForm=async()=>{
    setGuardando(true); setMsg(null);
    const r=await guardarRemitente({
      canal:form.canal, remitente:form.remitente, paises:[...form.paisesElegidos],
      activo:form.activo, adminKey, api:API, fetchImpl:fetch,
    });
    if(r.noAutorizado){ onNoAutorizado(); return; }
    setGuardando(false);
    if(r.ok){ setMsg({text:"Remitente guardado", ok:true}); cerrarForm(); cargar(); }
    else setMsg({text:r.mensaje||"Error", ok:false});
  };

  const quitar=async(r)=>{
    if(!window.confirm(
      `¿Quitar "${r.remitente}"? Los países que cubría pasan al remitente `+
      `por defecto de este canal (o al respaldo del entorno) desde el `+
      `próximo mensaje. No se puede deshacer.`
    )) return;
    setMsg(null);
    const res=await borrarRemitente({id:r.id, adminKey, api:API, fetchImpl:fetch});
    if(res.noAutorizado){ onNoAutorizado(); return; }
    if(res.ok){ setMsg({text:"Remitente quitado", ok:true}); cargar(); }
    else setMsg({text:res.mensaje||"Error", ok:false});
  };

  const wa=whatsappListo(proveedores);
  const inpForm={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,fontFamily:F_BODY};

  const CANALES=[
    {canal:AMBITO_SMS, titulo:"SMS", icono:<Smartphone size={13}/>},
    {canal:CANAL_WHATSAPP, titulo:"WhatsApp", icono:<MessageSquare size={13}/>},
  ];

  return(
    <GCard style={{padding:SPACING[16],marginBottom:16}}>
      <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:4,
        display:"flex",alignItems:"center",gap:SPACING[8],
        fontFamily:F_BODY}}>
        <Icon name="network" size={14}/> Remitentes por país</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Con qué remitente se manda el código según el país del jugador. Un
        remitente sin países marcados es el remitente por defecto del
        canal, no un error de carga: se usa cuando ningún otro cubre ese
        país.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center",fontFamily:F_BODY}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {cargando&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
        padding:SPACING[16],fontFamily:F_BODY}}>Cargando…</div>}

      {!cargando&&CANALES.map(({canal,titulo,icono})=>{
        const lista=remitentes[canal]||[];
        const sinCobertura=paisesSinCobertura(lista, paises);
        return(
          <div key={canal} style={{marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              marginBottom:8}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                display:"flex",alignItems:"center",gap:SPACING[8],
                fontFamily:F_BODY}}>{icono} {titulo}</div>
              <button onClick={()=>abrirNuevo(canal)}
                style={{background:"rgba(255,255,255,0.06)",border:`1px solid ${Q.border}`,
                  borderRadius:RADII.md,padding:"4px 8px",color:Q.violet2||Q.violet,
                  fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F_BODY,
                  display:"flex",alignItems:"center",gap:4}}>
                <Icon name="plus" size={12}/> Agregar</button>
            </div>

            {canal===CANAL_WHATSAPP&&!wa.listo&&(
              <GCard level={2} style={{padding:SPACING[12],marginBottom:10}}>
                <div style={{color:Q.amber,fontWeight:700,fontSize:12,marginBottom:4,
                  display:"flex",alignItems:"center",gap:SPACING[8],
                  fontFamily:F_BODY}}>
                  <Icon name="triangle-alert" size={13}/> WhatsApp todavía no se ofrece</div>
                <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
                  fontFamily:F_BODY}}>
                  Hace falta remitente <strong>y</strong> plantilla aprobada
                  a la vez, no alcanza con uno solo: {!wa.remitenteOk&&"falta el remitente"}
                  {!wa.remitenteOk&&!wa.plantillaOk&&" y "}
                  {!wa.plantillaOk&&"falta la plantilla aprobada"}. Se
                  cargan en la sección de credenciales, arriba. Los
                  remitentes de acá abajo no alcanzan solos: el registro de
                  jugadores sigue sin ofrecer WhatsApp hasta que estén los
                  dos.</div>
              </GCard>
            )}

            {lista.length===0&&<div style={{color:Q.dim,fontSize:12,
              fontFamily:F_BODY,marginBottom:8}}>
              Sin remitentes de {titulo} todavía.</div>}

            {lista.map(r=>(
              <div key={r.id} style={{marginBottom:SPACING[8],paddingBottom:SPACING[8],
                borderBottom:`1px solid ${Q.border}`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  gap:SPACING[8],marginBottom:6}}>
                  <span style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>{r.remitente}</span>
                  <HBadge label={r.activo?"Activo":"Inactivo"} color={r.activo?Q.green:Q.dim}/>
                </div>

                <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8],marginBottom:6}}>
                  {r.paises.length===0
                    ? <HBadge label="Por defecto para este canal" color={Q.cyan}/>
                    : r.paises.map(c=><HBadge key={c} label={nombrePais(c)} color={Q.violet}/>)}
                </div>

                {(r.actualizadoPor||r.actualizadoAt)&&(
                  <div style={{color:Q.dim,fontSize:12,marginBottom:8,
                    fontFamily:F_BODY}}>
                    Cambiado por {r.actualizadoPor||"—"}
                    {r.actualizadoAt?` · ${r.actualizadoAt}`:""}</div>
                )}

                <div style={{display:"flex",gap:SPACING[8]}}>
                  <button onClick={()=>abrirEditar(canal,r)}
                    style={{background:"rgba(255,255,255,0.06)",border:`1px solid ${Q.border}`,
                      borderRadius:RADII.md,padding:"4px 12px",color:Q.muted,
                      fontSize:12,cursor:"pointer",fontFamily:F_BODY}}>
                    Editar</button>
                  <button onClick={()=>quitar(r)}
                    style={{background:"transparent",border:`1px solid ${Q.red}55`,
                      borderRadius:RADII.md,padding:"4px 12px",color:Q.red,
                      fontSize:12,cursor:"pointer",fontFamily:F_BODY}}>
                    Quitar</button>
                </div>
              </div>
            ))}

            {canal===AMBITO_SMS&&sinCobertura.length>0&&(
              <GCard glow={Q.red} level={2} style={{padding:SPACING[12],marginTop:4}}>
                <div style={{color:Q.red,fontWeight:700,fontSize:12,marginBottom:4,
                  display:"flex",alignItems:"center",gap:SPACING[8],
                  fontFamily:F_BODY}}>
                  <Icon name="triangle-alert" size={13}/> Sin remitente para
                  {sinCobertura.length===1?" 1 país":` ${sinCobertura.length} países`}</div>
                <div style={{color:Q.muted,fontSize:12,lineHeight:1.55,
                  fontFamily:F_BODY}}>
                  {sinCobertura.map(p=>p.nombre).join(", ")}. Un jugador de
                  esos países se registra bien pero no puede verificar su
                  teléfono, y como el teléfono destraba los retiros, puede
                  depositar y jugar pero no cobrar.</div>
              </GCard>
            )}
          </div>
        );
      })}

      {form&&(
        <GCard level={2} style={{padding:SPACING[16]}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:10,
            fontFamily:F_BODY}}>
            {form.modo==="nuevo"
              ? `Nuevo remitente de ${form.canal===CANAL_WHATSAPP?"WhatsApp":"SMS"}`
              : `Editar "${form.remitenteOriginal}"`}</div>

          {form.modo==="nuevo"
            ? <input value={form.remitente} autoFocus
                onChange={e=>setForm(f=>({...f,remitente:e.target.value}))}
                placeholder="Nombre del remitente, ej: IAQP Col"
                style={{...inpForm,marginBottom:12}}/>
            : <div style={{color:Q.dim,fontSize:12,marginBottom:12,
                fontFamily:F_BODY}}>
                El nombre no se puede editar acá: cambiarlo crearía un
                remitente nuevo en vez de modificar este. Para renombrar,
                agregá uno nuevo y quitá este.</div>}

          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>Países que cubre</div>
          <div style={{color:Q.dim,fontSize:12,marginBottom:8,lineHeight:1.5,
            fontFamily:F_BODY}}>
            Sin ninguno marcado, este remitente queda como el remitente
            por defecto del canal: no es un estado vacío ni un error.</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",
            gap:SPACING[8],marginBottom:12,maxHeight:220,overflow:"auto"}}>
            {paises.map(p=>(
              <label key={p.codigo} style={{display:"flex",alignItems:"center",gap:SPACING[8],
                cursor:"pointer"}}>
                <input type="checkbox" checked={form.paisesElegidos.has(p.codigo)}
                  onChange={()=>alternarPais(p.codigo)}/>
                <span style={{color:Q.text,fontSize:12.5,fontFamily:F_BODY}}>{p.nombre}</span>
              </label>
            ))}
          </div>

          <label style={{display:"flex",alignItems:"center",gap:SPACING[8],cursor:"pointer",
            marginBottom:14}}>
            <input type="checkbox" checked={form.activo}
              onChange={e=>setForm(f=>({...f,activo:e.target.checked}))}/>
            <span style={{color:Q.text,fontSize:12.5,fontFamily:F_BODY}}>Activo</span>
          </label>

          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label={guardando?"Guardando…":"Guardar"} onClick={guardarForm}
              color={Q.violet} size="sm"
              disabled={guardando||!form.remitente.trim()}/>
            <button onClick={cerrarForm}
              style={{background:"transparent",border:`1px solid ${Q.border}`,
                borderRadius:RADII.md,padding:"0 16px",color:Q.muted,
                fontSize:12,cursor:"pointer",fontFamily:F_BODY}}>
              Cancelar</button>
          </div>
        </GCard>
      )}
    </GCard>
  );
}

// ── Bet Best: tope de mejora ──────────────────────────────────
// Cuánto se puede subir sobre la cuota del proveedor para igualar el
// boleto que trae el cliente de otra casa. Si con el tope no alcanza,
// igual se ofrece lo máximo que llega: el cliente ve una mejora aunque
// no supere del todo, y eso es mejor que perder la jugada.
function TabMejora({ adminKey, onNoAutorizado }){
  const [pct,setPct]=useState("6");
  const [datos,setDatos]=useState(null);
  const [analisis,setAnalisis]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [anaProc,setAnaProc]=useState(false);
  // Leer códigos de cupones de otras casas. Vive acá y no en una pantalla
  // propia porque es la misma función: el escáner con otra boca.
  const [altenar,setAltenar]=useState(null);
  const [altProc,setAltProc]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/mejora`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ const d=await r.json(); setDatos(d); setPct(String(d.pct)); }
    }catch(e){}
    try{
      const r=await fetch(`${API}/api/admin/altenar`,
        {headers:adminHeaders(adminKey)});
      if(r.ok) setAltenar(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const cambiarAltenar=async(valor)=>{
    setAltProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/altenar`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo:valor})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:d.activo?"Lectura de códigos encendida"
        :"Lectura de códigos apagada", ok:true});
      cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAltProc(false);
  };

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/mejora`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({pct:parseFloat(pct)})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:`Tope actualizado a ${d.pct}%`, ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const analizar=async()=>{
    setAnaProc(true); setAnalisis("");
    try{
      const r=await fetch(`${API}/api/admin/mejora/analizar`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({pct:parseFloat(pct)})});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok) setAnalisis(d.analisis||"");
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAnaProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:20,fontWeight:700,textAlign:"center",
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="scan-line" size={14}/> Bet Best</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Cuánto se puede subir sobre la cuota del proveedor para igualar el
        boleto que el cliente trae de otra casa. Si con el tope no se llega
        a igualar, igual se le ofrece lo máximo alcanzable.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>
          Tope de mejora (%)</div>
        <input value={pct} inputMode="decimal"
          onChange={e=>setPct(e.target.value)} style={inp}/>
        <div style={{color:Q.dim,fontSize:12,marginTop:8,lineHeight:1.5}}>
          Por defecto {datos?.pct_por_defecto ?? 6}%. Más alto capta más
          boletos de la competencia y cuesta margen en cada jugada mejorada.
          Máximo permitido: 30%.</div>
      </GCard>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <Btn label={anaProc?"Consultando…":<><Bot size={13}/> Pedir opinión</>}
          onClick={analizar} color={Q.cyan} outline full disabled={anaProc}/>
        <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
          color={Q.violet} full disabled={proc}/>
      </div>

      {/* LEER CÓDIGOS DE OTRAS CASAS
          El interruptor arranca apagado: esto sale primero en Ecuador.
          Lo de abajo NO es un contador de fallas, es una PROPORCIÓN, y la
          diferencia es el punto: el endpoint que leemos es de un tercero, es
          anónimo y no tiene contrato con nosotros, así que lo pueden cerrar
          cualquier día sin avisar. Cuando pase, la función deja de andar en
          silencio. Un número de fallas suelto no distingue eso de que nadie
          la haya usado; por eso se muestra junto al total. */}
      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",gap:SPACING[12],marginBottom:8}}>
          <div style={{minWidth:0}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>Leer códigos de otras casas</div>
            <div style={{color:Q.dim,fontSize:12,lineHeight:1.5,marginTop:4}}>
              El jugador pega el código de su combinada en vez de una captura.
              Apagado, el escáner sigue andando con fotos.</div>
          </div>
          <Btn label={altenar?.activo?"Apagar":"Encender"}
            onClick={()=>cambiarAltenar(!altenar?.activo)}
            color={altenar?.activo?Q.red:Q.green}
            outline={!altenar?.activo} disabled={altProc}/>
        </div>

        {altenar&&(altenar.lecturas_24h>0?(
          <div style={{color:Q.muted,fontSize:12,lineHeight:1.6,
            fontFamily:F_BODY}}>
            {altenar.lecturas_24h} lecturas en 24 h ·{" "}
            <span style={{color:altenar.sin_respuesta_pct>20?Q.red
              :altenar.sin_respuesta_pct>0?Q.amber:Q.green,fontWeight:700}}>
              {altenar.sin_respuesta_pct}% sin respuesta</span>
            {altenar.sin_respuesta_pct>20&&(
              <div style={{color:Q.red,marginTop:6}}>
                <Icon name="triangle-alert" size={13}/> Puede que la otra casa
                haya cerrado la puerta. Revisá las últimas fallas.</div>
            )}
          </div>
        ):(
          // Un 0% acá diría "todo bien" sobre una función que nadie usó.
          <div style={{color:Q.dim,fontSize:12,fontFamily:F_BODY}}>
            Sin lecturas en las últimas 24 h.</div>
        ))}

        {!!altenar?.ultimas_fallas?.length&&(
          <div style={{marginTop:10,borderTop:`1px solid ${Q.border}`,
            paddingTop:SPACING[8]}}>
            {altenar.ultimas_fallas.slice(0,5).map((f,i)=>(
              <div key={i} style={{display:"flex",gap:SPACING[8],
                justifyContent:"space-between",color:Q.dim,fontSize:12,
                fontFamily:F_BODY,padding:"4px 0"}}>
                <span style={{fontFamily:F_MONO}}>{f.codigo}</span>
                <span style={{color:f.motivo==="sin_respuesta"?Q.red:Q.muted}}>
                  {f.motivo}</span>
              </div>
            ))}
          </div>
        )}
      </GCard>

      {analisis&&(
        <GCard style={{padding:SPACING[16]}}>
          <div style={{color:Q.violet2||Q.violet,fontWeight:700,fontSize:12,
            marginBottom:6,fontFamily:F_BODY}}>
            <Bot size={12}/> Lectura del asesor</div>
          <div style={{color:Q.text,fontSize:12.5,lineHeight:1.6,
            whiteSpace:"pre-wrap",
            fontFamily:F_BODY}}>{analisis}</div>
        </GCard>
      )}
    </div>
  );
}

// ── Potencializador de combinadas ─────────────────────────────
// Porcentaje extra sobre el premio que crece con la cantidad de
// selecciones. Se paga SOLO si aciertan todas: por eso es sostenible,
// la casa paga más en el escenario menos probable.
function TabBoost({ adminKey, onNoAutorizado }){
  const [cfg,setCfg]=useState(null);
  const [tabla,setTabla]=useState([]);
  const [activo,setActivo]=useState(false);
  const [cuotaMin,setCuotaMin]=useState("1.20");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  // Simulación sobre datos propios + lectura de la IA. Primero el
  // número, después la opinión: una opinión sin datos vale poco.
  const [sim,setSim]=useState(null);
  const [analisis,setAnalisis]=useState("");
  const [simProc,setSimProc]=useState(false);

  const simular=async()=>{
    setSimProc(true); setSim(null); setAnalisis("");
    try{
      const cuerpo={tabla, cuota_min_pick:parseFloat(cuotaMin)||1.20, dias:90};
      const r=await fetch(`${API}/api/admin/boost/simular`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(cuerpo)});
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setSim(d);
      // La IA lee la simulación y opina
      const r2=await fetch(`${API}/api/admin/boost/analizar`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({...cuerpo, simulacion:d})});
      if(r2.ok){ const d2=await r2.json(); setAnalisis(d2.analisis||""); }
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setSimProc(false);
  };

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/boost`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const d=await r.json();
        setCfg(d); setActivo(!!d.activo);
        setTabla(d.tabla?.length?d.tabla:[{picks:3,pct:5}]);
        setCuotaMin(String(d.cuota_min_pick||1.20));
      }
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/boost`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo, tabla,
          cuota_min_pick:parseFloat(cuotaMin)||1.20}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Guardado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:14,fontFamily:F_BODY};

  // Lo pagado en extras viene agrupado por moneda: el potencializador se
  // ofrece al armar cualquier boleto, así que lo cobra cualquier agencia y un
  // número solo sumaba pesos con dólares.
  //
  // SE FILTRA EN CERO como antes —el renglón no aparecía cuando no se había
  // pagado nada— y el código de moneda sólo cuando quedan dos o más: `ARS`,
  // `CLP`, `COP` y `MXN` comparten el signo `$`, así que con una sola moneda
  // esta pantalla se ve exactamente igual que hoy.
  const extrasPagados=(cfg?.por_moneda||[]).filter(m=>m.pagado_historico>0);
  const variasMonedas=extrasPagados.length>1;

  // LA SIMULACIÓN TAMBIÉN VIENE AGRUPADA, y por el mismo motivo: recorre las
  // combinadas resueltas de TODA la casa, así que "habría costado" y el GGR
  // antes y después sumaban pesos con dólares. El impacto baja con ellos
  // porque es el cociente de dos de esas sumas.
  //
  // `Array.isArray` y no `||[]` por lo de siempre: con una respuesta vieja
  // de caché o un frontend desfasado del backend, la tarjeta no dibuja plata
  // en vez de reventar con un `.map`.
  const simFilas=Array.isArray(sim?.por_moneda)?sim.por_moneda:[];
  const variasSim=simFilas.length>1;
  const rotSim=(texto,m)=>variasSim?`${texto} · ${m.moneda}`:texto;
  // La tabla por cantidad de selecciones cruza agencias, así que el borde le
  // puso la moneda en la CLAVE: un escalón con combinadas en dos monedas son
  // dos renglones, y el código aparece sólo si la lista entera tiene más de
  // una. Con una sola se ve exactamente igual que hoy.
  const montoEscalon=formatoDeLista(sim?.por_cantidad);

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Rocket size={17}/> Potencializador</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:14,lineHeight:1.55,
        fontFamily:F_BODY}}>
        Un porcentaje extra sobre el premio que crece al sumar selecciones.
        Se paga <b style={{color:Q.text}}>solo si aciertan todas</b>: por eso
        es sostenible, la casa paga de más justo en el caso menos probable.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <button onClick={()=>setActivo(a=>!a)} style={{width:"100%",
        background:activo?`${Q.green}18`:"rgba(255,255,255,0.04)",
        border:`1px solid ${activo?Q.green:Q.border}`,borderRadius:RADII.md,
        padding:"12px",cursor:"pointer",marginBottom:12,
        color:activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
        fontFamily:F_BODY}}>
        {activo?<><Icon name="circle-check" size={13}/> Activo · se aplica a las combinadas</>
               :<><CircleOff size={13}/> Apagado · las combinadas pagan normal</>}</button>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:4,
          fontFamily:F_BODY}}>Escalones</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:10}}>
          Desde cuántas selecciones y cuánto extra. El porcentaje no puede
          bajar al sumar picks.</div>

        {tabla.map((t,i)=>(
          <div key={i} style={{display:"flex",gap:SPACING[8],alignItems:"center",
            marginBottom:8}}>
            <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>Desde</span>
            <input value={t.picks} inputMode="numeric"
              onChange={e=>setTabla(x=>x.map((y,j)=>j===i
                ?{...y,picks:parseInt(e.target.value)||0}:y))}
              style={{...inp,width:56,textAlign:"center"}}/>
            <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>picks →</span>
            <input value={t.pct} inputMode="decimal"
              onChange={e=>setTabla(x=>x.map((y,j)=>j===i
                ?{...y,pct:parseFloat(e.target.value)||0}:y))}
              style={{...inp,width:64,textAlign:"center"}}/>
            <span style={{color:Q.gold,fontSize:13,fontWeight:700}}>%</span>
            <button onClick={()=>setTabla(x=>x.filter((_,j)=>j!==i))}
              style={{marginLeft:"auto",background:"transparent",
                border:"none",color:Q.red,fontSize:16,cursor:"pointer",
                padding:"0 4px"}}>×</button>
          </div>
        ))}

        <button onClick={()=>setTabla(x=>[...x,
          {picks:(x[x.length-1]?.picks||2)+1,pct:(x[x.length-1]?.pct||0)+5}])}
          style={{width:"100%",background:"transparent",
            border:`1px dashed ${Q.border}`,borderRadius:RADII.md,padding:"8px",
            cursor:"pointer",color:Q.cyan,fontSize:12,marginTop:4,
            fontFamily:F_BODY}}>+ Agregar escalón</button>
      </GCard>

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:4,
          fontFamily:F_BODY}}>
          Cuota mínima por selección</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:8,lineHeight:1.5}}>
          Las patas por debajo de esta cuota no cuentan para el escalón. Sin
          esto, alguien suma diez selecciones de 1.05 y se lleva el extra sin
          correr riesgo.</div>
        <input value={cuotaMin} inputMode="decimal"
          onChange={e=>setCuotaMin(e.target.value)}
          style={{...inp,width:"100%"}}/>
      </GCard>

      {extrasPagados.map(m=>(
        <div key={m.moneda} style={{color:Q.muted,fontSize:12,marginBottom:12,
          textAlign:"center",fontFamily:F_BODY}}>
          Pagado en extras hasta ahora: <b style={{color:Q.gold}}>
            {money(m.pagado_historico,m.moneda,{conCodigo:variasMonedas})}</b></div>
      ))}

      <Btn label={simProc?"Calculando…":<><Icon name="search" size={13}/> Simular con mis datos</>}
        onClick={simular} color={Q.cyan} outline full disabled={simProc}/>

      {sim&&(
        <GCard style={{padding:SPACING[16],marginTop:12,marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}>
            Si hubiera estado activo estos {sim.dias} días</div>
          <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Combinadas</div>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>{sim.combinadas}</div>
              <div style={{color:Q.dim,fontSize:12}}>
                {sim.ganadas} acertadas ({sim.tasa_acierto}%)</div>
            </div>
            <div style={{flex:1}}>
              {simFilas.map(m=>(
                <div key={m.moneda} style={{marginBottom:variasSim?6:0}}>
                  <div style={{color:Q.muted,fontSize:12}}>
                    {rotSim("Habría costado",m)}</div>
                  <div style={{color:Q.amber,fontWeight:800,fontSize:18,
                    fontFamily:F_BODY}}>
                    {money(m.costo_del_boost,m.moneda,
                           {conCodigo:variasSim})}</div>
                  {m.impacto_pct!=null&&(
                    <div style={{color:Q.dim,fontSize:12}}>
                      {m.impacto_pct}% del margen</div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {simFilas.map(m=>(
          <div key={m.moneda} style={{borderTop:`1px solid ${Q.border}`,
            paddingTop:SPACING[8],
            display:"flex",justifyContent:"space-between",fontSize:12}}>
            <span style={{color:Q.muted}}>{rotSim("GGR sin / con",m)}</span>
            <span style={{fontFamily:F_BODY}}>
              <span style={{color:Q.muted}}>
                {money(m.ggr_sin_boost,m.moneda,{conCodigo:variasSim})}</span>
              <span style={{color:Q.dim}}> → </span>
              <span style={{color:m.ggr_con_boost>=0?Q.green:Q.red,
                fontWeight:700}}>
                {money(m.ggr_con_boost,m.moneda,{conCodigo:variasSim})}</span>
            </span>
          </div>
          ))}

          {(sim.por_cantidad||[]).length>0&&(
            <div style={{marginTop:10,paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:5}}>
                Por cantidad de selecciones</div>
              {sim.por_cantidad.filter(x=>x.jugadas>0).map(x=>(
                <div key={`${x.picks}-${x.moneda}`} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,padding:"4px 0",
                  color:Q.muted}}>
                  <span>{x.picks} picks · {x.jugadas} jugadas ·{" "}
                    {x.ganadas} ganadas{x.pct>0?` · +${x.pct}%`:""}</span>
                  <span style={{color:x.extra>0?Q.amber:Q.dim}}>
                    {montoEscalon(x.extra,x.moneda)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{color:Q.dim,fontSize:12,marginTop:9,lineHeight:1.5}}>
            {sim.aviso}</div>
        </GCard>
      )}

      {analisis&&(
        <GCard style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.violet2||Q.violet,fontWeight:700,fontSize:12,
            marginBottom:6,fontFamily:F_BODY}}>
            <Bot size={12}/> Lectura del asesor</div>
          <div style={{color:Q.text,fontSize:12.5,lineHeight:1.6,
            whiteSpace:"pre-wrap",
            fontFamily:F_BODY}}>{analisis}</div>
        </GCard>
      )}

      <div style={{marginTop:12}}>
        <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
          color={Q.violet} full disabled={proc}/>
      </div>
    </div>
  );
}

// ── Banners del sitio público ─────────────────────────────────
// Las imágenes se guardan en la base, no en un servicio aparte: para
// ocho banners es más simple, sobrevive a los redeploys y se respalda
// junto con todo lo demás.
function TabBanners({ adminKey, onNoAutorizado }){
  const [lista,setLista]=useState([]);
  const [cargando,setCargando]=useState(true);
  const [subiendo,setSubiendo]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [nuevo,setNuevo]=useState({titulo:"",texto:"",link:""});
  const archivoRef=useRef(null);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/web/banners`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){ const d=await r.json(); setLista(d.banners||[]); }
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const elegir=async(e)=>{
    const f=e.target.files?.[0];
    if(!f) return;
    if(f.size > 2*1024*1024){
      setMsg({text:`Pesa ${Math.round(f.size/1024)} KB. El máximo es 2048 KB.`, ok:false});
      e.target.value=""; return;
    }
    setSubiendo(true); setMsg(null);
    try{
      const b64=await new Promise((ok,fallo)=>{
        const fr=new FileReader();
        fr.onload=()=>ok(String(fr.result).split(",")[1]);
        fr.onerror=()=>fallo(new Error("No se pudo leer el archivo"));
        fr.readAsDataURL(f);
      });
      const r=await fetch(`${API}/api/admin/web/banners/subir`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({imagen_base64:b64, ...nuevo,
                             orden:lista.length}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok&&d.ok){
        setMsg({text:`Subido (${d.peso_kb} KB)`, ok:true});
        setNuevo({titulo:"",texto:"",link:""});
        cargar();
      } else setMsg({text:d.detail||"No se pudo subir", ok:false});
    }catch(err){ setMsg({text:err.message||"Error", ok:false}); }
    setSubiendo(false);
    if(archivoRef.current) archivoRef.current.value="";
  };

  const guardar=async(b)=>{
    try{
      const r=await fetch(`${API}/api/admin/web/banners/${b.id}`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(b)});
      if(r.status===401){ onNoAutorizado(); return; }
      setMsg({text:r.ok?"Guardado":"No se pudo guardar", ok:r.ok});
      cargar();
    }catch(e){ setMsg({text:"Error", ok:false}); }
  };

  const borrar=async(id)=>{
    if(!window.confirm("¿Borrar este banner? No se puede deshacer.")) return;
    try{
      const r=await fetch(`${API}/api/admin/web/banners/${id}/borrar`,
        {method:"POST",headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      setMsg({text:"Borrado", ok:true}); cargar();
    }catch(e){ setMsg({text:"Error", ok:false}); }
  };

  const cambiar=(id,campo,valor)=>setLista(l=>l.map(b=>
    b.id===id ? {...b,[campo]:valor} : b));

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,marginBottom:7,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><ImageIcon size={17}/> Banners del sitio</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:16,lineHeight:1.5,
        fontFamily:F_BODY}}>
        Se muestran en el carrusel de /sitio. Hasta 8. Si no hay ninguno
        activo, el sitio usa las placas generadas y no queda un hueco.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:12,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <GCard style={{padding:SPACING[16],marginBottom:18}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:10,
          fontFamily:F_BODY}}>Subir uno nuevo</div>
        <input placeholder="Título (opcional)" value={nuevo.titulo}
          onChange={e=>setNuevo({...nuevo,titulo:e.target.value})} style={inp}/>
        <input placeholder="Texto (opcional)" value={nuevo.texto}
          onChange={e=>setNuevo({...nuevo,texto:e.target.value})} style={inp}/>
        <input placeholder="Enlace al tocar (opcional)" value={nuevo.link}
          onChange={e=>setNuevo({...nuevo,link:e.target.value})} style={inp}/>
        <input ref={archivoRef} type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={elegir} disabled={subiendo||lista.length>=8}
          style={{...inp,padding:"8px",marginBottom:0}}/>
        <div style={{color:Q.muted,fontSize:12,marginTop:8,lineHeight:1.5}}>
          {lista.length>=8
            ? "Llegaste a 8 banners. Borrá alguno para subir otro."
            : `JPG, PNG, WEBP o GIF · hasta 2 MB · quedan ${8-lista.length} lugares`}
          <br/>Se recomienda 1200×400 píxeles para que no se recorte.
        </div>
        {subiendo&&<div style={{color:Q.cyan,fontSize:12,marginTop:8}}>
          Subiendo…</div>}
      </GCard>

      {cargando&&<div style={{color:Q.muted,fontSize:13,textAlign:"center",
        padding:"20px 0"}}>Cargando…</div>}

      {!cargando&&!lista.length&&(
        <GCard style={{padding:"24px 20px",textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:13}}>
            Todavía no hay banners cargados.</div>
        </GCard>
      )}

      {lista.map(b=>(
        <GCard key={b.id} style={{padding:0,marginBottom:12,overflow:"hidden"}}>
          <img src={`${API}${b.url}`} alt={b.titulo||"Banner"}
            style={{width:"100%",height:110,objectFit:"cover",display:"block",
              opacity:b.activo?1:0.4}}/>
          <div style={{padding:"12px 16px"}}>
            <input value={b.titulo||""} placeholder="Título"
              onChange={e=>cambiar(b.id,"titulo",e.target.value)} style={inp}/>
            <input value={b.texto||""} placeholder="Texto"
              onChange={e=>cambiar(b.id,"texto",e.target.value)} style={inp}/>
            <input value={b.link||""} placeholder="Enlace"
              onChange={e=>cambiar(b.id,"link",e.target.value)} style={inp}/>

            <div style={{display:"flex",gap:SPACING[8],alignItems:"center",
              marginBottom:10}}>
              <span style={{color:Q.muted,fontSize:12}}>Orden</span>
              <input type="number" value={b.orden??0}
                onChange={e=>cambiar(b.id,"orden",Number(e.target.value)||0)}
                style={{...inp,width:64,marginBottom:0,padding:"8px 8px"}}/>
              <button onClick={()=>cambiar(b.id,"activo",!b.activo)}
                style={{flex:1,background:b.activo?`${Q.green}22`:"rgba(255,255,255,0.04)",
                  border:`1px solid ${b.activo?Q.green:Q.border}`,borderRadius:RADII.md,
                  padding:"8px",cursor:"pointer",fontSize:12,fontWeight:700,
                  color:b.activo?Q.green:Q.muted}}>
                {b.activo?"Visible":"Oculto"}</button>
            </div>

            <div style={{display:"flex",gap:SPACING[8],alignItems:"center"}}>
              <Btn label="Guardar" onClick={()=>guardar(b)} color={Q.violet}/>
              <button onClick={()=>borrar(b.id)} style={{background:"transparent",
                border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"8px 16px",
                color:Q.red,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                Borrar</button>
              <span style={{marginLeft:"auto",color:Q.dim,fontSize:12}}>
                {b.peso_kb} KB</span>
            </div>
          </div>
        </GCard>
      ))}
    </div>
  );
}

// ── Exposición por evento ─────────────────────────────────────
// Cuánto tendría que pagar la casa si gana cada resultado. Es el
// número que decide si hay que suspender un mercado o mover la cuota.
//
// En una combinada el premio entero cuenta contra CADA selección,
// porque tienen que ganar todas. Por eso la suma de la columna puede
// ser mayor que la plata que realmente se pagaría: es el peor caso de
// cada resultado por separado, no un total a desembolsar.
function TabRiesgo({ adminKey, onNoAutorizado }){
  const [data,setData]=useState(null);
  const [cargando,setCargando]=useState(true);
  const [activo,setActivo]=useState(false);
  const [tope,setTope]=useState("");
  // El tope es por moneda: 500.000 en pesos es razonable, en dólares
  // sería una exposición enorme.
  const [monedaTope,setMonedaTope]=useState("ARS");
  // Todas las monedas del sistema, no solo las que ya se usan: así se
  // puede dejar el tope cargado antes de abrir la agencia.
  const [monedas,setMonedas]=useState(["ARS"]);
  const [enUso,setEnUso]=useState(new Set());
  // Los topes ya cargados, para saber cuáles están configurados sin
  // tener que ir moneda por moneda.
  const [topes,setTopes]=useState(null);

  const cargarTopes=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/riesgo/topes`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setTopes(await r.json());
    }catch(e){}
  };
  useEffect(()=>{
    fetch(`${API}/api/monedas`).then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.monedas?.length)
        setMonedas(d.monedas.map(m=>m.codigo)); })
      .catch(()=>{});
    fetch(`${API}/api/agencias`,{headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d){ const l=Array.isArray(d)?d:(d.agencias||[]);
        setEnUso(new Set(l.map(a=>a.moneda).filter(Boolean))); } })
      .catch(()=>{});
    // eslint-disable-next-line
  },[]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=async()=>{
    try{
      // La moneda VIAJA. El efecto de `monedaTope` recargaba sin mandarla,
      // así que el backend caía siempre al `moneda="ARS"` por defecto: el
      // selector cambiaba el rótulo del formulario y el tope que volvía
      // seguía siendo el de pesos.
      const r=await fetch(
        `${API}/api/admin/riesgo/exposicion?moneda=${encodeURIComponent(monedaTope)}`,
        {headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok){
        const d=await r.json();
        setData(d);
        setActivo(!!d.control_activo);
        setTope(d.tope_por_seleccion?String(d.tope_por_seleccion):"");
      }
    }catch(e){}
    setCargando(false);
  };
  // Al cambiar de moneda se recarga: cada una tiene su propio tope
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[monedaTope]);
  useEffect(()=>{ cargarTopes(); /* eslint-disable-next-line */ },[]);

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/riesgo/config`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify({activo, max_seleccion:Number(tope)||0,
          moneda:monedaTope}),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok&&d.ok){ setMsg({text:"Guardado", ok:true}); cargar(); cargarTopes(); }
      else setMsg({text:d.detail||"Error", ok:false});
    }catch(e){ setMsg({text:"Error", ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  const lineas=data?.lineas||[];
  const topeNum=Number(tope)||0;
  // El riesgo abierto viene agrupado por moneda: sumar entre monedas no da
  // una cantidad de nada. Si el backend viejo todavía manda un número
  // plano, se dibuja como una sola fila en pesos en vez de romper la
  // pestaña, que es el mismo criterio que la caja y el tablero global.
  const totales=Array.isArray(data?.riesgo_total_abierto)
    ? data.riesgo_total_abierto
    : (data?.riesgo_total_abierto!=null
        ? [{moneda:"ARS",total:data.riesgo_total_abierto}] : []);
  const variasMonedas=totales.length>1
    || new Set(lineas.map(l=>l.moneda||"ARS")).size>1;

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="shield-alert" size={14}/> Riesgo por evento</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:16,
        fontFamily:F_BODY}}>
        Cuánto habría que pagar si gana cada resultado, sumando los boletos
        activos. Sirve para suspender un mercado antes de que se desbalancee.</div>

      <GCard style={{padding:SPACING[16],marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>Riesgo total abierto</div>
            {cargando
              ? <div style={{color:Q.gold,fontSize:26,fontWeight:800,
                  fontFamily:F_BODY}}>…</div>
              : (totales.length
                  ? totales.map(t=>(
                      <div key={t.moneda} style={{color:Q.gold,fontSize:26,
                        fontWeight:800,fontFamily:F_BODY}}>
                        {money(t.total,t.moneda,{conCodigo:variasMonedas})}</div>
                    ))
                  : <div style={{color:Q.gold,fontSize:26,fontWeight:800,
                      fontFamily:F_BODY}}>{money(0,monedaTope)}</div>)}
          </div>
          <button onClick={cargar} style={{background:"transparent",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.muted,fontSize:12,cursor:"pointer"}}>↻ Actualizar</button>
        </div>

        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>
          Moneda de este tope</div>
        <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",marginBottom:6}}>
          {monedas.map(m=>(
            <button key={m} onClick={()=>setMonedaTope(m)} style={{
              background:monedaTope===m?`${Q.violet}33`
                :"rgba(255,255,255,0.04)",
              border:`1px solid ${monedaTope===m?Q.violet
                :(enUso.has(m)?`${Q.cyan}55`:Q.border)}`,
              borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
              color:monedaTope===m?Q.cyan:(enUso.has(m)?Q.text:Q.muted),
              fontSize:12,fontWeight:monedaTope===m?700:400,
              fontFamily:F_BODY}}>
              {m}{enUso.has(m)&&<span style={{color:Q.cyan,fontSize:12,
                marginLeft:3}}>●</span>}</button>
          ))}
        </div>
        <div style={{color:Q.dim,fontSize:12,marginBottom:10}}>
          ● en uso por alguna agencia</div>

        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>
          Tope por resultado en {monedaTope} (0 = sin tope)</div>
        <input value={tope} onChange={e=>setTope(e.target.value.replace(/\D/g,""))}
          placeholder={monedaTope==="ARS"?"Ej: 500000":"Ej: 2000"}
          inputMode="numeric" style={inp}/>
        <div style={{color:Q.dim,fontSize:12,margin:"6px 0 10px",
          lineHeight:1.5,fontFamily:F_BODY}}>
          Cada moneda tiene su propio tope. Una agencia en una moneda
          sin tope cargado usa el de pesos, que va a estar mal escalado.</div>

        <button onClick={()=>setActivo(a=>!a)} style={{width:"100%",
          background:activo?`${Q.green}22`:"rgba(255,255,255,0.04)",
          border:`1px solid ${activo?Q.green:Q.border}`,borderRadius:RADII.md,
          padding:"12px",cursor:"pointer",marginBottom:10,
          color:activo?Q.green:Q.muted,fontSize:13,fontWeight:700,
          fontFamily:F_BODY}}>
          {activo?<><Icon name="circle-check" size={13}/> Control activo: rechaza apuestas que pasen el tope</>
                 :<><CircleOff size={13}/> Control desactivado: solo mira, no rechaza</>}</button>

        <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
          color={Q.violet} full disabled={proc}/>
        {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginTop:8,
          textAlign:"center"}}>
          <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

        {activo&&topeNum===0&&(
          <div style={{color:Q.amber,fontSize:12,marginTop:8,lineHeight:1.5}}>
            El control está activo pero el tope es 0, o sea sin límite:
            no va a rechazar nada. Poné un monto para que tenga efecto.</div>
        )}
      </GCard>

      {/* Los topes ya cargados. Sin esto había que ir moneda por moneda
          para saber cuáles estaban configurados. */}
      {topes&&(topes.topes||[]).some(t=>t.tope>0)&&(
        <GCard style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:8,
            fontFamily:F_BODY}}>
            Topes cargados</div>
          {topes.topes.filter(t=>t.tope>0).map(t=>(
            <div key={t.moneda} style={{display:"flex",
              justifyContent:"space-between",alignItems:"center",
              padding:"8px 0",borderTop:`1px solid ${Q.border}`}}>
              <div>
                <span style={{color:t.moneda===monedaTope?Q.cyan:Q.text,
                  fontWeight:700,fontSize:12.5,
                  fontFamily:F_BODY}}>{t.moneda}</span>
                <span style={{color:Q.dim,fontSize:12,marginLeft:7}}>
                  {fechaHora(t.fecha)}</span>
              </div>
              <span style={{color:Q.gold,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>
                {/* El tope está en `t.moneda`, la clave de la fila, que ya
                    se escribe a la izquierda. `Math.round` se va porque
                    `money` redondea a la precisión de la moneda, y en una
                    de dos decimales redondear a entero era perder plata al
                    dibujarla. */}
                {money(t.tope, t.moneda)}</span>
            </div>
          ))}

          {(topes.sin_tope||[]).length>0&&(
            <div style={{color:Q.amber,fontSize:12,marginTop:10,
              paddingTop:SPACING[8],borderTop:`1px solid ${Q.border}`,lineHeight:1.5,
              fontFamily:F_BODY}}>
              Sin tope propio: {topes.sin_tope.join(", ")}. Esas agencias
              usan el de pesos, que va a estar mal escalado.</div>
          )}
        </GCard>
      )}

      {cargando&&(
        <div style={{color:Q.muted,fontSize:13,textAlign:"center",
          padding:"20px 0"}}>Cargando…</div>
      )}

      {!cargando&&!lineas.length&&(
        <GCard style={{padding:"24px 20px",textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:13,lineHeight:1.6}}>
            Todavía no hay exposición registrada.<br/>
            Se llena con las apuestas nuevas que entren por la app.</div>
        </GCard>
      )}

      {lineas.map((l,i)=>{
        const mon=l.moneda||"ARS";
        // El tope contra el que se mira ESTA línea. Para la moneda
        // seleccionada se usa el del input, que todavía no está guardado:
        // así se puede ver a qué líneas afectaría el tope antes de
        // apretar Guardar. Para las demás, el que ya está cargado y vino
        // del backend. Lo que NO puede pasar es mirar una línea en una
        // moneda contra el tope de otra: ése era el defecto.
        const topeL=mon===monedaTope?topeNum:Number(l.tope)||0;
        const pasa=topeL>0&&l.riesgo>topeL;
        return(
          <GCard key={i} glow={pasa?Q.red:null}
            style={{padding:"12px 16px",marginBottom:8}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"baseline",gap:SPACING[12]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:13.5,fontWeight:700,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                  fontFamily:F_BODY}}>{l.seleccion}</div>
                <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                  {l.mercado} · {l.boletos} {l.boletos===1?"boleto":"boletos"}
                  {l.sport_key?` · ${l.sport_key}`:""}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{color:pasa?Q.red:Q.gold,fontSize:18,fontWeight:800,
                  fontFamily:F_BODY}}>
                  {money(l.riesgo,mon,{conCodigo:variasMonedas})}</div>
                {pasa&&<div style={{color:Q.red,fontSize:12,fontWeight:700}}>
                  SUPERA EL TOPE</div>}
              </div>
            </div>
            {topeL>0&&(
              <div style={{height:3,borderRadius:RADII.sm,background:"rgba(255,255,255,0.07)",
                marginTop:8,overflow:"hidden"}}>
                <div style={{height:"100%",
                  width:`${Math.min(100,(l.riesgo/topeL)*100)}%`,
                  background:pasa?Q.red:(l.riesgo/topeL>0.7?Q.amber:Q.green)}}/>
              </div>
            )}
          </GCard>
        );
      })}

      {!cargando&&lineas.length>0&&(
        <div style={{color:Q.muted,fontSize:12,lineHeight:1.6,marginTop:12,
          padding:"0 4px"}}>
          En una combinada el premio completo cuenta contra cada selección,
          porque tienen que ganar todas. Por eso la suma de esta lista es
          mayor que la plata que realmente saldría: cada línea es el peor
          caso de ese resultado por separado.</div>
      )}
    </div>
  );
}

function TabLimites({ adminKey, onNoAutorizado }){
  const [limites,setLimites]=useState([]);
  const [agencias,setAgencias]=useState([]);
  const [form,setForm]=useState({alcance:"global",agencia_code:"",
    moneda:"ARS",monto_min:"",monto_max:"",pago_max:""});
  // Todas las monedas que el sistema maneja, para poder dejar los
  // límites cargados antes de abrir la primera agencia con esa moneda.
  // Las que ya están en uso se marcan, para saber cuáles importan.
  const [MONEDAS,setMonedas]=useState(["ARS"]);
  const enUso=new Set((agencias||[]).map(a=>a.moneda).filter(Boolean));
  useEffect(()=>{
    fetch(`${API}/api/monedas`).then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.monedas?.length)
        setMonedas(d.monedas.map(m=>m.codigo)); })
      .catch(()=>{});
  },[]);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API}/api/admin/limites`,{headers:adminHeaders(adminKey)});
      if(r.status===401){ onNoAutorizado(); return; }
      if(r.ok) setLimites((await r.json()).limites||[]);
      const ra=await fetch(`${API}/api/admin/arbol`,{headers:adminHeaders(adminKey)});
      if(ra.ok){ const d=await ra.json();
        // La moneda se conserva: es lo que alimenta el selector
        const cruda=Array.isArray(d)?d:(d.agencias||[]);
        const lista=cruda.map(a=>({code:a.code,name:a.name||a.code,
                                   moneda:a.moneda}));
        setAgencias(lista);
      }
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const guardar=async()=>{
    if(form.alcance!=="global"&&!form.agencia_code){ setMsg({text:"Elegí una agencia",ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API}/api/admin/limites`,{
        method:"POST",headers:{"Content-Type":"application/json",...adminHeaders(adminKey)},
        body:JSON.stringify(form),
      });
      if(r.status===401){ onNoAutorizado(); return; }
      const d=await r.json();
      if(r.ok&&d.ok){ setMsg({text:`Guardado${d.aplicado_a?` (${d.aplicado_a} agencias)`:""}`,ok:true});
        setForm(f=>({...f,monto_min:"",monto_max:"",pago_max:""})); cargar(); }
      else setMsg({text:(d.detail||"Error"),ok:false});
    }catch(e){ setMsg({text:"Error",ok:false}); }
    setProc(false);
  };

  const borrar=async(id)=>{
    try{
      await fetch(`${API}/api/admin/limites/${id}`,{method:"DELETE",headers:adminHeaders(adminKey)});
      cargar();
    }catch(e){}
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
    borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:800,fontSize:17,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="sliders-horizontal" size={14}/> Límites de apuestas</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:16,
        fontFamily:F_BODY}}>
        Monto mínimo, máximo y pago máximo. Aplicá a toda la red, a una rama
        (agencia + descendientes) o a una agencia sola.</div>

      <GCard style={{padding:SPACING[16],marginBottom:16}}>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Alcance</div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
          {[["global",<><Globe size={13}/> Toda la red</>],["rama",<><GitBranch size={13}/> Una rama</>],["agencia",<><Building2 size={13}/> Una agencia</>]].map(([k,l])=>(
            <button key={k} onClick={()=>setForm(f=>({...f,alcance:k}))} style={{flex:1,
              background:form.alcance===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${form.alcance===k?Q.violet:Q.border}`,borderRadius:RADII.md,
              padding:"8px 4px",cursor:"pointer",color:form.alcance===k?Q.violet2||Q.violet:Q.muted,
              fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>
        {/* Si ya hay un límite para esa combinación, se está editando */}
        {(()=>{
          const ya=limites.find(l=>l.alcance===form.alcance&&
            (form.alcance==="global"
              ? (l.moneda||"ARS")===form.moneda
              : l.agencia_code===form.agencia_code));
          return ya?(
            <div style={{background:`${Q.cyan}12`,
              border:`1px solid ${Q.cyan}44`,borderRadius:RADII.md,
              padding:"8px 12px",marginBottom:10,fontSize:12,
              color:Q.cyan,lineHeight:1.5,
              fontFamily:F_BODY}}>
              Ya existe un límite para esto. Al guardar se reemplaza:
              Min {ya.monto_min!=null?money(ya.monto_min, ya.moneda):"—"} ·
              Max {ya.monto_max!=null?money(ya.monto_max, ya.moneda):"—"}</div>
          ):null;
        })()}

        {form.alcance==="global"&&(
          <>
              <div style={{color:Q.muted,fontSize:12,marginBottom:4,
                fontFamily:F_BODY}}>
                Moneda de estos límites</div>
              <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",
                marginBottom:10}}>
                {MONEDAS.map(m=>(
                  <button key={m} onClick={()=>setForm(f=>({...f,moneda:m}))}
                    style={{
                    background:form.moneda===m?`${Q.violet}33`
                      :"rgba(255,255,255,0.04)",
                    border:`1px solid ${form.moneda===m?Q.violet
                      :(enUso.has(m)?`${Q.cyan}55`:Q.border)}`,
                    borderRadius:RADII.md,padding:"8px 12px",cursor:"pointer",
                    color:form.moneda===m?Q.cyan
                      :(enUso.has(m)?Q.text:Q.muted),fontSize:12,
                    fontWeight:form.moneda===m?700:400,
                    fontFamily:F_BODY}}>
                    {m}{enUso.has(m)&&<span style={{color:Q.cyan,
                      fontSize:12,marginLeft:3}}>●</span>}</button>
                ))}
              </div>
              <div style={{color:Q.dim,fontSize:12,marginBottom:8}}>
                ● en uso por alguna agencia</div>
              <div style={{color:Q.dim,fontSize:12,marginBottom:10,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Cada moneda lleva sus propios números. Una agencia en una
                moneda sin límite cargado usa el de pesos, que va a estar
                mal escalado.</div>
            </>
        )}

        {form.alcance!=="global"&&(
          <select value={form.agencia_code} onChange={e=>setForm(f=>({...f,agencia_code:e.target.value}))}
            style={inp}>
            <option value="">Elegí la agencia{form.alcance==="rama"?" (raíz de la rama)":""}</option>
            {agencias.map(a=>(<option key={a.code} value={a.code}>{a.code} · {a.name}</option>))}
          </select>
        )}
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,marginTop:4,
          fontFamily:F_BODY}}>Monto mínimo por apuesta</div>
        <input value={form.monto_min} onChange={e=>setForm(f=>({...f,monto_min:e.target.value.replace(/\D/g,"")}))}
          placeholder="Ej: 100" inputMode="numeric" style={inp}/>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Monto máximo por apuesta</div>
        <input value={form.monto_max} onChange={e=>setForm(f=>({...f,monto_max:e.target.value.replace(/\D/g,"")}))}
          placeholder="Ej: 500000" inputMode="numeric" style={inp}/>
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>Pago máximo por apuesta</div>
        <input value={form.pago_max} onChange={e=>setForm(f=>({...f,pago_max:e.target.value.replace(/\D/g,"")}))}
          placeholder="Ej: 5000000" inputMode="numeric" style={inp}/>
        <button onClick={guardar} disabled={proc} style={{width:"100%",marginTop:6,
          background:`linear-gradient(135deg,${Q.violet},${Q.cyan})`,border:"none",
          borderRadius:RADII.md,padding:"12px",color:"#fff",fontWeight:700,fontSize:14,
          cursor:"pointer",fontFamily:F_BODY}}>
          {proc?"Guardando...":"Guardar límite"}</button>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>

      <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
        marginBottom:8,marginLeft:4,fontFamily:F_BODY}}>Configurados</div>
      {limites.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Sin límites configurados</div>}
      {/* Los globales van destacados: son la referencia de toda la red */}
      {limites.map((l)=>(
        <GCard key={l.id} glow={l.alcance==="global"?Q.cyan:null}
          style={{padding:"12px 12px",marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:12,
                fontFamily:F_BODY}}>
                {l.alcance==="global"?<><Globe size={11}/> Toda la red · {l.moneda||"ARS"}</>
                  :<><Building2 size={11}/> {l.agencia_code}</>}
                {l.agencia_nombre?` · ${l.agencia_nombre}`:""}</div>
              <div style={{color:Q.muted,fontSize:12,
                fontFamily:F_BODY}}>
                Min {l.monto_min!=null?money(l.monto_min, l.moneda):"—"} · Max {l.monto_max!=null?money(l.monto_max, l.moneda):"—"} · Pago máx {l.pago_max!=null?money(l.pago_max, l.moneda):"—"}</div>
            </div>
            <button onClick={()=>{
                // Carga los valores al formulario para ajustarlos sin
                // tener que borrar y volver a crear.
                setForm({
                  alcance:l.alcance,
                  agencia_code:l.agencia_code||"",
                  moneda:l.moneda||"ARS",
                  monto_min:l.monto_min!=null?String(l.monto_min):"",
                  monto_max:l.monto_max!=null?String(l.monto_max):"",
                  pago_max:l.pago_max!=null?String(l.pago_max):"",
                });
                setMsg({text:"Valores cargados arriba: ajustá y guardá",ok:false});
                window.scrollTo({top:0,behavior:"smooth"});
              }} style={{background:"transparent",
                border:`1px solid ${Q.cyan}55`,borderRadius:RADII.sm,
                padding:"4px 12px",cursor:"pointer",color:Q.cyan,
                fontSize:12,marginRight:6,flexShrink:0,
                fontFamily:F_BODY}}>Editar</button>

            {l.alcance!=="global"&&(
              <button onClick={()=>borrar(l.id)} style={{background:"transparent",
                border:`1px solid ${Q.red}55`,borderRadius:RADII.md,padding:"4px 12px",
                color:Q.red,fontSize:12,cursor:"pointer"}}>Quitar</button>
            )}
          </div>
        </GCard>
      ))}
    </div>
  );
}

function AdminPanel({ adminKey, onLogout }){
  const [tab,setTab]=useState("global");
  // At 1024px and up (the prototype's own breakpoint) the tab bar becomes
  // a sidebar; below it nothing changes — see odd/tasks/desktop-shell.md.
  const isDesktop=useDesktopShellWidth();
  const [menuOpen,setMenuOpen]=useState(false);
  // Alertas de riesgo abiertas, para el indicador de la pestaña Config.
  // Se refresca cada 2 minutos: si algo crítico aparece, se ve sin
  // tener que entrar a buscarlo.
  const [alertasRiesgo,setAlertasRiesgo]=useState(0);
  useEffect(()=>{
    if(!adminKey) return;
    let vivo=true;
    const traer=()=>fetch(`${API}/api/admin/riesgo/pendientes`,
        {headers:adminHeaders(adminKey)})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!vivo||!d) return;
        // Suena solo cuando aparecen alertas nuevas, no en cada
        // consulta: repetir el sonido cada dos minutos es la forma
        // más rápida de que lo silencien todo.
        setAlertasRiesgo(prev=>{
          const n=d.urgentes||0;
          if(n>prev&&prev>=0) sonarAlerta((d.por_severidad?.critica||0)>0);
          return n;
        });
      })
      .catch(()=>{});
    traer();
    const t=setInterval(traer,120000);
    return()=>{ vivo=false; clearInterval(t); };
  },[adminKey]);
  return(
    <div style={isDesktop ? {
      background:Q.void,minHeight:"100vh",
      fontFamily:"system-ui,-apple-system,sans-serif",
      display:"grid",gridTemplateColumns:"264px minmax(0,1fr)",
      gridTemplateRows:"auto 1fr",maxWidth:1600,margin:"0 auto",
    } : {background:Q.void,minHeight:"100vh",overflowX:"hidden",
      fontFamily:"system-ui,-apple-system,sans-serif"}}>
      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        backgroundImage:`linear-gradient(${Q.violet}04 1px,transparent 1px),linear-gradient(90deg,${Q.violet}04 1px,transparent 1px)`,
        backgroundSize:"28px 28px"}}/>

      <div style={isDesktop ? {background:Q.deep,borderBottom:`1px solid ${Q.border}`,
        padding:"12px 16px",display:"flex",alignItems:"center",
        justifyContent:"space-between",position:"sticky",top:0,zIndex:50,overflow:"hidden",minHeight:64,
        gridColumn:"2",gridRow:"1"} : {background:Q.deep,borderBottom:`1px solid ${Q.border}`,
        padding:"12px 16px",display:"flex",alignItems:"center",
        justifyContent:"space-between",position:"sticky",top:0,zIndex:50,overflow:"hidden"}}>
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:1,
          background:`linear-gradient(90deg,transparent,${Q.violet},${Q.cyan},${Q.violet},transparent)`}}/>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12],minWidth:0}}>
          {/* Mobile only: opens MobileTabMenu, the megamenu that replaces
              the fixed bottom tab bar below 1024px. */}
          {!isDesktop&&(
            <button onClick={()=>setMenuOpen(true)} aria-label="Abrir menú" style={{
              background:"transparent",border:"none",color:Q.text,
              cursor:"pointer",padding:SPACING[4],display:"flex",flexShrink:0}}>
              <Icon name="menu" size={22}/>
            </button>
          )}
          <QPLogo size={isDesktop?40:16}/>
          {isDesktop&&<span style={{color:Q.dim,fontSize:TEXT[20],
            fontWeight:300,lineHeight:1,fontFamily:F_BODY}}>|</span>}
          {isDesktop&&<span style={{color:Q.muted,fontSize:TEXT[14],fontWeight:600,
            whiteSpace:"nowrap",fontFamily:F_BODY}}>Panel Administrador</span>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12]}}>
          <HBadge label="ADMIN" color={Q.violet}/>
          <button onClick={onLogout} style={{background:"transparent",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"4px 12px",
            color:Q.muted,fontSize:12,cursor:"pointer"}}>Salir</button>
        </div>
      </div>

      {/* `width:"100%"` is load-bearing, not redundant. This div is a direct
          grid item, and a grid item with `margin:0 auto` has its auto margins
          absorb the column's free space, which overrides the default stretch
          and leaves the box sized to its own content. Admin's cards are
          narrow, so the whole panel collapsed to about a third of the column
          and read as a phone inside a desktop. Agencia never showed this
          because its wrapper sits inside a scroll container, not in the grid.
          With an explicit width the box fills the column, stops at 1100, and
          the auto margins only centre what is left over. */}
      <div style={isDesktop ? {padding:"16px",width:"100%",maxWidth:1100,margin:"0 auto",
        position:"relative",zIndex:1,paddingBottom:"40px",
        gridColumn:"2",gridRow:"2"} : {padding:"16px",maxWidth:620,margin:"0 auto",
        position:"relative",zIndex:1,
        // No longer reserving room for a fixed bottom tab bar — the
        // megamenu behind the header's hamburger replaced it, and it is
        // an overlay, not a persistent fixed element.
        paddingBottom:"calc(28px + env(safe-area-inset-bottom))"}}>
        {tab==="global"   &&<TabGlobal   adminKey={adminKey} onNoAutorizado={onLogout} onIr={setTab}/>}
        {tab==="cierre"   &&<TabCierre   adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="combos"   &&<TabCombos   adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="eventos"  &&<TabEventos  adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="agencias" &&<TabAgencias adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="influencers"&&<TabInfluencers adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="billetera"&&<TabBilletera adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="usuarios" &&<TabUsuarios adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="config"   &&<TabConfig   adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="diag"     &&<TabDiag adminKey={adminKey} onNoAutorizado={onLogout}/>}
        {tab==="chat"     &&<PanelComunicacion adminKey={adminKey} onNoAutorizado={onLogout}/>}
      </div>

      {/* Below 1024px this sidebar is gone entirely, replaced by the
          hamburger's MobileTabMenu overlay — same treatment as Agencia's
          panel. Desktop keeps exactly the sidebar it already had. */}
      {isDesktop&&(
      <div style={{
        gridColumn:"1",gridRow:"1 / span 2",position:"sticky",top:0,
        alignSelf:"start",width:264,height:"100vh",
        background:"rgba(6,6,18,0.97)",backdropFilter:"blur(20px)",
        borderRight:`1px solid ${Q.border}`,
        display:"flex",flexDirection:"column",alignItems:"stretch",
        gap:SPACING[4],padding:`${SPACING[24]}px ${SPACING[12]}px`,
        overflowY:"auto",zIndex:50,
      }}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:1,
          background:`linear-gradient(90deg,transparent,${Q.violet},${Q.cyan},${Q.violet},transparent)`}}/>
        {TAB_GROUPS.flatMap((group,gi)=>{
          const groupTabs = group.keys.map(k=>TABS.find(t=>t.k===k)).filter(Boolean);
          if(groupTabs.length===0) return [];
          return [
            <div key={`group-${group.label}`} style={{
              marginTop:gi===0?0:SPACING[16],
              padding:"0 12px",
              color:Q.dim,fontSize:TEXT[12],fontWeight:700,
              textTransform:"uppercase",letterSpacing:1,fontFamily:F_BODY,
            }}>{group.label}</div>,
            ...groupTabs.map(t=>(
              <button key={t.k} onClick={()=>setTab(t.k)} style={{
                minWidth:0,
                background:tab===t.k?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:"transparent",
                border:`1px solid ${tab===t.k?Q.violet:"transparent"}`,
                borderRadius:RADII.md,
                padding:"0 12px",minHeight:44,cursor:"pointer",
                display:"flex",flexDirection:"row",alignItems:"center",
                justifyContent:"flex-start",gap:SPACING[12],
                position:"relative",overflow:"visible",width:"100%",flexShrink:0,
              }}>
                <span style={{fontSize:17,position:"relative",
                  filter:tab===t.k?`drop-shadow(0 0 6px ${Q.cyan})`:"none"}}>
                  {t.i}
                  {t.k==="config"&&alertasRiesgo>0&&(
                    <span style={{position:"absolute",top:-3,right:-8,
                      background:Q.red,color:"#fff",borderRadius:RADII.md,
                      minWidth:15,height:15,fontSize:12,fontWeight:800,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      padding:"0 4px",lineHeight:1,
                      fontFamily:F_BODY}}>
                      {alertasRiesgo>99?"99+":alertasRiesgo}</span>
                  )}
                </span>
                <span style={{color:tab===t.k?Q.cyan:Q.muted,fontSize:12,
                  fontWeight:tab===t.k?700:400,maxWidth:"100%",
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                  fontFamily:F_BODY,letterSpacing:0.3,
                  textTransform:"uppercase"}}>{t.l}</span>
              </button>
            )),
          ];
        })}
      </div>
      )}

      <MobileTabMenu open={menuOpen} onClose={()=>setMenuOpen(false)}
        groups={TAB_GROUPS} tabs={TABS} activeTab={tab} onSelect={setTab}
        badges={{config:alertasRiesgo}}/>
    </div>
  );
}

export default function QuartzAdmin(){
  // La clave se guarda en el navegador para no perder la sesión al recargar.
  const [adminKey,setAdminKey]=useState(()=>{
    try{ return localStorage.getItem("qp_admin_key") || null; }catch(e){ return null; }
  });

  const entrar=(k)=>{
    try{ localStorage.setItem("qp_admin_key", k); }catch(e){}
    setAdminKey(k);
  };
  const salir=()=>{
    try{ localStorage.removeItem("qp_admin_key"); }catch(e){}
    setAdminKey(null);
  };

  // Evitar que el botón "atrás" del navegador saque del admin.
  // Mientras haya sesión, atrás vuelve dentro de la app en vez de salir.
  useEffect(()=>{
    if(!adminKey) return;
    try{ window.history.pushState({qp:true},""); }catch(e){}
    const onPop=(e)=>{
      // Al tocar atrás, re-empujamos el estado para no abandonar la página.
      try{ window.history.pushState({qp:true},""); }catch(err){}
    };
    window.addEventListener("popstate", onPop);
    return ()=>window.removeEventListener("popstate", onPop);
  },[adminKey]);

  return(
    <div style={{background:Q.void,minHeight:"100vh"}}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        input:focus{outline:none} button:active{opacity:.85}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:rgba(124,58,237,0.3)}
      `}</style>
      {!adminKey
        ?<AdminLogin onLogin={entrar}/>
        :<AdminPanel adminKey={adminKey} onLogout={salir}/>
      }
    </div>
  );
}
